(() => {
  try {
    const root = document.querySelector("[data-gd-configurator]");
    if (!root) return;

    const configEl = root.querySelector("[data-gd-config]");
    const entryConfigEl = root.querySelector("[data-gd-entry-config]");
    if (!configEl) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent || "{}");
    } catch (error) {
      console.error("[gd-configurator] invalid JSON config", error);
      return;
    }

    let entryConfig = {};
    if (entryConfigEl) {
      try {
        entryConfig = JSON.parse(entryConfigEl.textContent || "{}");
      } catch (error) {
        console.warn("[gd-configurator] invalid entry JSON config", error);
        entryConfig = {};
      }
    }

    const previewEl = root.querySelector("[data-gd-preview]");
    const previewSummaryEl = root.querySelector("[data-gd-preview-summary]");
    const designSidebarEl = root.querySelector("[data-gd-design-sidebar]");
    const railEl = root.querySelector("[data-gd-rail]");
    const layoutEl = root.querySelector(".gd-configurator__layout");
    const dockEl = root.querySelector("[data-gd-dock]");
    const dockTotalEl = root.querySelector("[data-gd-dock-total]");
    const dockHintEl = root.querySelector("[data-gd-dock-hint]");
    const dockAddButton = root.querySelector("[data-gd-dock-add]");

    if (!previewEl || !railEl || !dockEl || !dockTotalEl || !dockHintEl || !dockAddButton) {
      console.warn("[gd-configurator] required mount nodes are missing");
      return;
    }

    function escapeHtml(value) {
      return String(value ?? "").replace(/[&<>"']/g, (char) => {
        switch (char) {
          case "&":
            return "&amp;";
          case "<":
            return "&lt;";
          case ">":
            return "&gt;";
          case '"':
            return "&quot;";
          case "'":
            return "&#39;";
          default:
            return char;
        }
      });
    }

    function setPreviewSummaryMarkup(markup) {
      if (!previewSummaryEl) return;
      previewSummaryEl.innerHTML = String(markup || "");
    }

    function escapeSvg(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
    }

    function showOrderSuccessModal(message, referenceNumber = "") {
      return new Promise((resolve) => {
        const existing = document.querySelector("[data-gd-order-success-modal]");
        if (existing) {
          existing.remove();
        }

        const overlay = document.createElement("div");
        overlay.className = "gd-order-success-modal";
        overlay.setAttribute("data-gd-order-success-modal", "true");
        overlay.setAttribute("role", "dialog");
        overlay.setAttribute("aria-modal", "true");

        const dialog = document.createElement("div");
        dialog.className = "gd-order-success-modal__dialog";

        const title = document.createElement("h3");
        title.className = "gd-order-success-modal__title";
        title.textContent = "Quote submitted";

        const body = document.createElement("p");
        body.className = "gd-order-success-modal__message";
        body.textContent = message;

        const safeReferenceNumber = String(referenceNumber || "").trim();
        const reference = safeReferenceNumber
          ? (() => {
              const element = document.createElement("p");
              element.className = "gd-order-success-modal__reference";
              element.textContent = safeReferenceNumber;
              return element;
            })()
          : null;

        const button = document.createElement("button");
        button.className = "gd-order-success-modal__button";
        button.type = "button";
        button.textContent = "OK";

        const closeModal = () => {
          overlay.remove();
          resolve();
        };

        overlay.addEventListener("click", (event) => {
          if (event.target === overlay) {
            closeModal();
          }
        });

        button.addEventListener("click", closeModal);

        dialog.appendChild(title);
        dialog.appendChild(body);
        if (reference) {
          dialog.appendChild(reference);
        }
        dialog.appendChild(button);
        overlay.appendChild(dialog);

        document.body.appendChild(overlay);
      });
    }

    function dedupeTextValues(values) {
      if (!Array.isArray(values)) return [];
      const next = [];
      values.forEach((value) => {
        const text = String(value || "").trim();
        if (!text || next.includes(text)) return;
        next.push(text);
      });
      return next;
    }

    function buildPreviewImageUploadLabel(value, index) {
      const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      return normalized || `door-${index + 1}`;
    }

    function getSelectedTrackPackageLabel() {
      const trackStep =
        getVisibleSteps().find((step) => isTrackSystemStep(step)) ||
        steps.find((step) => isTrackSystemStep(step)) ||
        null;
      if (!trackStep) return "";

      const selectedOption = getSelectedOptionFromStep(trackStep);
      if (!selectedOption) return "";
      if (isNoTrackOption(selectedOption)) return "No Track";
      return String(selectedOption.label || "Track").trim();
    }

    function hasTrackSystemStep() {
      return steps.some((step) => isTrackSystemStep(step));
    }

    const SPECIAL_TRACK_FIELDS = Object.freeze([
      { key: "hardwareType", label: "Hardware Type", options: ['2"', '3"'] },
      { key: "mountType", label: "Mount Type", options: ["Angle Mount", "Bracket Mount"] },
      { key: "mountTo", label: "Mount to", options: ["Wood", "Steel", "Concrete"] },
      { key: "springCycle", label: "Springs Type", options: ["Std 10k", "20k", "50k", "100k"] },
      { key: "trackSetup", label: "Track Setup", options: ["Std 12R", "Std 15R", "High Lift", "Vertical Lift", "Follow the Pitch", "LHR", "Zero Clearance"] }
    ]);

    function normalizeSpecialTrackConfig(source) {
      const value = source && typeof source === "object" ? source : {};
      const normalized = {};
      SPECIAL_TRACK_FIELDS.forEach((field) => {
        normalized[field.key] = field.options.includes(value[field.key]) ? value[field.key] : "";
      });
      ["floorToCeilingIn", "floorToObstructionIn", "pitchAngle"].forEach((key) => {
        normalized[key] = String(value[key] ?? "").trim().slice(0, 32);
      });
      normalized.pitchAngle = normalized.pitchAngle.replace(/\s+/g, "");
      return normalized;
    }

    function isSpecialTrackSelected() {
      const step = steps.find((item) => isTrackSystemStep(item));
      const option = step ? getSelectedOptionFromStep(step) : null;
      return Boolean(option && isTrackTokenMatch(getTrackOptionSearchText(option), "special"));
    }

    function getSpecialTrackProfile() {
      if (!isSpecialTrackSelected()) return null;
      const profile = normalizeSpecialTrackConfig(state.specialTrack);
      if (profile.trackSetup !== "High Lift") profile.floorToCeilingIn = "";
      if (profile.trackSetup !== "Follow the Pitch") {
        profile.floorToObstructionIn = "";
        profile.pitchAngle = "";
      }
      return profile;
    }

    function getSpecialTrackSummaryFields(profile = getSpecialTrackProfile()) {
      if (!profile) return [];
      return [
        ...SPECIAL_TRACK_FIELDS.map((field) => ({ label: field.label, value: profile[field.key] || "" })),
        { label: "Floor to ceiling", value: profile.floorToCeilingIn ? `${profile.floorToCeilingIn} in` : "" },
        { label: "Floor to first obstruction", value: profile.floorToObstructionIn ? `${profile.floorToObstructionIn} in` : "" },
        { label: "Pitch angle", value: profile.pitchAngle || "" }
      ].filter((field) => field.value);
    }

    function getSpecialTrackValidationIssue(profile = getSpecialTrackProfile()) {
      if (!profile) return null;
      const missing = SPECIAL_TRACK_FIELDS.find((field) => !field.options.includes(profile[field.key]));
      if (missing) return { field: missing.key, message: `Select ${missing.label} for this Special Track order.` };
      const validInches = (value) => /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(value) && Number(value) > 0 && Number.isFinite(Number(value));
      if (profile.trackSetup === "High Lift" && !validInches(profile.floorToCeilingIn)) {
        return { field: "floorToCeilingIn", message: "Enter floor to ceiling in inches, greater than 0." };
      }
      if (profile.trackSetup === "Follow the Pitch") {
        if (!validInches(profile.floorToObstructionIn)) {
          return { field: "floorToObstructionIn", message: "Enter floor to first obstruction in inches, greater than 0." };
        }
        const pitch = String(profile.pitchAngle || "").match(/^(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)$/);
        if (!pitch || !Number.isFinite(Number(pitch[1])) || !Number.isFinite(Number(pitch[2])) || Number(pitch[2]) <= 0) {
          return { field: "pitchAngle", message: "Enter pitch as rise/run, for example 5/12." };
        }
      }
      return null;
    }

    function showSpecialTrackValidationIssue(issue) {
      if (!issue) return;
      state.designNavSectionId = "track";
      const step = steps.find((item) => isTrackSystemStep(item));
      if (step) state.mobileOpenStepId = step.id;
      setHint(issue.message, "error");
      render();
      const input = railEl.querySelector(`[data-gd-special-track-field="${issue.field}"]`);
      if (input) {
        input.setCustomValidity(issue.message);
        input.focus();
        input.reportValidity();
      }
    }

    function renderSpecialTrackFields() {
      if (!isSpecialTrackSelected()) return "";
      const value = normalizeSpecialTrackConfig(state.specialTrack);
      const selectMarkup = SPECIAL_TRACK_FIELDS.map((field) => `
        <label class="gd-special-track__field">
          <span>${escapeHtml(field.label)} <span aria-hidden="true">*</span></span>
          <select data-gd-special-track-field="${field.key}" required>
            <option value="">Select ${escapeHtml(field.label.toLowerCase())}</option>
            ${field.options.map((option) => `<option value="${escapeHtml(option)}"${value[field.key] === option ? " selected" : ""}>${escapeHtml(option)}</option>`).join("")}
          </select>
        </label>`).join("");
      const inchesInput = (key, label) => `
        <label class="gd-special-track__field">
          <span>${escapeHtml(label)} (inches) <span aria-hidden="true">*</span></span>
          <input type="text" inputmode="decimal" maxlength="32" data-gd-special-track-field="${key}" value="${escapeHtml(value[key])}" placeholder="e.g. 120" required>
        </label>`;
      const conditionalMarkup = value.trackSetup === "High Lift"
        ? inchesInput("floorToCeilingIn", "Floor to ceiling")
        : value.trackSetup === "Follow the Pitch"
          ? `${inchesInput("floorToObstructionIn", "Floor to first obstruction")}
            <label class="gd-special-track__field">
              <span>Pitch angle (rise/run) <span aria-hidden="true">*</span></span>
              <input type="text" maxlength="32" data-gd-special-track-field="pitchAngle" value="${escapeHtml(value.pitchAngle)}" placeholder="e.g. 5/12" required>
            </label>`
          : "";
      return `
        <fieldset class="gd-special-track" data-gd-special-track>
          <legend>Special Track Order</legend>
          <p class="gd-special-track__note">Complete the required installation details for this door.</p>
          <div class="gd-special-track__grid">${selectMarkup}${conditionalMarkup}</div>
        </fieldset>`;
    }

    function handleSpecialTrackInput(event) {
      const input = event.target.closest("[data-gd-special-track-field]");
      if (!input || !railEl.contains(input)) return false;
      const key = input.getAttribute("data-gd-special-track-field");
      if (![...SPECIAL_TRACK_FIELDS.map((field) => field.key), "floorToCeilingIn", "floorToObstructionIn", "pitchAngle"].includes(key)) return false;
      const next = normalizeSpecialTrackConfig(state.specialTrack);
      next[key] = String(input.value || "").slice(0, 32);
      state.specialTrack = normalizeSpecialTrackConfig(next);
      input.setCustomValidity("");
      setHint("");
      if (event.type === "change" && input.tagName === "SELECT") {
        render();
        const replacement = railEl.querySelector(`[data-gd-special-track-field="${key}"]`);
        if (replacement) replacement.focus();
      } else {
        renderDock();
      }
      return true;
    }

    function buildDoorPanelStructure(doorConfig) {
      const value = doorConfig && typeof doorConfig === "object" ? doorConfig : {};
      return {
        panelCount: Math.max(0, Number(value.panelCount || 0)),
        panelHeightsIn: Array.isArray(value.panelHeightsIn)
          ? value.panelHeightsIn.map((item) => Number(item)).filter((item) => Number.isFinite(item) && item > 0)
          : [],
        panelRuleMatched: Boolean(value.panelRuleMatched),
        panelShortCount: Math.max(0, Number(value.panelShortCount || 0)),
        panelTallCount: Math.max(0, Number(value.panelTallCount || 0)),
        panelLargeCount: Math.max(0, Number(value.panelLargeCount || 0)),
        panelPreference: normalizeEntryPanelPreferenceValue(value.panelPreference),
        panelPreferenceLabel: formatEntryPanelPreferenceLabel(value.panelPreference)
      };
    }

    function buildDoorReviewSummary(door) {
      return withActiveDoorState(door, () => {
        const entryProfile = getEntryProfile();
        const windowProfile = getWindowSelectionProfile();
        const doorConfig = getCurrentDoorVisualConfig();
        const windowLayoutStep = getWindowLayoutStep();
        const windowPositionStep = getWindowPositionStep();
        const selectedLayout = getSelectedOptionFromStep(windowLayoutStep);
        const hasWindowLayout = Boolean(selectedLayout && !isNoWindowLayoutOption(selectedLayout));
        const windowGridRows = windowPositionStep ? getWindowGridRows(windowPositionStep, doorConfig) : [];
        const selectedCells = hasWindowLayout ? getOrderedWindowCells(doorConfig) : [];
        const selectedWindowCount = selectedCells.length;
        const trackSystemLabel = getSelectedTrackPackageLabel();
        const showTrackSystem = hasTrackSystemStep();
        const windowRows = selectedCells
          .map((cell) => {
            const config = cell && cell.config && typeof cell.config === "object" ? cell.config : {};
            return {
              layout: String(config.layoutLabel || "").trim(),
              position: buildWindowCellDisplayLabel(cell, windowGridRows),
              glass: String(config.glassLabel || "").trim(),
              color: String(config.colorLabel || "").trim(),
              insert: String(config.insertLabel || "").trim(),
              sku: String(config.variantSku || "").trim()
            };
          })
          .filter((row) => row.layout || row.position || row.glass || row.color || row.insert || row.sku);

        return {
          id: door && door.id ? String(door.id) : "",
          label: getDoorLabel(door),
          quantity: getDoorQuantityValue(door),
          entryProfile,
          windowProfile,
          windowRows,
          panelLayout: buildDoorPanelStructure(doorConfig),
          trackSystemLabel: showTrackSystem ? trackSystemLabel || "Not selected" : "",
          specialTrack: getSpecialTrackProfile(),
          selectedWindowCount,
          fields: [
            { label: "Product", value: entryProfile.productTitle || entryProfile.seriesLabel || "" },
            { label: "Quantity", value: entryProfile.quantityLabel || "" },
            { label: "Size", value: entryProfile.sizeLabel || "" },
            { label: "Panel Preference", value: entryProfile.panelPreferenceLabel || "" },
            { label: "Custom width", value: entryProfile.sizeMode === "custom" ? entryProfile.customWidth || "" : "" },
            { label: "Custom height", value: entryProfile.sizeMode === "custom" ? entryProfile.customHeight || "" : "" },
            { label: "Color", value: entryProfile.colorLabel || "" },
            { label: "SKU", value: entryProfile.variantSku || "" },
            { label: "Track System", value: showTrackSystem ? trackSystemLabel || "Not selected" : "" },
            ...getSpecialTrackSummaryFields(),
            {
              label: "Window Count",
              value: selectedWindowCount > 0 ? `${selectedWindowCount} window${selectedWindowCount === 1 ? "" : "s"}` : "0 (No Window)"
            },
            { label: "Window Layout", value: windowProfile.layoutLabel || "" },
            { label: "Window Glass", value: windowProfile.glassLabel || "" },
            { label: "Window Color", value: windowProfile.colorLabel || "" },
            { label: "Window Insert", value: windowProfile.insertLabel || "" },
            { label: "Window Positions", value: windowProfile.positionLabel || "" }
          ].filter((item) => String(item.value || "").trim() !== "")
        };
      });
    }

    function appendUniqueAttributeValue(map, key, rawValue) {
      if (!(map instanceof Map)) return;
      const normalizedKey = String(key || "").trim();
      const normalizedValue = String(rawValue || "").trim();
      if (!normalizedKey || !normalizedValue) return;
      if (!map.has(normalizedKey)) {
        map.set(normalizedKey, new Set());
      }
      map.get(normalizedKey).add(normalizedValue);
    }

    function finalizeAggregatedCustomAttributes(attributeMap) {
      if (!(attributeMap instanceof Map) || attributeMap.size === 0) return [];
      return Array.from(attributeMap.entries())
        .map(([key, values]) => {
          const list = Array.from(values.values()).filter(Boolean);
          if (list.length === 0) return null;
          return {
            key,
            value: list.join(" | ")
          };
        })
        .filter(Boolean);
    }

    function resolveOrderReviewTotalLabel(baseTotalCents = totalCents(), hasCustomPrice = hasAnyDoorWithoutPrice(), financials = null) {
      const normalizedBaseTotalCents = Math.max(0, Math.round(Number(baseTotalCents) || 0));
      const safeFinancials =
        financials && typeof financials === "object" ? financials : getOrderFinancialSummary(normalizedBaseTotalCents);
      const fallbackTotalLabel = hasCustomPrice ? "Custom Price" : formatMoney(normalizedBaseTotalCents);
      return hasCustomPrice && !safeFinancials.subtotalOverridden ? fallbackTotalLabel : safeFinancials.balanceLabel || fallbackTotalLabel;
    }

    function resolveOrderReviewConfirmLabel(
      baseTotalCents = totalCents(),
      hasCustomPrice = hasAnyDoorWithoutPrice(),
      financials = null
    ) {
      return "Quote Now";
    }

    function buildOrderReviewSnapshot(submission) {
      const prepared = submission && typeof submission === "object" ? submission : {};
      const financials =
        prepared.financials && typeof prepared.financials === "object"
          ? prepared.financials
          : getOrderFinancialSummary(prepared.totalCents);
      const baseTotalCents = Math.max(0, Math.round(Number(prepared.totalCents) || 0));
      const hasCustomPrice = Boolean(prepared.hasCustomPrice);
      const totalLabel = resolveOrderReviewTotalLabel(baseTotalCents, hasCustomPrice, financials);
      const doorSummaries =
        Array.isArray(prepared.doorSummaries) && prepared.doorSummaries.length > 0
          ? prepared.doorSummaries
          : getCommittedDoors().map((door) => buildDoorReviewSummary(door));

      return {
        totalLabel,
        confirmLabel: resolveOrderReviewConfirmLabel(baseTotalCents, hasCustomPrice, financials),
        submittingLabel: prepared.apiOrderMode ? "Submitting quote..." : "Preparing quote...",
        doorSummaries,
        baseTotalCents,
        hasCustomPrice
      };
    }

    function buildOrderReviewFactsMarkup(rows, groupedLabels = []) {
      const items = Array.isArray(rows) ? rows : [];
      if (items.length === 0) {
        return "<p class=\"gd-order-review-modal__empty\">No details available.</p>";
      }

      const normalizedGroupLabels = (Array.isArray(groupedLabels) ? groupedLabels : [])
        .map((label) => String(label || "").trim())
        .filter(Boolean);
      const groupedLabelSet = new Set(normalizedGroupLabels);
      const factMarkupByLabel = new Map();

      items.forEach((item) => {
        const label = String(item && item.label ? item.label : "").trim();
        if (!label) return;
        factMarkupByLabel.set(
          label,
          `
            <div class="gd-order-review-modal__fact${groupedLabelSet.has(label) ? " gd-order-review-modal__fact--compact" : ""}">
              <dt>${escapeHtml(item.label || "")}</dt>
              <dd>${escapeHtml(item.value || "")}</dd>
            </div>
          `
        );
      });

      const groupedRowMarkup =
        normalizedGroupLabels.length > 0
          ? normalizedGroupLabels
              .map((label) => factMarkupByLabel.get(label) || "")
              .filter(Boolean)
              .join("")
          : "";
      const remainingFactsMarkup = items
        .filter((item) => !groupedLabelSet.has(String(item && item.label ? item.label : "").trim()))
        .map((item) => {
          return `
            <div class="gd-order-review-modal__fact">
              <dt>${escapeHtml(item.label || "")}</dt>
              <dd>${escapeHtml(item.value || "")}</dd>
            </div>
          `;
        })
        .join("");

      return `
        <dl class="gd-order-review-modal__facts">
          ${
            groupedRowMarkup
              ? `<div class="gd-order-review-modal__fact-row gd-order-review-modal__fact-row--mobile-triple">${groupedRowMarkup}</div>`
              : ""
          }
          ${remainingFactsMarkup}
        </dl>
      `;
    }

    function buildOrderReviewMarkup(review) {
      const snapshot = review && typeof review === "object" ? review : {};
      const doorSectionsMarkup = (Array.isArray(snapshot.doorSummaries) ? snapshot.doorSummaries : [])
        .map((doorSummary) => {
          return `
            <article class="gd-order-review-modal__door-card">
              <h5 class="gd-order-review-modal__door-card-title">${escapeHtml(doorSummary && doorSummary.label ? doorSummary.label : "Door")}</h5>
              ${buildOrderReviewFactsMarkup(doorSummary && Array.isArray(doorSummary.fields) ? doorSummary.fields : [], ["Product", "Quantity", "Size", "Color"])}
            </article>
          `;
        })
        .join("");

      return `
        <div class="gd-order-review-modal__dialog" role="document" tabindex="-1">
          <div class="gd-order-review-modal__header">
            <button type="button" class="gd-order-review-modal__close" aria-label="Close review" data-gd-order-review-cancel>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="gd-order-review-modal__body">
            <section class="gd-order-review-modal__section">
              <h4 class="gd-order-review-modal__section-title">Quote</h4>
              ${renderOrderDetailsPanel({ flat: true })}
            </section>
            <section class="gd-order-review-modal__section">
              <h4 class="gd-order-review-modal__section-title">Doors</h4>
              <div class="gd-order-review-modal__door-list">
                ${doorSectionsMarkup || '<p class="gd-order-review-modal__empty">No door details available.</p>'}
              </div>
            </section>
          </div>
          <div class="gd-order-review-modal__footer">
            <p class="gd-order-review-modal__status" data-gd-order-review-status hidden></p>
            <div class="gd-order-review-modal__actions">
              <button
                type="button"
                class="gd-order-review-modal__button gd-order-review-modal__button--primary"
                data-gd-order-review-confirm
              >
                ${escapeHtml(snapshot.confirmLabel || "Quote Now")}
              </button>
            </div>
          </div>
        </div>
      `;
    }

    function showOrderReviewModal(review, onConfirm) {
      return new Promise((resolve) => {
        const existing = document.querySelector("[data-gd-order-review-modal]");
        if (existing) {
          existing.remove();
        }

        const previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const overlay = document.createElement("div");
        overlay.className = "gd-order-review-modal";
        overlay.setAttribute("data-gd-order-review-modal", "true");
        overlay.setAttribute("role", "dialog");
        overlay.setAttribute("aria-modal", "true");
        overlay.setAttribute("aria-label", "Review quote");
        overlay.innerHTML = buildOrderReviewMarkup(review);

        const dialog = overlay.querySelector(".gd-order-review-modal__dialog");
        const cancelButtons = Array.from(overlay.querySelectorAll("[data-gd-order-review-cancel]"));
        const confirmButton = overlay.querySelector("[data-gd-order-review-confirm]");
        const statusEl = overlay.querySelector("[data-gd-order-review-status]");
        const snapshot = review && typeof review === "object" ? review : {};
        let settled = false;
        let submitting = false;

        function getConfirmButtonLabel() {
          return String(snapshot.confirmLabel || "Quote Now");
        }

        function getFocusableElements() {
          if (!dialog) return [];
          return Array.from(
            dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
          ).filter((element) => {
            return !element.hasAttribute("disabled");
          });
        }

        function cleanup() {
          document.removeEventListener("keydown", onKeyDown);
          overlay.remove();
          if (previousActiveElement && typeof previousActiveElement.focus === "function") {
            previousActiveElement.focus();
          }
        }

        function settle(confirmed) {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(Boolean(confirmed));
        }

        function setStatus(message, tone = "info") {
          if (!statusEl) return;
          const text = String(message || "").trim();
          statusEl.textContent = text;
          statusEl.setAttribute("data-tone", tone);
          statusEl.toggleAttribute("hidden", !text);
        }

        function setSubmittingState(active, message = "") {
          submitting = Boolean(active);
          overlay.toggleAttribute("data-submitting", submitting);
          if (confirmButton) {
            confirmButton.disabled = submitting;
            confirmButton.textContent = submitting
              ? String(message || snapshot.submittingLabel || "Submitting...")
              : getConfirmButtonLabel();
          }
          cancelButtons.forEach((button) => {
            button.disabled = submitting;
          });
          if (submitting) {
            setStatus("");
          }
        }

        function refreshOrderReviewForm(activeInput = null) {
          syncOrderFinancialSummaryFields(activeInput, overlay);
          syncOrderNoteCounter(overlay);
          if (confirmButton && !submitting) {
            confirmButton.textContent = getConfirmButtonLabel();
          }
        }

        function onKeyDown(event) {
          if (submitting) return;
          if (event.key === "Escape") {
            event.preventDefault();
            settle(false);
            return;
          }

          if (event.key !== "Tab") return;
          const focusable = getFocusableElements();
          if (focusable.length === 0) {
            if (dialog && typeof dialog.focus === "function") {
              event.preventDefault();
              dialog.focus();
            }
            return;
          }

          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          const activeElement = document.activeElement;

          if (!dialog || !dialog.contains(activeElement)) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus();
            return;
          }

          if (event.shiftKey && activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }

        overlay.addEventListener("click", (event) => {
          if (submitting) return;
          if (event.target === overlay) {
            settle(false);
          }
        });

        overlay.addEventListener("input", (event) => {
          if (submitting) return;
          if (handleOrderFormInput(event, overlay)) {
            refreshOrderReviewForm(event.target instanceof HTMLElement ? event.target : null);
          }
        });

        overlay.addEventListener("change", (event) => {
          if (submitting) return;
          if (handleOrderFormChange(event, overlay)) {
            refreshOrderReviewForm();
          }
        });

        cancelButtons.forEach((button) => {
          button.addEventListener("click", () => settle(false));
        });

        if (confirmButton) {
          confirmButton.addEventListener("click", async () => {
            if (settled || submitting) return;
            if (typeof onConfirm !== "function") {
              settle(true);
              return;
            }

            setSubmittingState(true, snapshot.submittingLabel || "Submitting...");
            try {
              const result = await onConfirm({
                close: () => settle(true),
                setStatus: (message, tone = "info") => setStatus(message, tone),
                setSubmitting: (active, message = "") => setSubmittingState(active, message)
              });

              if (settled) return;

              if (result && result.ok === false) {
                setSubmittingState(false);
                setStatus(result.message || "Unable to submit quote.", "error");
                return;
              }

              settle(true);
            } catch (error) {
              if (settled) return;
              setSubmittingState(false);
              setStatus(error && error.message ? String(error.message) : "Unable to submit quote.", "error");
            }
          });
        }

        document.addEventListener("keydown", onKeyDown);
        document.body.appendChild(overlay);

        window.requestAnimationFrame(() => {
          refreshOrderReviewForm();
          if (dialog && typeof dialog.focus === "function") {
            dialog.focus();
          }
        });
      });
    }

    function hashString(value) {
      let hash = 0;
      const text = String(value || "");
      for (let index = 0; index < text.length; index += 1) {
        hash = (hash << 5) - hash + text.charCodeAt(index);
        hash |= 0;
      }
      return Math.abs(hash);
    }

    function resolveDoorCollectionUrl() {
      const handle = String(entry.doorCollectionHandle || "").trim();
      if (handle) {
        return `/collections/${encodeURIComponent(handle)}`;
      }
      const fallback = String(entry.defaultProductUrl || "").trim();
      return fallback || "/collections/all";
    }

    function buildFallbackImage(step, option) {
      const palettes = [
        ["#0f172a", "#1e3a8a"],
        ["#1f2937", "#0f766e"],
        ["#1e293b", "#334155"],
        ["#422006", "#9a3412"],
        ["#3f3f46", "#525252"],
        ["#1f2937", "#4f46e5"],
        ["#3b0764", "#1d4ed8"],
        ["#052e16", "#166534"]
      ];

      const seed = `${step.id}:${option.id}:${option.label}`;
      const palette = palettes[hashString(seed) % palettes.length];
      const stepLabel = escapeSvg(step.title || "Garage Door");
      const optionLabel = escapeSvg(option.label || "Option");

      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720" viewBox="0 0 1280 720" role="img" aria-label="${optionLabel}">
          <defs>
            <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="${palette[0]}" />
              <stop offset="100%" stop-color="${palette[1]}" />
            </linearGradient>
          </defs>
          <rect width="1280" height="720" fill="url(#g)" />
          <rect x="70" y="70" width="1140" height="580" rx="32" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.22)" />
          <text x="130" y="230" fill="#ffffff" font-size="40" font-family="Arial,sans-serif" letter-spacing="2" opacity="0.85">${stepLabel}</text>
          <text x="130" y="330" fill="#ffffff" font-size="72" font-weight="700" font-family="Arial,sans-serif">${optionLabel}</text>
          <text x="130" y="410" fill="#ffffff" font-size="28" font-family="Arial,sans-serif" opacity="0.82">Custom-built configuration component</text>
        </svg>
      `;

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    }

    function normalizeStepPositionOverrides(rawOverrides) {
      const next = {};
      if (!rawOverrides || typeof rawOverrides !== "object" || Array.isArray(rawOverrides)) return next;

      Object.keys(rawOverrides).forEach((key) => {
        const handle = String(key || "").trim();
        if (!handle) return;

        const numeric = Number(rawOverrides[key]);
        if (!Number.isFinite(numeric)) return;
        next[handle] = numeric;
      });

      return next;
    }

    const stepPositionOverrides = normalizeStepPositionOverrides(
      entryConfig && typeof entryConfig === "object" ? entryConfig.step_position_overrides : null
    );

    function resolveStepPosition(step) {
      const handle = String(step && step.handle ? step.handle : "").trim();
      if (handle && Object.prototype.hasOwnProperty.call(stepPositionOverrides, handle)) {
        return Number(stepPositionOverrides[handle]);
      }
      return Number(step && step.position ? step.position : 0);
    }

    function normalizeOption(step, option) {
      const components = Array.isArray(option && option.components) ? option.components : [];
      const normalizedComponents = components.map((component) => {
        return {
          ...component,
          image: component && component.image ? String(component.image) : ""
        };
      });

      const next = {
        ...option,
        id: String(option && option.id ? option.id : ""),
        handle: String(option && option.handle ? option.handle : ""),
        label: option && option.label ? String(option.label) : "Option",
        image: option && option.image ? String(option.image) : "",
        components: normalizedComponents
      };

      if (!next.image) {
        const componentWithImage = normalizedComponents.find((component) => component.image);
        if (componentWithImage) {
          next.image = componentWithImage.image;
        }
      }

      if (!next.image) {
        next.image = buildFallbackImage(step, next);
      }

      return next;
    }

    function normalizeStepTitle(value) {
      const raw = String(value || "").trim();
      if (!raw) return "Step";

      const withoutPrefix = raw
        .replace(/^\s*(select|choose|pick)\s+/i, "")
        .replace(/^\s*(请选择|选择|挑选)\s*/i, "")
        .trim();

      return withoutPrefix || raw;
    }

    function normalizeStep(step) {
      const nextStep = {
        ...step,
        id: String(step && step.id ? step.id : ""),
        handle: String(step && step.handle ? step.handle : ""),
        title: normalizeStepTitle(step && step.title ? String(step.title) : "Step"),
        position: resolveStepPosition(step),
        required: Boolean(step && step.required),
        multi_select: Boolean(step && step.multi_select)
      };

      const rawOptions = Array.isArray(step && step.options) ? step.options : [];
      nextStep.options = rawOptions.map((option) => normalizeOption(nextStep, option));
      return nextStep;
    }

    const steps = Array.isArray(config.steps)
      ? config.steps
          .slice()
          .sort((a, b) => resolveStepPosition(a) - resolveStepPosition(b))
          .map(normalizeStep)
      : [];

    const WINDOW_LAYOUT_STEP_HANDLE = "gd-step-window-layout";
    const WINDOW_GLASS_STEP_HANDLE = "gd-step-glass-type";
    const WINDOW_COLOR_STEP_HANDLE = "gd-step-window-color";
    const WINDOW_INSERT_STEP_HANDLE = "gd-step-window-insert";
    const WINDOW_POSITION_STEP_HANDLE = "gd-step-window-position";
    const SPRING_CYCLE_STEP_HANDLE = "gd-step-spring-cycle";

    const defaultEntryConfig = {
      seriesStepHandle: "gd-step-door-collection",
      colorStepHandle: "gd-step-color-finish",
      startStepHandle: "gd-step-color-finish",
      entryRequired: true,
      defaultProductUrl: "/collections/all",
      doorCollectionHandle: "frontpage",
      doorCollectionTitle: "Garage Door",
      trackStepHandle: "gd-step-track-system",
      trackCollectionHandle: "accessories",
      trackProductType: "track",
      trackDefaultOption: "15R",
      trackSortOrder: ["15R", "12R", "LHR", "Special"],
      windowLayoutStepHandle: WINDOW_LAYOUT_STEP_HANDLE,
      windowGlassStepHandle: WINDOW_GLASS_STEP_HANDLE,
      windowColorStepHandle: WINDOW_COLOR_STEP_HANDLE,
      windowInsertStepHandle: WINDOW_INSERT_STEP_HANDLE,
      windowCollectionHandle: "window",
      windowProductType: "window",
      windowPreferredColor: "white",
      windowProductsEndpoint: "/apps/gd-order/window-products",
      orderMode: "api_order",
      orderApiEndpoint: "https://api.openteam.ai/asure/qbo/sales-order",
      orderApiSendInvoice: false,
      orderApiPaymentTermsName: "Due on receipt",
      orderApiQboPoFieldName: "PO",
      orderApiQboPaymentFieldName: "payment",
      orderApiQboPaymentFieldValue: "unpay",
      orderApiPickupTitle: "Local pickup",
      orderApiRedirect: "invoice",
      orderSuccessRedirectUrl: "/account/orders",
      customerContextEndpoint: "/apps/gd-order/customer-context",
      orderApiShopifyCustomerId: "",
      orderApiShopifyCustomerEmail: ""
    };

    function parseConfigBoolean(value, fallback) {
      if (typeof value === "boolean") return value;
      if (typeof value === "number") return value !== 0;
      const text = String(value ?? "").trim().toLowerCase();
      if (!text) return fallback;
      if (text === "true" || text === "1" || text === "yes" || text === "on") return true;
      if (text === "false" || text === "0" || text === "no" || text === "off") return false;
      return fallback;
    }

    function parseConfigTokenList(value, fallback) {
      const fallbackList = Array.isArray(fallback) ? fallback : [];
      const source = Array.isArray(value) ? value : typeof value === "string" ? value.split(/[|,]/g) : [];
      const cleaned = source
        .map((item) => String(item || "").trim())
        .filter(Boolean);
      return cleaned.length > 0 ? cleaned : fallbackList.slice();
    }

    function normalizeEntryConfig(raw) {
      const source = raw && typeof raw === "object" ? raw : {};
      const orderModeRaw = String(source.order_mode || defaultEntryConfig.orderMode)
        .trim()
        .toLowerCase();
      const normalizedOrderMode =
        orderModeRaw === "cart_checkout" ? "cart_checkout" : orderModeRaw === "api_order" || orderModeRaw === "draft_order" ? "api_order" : defaultEntryConfig.orderMode;
      return {
        seriesStepHandle:
          String(source.series_step_handle || defaultEntryConfig.seriesStepHandle).trim() || defaultEntryConfig.seriesStepHandle,
        colorStepHandle:
          String(source.color_step_handle || defaultEntryConfig.colorStepHandle).trim() || defaultEntryConfig.colorStepHandle,
        startStepHandle:
          String(source.start_step_handle || defaultEntryConfig.startStepHandle).trim() || defaultEntryConfig.startStepHandle,
        entryRequired:
          typeof source.entry_required === "boolean" ? source.entry_required : defaultEntryConfig.entryRequired,
        defaultProductUrl:
          String(source.default_product_url || defaultEntryConfig.defaultProductUrl).trim() ||
          defaultEntryConfig.defaultProductUrl,
        doorCollectionHandle:
          String(source.door_collection_handle || defaultEntryConfig.doorCollectionHandle).trim() ||
          defaultEntryConfig.doorCollectionHandle,
        doorCollectionTitle:
          String(source.door_collection_title || defaultEntryConfig.doorCollectionTitle).trim() ||
          defaultEntryConfig.doorCollectionTitle,
        trackStepHandle:
          String(source.track_step_handle || defaultEntryConfig.trackStepHandle).trim() || defaultEntryConfig.trackStepHandle,
        trackCollectionHandle:
          String(source.track_collection_handle || defaultEntryConfig.trackCollectionHandle).trim() ||
          defaultEntryConfig.trackCollectionHandle,
        trackProductType:
          String(source.track_product_type || defaultEntryConfig.trackProductType).trim() || defaultEntryConfig.trackProductType,
        trackDefaultOption:
          String(source.track_default_option || defaultEntryConfig.trackDefaultOption).trim() || defaultEntryConfig.trackDefaultOption,
        trackSortOrder: parseConfigTokenList(source.track_sort_order, defaultEntryConfig.trackSortOrder),
        windowLayoutStepHandle:
          String(source.window_layout_step_handle || defaultEntryConfig.windowLayoutStepHandle).trim() ||
          defaultEntryConfig.windowLayoutStepHandle,
        windowGlassStepHandle:
          String(source.window_glass_step_handle || defaultEntryConfig.windowGlassStepHandle).trim() ||
          defaultEntryConfig.windowGlassStepHandle,
        windowColorStepHandle:
          String(source.window_color_step_handle || defaultEntryConfig.windowColorStepHandle).trim() ||
          defaultEntryConfig.windowColorStepHandle,
        windowInsertStepHandle:
          String(source.window_insert_step_handle || defaultEntryConfig.windowInsertStepHandle).trim() ||
          defaultEntryConfig.windowInsertStepHandle,
        windowCollectionHandle:
          String(source.window_collection_handle || defaultEntryConfig.windowCollectionHandle).trim() ||
          defaultEntryConfig.windowCollectionHandle,
        windowProductType:
          String(source.window_product_type || defaultEntryConfig.windowProductType).trim() || defaultEntryConfig.windowProductType,
        windowPreferredColor:
          String(source.window_preferred_color || defaultEntryConfig.windowPreferredColor).trim() || defaultEntryConfig.windowPreferredColor,
        windowProductsEndpoint:
          String(source.window_products_endpoint || source.order_api_window_products_endpoint || defaultEntryConfig.windowProductsEndpoint).trim() ||
          defaultEntryConfig.windowProductsEndpoint,
        orderMode: normalizedOrderMode,
        orderApiEndpoint:
          String(source.order_api_endpoint || source.draft_order_endpoint || defaultEntryConfig.orderApiEndpoint).trim() ||
          defaultEntryConfig.orderApiEndpoint,
        orderApiSendInvoice: parseConfigBoolean(
          source.order_api_send_invoice ?? source.draft_order_send_invoice,
          defaultEntryConfig.orderApiSendInvoice
        ),
        orderApiPaymentTermsName:
          String(
            source.order_api_payment_terms_name ||
              source.draft_order_payment_terms_name ||
              defaultEntryConfig.orderApiPaymentTermsName
          ).trim() || defaultEntryConfig.orderApiPaymentTermsName,
        orderApiQboPoFieldName:
          String(source.order_api_qbo_po_field_name || defaultEntryConfig.orderApiQboPoFieldName).trim() ||
          defaultEntryConfig.orderApiQboPoFieldName,
        orderApiQboPaymentFieldName:
          String(source.order_api_qbo_payment_field_name || defaultEntryConfig.orderApiQboPaymentFieldName).trim() ||
          defaultEntryConfig.orderApiQboPaymentFieldName,
        orderApiQboPaymentFieldValue:
          String(source.order_api_qbo_payment_field_value || defaultEntryConfig.orderApiQboPaymentFieldValue).trim() ||
          defaultEntryConfig.orderApiQboPaymentFieldValue,
        orderApiPickupTitle:
          String(source.order_api_pickup_title || source.draft_order_pickup_title || defaultEntryConfig.orderApiPickupTitle).trim() ||
          defaultEntryConfig.orderApiPickupTitle,
        orderApiRedirect:
          String(source.order_api_redirect || source.draft_order_redirect || defaultEntryConfig.orderApiRedirect)
            .trim()
            .toLowerCase() || defaultEntryConfig.orderApiRedirect,
        orderSuccessRedirectUrl:
          String(source.order_success_redirect_url || defaultEntryConfig.orderSuccessRedirectUrl).trim() ||
          defaultEntryConfig.orderSuccessRedirectUrl,
        customerContextEndpoint:
          String(source.order_api_customer_context_endpoint || source.customer_context_endpoint || defaultEntryConfig.customerContextEndpoint).trim() ||
          defaultEntryConfig.customerContextEndpoint,
        orderApiShopifyCustomerId:
          String(source.order_api_shopify_customer_id || source.shopify_customer_id || defaultEntryConfig.orderApiShopifyCustomerId).trim() ||
          defaultEntryConfig.orderApiShopifyCustomerId,
        orderApiShopifyCustomerEmail:
          String(
            source.order_api_shopify_customer_email ||
              source.shopify_customer_email ||
              defaultEntryConfig.orderApiShopifyCustomerEmail
          ).trim() || defaultEntryConfig.orderApiShopifyCustomerEmail
      };
    }

    function inferSeriesHandle(value) {
      const text = String(value || "").toLowerCase();
      if (!text) return "";
      if (text.includes("flush") || text.includes("grooved") || text.includes("commercial")) return "gd-opt-door-flush";
      if (text.includes("raised")) return "gd-opt-door-raised";
      if (text.includes("carriage")) return "gd-opt-door-carriage";
      return "";
    }

    function inferColorHandle(value) {
      const text = String(value || "").toLowerCase();
      if (!text) return "";
      if (text.includes("white")) return "gd-opt-finish-white";
      if (text.includes("almond")) return "gd-opt-finish-almond";
      if (text.includes("sand")) return "gd-opt-finish-sand";
      if (text.includes("char")) return "gd-opt-finish-char";
      if (text.includes("basalt black")) return "gd-opt-finish-basalt-black";
      if (text.includes("basalt dark brown") || text.includes("basalt brown")) return "gd-opt-finish-basalt-brown";
      if (text.includes("iron ore")) return "gd-opt-finish-iron-ore";
      if (text.includes("dark brown")) return "gd-opt-finish-dark-brown";
      if (text.includes("dark walnut")) return "gd-opt-finish-dark-walnut";
      if (text.includes("brown")) return "gd-opt-finish-brown";
      if (text.includes("wood")) return "gd-opt-finish-woodgrain";
      if (text.includes("teak")) return "gd-opt-finish-teak";
      if (text.includes("mahogany")) return "gd-opt-finish-mahogany";
      if (text.includes("plank")) return "gd-opt-finish-plank";
      if (text.includes("black")) return "gd-opt-finish-black";
      return "";
    }

    function normalizeCustomerId(rawValue) {
      const text = String(rawValue || "").trim();
      if (!text) return "";
      if (/^\d+$/.test(text)) return text;
      const gidMatch = text.match(/\/(\d+)$/);
      return gidMatch ? gidMatch[1] : "";
    }

    function readEntryContext() {
      const params = new URLSearchParams(window.location.search || "");
      const hasExplicitSource = params.get("gd_entry") === "1";
      const variantIdRaw = String(params.get("entry_variant_id") || "").trim();
      const hasVariantId = /^\d+$/.test(variantIdRaw);
      const fromProduct = hasExplicitSource || hasVariantId;

      const seriesLabel = String(params.get("entry_series_label") || "").trim();
      const colorLabel = String(params.get("entry_color_label") || "").trim();
      const sizeLabel = String(params.get("entry_size_label") || "").trim();
      const customWidth = String(params.get("entry_custom_width") || "").trim();
      const customHeight = String(params.get("entry_custom_height") || "").trim();
      const quantityRaw = String(params.get("entry_quantity") || "").trim();
      const quantityDigits = quantityRaw.replace(/[^\d]+/g, "");
      const quantity = quantityDigits ? Number(quantityDigits) : Number(quantityRaw);
      const panelPreference = String(params.get("entry_panel_preference") || "").trim();
      const productHandle = String(params.get("entry_product_handle") || "").trim();
      const productTitle = String(params.get("entry_product_title") || "").trim();
      const variantTitle = String(params.get("entry_variant_title") || "").trim();

      const directSeriesHandle = String(params.get("entry_series_handle") || "").trim();
      const directColorHandle = String(params.get("entry_color_handle") || "").trim();
      const seriesHandle =
        directSeriesHandle ||
        inferSeriesHandle(seriesLabel) ||
        inferSeriesHandle(variantTitle) ||
        inferSeriesHandle(productHandle) ||
        inferSeriesHandle(productTitle);
      const colorHandle = directColorHandle || inferColorHandle(colorLabel) || inferColorHandle(variantTitle);

      return {
        fromProduct,
        variantId: hasVariantId ? Number(variantIdRaw) : 0,
        variantSku: String(params.get("entry_variant_sku") || "").trim(),
        variantTitle,
        productHandle,
        productTitle,
        seriesLabel,
        colorLabel,
        sizeLabel,
        customWidth,
        customHeight,
        quantity: Number.isFinite(quantity) && quantity > 0 ? Math.round(quantity) : 1,
        panelPreference,
        isCustomSize: /custom/i.test(`${sizeLabel} ${variantTitle}`) || Boolean(customWidth || customHeight),
        seriesHandle,
        colorHandle
      };
    }

    const entry = normalizeEntryConfig(entryConfig);
    const entryContext = readEntryContext();
      const ENTRY_SIZE_WIDTH_TOKEN = "__gd_size_width__";
      const ENTRY_SIZE_HEIGHT_TOKEN = "__gd_size_height__";
      const ENTRY_COLOR_TOKEN = "__gd_color__";
      const ENTRY_PANEL_PREFERENCE_TOKEN = "__gd_panel_preference__";
      const ENTRY_DOOR_QUANTITY_TOKEN = "__gd_door_quantity__";
      const ENTRY_DEFAULT_PRODUCT_TITLE = "Classic 300";
      const ENTRY_DEFAULT_WIDTH = "8";
      const ENTRY_DEFAULT_HEIGHT = "7";
      const ENTRY_STANDARD_WIDTH_OPTIONS = Object.freeze(["8", "9", "16"]);
      const ENTRY_STANDARD_HEIGHT_OPTIONS = Object.freeze(["7", "8"]);
      const ENTRY_DEFAULT_COLOR = "White";
      const ENTRY_DEFAULT_PANEL_PREFERENCE = "18";
      const ENTRY_PANEL_PREFERENCE_OPTIONS = Object.freeze(["18", "21", "24"]);
      const ENTRY_DEFAULT_DOOR_QUANTITY = 1;
      const ENTRY_MAX_DOOR_QUANTITY = 999;
      const ENTRY_DOOR_QUANTITY_PRESET_MAX = 20;
      const ENTRY_DOOR_QUANTITY_CUSTOM_OPTION = "Custom";
      const ENTRY_DOOR_QUANTITY_OPTIONS = Object.freeze(
        Array.from({ length: ENTRY_DOOR_QUANTITY_PRESET_MAX }, (_, index) => String(index + 1))
      );
      const ENTRY_PANEL_PREFERENCE_DISPLAY_BY_VALUE = Object.freeze({
        "18": '18"',
        "21": '21"',
        "24": '24"'
      });
    const ENTRY_SERIES_SUBTITLE_RULES = Object.freeze([
      Object.freeze({ token: "classic 300", label: "Insulated" }),
      Object.freeze({ token: "coach house 400", label: "Carriage Style" }),
      Object.freeze({ token: "heritage 500", label: "Raised Panel" }),
      Object.freeze({ token: "carriage 600", label: "Textured Panel" }),
      Object.freeze({ token: "modern flush 800", label: "Contemporary" }),
      Object.freeze({ token: "commercial grooved 900", label: "Commercial" }),
      Object.freeze({ token: "x-finity", label: "Full View" })
    ]);
    const ENTRY_SERIES_SORT_RULES = Object.freeze([
      Object.freeze({ token: "classic 300", rank: 300 }),
      Object.freeze({ token: "coach house 400", rank: 400 }),
      Object.freeze({ token: "heritage 500", rank: 500 }),
      Object.freeze({ token: "carriage 600", rank: 600 }),
      Object.freeze({ token: "modern flush 800", rank: 800 }),
      Object.freeze({ token: "commercial grooved 900", rank: 900 }),
      Object.freeze({ token: "x finity", rank: 1000 }),
      Object.freeze({ token: "xfinity", rank: 1000 })
    ]);
    const WINDOW_DEFAULT_LAYOUT_SIZE_CODE = "1240";
    const WINDOW_INSERT_SUPPORTED_GLASS_TOKENS = Object.freeze(["clear", "frosted", "tinted", "tinted frosted"]);
    const WINDOW_INSERT_CODE_SUFFIX_PATTERN = /-[A-Z]\d{2}$/i;
    const WINDOW_INSERT_CODE_TITLE_PATTERN = /\b[A-Z]\d{2}\b/i;
    const WINDOW_NO_INSERT_LABEL = "No Insert";
    const ORDER_PO_NUMBER_MAX_LENGTH = 120;
    const ORDER_CUSTOMER_ID_MAX_LENGTH = 120;
    const ORDER_NOTE_MAX_LENGTH = 500;
    const ORDER_AMOUNT_MAX = 99999999;
    const ORDER_DEPOSIT_METHOD_OPTIONS = Object.freeze(["EMT", "Cash", "Transfer"]);
    const ORDER_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
    const SHOPIFY_PO_FIELD_NAME = "PO Number";
    const SHOPIFY_PICKUP_DATE_FIELD_NAME = "Pickup Date";
    let doorIdCounter = 0;

    function createDoorId() {
      doorIdCounter += 1;
      return `gd-door-${Date.now().toString(36)}-${doorIdCounter.toString(36)}`;
    }

    function cloneEntryContextValue(source) {
      const value = source && typeof source === "object" ? source : {};
      return {
        fromProduct: Boolean(value.fromProduct),
        variantId: Number(value.variantId || 0),
        variantSku: String(value.variantSku || "").trim(),
        variantTitle: String(value.variantTitle || "").trim(),
        productHandle: String(value.productHandle || "").trim(),
        productTitle: String(value.productTitle || "").trim(),
        seriesLabel: String(value.seriesLabel || "").trim(),
        colorLabel: String(value.colorLabel || "").trim(),
        sizeLabel: String(value.sizeLabel || "").trim(),
        customWidth: String(value.customWidth || "").trim(),
        customHeight: String(value.customHeight || "").trim(),
        quantity: Number.isFinite(Number(value.quantity)) && Number(value.quantity) > 0 ? Math.round(Number(value.quantity)) : 1,
        panelPreference: normalizeEntryPanelPreferenceValue(value.panelPreference),
        isCustomSize: Boolean(value.isCustomSize),
        seriesHandle: String(value.seriesHandle || "").trim(),
        colorHandle: String(value.colorHandle || "").trim()
      };
    }

    function cloneSelectionMap(source) {
      const map = source instanceof Map ? source : new Map();
      const next = new Map();
      map.forEach((value, key) => {
        next.set(String(key || ""), value instanceof Set ? new Set(value) : new Set());
      });
      return next;
    }

    function normalizeWindowCellConfig(source) {
      const value = source && typeof source === "object" ? source : {};
      const normalized = {
        layoutLabel: String(value.layoutLabel || "").trim(),
        glassLabel: String(value.glassLabel || "").trim(),
        colorLabel: String(value.colorLabel || "").trim(),
        insertLabel: String(value.insertLabel || "").trim(),
        productTitle: String(value.productTitle || "").trim(),
        variantId: Number(value.variantId || 0),
        variantTitle: String(value.variantTitle || "").trim(),
        variantSku: String(value.variantSku || "").trim(),
        priceCents: Number(value.priceCents || 0),
        sizeCode: String(value.sizeCode || "").trim(),
        widthIn: Number(value.widthIn || 0),
        heightIn: Number(value.heightIn || 0)
      };

      normalized.insertLabel = getDisplayWindowInsertLabel(normalized.glassLabel, normalized.insertLabel);

      const expectsNoInsert =
        !doesWindowGlassSupportInsert(normalized.glassLabel) || isNoWindowInsertChoiceValue(normalized.insertLabel);
      const carriesInsertCode =
        hasWindowSkuTrailingInsertCode(normalized.variantSku) || hasWindowTitleStandaloneInsertCode(normalized.variantTitle);

      if (expectsNoInsert && carriesInsertCode) {
        const syntheticSku = stripTrailingWindowInsertCode(normalized.variantSku);
        normalized.variantId = 0;
        normalized.variantSku = syntheticSku || normalized.variantSku;
        normalized.variantTitle = buildSyntheticNoInsertWindowTitle(
          normalized.productTitle,
          normalized.sizeCode,
          normalized.glassLabel,
          normalized.colorLabel
        );
      };

      return normalized;
    }

    function areWindowCellConfigsEquivalent(left, right) {
      const a = normalizeWindowCellConfig(left);
      const b = normalizeWindowCellConfig(right);
      return (
        a.layoutLabel === b.layoutLabel &&
        a.glassLabel === b.glassLabel &&
        a.colorLabel === b.colorLabel &&
        a.insertLabel === b.insertLabel &&
        a.productTitle === b.productTitle &&
        a.variantId === b.variantId &&
        a.variantTitle === b.variantTitle &&
        a.variantSku === b.variantSku &&
        a.priceCents === b.priceCents &&
        a.sizeCode === b.sizeCode &&
        a.widthIn === b.widthIn &&
        a.heightIn === b.heightIn
      );
    }

    function cloneWindowCellConfigMap(source) {
      const map = source instanceof Map ? source : new Map();
      const next = new Map();
      map.forEach((value, key) => {
        const normalizedKey = String(key || "").trim();
        if (!normalizedKey) return;
        next.set(normalizedKey, normalizeWindowCellConfig(value));
      });
      return next;
    }

    function buildDoorPreflightValue(doorEntryContext, source) {
      const value = source && typeof source === "object" ? source : {};
      return {
        completed:
          typeof value.completed === "boolean" ? value.completed : !entry.entryRequired || Boolean(doorEntryContext.fromProduct),
        seriesHandle: String(value.seriesHandle || doorEntryContext.seriesHandle || "").trim(),
        colorHandle: String(value.colorHandle || doorEntryContext.colorHandle || "").trim(),
        sizeId: String(value.sizeId || "").trim(),
        sizeLabel: String(value.sizeLabel || doorEntryContext.sizeLabel || "").trim(),
        panelPreference: normalizeEntryPanelPreferenceValue(value.panelPreference || doorEntryContext.panelPreference)
      };
    }

    function createDoorState(source = {}) {
      const doorEntryContext = cloneEntryContextValue(source.entryContext || entryContext);
      return {
        id: String(source.id || createDoorId()),
        committed: Boolean(source.committed),
        quantity: normalizeDoorQuantityValue(source.quantity ?? doorEntryContext.quantity),
        entryProduct: source.entryProduct || null,
        selected: cloneSelectionMap(source.selected),
        specialTrack: normalizeSpecialTrackConfig(source.specialTrack),
        windowCells: source.windowCells instanceof Set ? new Set(source.windowCells) : new Set(),
        windowCellConfigs: cloneWindowCellConfigMap(source.windowCellConfigs),
        windowHoverCell: source.windowHoverCell ? { ...source.windowHoverCell } : null,
        windowColorAutoSync: typeof source.windowColorAutoSync === "boolean" ? source.windowColorAutoSync : true,
        entryContext: doorEntryContext,
        preflight: buildDoorPreflightValue(doorEntryContext, source.preflight),
        activeStepId: String(source.activeStepId || "").trim(),
        activeOptionId: String(source.activeOptionId || "").trim(),
        mobileOpenStepId: String(source.mobileOpenStepId || "").trim(),
        windowPlacementAttention: Boolean(source.windowPlacementAttention),
        lastTouchedStepId: String(source.lastTouchedStepId || "").trim(),
        lastTouchedOptionId: String(source.lastTouchedOptionId || "").trim()
      };
    }

    const initialDoorState = createDoorState();

    const state = {
      adding: false,
      designModalOpen: false,
      designEditingDoorId: "",
      designEditingSnapshot: null,
      designNavSectionId: "",
      entryDropdownOpenKey: "",
      entryCollectionProducts: [],
      entryProductError: "",
      entryProductLoading: false,
      windowCollectionProducts: [],
      windowProductByLayoutOptionId: new Map(),
      doors: [initialDoorState],
      activeDoorId: initialDoorState.id,
      previewDoorId: "",
      customerContext: {
        shopifyCustomerId: "",
        email: "",
        authenticated: false,
        loaded: false,
        loading: false,
        error: ""
      },
      poNumber: "",
      orderDate: getTodayDateInputValue(),
      pickupDate: "",
      manualCustomerId: "",
      subtotalAmount: "",
      creditAmount: "",
      depositAmount: "",
      depositMethod: "",
      customerNote: "",
      hintMessage: "",
      hintTone: "info"
    };
    let customerContextRequest = null;

    function getDoorStateById(doorId) {
      const target = String(doorId || "").trim();
      if (!target) return null;
      return state.doors.find((door) => String(door && door.id ? door.id : "") === target) || null;
    }

    function getActiveDoorState() {
      return getDoorStateById(state.activeDoorId) || state.doors[0] || null;
    }

    function isCommittedDoor(door) {
      return Boolean(door && door.committed);
    }

    function getCommittedDoors() {
      return state.doors.filter((door) => isCommittedDoor(door));
    }

    function getPreviewDoors() {
      return getCommittedDoors();
    }

    function getDraftDoorState() {
      const activeDoor = getActiveDoorState();
      if (activeDoor && !activeDoor.committed) return activeDoor;
      return state.doors.find((door) => !door.committed) || activeDoor || null;
    }

    function getDoorIndex(doorOrId) {
      const targetId =
        typeof doorOrId === "string" || typeof doorOrId === "number"
          ? String(doorOrId)
          : String(doorOrId && doorOrId.id ? doorOrId.id : "");
      return state.doors.findIndex((door) => String(door && door.id ? door.id : "") === targetId);
    }

    function getCommittedDoorIndex(doorOrId) {
      const targetId =
        typeof doorOrId === "string" || typeof doorOrId === "number"
          ? String(doorOrId)
          : String(doorOrId && doorOrId.id ? doorOrId.id : "");
      return getCommittedDoors().findIndex((door) => String(door && door.id ? door.id : "") === targetId);
    }

    function getDoorLabel(doorOrId) {
      const committedIndex = getCommittedDoorIndex(doorOrId);
      if (committedIndex >= 0) return `Door ${committedIndex + 1}`;
      return `Door ${getCommittedDoors().length + 1}`;
    }

    function setActiveDoorById(doorId) {
      const target = getDoorStateById(doorId);
      if (!target) return false;
      state.activeDoorId = target.id;
      return true;
    }

    function getPreviewFocusedDoor() {
      const previewDoor = getDoorStateById(state.previewDoorId);
      if (previewDoor && previewDoor.committed) return previewDoor;
      return getPreviewDoors()[0] || null;
    }

    function getDesignEditingDoor() {
      const target = getDoorStateById(state.designEditingDoorId);
      return target && target.committed ? target : null;
    }

    function restoreDoorState(targetDoor, sourceDoor) {
      if (!targetDoor || !sourceDoor) return null;
      Object.assign(targetDoor, createDoorState(sourceDoor));
      return targetDoor;
    }

    function withActiveDoorState(doorState, callback) {
      if (!doorState || typeof callback !== "function") return typeof callback === "function" ? callback() : undefined;
      const previousDoorId = state.activeDoorId;
      state.activeDoorId = doorState.id;
      syncWindowChoiceSteps();
      syncWindowCellsToLayout();
      try {
        return callback();
      } finally {
        state.activeDoorId = previousDoorId;
        syncWindowChoiceSteps();
        syncWindowCellsToLayout();
      }
    }

    [
      "entryProduct",
      "selected",
      "specialTrack",
      "windowCells",
      "windowCellConfigs",
      "windowHoverCell",
      "windowColorAutoSync",
      "quantity",
      "entryContext",
      "preflight",
      "activeStepId",
      "activeOptionId",
      "mobileOpenStepId",
      "lastTouchedStepId",
      "lastTouchedOptionId"
    ].forEach((key) => {
      Object.defineProperty(state, key, {
        configurable: true,
        enumerable: true,
        get() {
          const door = getActiveDoorState();
          return door ? door[key] : undefined;
        },
        set(value) {
          const door = getActiveDoorState();
          if (!door) return;
          door[key] = value;
        }
      });
    });

    const mobileMq = window.matchMedia("(max-width: 989px)");

    function isMobileLayout() {
      return mobileMq.matches;
    }

    function isApiOrderMode() {
      return String(entry.orderMode || "").toLowerCase() === "api_order";
    }

    function setHint(message, tone = "info") {
      state.hintMessage = String(message || "").trim();
      state.hintTone = tone;
    }

    function syncDesignModalUiState() {
      root.setAttribute("data-gd-design-open", state.designModalOpen ? "true" : "false");
      document.body.classList.toggle("gd-design-modal-open", state.designModalOpen);
    }

    function openDesignModal(doorId = "") {
      if (!state.preflight.completed) {
        setHint("Start from a product page to begin configuration.", "error");
        renderDock();
        return;
      }

      const targetDoor = getDoorStateById(doorId);
      const editingDoor = targetDoor && targetDoor.committed ? targetDoor : null;
      const designDoor = editingDoor || getDraftDoorState();
      if (!designDoor) return;

      if (editingDoor) {
        state.previewDoorId = editingDoor.id;
        state.designEditingDoorId = editingDoor.id;
        state.designEditingSnapshot = createDoorState(editingDoor);
        pendingRailSelectedReveal = false;
      } else {
        state.designEditingDoorId = "";
        state.designEditingSnapshot = null;
        pendingRailSelectedReveal = false;
      }

      state.activeDoorId = designDoor.id;
      state.designNavSectionId = "dimensions";
      state.designModalOpen = true;
      closeEntryDropdown();
      syncDesignModalUiState();
      render();
    }

    function closeDesignModal() {
      if (!state.designModalOpen) return;
      const editingDoor = getDesignEditingDoor();
      if (editingDoor && state.designEditingSnapshot) {
        restoreDoorState(editingDoor, state.designEditingSnapshot);
      }
      pendingRailSelectedReveal = false;
      state.designEditingDoorId = "";
      state.designEditingSnapshot = null;
      state.designNavSectionId = "";
      state.designModalOpen = false;
      closeEntryDropdown();
      syncDesignModalUiState();
      render();
    }

    function normalizeCustomerNoteValue(value) {
      const normalized = String(value ?? "").replace(/\r\n?/g, "\n");
      if (!normalized) return "";
      return normalized.length <= ORDER_NOTE_MAX_LENGTH ? normalized : normalized.slice(0, ORDER_NOTE_MAX_LENGTH);
    }

    function normalizePoNumberValue(value) {
      const normalized = String(value ?? "")
        .replace(/\r\n?/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (!normalized) return "";
      return normalized.length <= ORDER_PO_NUMBER_MAX_LENGTH ? normalized : normalized.slice(0, ORDER_PO_NUMBER_MAX_LENGTH);
    }

    function normalizeManualCustomerIdValue(value) {
      const normalized = String(value ?? "")
        .replace(/\r\n?/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (!normalized) return "";
      return normalized.length <= ORDER_CUSTOMER_ID_MAX_LENGTH
        ? normalized
        : normalized.slice(0, ORDER_CUSTOMER_ID_MAX_LENGTH);
    }

    function formatOrderDateValue(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
      const year = String(date.getFullYear()).padStart(4, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    function getTodayDateInputValue() {
      return formatOrderDateValue(new Date());
    }

    function normalizeOrderDateValue(value, fallback = "") {
      const normalized = String(value ?? "").trim();
      if (!normalized) return fallback;
      if (ORDER_DATE_PATTERN.test(normalized)) return normalized;
      const parsed = new Date(normalized);
      if (Number.isNaN(parsed.getTime())) return fallback;
      return formatOrderDateValue(parsed);
    }

    function getPoNumber() {
      return normalizePoNumberValue(state.poNumber);
    }

    function getOrderDate() {
      return normalizeOrderDateValue(state.orderDate, getTodayDateInputValue());
    }

    function getPickupDate() {
      return normalizeOrderDateValue(state.pickupDate, "");
    }

    function getManualCustomerId() {
      return normalizeManualCustomerIdValue(state.manualCustomerId);
    }

    function getCustomerNote() {
      return normalizeCustomerNoteValue(state.customerNote);
    }

    function normalizeOrderDepositMethodValue(value) {
      const normalizedValue = String(value ?? "").trim();
      if (!normalizedValue) return "";
      const matched = ORDER_DEPOSIT_METHOD_OPTIONS.find((option) => {
        return normalizeCompareToken(option) === normalizeCompareToken(normalizedValue);
      });
      return matched || "";
    }

    function getOrderDepositMethod() {
      return normalizeOrderDepositMethodValue(state.depositMethod);
    }

    function normalizeOrderAmountInputValue(value) {
      const raw = String(value ?? "")
        .replace(/[$,\s]+/g, "")
        .trim();
      if (!raw) return "";

      let integerPart = "";
      let decimalPart = "";
      let hasDecimal = false;

      for (const character of raw) {
        if (character >= "0" && character <= "9") {
          if (hasDecimal) {
            if (decimalPart.length < 2) {
              decimalPart += character;
            }
          } else {
            integerPart += character;
          }
          continue;
        }

        if (character === "." && !hasDecimal) {
          hasDecimal = true;
        }
      }

      integerPart = integerPart.replace(/^0+(?=\d)/, "");
      if (!integerPart && (hasDecimal || decimalPart)) {
        integerPart = "0";
      }

      let normalized = integerPart;
      if (hasDecimal) {
        normalized += ".";
      }
      if (decimalPart) {
        normalized += decimalPart;
      }

      if (!normalized || normalized === ".") {
        return "";
      }

      const parsed = Number(normalized);
      if (Number.isFinite(parsed) && parsed >= 0 && Math.round(parsed * 100) > ORDER_AMOUNT_MAX) {
        return formatOrderAmountInputValueFromCents(ORDER_AMOUNT_MAX);
      }

      return normalized;
    }

    function parseOrderAmountInputToCents(value, fallback = 0) {
      const normalized = normalizeOrderAmountInputValue(value);
      if (!normalized) {
        return fallback;
      }
      const parsed = Number(normalized);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return fallback;
      }
      return Math.min(ORDER_AMOUNT_MAX, Math.round(parsed * 100));
    }

    function formatOrderAmountInputValueFromCents(cents) {
      const normalizedCents = Math.max(0, Math.round(Number(cents) || 0));
      return (normalizedCents / 100).toFixed(2);
    }

    function getOrderFinancialSummary(baseSubtotalCents = totalCents()) {
      const normalizedBaseSubtotalCents = Math.max(0, Math.round(Number(baseSubtotalCents) || 0));
      const subtotalAmount = normalizeOrderAmountInputValue(state.subtotalAmount);
      const creditAmount = normalizeOrderAmountInputValue(state.creditAmount);
      const depositAmount = normalizeOrderAmountInputValue(state.depositAmount);
      const subtotalOverridden = subtotalAmount !== "";
      const subtotalCents = subtotalOverridden
        ? parseOrderAmountInputToCents(subtotalAmount, normalizedBaseSubtotalCents)
        : normalizedBaseSubtotalCents;
      const creditCents = parseOrderAmountInputToCents(creditAmount, 0) || 0;
      const totalAfterCreditCents = Math.max(0, subtotalCents - creditCents);
      const depositCents = parseOrderAmountInputToCents(depositAmount, 0) || 0;
      const balanceCents = Math.max(0, totalAfterCreditCents - depositCents);

      return {
        subtotalOverridden,
        subtotalCents,
        creditCents,
        totalCents: totalAfterCreditCents,
        depositCents,
        balanceCents,
        subtotalInputValue: subtotalOverridden
          ? subtotalAmount
          : formatOrderAmountInputValueFromCents(normalizedBaseSubtotalCents),
        creditInputValue: creditAmount,
        totalInputValue: formatOrderAmountInputValueFromCents(totalAfterCreditCents),
        depositInputValue: depositAmount,
        balanceInputValue: formatOrderAmountInputValueFromCents(balanceCents),
        subtotalLabel: formatMoney(subtotalCents),
        creditLabel: formatMoney(creditCents),
        totalLabel: formatMoney(totalAfterCreditCents),
        depositLabel: formatMoney(depositCents),
        balanceLabel: formatMoney(balanceCents)
      };
    }

    function buildOrderFinancialNoteLines(financials, depositMethod = "") {
      const summary = financials && typeof financials === "object" ? financials : getOrderFinancialSummary(totalCents());
      const safeDepositMethod = normalizeOrderDepositMethodValue(depositMethod);
      return [
        `Subtotal: ${summary.subtotalLabel || formatMoney(0)}`,
        `Credit: ${summary.creditLabel || formatMoney(0)}`,
        `Total: ${summary.totalLabel || formatMoney(0)}`,
        `Deposit: ${summary.depositLabel || formatMoney(0)}${safeDepositMethod ? ` (${safeDepositMethod})` : ""}`,
        `Balance: ${summary.balanceLabel || formatMoney(0)}`
      ];
    }

    function syncOrderFinancialSummaryFields(activeInput = null, container = null) {
      const scope = container instanceof Element ? container : railEl;
      if (!(scope instanceof Element)) return;
      const financials = getOrderFinancialSummary(totalCents());
      [
        {
          selector: "[data-gd-order-subtotal]",
          value: financials.subtotalInputValue,
          preserveActive: true
        },
        {
          selector: "[data-gd-order-credit]",
          value: financials.creditInputValue,
          preserveActive: true
        },
        {
          selector: "[data-gd-order-total]",
          value: financials.totalInputValue,
          preserveActive: false
        },
        {
          selector: "[data-gd-order-deposit]",
          value: financials.depositInputValue,
          preserveActive: true
        },
        {
          selector: "[data-gd-order-balance]",
          value: financials.balanceInputValue,
          preserveActive: false
        }
      ].forEach((fieldConfig) => {
        scope.querySelectorAll(fieldConfig.selector).forEach((field) => {
          if (!(field instanceof HTMLInputElement)) return;
          if (fieldConfig.preserveActive && activeInput && field === activeInput) return;
          if (field.value !== fieldConfig.value) {
            field.value = fieldConfig.value;
          }
        });
      });
    }

    function syncOrderNoteCounter(container = null) {
      const scope = container instanceof Element ? container : railEl;
      if (!(scope instanceof Element)) return;
      const counter = scope.querySelector("[data-gd-order-note-count]");
      if (!counter) return;
      const note = getCustomerNote();
      counter.textContent = `${note.length}/${ORDER_NOTE_MAX_LENGTH}`;
    }

    function handleOrderFormInput(event, container = railEl) {
      const scope = container instanceof Element ? container : railEl;
      const target = event.target instanceof Element ? event.target : null;
      if (!(scope instanceof Element) || !target) return false;

      const orderPoInput = target.closest("[data-gd-order-po]");
      if (orderPoInput && scope.contains(orderPoInput)) {
        const nextValue = normalizePoNumberValue(orderPoInput.value || "");
        if (orderPoInput.value !== nextValue) {
          orderPoInput.value = nextValue;
        }
        state.poNumber = nextValue;
        return true;
      }

      const orderDateInput = target.closest("[data-gd-order-date]");
      if (orderDateInput && scope.contains(orderDateInput)) {
        const nextValue = normalizeOrderDateValue(orderDateInput.value, getTodayDateInputValue());
        if (orderDateInput.value !== nextValue) {
          orderDateInput.value = nextValue;
        }
        state.orderDate = nextValue;
        return true;
      }

      const orderPickupDateInput = target.closest("[data-gd-order-pickup-date]");
      if (orderPickupDateInput && scope.contains(orderPickupDateInput)) {
        const nextValue = normalizeOrderDateValue(orderPickupDateInput.value, "");
        if (orderPickupDateInput.value !== nextValue) {
          orderPickupDateInput.value = nextValue;
        }
        state.pickupDate = nextValue;
        return true;
      }

      const orderCustomerIdInput = target.closest("[data-gd-order-customer-id]");
      if (orderCustomerIdInput && scope.contains(orderCustomerIdInput)) {
        const nextValue = normalizeManualCustomerIdValue(orderCustomerIdInput.value || "");
        if (orderCustomerIdInput.value !== nextValue) {
          orderCustomerIdInput.value = nextValue;
        }
        state.manualCustomerId = nextValue;
        return true;
      }

      const orderSubtotalInput = target.closest("[data-gd-order-subtotal]");
      if (orderSubtotalInput && scope.contains(orderSubtotalInput)) {
        const nextValue = normalizeOrderAmountInputValue(orderSubtotalInput.value || "");
        if (orderSubtotalInput.value !== nextValue) {
          orderSubtotalInput.value = nextValue;
        }
        state.subtotalAmount = nextValue;
        syncOrderFinancialSummaryFields(orderSubtotalInput, scope);
        renderDock();
        return true;
      }

      const orderCreditInput = target.closest("[data-gd-order-credit]");
      if (orderCreditInput && scope.contains(orderCreditInput)) {
        const nextValue = normalizeOrderAmountInputValue(orderCreditInput.value || "");
        if (orderCreditInput.value !== nextValue) {
          orderCreditInput.value = nextValue;
        }
        state.creditAmount = nextValue;
        syncOrderFinancialSummaryFields(orderCreditInput, scope);
        renderDock();
        return true;
      }

      const orderDepositInput = target.closest("[data-gd-order-deposit]");
      if (orderDepositInput && scope.contains(orderDepositInput)) {
        const nextValue = normalizeOrderAmountInputValue(orderDepositInput.value || "");
        if (orderDepositInput.value !== nextValue) {
          orderDepositInput.value = nextValue;
        }
        state.depositAmount = nextValue;
        syncOrderFinancialSummaryFields(orderDepositInput, scope);
        renderDock();
        return true;
      }

      const orderNoteInput = target.closest("[data-gd-order-note]");
      if (orderNoteInput && scope.contains(orderNoteInput)) {
        const nextValue = normalizeCustomerNoteValue(orderNoteInput.value || "");
        if (orderNoteInput.value !== nextValue) {
          orderNoteInput.value = nextValue;
        }
        state.customerNote = nextValue;
        syncOrderNoteCounter(scope);
        return true;
      }

      return false;
    }

    function handleOrderFormChange(event, container = railEl) {
      const scope = container instanceof Element ? container : railEl;
      const target = event.target instanceof Element ? event.target : null;
      if (!(scope instanceof Element) || !target) return false;

      const orderSubtotalInput = target.closest("[data-gd-order-subtotal]");
      if (orderSubtotalInput && scope.contains(orderSubtotalInput)) {
        const nextValue = normalizeOrderAmountInputValue(orderSubtotalInput.value || "");
        state.subtotalAmount = nextValue;
        syncOrderFinancialSummaryFields(null, scope);
        renderDock();
        return true;
      }

      const orderCreditInput = target.closest("[data-gd-order-credit]");
      if (orderCreditInput && scope.contains(orderCreditInput)) {
        const nextValue = normalizeOrderAmountInputValue(orderCreditInput.value || "");
        state.creditAmount = nextValue;
        syncOrderFinancialSummaryFields(null, scope);
        renderDock();
        return true;
      }

      const orderDepositInput = target.closest("[data-gd-order-deposit]");
      if (orderDepositInput && scope.contains(orderDepositInput)) {
        const nextValue = normalizeOrderAmountInputValue(orderDepositInput.value || "");
        state.depositAmount = nextValue;
        syncOrderFinancialSummaryFields(null, scope);
        renderDock();
        return true;
      }

      const orderDepositMethodInput = target.closest("[data-gd-order-deposit-method]");
      if (orderDepositMethodInput && scope.contains(orderDepositMethodInput)) {
        state.depositMethod = normalizeOrderDepositMethodValue(orderDepositMethodInput.value || "");
        return true;
      }

      return false;
    }

    function resolveCustomerContextEndpointUrl() {
      const endpoint = String(entry.customerContextEndpoint || "").trim();
      if (!endpoint) return "";

      if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
        return endpoint;
      }

      if (endpoint.startsWith("/")) {
        return `${window.location.origin}${endpoint}`;
      }

      return `${window.location.origin}/${endpoint}`;
    }

    function getCustomerIdentity() {
      return {
        shopifyCustomerId: normalizeCustomerId(state.customerContext && state.customerContext.shopifyCustomerId),
        email: String(state.customerContext && state.customerContext.email ? state.customerContext.email : "").trim()
      };
    }

    function normalizeCustomerContextPayload(payload) {
      const rawSource = payload && typeof payload === "object" ? payload : {};
      const source =
        rawSource && rawSource.data && typeof rawSource.data === "object" && !Array.isArray(rawSource.data)
          ? rawSource.data
          : rawSource;
      const shopifyCustomerId = normalizeCustomerId(
        source.shopifyCustomerId ||
          source.shopify_customer_id ||
          source.loggedInCustomerId ||
          source.logged_in_customer_id ||
          (source.customer && (source.customer.id || source.customer.customerId)) ||
          ""
      );
      const email = String(
        source.email ||
          source.customerEmail ||
          source.customer_email ||
          (source.customer && source.customer.email) ||
          ""
      ).trim();
      const authenticated =
        typeof source.authenticated === "boolean"
          ? Boolean(source.authenticated && shopifyCustomerId)
          : Boolean(shopifyCustomerId);

      return {
        shopifyCustomerId,
        email,
        authenticated
      };
    }

    async function fetchCustomerContext(force = false) {
      if (!isApiOrderMode()) return getCustomerIdentity();
      if (customerContextRequest && !force) return customerContextRequest;
      if (state.customerContext.loaded && !force) return getCustomerIdentity();

      const endpointUrl = resolveCustomerContextEndpointUrl();
      if (!endpointUrl) {
        state.customerContext = {
          shopifyCustomerId: "",
          email: "",
          authenticated: false,
          loaded: true,
          loading: false,
          error: "Customer context endpoint is not configured."
        };
        renderDock();
        return getCustomerIdentity();
      }

      state.customerContext.loading = true;
      state.customerContext.error = "";

      const request = (async () => {
        try {
          console.log("[gd-configurator] customer context request", { endpointUrl, force });
          const response = await fetch(endpointUrl, {
            method: "GET",
            headers: { Accept: "application/json" },
            credentials: "same-origin"
          });

          let payload = null;
          try {
            payload = await response.json();
          } catch (error) {
            payload = null;
          }

          console.log("[gd-configurator] customer context response", {
            endpointUrl,
            ok: response.ok,
            status: response.status,
            payload
          });

          if (!response.ok || !payload || payload.ok === false) {
            const message =
              payload && payload.message
                ? String(payload.message)
                : `Customer context request failed with status ${response.status}`;
            throw new Error(message);
          }

          const identity = normalizeCustomerContextPayload(payload);
          state.customerContext = {
            ...state.customerContext,
            ...identity,
            loaded: true,
            loading: false,
            error: ""
          };
        } catch (error) {
          state.customerContext = {
            ...state.customerContext,
            shopifyCustomerId: "",
            email: "",
            authenticated: false,
            loaded: true,
            loading: false,
            error: error && error.message ? String(error.message) : "Unable to fetch customer context."
          };
          console.warn("[gd-configurator] customer context error", {
            endpointUrl,
            message: state.customerContext.error
          });
        } finally {
          customerContextRequest = null;
          renderDock();
        }

        return getCustomerIdentity();
      })();

      customerContextRequest = request;
      return request;
    }

    function logCustomerIdentity(stage = "boot") {
      const identity = getCustomerIdentity();
      console.log("[gd-configurator] customer identity", {
        stage,
        customerContextEndpoint: resolveCustomerContextEndpointUrl(),
        customerContextLoaded: Boolean(state.customerContext && state.customerContext.loaded),
        customerContextLoading: Boolean(state.customerContext && state.customerContext.loading),
        customerContextError: String(state.customerContext && state.customerContext.error ? state.customerContext.error : ""),
        resolvedCustomerId: identity.shopifyCustomerId,
        resolvedCustomerEmail: identity.email,
        entryConfigCustomerId: String(entry.orderApiShopifyCustomerId || ""),
        entryConfigCustomerEmail: String(entry.orderApiShopifyCustomerEmail || ""),
        themeCustomerId:
          window.theme && window.theme.customer && window.theme.customer.id !== undefined
            ? String(window.theme.customer.id || "")
            : "",
        themeCustomerEmail:
          window.theme && window.theme.customer && window.theme.customer.email
            ? String(window.theme.customer.email || "")
            : "",
        shopifyCustomerId:
          window.Shopify && window.Shopify.customer && window.Shopify.customer.id !== undefined
            ? String(window.Shopify.customer.id || "")
            : "",
        shopifyCustomerEmail:
          window.Shopify && window.Shopify.customer && window.Shopify.customer.email
            ? String(window.Shopify.customer.email || "")
            : "",
        stCid: window.__st && window.__st.cid !== undefined ? String(window.__st.cid || "") : "",
        stCustomerId: window.__st && window.__st.customer_id !== undefined ? String(window.__st.customer_id || "") : ""
      });
    }

    function formatMoney(cents) {
      const amount = Number(cents || 0);
      if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === "function") {
        const themeSettings = window.theme && window.theme.settings ? window.theme.settings : {};
        const moneyFormat = String(themeSettings.moneyFormat || "${{amount}}");

        try {
          return window.theme.Currency.formatMoney(amount, moneyFormat);
        } catch (error) {
          console.warn("[gd-configurator] currency formatting fallback", error);
        }
      }

      return new Intl.NumberFormat(undefined, {
        currency: "USD",
        style: "currency"
      }).format(amount / 100);
    }

    function normalizeCompareToken(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    }

    function toHandleToken(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    function normalizeEntryOptionName(value) {
      const normalized = normalizeCompareToken(value);
      return normalized.replace(/\s+/g, " ");
    }

    function hasLiveEntryProduct() {
      return Boolean(state.entryProduct && Array.isArray(state.entryProduct.variants) && state.entryProduct.variants.length > 0);
    }

    function isSeriesStep(step) {
      if (!step) return false;
      return String(step.handle || "") === String(entry.seriesStepHandle || "");
    }

    function isEntryColorStep(step) {
      if (!step) return false;
      return String(step.handle || "") === String(entry.colorStepHandle || "");
    }

    function isTemporarilyHiddenAccessoryStep(step) {
      if (!step) return false;
      const title = normalizeCompareToken(step.title || "");
      const handle = normalizeCompareToken(step.handle || "");
      const haystack = `${title} ${handle}`.trim();
      if (!haystack) return false;

      const isGarageDoorOpener =
        haystack.includes("garage door opener") ||
        haystack.includes("garage opener") ||
        haystack.includes("door opener");

      const isSmartAccessories =
        haystack.includes("smart accessories") ||
        haystack.includes("smart accessory");

      return isGarageDoorOpener || isSmartAccessories;
    }

    function isStepHiddenFromRail(step) {
      if (!step) return false;
      if (isSpringCycleStep(step)) return true;
      if (isWindowPositionStep(step)) return true;
      if (isEntryColorStep(step)) return true;
      if (isColorStep(step) && !isWindowColorStep(step)) return true;
      if (isTemporarilyHiddenAccessoryStep(step)) return true;
      return hasLiveEntryProduct() && isSeriesStep(step);
    }

    function getVisibleSteps() {
      return steps.filter((step) => !isStepHiddenFromRail(step));
    }

    function isWindowEmbeddedStep(step) {
      return isWindowLayoutStep(step) || isWindowGlassStep(step) || isWindowColorStep(step) || isWindowInsertStep(step);
    }

    function isStepRenderedAsRailGroup(step) {
      if (!step) return false;
      return !isStepHiddenFromRail(step) && !isWindowEmbeddedStep(step);
    }

    function getRailSteps() {
      return getVisibleSteps().filter((step) => isStepRenderedAsRailGroup(step));
    }

    function stripHtmlToText(value) {
      const html = String(value || "").trim();
      if (!html) return "";
      const node = document.createElement("div");
      node.innerHTML = html;
      return String(node.textContent || node.innerText || "").replace(/\s+/g, " ").trim();
    }

    function extractVariantOptionValues(variant, optionCount) {
      const fallback = [variant && variant.option1, variant && variant.option2, variant && variant.option3]
        .map((item) => String(item || "").trim())
        .filter((item) => item);
      const raw = Array.isArray(variant && variant.options) ? variant.options : fallback;
      const values = raw.map((item) => String(item || "").trim());
      const count = Math.max(Number(optionCount || 0), values.length);

      if (count === 0) return [];

      while (values.length < count) values.push("");
      return values.slice(0, count);
    }

    function detectSizeOptionIndex(optionNames) {
      const names = Array.isArray(optionNames) ? optionNames : [];
      for (let index = 0; index < names.length; index += 1) {
        const normalized = normalizeEntryOptionName(names[index]);
        if (
          normalized.includes("size") ||
          normalized.includes("dimension") ||
          normalized.includes("width") ||
          normalized.includes("height") ||
          normalized.includes("length")
        ) {
          return index;
        }
      }
      return -1;
    }

    function detectColorOptionIndex(optionNames) {
      const names = Array.isArray(optionNames) ? optionNames : [];
      for (let index = 0; index < names.length; index += 1) {
        const normalized = normalizeEntryOptionName(names[index]);
        if (normalized.includes("color") || normalized.includes("colour") || normalized.includes("finish")) {
          return index;
        }
      }
      return -1;
    }

    function coerceNumericId(value) {
      const raw = String(value || "").trim();
      if (/^\d+$/.test(raw)) return Number(raw);
      const match = raw.match(/(\d+)(?!.*\d)/);
      return match ? Number(match[1]) : 0;
    }

    function moneyToCents(value) {
      if (typeof value === "number") {
        if (!Number.isFinite(value)) return 0;
        if (Math.abs(value) >= 10000 && Number.isInteger(value)) return value;
        return Math.round(value * 100);
      }

      const text = String(value || "").trim();
      if (!text) return 0;
      const numeric = Number(text.replace(/[^0-9.-]/g, ""));
      if (!Number.isFinite(numeric)) return 0;
      if (!text.includes(".") && Math.abs(numeric) >= 10000) return Math.round(numeric);
      return Math.round(numeric * 100);
    }

    function parseEntryProduct(rawProduct) {
      if (!rawProduct || typeof rawProduct !== "object") return null;

      const rawOptionNames = Array.isArray(rawProduct.options) ? rawProduct.options : [];
      const optionNames = rawOptionNames
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object" && item.name) return item.name;
          return "";
        })
        .map((item) => String(item || "").trim())
        .filter(Boolean);

      const rawVariants = Array.isArray(rawProduct.variants) ? rawProduct.variants : [];
      const normalizedVariants = rawVariants
        .map((variant) => {
          const values = extractVariantOptionValues(variant, optionNames.length);
          const imageObject = variant && variant.featured_image ? variant.featured_image : null;
          const imageSrc =
            imageObject && typeof imageObject === "object"
              ? String(imageObject.src || imageObject.url || "").trim()
              : String(imageObject || "").trim();
          const quantity = Number(variant && variant.inventory_quantity);
          const hasTrackedQty = Number.isFinite(quantity);
          const hasStock = hasTrackedQty ? quantity > 0 : true;
          const availableByShopify =
            variant && variant.available === false ? false : variant && variant.availableForSale === false ? false : true;
          const available = availableByShopify && hasStock;

          return {
            id: coerceNumericId(variant && variant.id ? variant.id : 0),
            sku: String(variant && variant.sku ? variant.sku : "").trim(),
            title: String(variant && (variant.public_title || variant.title) ? variant.public_title || variant.title : "").trim(),
            price_cents: moneyToCents(variant && variant.price ? variant.price : 0),
            available,
            optionValues: values,
            image: imageSrc
          };
        })
        .filter((variant) => variant.id > 0);

      if (normalizedVariants.length === 0) return null;

      const selectedByContext = normalizedVariants.find((variant) => variant.id === Number(state.entryContext.variantId || 0)) || null;
      const selectedDefault = selectedByContext || normalizedVariants.find((variant) => variant.available) || normalizedVariants[0];
      const selectedOptions = selectedDefault && Array.isArray(selectedDefault.optionValues) ? selectedDefault.optionValues.slice() : [];
      const sizeOptionIndex = detectSizeOptionIndex(optionNames);
      const colorOptionIndex = detectColorOptionIndex(optionNames);

      const featuredImageRaw = rawProduct.featured_image || rawProduct.image;
      const featuredImage =
        featuredImageRaw && typeof featuredImageRaw === "object"
          ? String(featuredImageRaw.src || featuredImageRaw.url || "").trim()
          : String(featuredImageRaw || "").trim();
      const rawImages = Array.isArray(rawProduct.images) ? rawProduct.images : [];
      const parsedImages = rawImages
        .map((item) => {
          if (typeof item === "string") return String(item || "").trim();
          if (item && typeof item === "object") return String(item.src || item.url || "").trim();
          return "";
        })
        .filter(Boolean);
      const variantImages = normalizedVariants.map((variant) => String(variant.image || "").trim()).filter(Boolean);
      const imageSet = new Set();
      [featuredImage, ...parsedImages, ...variantImages].forEach((url) => {
        const normalized = String(url || "").trim();
        if (!normalized) return;
        imageSet.add(normalized);
      });
      const images = Array.from(imageSet.values());

      return {
        id: coerceNumericId(rawProduct.id),
        handle: String(rawProduct.handle || state.entryContext.productHandle || "").trim(),
        vendor: String(rawProduct.vendor || "").trim(),
        title: String(rawProduct.title || state.entryContext.productTitle || "Door Collection").trim(),
        description: stripHtmlToText(rawProduct.description || rawProduct.body_html || ""),
        image: featuredImage,
        images,
        optionNames,
        sizeOptionIndex,
        colorOptionIndex,
        variants: normalizedVariants,
        selectedVariantId: Number(selectedDefault.id || 0),
        selectedOptions
      };
    }

    function cloneEntryProduct(product) {
      if (!product || typeof product !== "object") return null;
      return {
        ...product,
        images: Array.isArray(product.images) ? product.images.slice() : [],
        optionNames: Array.isArray(product.optionNames) ? product.optionNames.slice() : [],
        variants: Array.isArray(product.variants)
          ? product.variants.map((variant) => {
              return {
                ...variant,
                optionValues: Array.isArray(variant && variant.optionValues) ? variant.optionValues.slice() : []
              };
            })
          : [],
        selectedOptions: Array.isArray(product.selectedOptions) ? product.selectedOptions.slice() : []
      };
    }

    function cloneDoorState(sourceDoor) {
      const source = sourceDoor && typeof sourceDoor === "object" ? sourceDoor : {};
      return createDoorState({
        quantity: source.quantity,
        entryProduct: cloneEntryProduct(source.entryProduct),
        selected: cloneSelectionMap(source.selected),
        specialTrack: normalizeSpecialTrackConfig(source.specialTrack),
        windowCells: source.windowCells instanceof Set ? new Set(source.windowCells) : new Set(),
        windowCellConfigs: cloneWindowCellConfigMap(source.windowCellConfigs),
        windowHoverCell: null,
        windowColorAutoSync: typeof source.windowColorAutoSync === "boolean" ? source.windowColorAutoSync : true,
        entryContext: cloneEntryContextValue(source.entryContext),
        preflight: buildDoorPreflightValue(cloneEntryContextValue(source.entryContext), source.preflight),
        activeStepId: String(source.activeStepId || ""),
        activeOptionId: "",
        mobileOpenStepId: String(source.mobileOpenStepId || source.activeStepId || ""),
        windowPlacementAttention: Boolean(source.windowPlacementAttention),
        lastTouchedStepId: String(source.lastTouchedStepId || ""),
        lastTouchedOptionId: String(source.lastTouchedOptionId || "")
      });
    }

    function createNextDraftDoorState(sourceDoor) {
      const nextDoor = cloneDoorState(sourceDoor);
      const layoutStep = getWindowLayoutStep();
      const windowSteps = [layoutStep, getWindowGlassStep(), getWindowColorStep(), getWindowInsertStep()];
      const selected = cloneSelectionMap(nextDoor.selected);

      windowSteps.forEach((step) => {
        if (!step) return;
        selected.delete(String(step.id || ""));
      });

      nextDoor.selected = selected;
      nextDoor.windowCells = new Set();
      nextDoor.windowCellConfigs = new Map();
      nextDoor.windowHoverCell = null;
      nextDoor.windowColorAutoSync = true;
      nextDoor.quantity = ENTRY_DEFAULT_DOOR_QUANTITY;
      nextDoor.activeOptionId = "";
      nextDoor.windowPlacementAttention = false;
      nextDoor.lastTouchedOptionId = "";
      return nextDoor;
    }

    function getEntryCollectionProductByHandle(handle) {
      const target = String(handle || "").trim();
      if (!target) return null;
      return state.entryCollectionProducts.find((product) => String(product && product.handle ? product.handle : "") === target) || null;
    }

    function getEntryVariantById(variantId) {
      const entryProduct = state.entryProduct;
      if (!entryProduct || !Array.isArray(entryProduct.variants)) return null;
      const numeric = Number(variantId || 0);
      return entryProduct.variants.find((variant) => variant.id === numeric) || null;
    }

    function getSelectedEntryVariant() {
      if (!hasLiveEntryProduct()) return null;
      return (
        getEntryVariantById(state.entryProduct.selectedVariantId) ||
        state.entryProduct.variants.find((variant) => variant.available) ||
        state.entryProduct.variants[0] ||
        null
      );
    }

    function getEntryProductDisplayVariant(product) {
      if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;
      return product.variants.find((variant) => variant.available) || product.variants[0];
    }

    function shouldAutoSelectConfiguratorDefaults() {
      return Number(state.entryContext.variantId || 0) <= 0;
    }

    function getDefaultEntryCollectionProduct(products) {
      const list = Array.isArray(products) ? products : [];
      if (list.length === 0) return null;

      const preferredTitleToken = normalizeCompareToken(ENTRY_DEFAULT_PRODUCT_TITLE);
      const byExactTitle = list.find((product) => {
        const titleToken = normalizeCompareToken(product && product.title ? product.title : "");
        return titleToken === preferredTitleToken;
      });
      if (byExactTitle) return byExactTitle;

      const byLooseTitle = list.find((product) => {
        const titleToken = normalizeCompareToken(product && product.title ? product.title : "");
        return preferredTitleToken && titleToken.includes(preferredTitleToken);
      });
      if (byLooseTitle) return byLooseTitle;

      return list[0];
    }

    function isVariantMatchingDefaultSize(variant, sizeOptionIndex) {
      const preferredWidth = normalizeSizeDimensionToken(ENTRY_DEFAULT_WIDTH);
      const preferredHeight = normalizeSizeDimensionToken(ENTRY_DEFAULT_HEIGHT);
      const values = Array.isArray(variant && variant.optionValues) ? variant.optionValues : [];
      const sources = [];

      if (Number.isInteger(sizeOptionIndex) && sizeOptionIndex >= 0) {
        const direct = String(values[sizeOptionIndex] || "").trim();
        if (direct) sources.push(direct);
      }

      const merged = [...values, String(variant && variant.title ? variant.title : "")]
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .join(" ");
      if (merged) sources.push(merged);

      return sources.some((source) => {
        const parsed = parseEntrySizeDimensionParts(source);
        if (parsed && !parsed.isCustom && parsed.width === preferredWidth && parsed.height === preferredHeight) return true;
        return new RegExp(`(^|[^0-9])${preferredWidth}\\s*(?:x|×)\\s*${preferredHeight}([^0-9]|$)`, "i").test(source);
      });
    }

    function isVariantMatchingDefaultColor(variant, colorOptionIndex) {
      const preferredToken = normalizeCompareToken(ENTRY_DEFAULT_COLOR);
      if (!preferredToken) return false;

      const values = Array.isArray(variant && variant.optionValues) ? variant.optionValues : [];
      if (Number.isInteger(colorOptionIndex) && colorOptionIndex >= 0) {
        const colorValue = normalizeCompareToken(values[colorOptionIndex] || "");
        if (colorValue && (colorValue === preferredToken || colorValue.includes(preferredToken))) return true;
      }

      const merged = normalizeCompareToken([...values, String(variant && variant.title ? variant.title : "")].join(" "));
      return Boolean(merged && (merged === preferredToken || merged.includes(preferredToken)));
    }

    function getDefaultEntryVariantForProduct(entryProduct) {
      if (!entryProduct || !Array.isArray(entryProduct.variants) || entryProduct.variants.length === 0) return null;

      const sizeOptionIndex = Number(entryProduct.sizeOptionIndex);
      const ranked = entryProduct.variants
        .map((variant, index) => {
          const sizeMatch = isVariantMatchingDefaultSize(variant, sizeOptionIndex);

          let score = 90;
          if (sizeMatch) score = 0;

          return { variant, index, score };
        })
        .sort((left, right) => {
          if (left.score !== right.score) return left.score - right.score;
          return left.index - right.index;
        });

      return ranked[0] ? ranked[0].variant : null;
    }

    function applyDefaultEntryVariantSelection() {
      if (!hasLiveEntryProduct()) return false;
      const fallbackVariant = getDefaultEntryVariantForProduct(state.entryProduct);
      if (!fallbackVariant) return false;

      state.entryProduct.selectedVariantId = Number(fallbackVariant.id || 0);
      state.entryProduct.selectedOptions = Array.isArray(fallbackVariant.optionValues) ? fallbackVariant.optionValues.slice() : [];
      syncEntryVariantToContext(fallbackVariant);
      return true;
    }

    function resolveEntryVariantForOptions(optionValues) {
      const entryProduct = state.entryProduct;
      if (!entryProduct || !Array.isArray(entryProduct.variants) || entryProduct.variants.length === 0) return null;

      const target = Array.isArray(optionValues) ? optionValues : [];
      const colorOptionIndex = Number(entryProduct.colorOptionIndex);
      const matching = entryProduct.variants.filter((variant) => {
        const values = Array.isArray(variant.optionValues) ? variant.optionValues : [];
        const count = Math.max(values.length, target.length);
        for (let index = 0; index < count; index += 1) {
          if (index === colorOptionIndex) continue;
          const expected = String(target[index] || "").trim();
          if (!expected) continue;
          const actual = String(values[index] || "").trim();
          if (actual !== expected) return false;
        }
        return true;
      });

      if (matching.length === 0) return null;
      return matching.find((variant) => variant.available) || matching[0];
    }

    function getEntryProductOptionValuesForIndexFromProduct(product, optionIndex) {
      if (!product || !Array.isArray(product.variants) || optionIndex < 0) return [];
      const unique = new Set();
      for (const variant of product.variants) {
        const value = String(variant.optionValues && variant.optionValues[optionIndex] ? variant.optionValues[optionIndex] : "").trim();
        if (!value) continue;
        unique.add(value);
      }
      return Array.from(unique.values());
    }

    function getEntryProductOptionValuesForIndex(optionIndex) {
      return getEntryProductOptionValuesForIndexFromProduct(state.entryProduct, optionIndex);
    }

    function normalizeEntryPanelPreferenceValue(value) {
      const digits = String(value || "").match(/\d+/);
      const normalized = digits ? digits[0] : "";
      return ENTRY_PANEL_PREFERENCE_OPTIONS.includes(normalized) ? normalized : ENTRY_DEFAULT_PANEL_PREFERENCE;
    }

    function normalizeDoorQuantityValue(value) {
      const digits = String(value || "").match(/\d+/);
      const parsed = digits ? Number(digits[0]) : Number(value);
      if (!Number.isFinite(parsed)) return ENTRY_DEFAULT_DOOR_QUANTITY;
      return Math.min(ENTRY_MAX_DOOR_QUANTITY, Math.max(ENTRY_DEFAULT_DOOR_QUANTITY, Math.round(parsed)));
    }

    function normalizeDoorQuantityInputValue(value, options = {}) {
      const allowEmpty = options && options.allowEmpty === true;
      const digits = String(value ?? "").replace(/[^\d]+/g, "");
      if (!digits) return allowEmpty ? "" : String(ENTRY_DEFAULT_DOOR_QUANTITY);
      return String(normalizeDoorQuantityValue(digits));
    }

    function formatDoorQuantityLabel(value) {
      const quantity = normalizeDoorQuantityValue(value);
      return `${quantity} door${quantity === 1 ? "" : "s"}`;
    }

    function formatEntryPanelPreferenceLabel(value) {
      const normalized = normalizeEntryPanelPreferenceValue(value);
      return ENTRY_PANEL_PREFERENCE_DISPLAY_BY_VALUE[normalized] || ENTRY_PANEL_PREFERENCE_DISPLAY_BY_VALUE[ENTRY_DEFAULT_PANEL_PREFERENCE];
    }

    function getEntryPanelPreferenceValue() {
      return normalizeEntryPanelPreferenceValue(state.entryContext.panelPreference || state.preflight.panelPreference);
    }

    function getEntryPanelPreferenceOptionLabels() {
      return ENTRY_PANEL_PREFERENCE_OPTIONS.map((value) => formatEntryPanelPreferenceLabel(value));
    }

    function getDoorQuantityValue(door = getActiveDoorState()) {
      return normalizeDoorQuantityValue(door && typeof door === "object" ? door.quantity : "");
    }

    function getCommittedDoorUnitCount() {
      return getCommittedDoors().reduce((sum, door) => sum + getDoorQuantityValue(door), 0);
    }

    function countDoorSummaryUnits(doorSummaries) {
      return (Array.isArray(doorSummaries) ? doorSummaries : []).reduce((sum, summary) => {
        const quantity =
          summary && typeof summary === "object" && "quantity" in summary
            ? summary.quantity
            : summary && summary.entryProfile && typeof summary.entryProfile === "object"
              ? summary.entryProfile.quantity
              : "";
        return sum + normalizeDoorQuantityValue(quantity);
      }, 0);
    }

    function getEntryDoorQuantityOptionLabels() {
      return [...ENTRY_DOOR_QUANTITY_OPTIONS, ENTRY_DOOR_QUANTITY_CUSTOM_OPTION];
    }

    function isCustomDoorQuantityValue(value) {
      return normalizeDoorQuantityValue(value) > ENTRY_DOOR_QUANTITY_PRESET_MAX;
    }

    function getEntryDoorQuantitySelectValue() {
      const quantity = getDoorQuantityValue();
      return isCustomDoorQuantityValue(quantity) ? ENTRY_DOOR_QUANTITY_CUSTOM_OPTION : String(quantity);
    }

    function getEntryDoorCustomQuantityValue() {
      const quantity = getDoorQuantityValue();
      if (isCustomDoorQuantityValue(quantity)) {
        return String(quantity);
      }
      return String(ENTRY_DOOR_QUANTITY_PRESET_MAX + 1);
    }

    function setActiveDoorQuantity(nextValue = "") {
      const activeDoor = getActiveDoorState();
      const normalized = normalizeDoorQuantityValue(nextValue);
      if (activeDoor) {
        activeDoor.quantity = normalized;
      }
      return normalized;
    }

    function setEntryPanelPreferenceSelection(nextValue = "") {
      const normalized = normalizeEntryPanelPreferenceValue(nextValue);
      state.entryContext.panelPreference = normalized;
      state.preflight.panelPreference = normalized;
      if (supportsEntryIndependentColors(state.entryProduct)) {
        setEntryIndependentColorSelection(state.entryContext.colorLabel, state.entryProduct);
      }
      return normalized;
    }

    function sortEntryColorValues(values) {
      const optionByKey = new Map();
      const unknown = [];

      dedupeTextValues(values).forEach((value) => {
        const key = resolveKnownColorKey(value);
        if (!key) {
          unknown.push(value);
          return;
        }
        if (!optionByKey.has(key)) {
          optionByKey.set(key, value);
        }
      });

      const ordered = COLOR_OPTION_ORDER.map((key) => optionByKey.get(key)).filter(Boolean);
      const remaining = unknown.sort((left, right) => String(left).localeCompare(String(right), undefined, { sensitivity: "base" }));
      return [...ordered, ...remaining];
    }

    function isEntryColorUnsupportedProduct(product) {
      const source = product && typeof product === "object" ? product : {};
      const text = normalizeCompareToken(`${source.handle || ""} ${source.title || ""}`);
      return Boolean(text && text.includes("x finity"));
    }

    function supportsEntryIndependentColors(product = state.entryProduct) {
      return Boolean(product) && !isEntryColorUnsupportedProduct(product);
    }

    function getEntryAllowedColorKeys(panelPreference = getEntryPanelPreferenceValue()) {
      const normalized = normalizeEntryPanelPreferenceValue(panelPreference);
      return normalized === "24" ? ["white", "iron ore", "black"] : COLOR_OPTION_ORDER.slice();
    }

    function getEntryAllowedColorValues(product = state.entryProduct) {
      if (!supportsEntryIndependentColors(product)) return [];
      return getEntryAllowedColorKeys().map((key) => ENTRY_INDEPENDENT_COLOR_LABELS_BY_KEY[key]).filter(Boolean);
    }

    function getEntryIndependentColorDisplayLabel(value) {
      const key = resolveKnownColorKey(value);
      if (key && ENTRY_INDEPENDENT_COLOR_LABELS_BY_KEY[key]) {
        return ENTRY_INDEPENDENT_COLOR_LABELS_BY_KEY[key];
      }
      return String(value || "").trim();
    }

    function resolveEntryIndependentColorSelection(nextValue = "", product = state.entryProduct) {
      const allowedColors = getEntryAllowedColorValues(product);
      if (allowedColors.length === 0) return "";

      const requestedColor = getEntryIndependentColorDisplayLabel(nextValue);
      const matchedRequested = allowedColors.find((value) => areColorLabelsEquivalent(value, requestedColor));
      if (matchedRequested) return matchedRequested;

      const currentColor = getEntryIndependentColorDisplayLabel(state.entryContext.colorLabel);
      const matchedCurrent = allowedColors.find((value) => areColorLabelsEquivalent(value, currentColor));
      if (matchedCurrent) return matchedCurrent;

      return allowedColors[0] || ENTRY_DEFAULT_COLOR;
    }

    function setEntryIndependentColorSelection(nextValue = "", product = state.entryProduct) {
      if (!supportsEntryIndependentColors(product)) {
        state.entryContext.colorLabel = "";
        state.preflight.colorHandle = "";
        return "";
      }

      const resolvedColor = resolveEntryIndependentColorSelection(nextValue, product) || ENTRY_DEFAULT_COLOR;

      state.entryContext.colorLabel = resolvedColor;
      state.preflight.colorHandle = inferColorHandle(resolvedColor) || "";
      return resolvedColor;
    }

    function getEntryCollectionColorValues(product = state.entryProduct) {
      return dedupeTextValues(getEntryAllowedColorValues(product));
    }

    function isCustomSizeDimension(value) {
      return /custom/i.test(String(value || "").trim());
    }

    function getEntryStandardDimensionOptions(part) {
      const normalizedPart = String(part || "").toLowerCase() === "height" ? "height" : "width";
      return normalizedPart === "height" ? ENTRY_STANDARD_HEIGHT_OPTIONS.slice() : ENTRY_STANDARD_WIDTH_OPTIONS.slice();
    }

    function isEntryStandardDimensionValue(part, value) {
      const normalized = normalizeSizeDimensionToken(value);
      if (!normalized || normalized === "Custom") return false;
      return getEntryStandardDimensionOptions(part).includes(normalized);
    }

    function normalizeSizeDimensionToken(value) {
      const raw = String(value || "").trim();
      if (!raw) return "";
      if (isCustomSizeDimension(raw)) return "Custom";

      const numeric = Number(raw.replace(/[^0-9.]+/g, ""));
      if (!Number.isFinite(numeric) || numeric <= 0) return raw;
      if (Number.isInteger(numeric)) return String(Math.round(numeric));
      return String(numeric).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
    }

    function normalizeFeetInchesQuoteMarks(value) {
      return String(value ?? "")
        .replace(/[\u2018\u2019\u201b\u2032\u02bc\u0060\u00b4\uff07]/g, "'")
        .replace(/[\u201c\u201d\u201f\u2033\uff02]/g, '"')
        .replace(/\u00a0/g, " ");
    }

    function normalizeFeetInchesInputValue(value) {
      return normalizeFeetInchesQuoteMarks(value).replace(/\s+/g, " ").trim();
    }

    function parseEntrySizeDimensionParts(sizeValue) {
      const raw = normalizeFeetInchesInputValue(sizeValue);
      if (!raw) return { raw: "", width: "", height: "", isCustom: false };
      if (isCustomSizeDimension(raw)) return { raw, width: "Custom", height: "Custom", isCustom: true };

      const xMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:'|ft|feet|foot|in|inch|inches)?\s*[x×]\s*(\d+(?:\.\d+)?)/i);
      if (xMatch) {
        return {
          raw,
          width: normalizeSizeDimensionToken(xMatch[1]),
          height: normalizeSizeDimensionToken(xMatch[2]),
          isCustom: false
        };
      }

      const numbers = (raw.match(/\d+(?:\.\d+)?/g) || []).map((part) => normalizeSizeDimensionToken(part)).filter(Boolean);
      if (numbers.length >= 2) {
        return {
          raw,
          width: numbers[0],
          height: numbers[1],
          isCustom: false
        };
      }

      return { raw, width: "", height: "", isCustom: false };
    }

    function formatEntryDimensionInputValue(value) {
      const normalized = normalizeSizeDimensionToken(value);
      if (!normalized || normalized === "Custom") return "";

      const numeric = Number(normalized);
      if (!Number.isFinite(numeric) || numeric <= 0) return normalized;

      const totalInches = Math.max(1, Math.round(numeric * 12));
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      return `${feet}'${inches > 0 ? `${inches}"` : ""}`;
    }

    function getImplicitCustomSizeDefaults(sizeLabel) {
      const parsed = parseEntrySizeDimensionParts(sizeLabel);
      if (!parsed.raw) {
        return {
          active: false,
          widthRaw: "",
          heightRaw: ""
        };
      }

      if (parsed.isCustom) {
        return {
          active: true,
          widthRaw: "",
          heightRaw: ""
        };
      }

      const widthAllowed = isEntryStandardDimensionValue("width", parsed.width);
      const heightAllowed = isEntryStandardDimensionValue("height", parsed.height);
      if (widthAllowed && heightAllowed) {
        return {
          active: false,
          widthRaw: "",
          heightRaw: ""
        };
      }

      return {
        active: Boolean(parsed.width || parsed.height),
        widthRaw: formatEntryDimensionInputValue(parsed.width),
        heightRaw: formatEntryDimensionInputValue(parsed.height)
      };
    }

    function getEntrySuggestedCustomSizeDefaults(sizeLabel) {
      const inferred = getImplicitCustomSizeDefaults(sizeLabel);
      const widthRaw = inferred.widthRaw || formatEntryDimensionInputValue(ENTRY_DEFAULT_WIDTH);
      const heightRaw = inferred.heightRaw || formatEntryDimensionInputValue(ENTRY_DEFAULT_HEIGHT);
      return {
        widthRaw,
        heightRaw
      };
    }

    function parseFeetInchesInput(value) {
      const raw = normalizeFeetInchesInputValue(value);
      if (!raw) return { raw: "", normalized: "", valid: false, empty: true, totalInches: 0 };

      const match = raw.match(/^(\d+)\s*(?:ft|feet|foot|')\s*(?:(\d+)\s*(?:in|inch|inches|"))?\s*$/i);
      if (!match) return { raw, normalized: raw, valid: false, empty: false, totalInches: 0 };

      let feet = Number(match[1] || 0);
      let inches = Number(match[2] || 0);
      if (!Number.isInteger(feet) || feet <= 0 || !Number.isInteger(inches) || inches < 0) {
        return { raw, normalized: raw, valid: false, empty: false, totalInches: 0 };
      }

      feet += Math.floor(inches / 12);
      inches = inches % 12;

      return {
        raw,
        normalized: `${feet}'${inches > 0 ? `${inches}"` : ""}`,
        valid: true,
        empty: false,
        totalInches: feet * 12 + inches
      };
    }

    function clearEntryCustomSize() {
      state.entryContext.customWidth = "";
      state.entryContext.customHeight = "";
      state.entryContext.isCustomSize = false;
    }

    function setEntryCustomSizeValue(part, value) {
      const key = String(part || "").toLowerCase() === "height" ? "customHeight" : "customWidth";
      state.entryContext[key] = String(value || "").trim();
    }

    function getRawEntrySizeLabel() {
      if (hasLiveEntryProduct()) {
        const entryProduct = state.entryProduct;
        const variant = getSelectedEntryVariant();
        const sizeIndex = entryProduct ? Number(entryProduct.sizeOptionIndex) : -1;
        if (entryProduct && variant && sizeIndex >= 0) {
          return String(
            entryProduct.selectedOptions && entryProduct.selectedOptions[sizeIndex]
              ? entryProduct.selectedOptions[sizeIndex]
              : variant.optionValues && variant.optionValues[sizeIndex]
                ? variant.optionValues[sizeIndex]
                : ""
          ).trim();
        }
      }

      return String(state.preflight.sizeLabel || state.entryContext.sizeLabel || "").trim();
    }

    function getEntryCustomSizeState() {
      const rawSizeLabel = getRawEntrySizeLabel();
      const inferredCustom = getImplicitCustomSizeDefaults(rawSizeLabel);
      const widthRaw = String(state.entryContext.customWidth || inferredCustom.widthRaw || "").trim();
      const heightRaw = String(state.entryContext.customHeight || inferredCustom.heightRaw || "").trim();
      const width = parseFeetInchesInput(widthRaw);
      const height = parseFeetInchesInput(heightRaw);
      const active = Boolean(state.entryContext.isCustomSize || isCustomSizeDimension(rawSizeLabel) || inferredCustom.active);
      const valid = active && width.valid && height.valid;

      let message = "";
      if (active) {
        if (!widthRaw || !heightRaw) {
          message = "Enter both custom width and height.";
        } else if (!width.valid || !height.valid) {
          message = "Use feet and inches, for example 8'9\".";
        }
      }

      return {
        active,
        valid,
        mode: active ? "custom" : "standard",
        widthRaw,
        heightRaw,
        width,
        height,
        label: valid ? `${width.normalized} x ${height.normalized}` : "",
        message
      };
    }

    function getEffectiveEntrySizeLabel(rawSizeLabel) {
      const fallback = String(rawSizeLabel || "").trim();
      const customSize = getEntryCustomSizeState();
      if (customSize.active && customSize.valid) return customSize.label;
      return fallback;
    }

    function sortEntryDimensionTokens(values) {
      return values.slice().sort((leftRaw, rightRaw) => {
        const left = normalizeSizeDimensionToken(leftRaw);
        const right = normalizeSizeDimensionToken(rightRaw);
        const leftCustom = isCustomSizeDimension(left);
        const rightCustom = isCustomSizeDimension(right);
        if (leftCustom && rightCustom) return 0;
        if (leftCustom) return 1;
        if (rightCustom) return -1;

        const leftNumeric = Number(left);
        const rightNumeric = Number(right);
        const leftIsNumeric = Number.isFinite(leftNumeric);
        const rightIsNumeric = Number.isFinite(rightNumeric);
        if (leftIsNumeric && rightIsNumeric) return leftNumeric - rightNumeric;
        if (leftIsNumeric) return -1;
        if (rightIsNumeric) return 1;
        return String(left).localeCompare(String(right));
      });
    }

    function deriveEntrySizeSplitOptions(sizeValues) {
      const hasAnySize = (Array.isArray(sizeValues) ? sizeValues : []).some((value) => {
        const parsed = parseEntrySizeDimensionParts(value);
        return parsed.isCustom || (parsed.width && parsed.height);
      });
      if (!hasAnySize) {
        return {
          widthOptions: [],
          heightOptions: []
        };
      }

      const widthOptions = sortEntryDimensionTokens(getEntryStandardDimensionOptions("width"));
      const heightOptions = sortEntryDimensionTokens(getEntryStandardDimensionOptions("height"));
      widthOptions.push("Custom");
      heightOptions.push("Custom");

      return {
        widthOptions,
        heightOptions
      };
    }

    function doesEntryVariantMatchSelectedOptionsExceptSize(variant, sizeOptionIndex) {
      const entryProduct = state.entryProduct;
      if (!entryProduct || !variant) return false;

      const selectedOptions = Array.isArray(entryProduct.selectedOptions) ? entryProduct.selectedOptions : [];
      const variantValues = Array.isArray(variant.optionValues) ? variant.optionValues : [];
      const count = Math.max(selectedOptions.length, variantValues.length);
      const colorOptionIndex = Number(entryProduct.colorOptionIndex);

      for (let index = 0; index < count; index += 1) {
        if (index === sizeOptionIndex || index === colorOptionIndex) continue;
        const expected = String(selectedOptions[index] || "").trim();
        if (!expected) continue;
        const actual = String(variantValues[index] || "").trim();
        if (actual !== expected) return false;
      }

      return true;
    }

    function doesEntrySizeValueMatchDimensions(sizeValue, widthValue, heightValue) {
      const parsed = parseEntrySizeDimensionParts(sizeValue);
      const width = normalizeSizeDimensionToken(widthValue);
      const height = normalizeSizeDimensionToken(heightValue);

      if (isCustomSizeDimension(width) || isCustomSizeDimension(height)) {
        return parsed.isCustom;
      }
      if (!parsed.width && !parsed.height) return false;
      if (width && parsed.width !== width) return false;
      if (height && parsed.height !== height) return false;
      return true;
    }

    function findEntryVariantsBySizeDimensions(sizeOptionIndex, widthValue, heightValue, availableOnly = true) {
      const entryProduct = state.entryProduct;
      if (!entryProduct || !Array.isArray(entryProduct.variants)) return [];

      return entryProduct.variants.filter((variant) => {
        if (availableOnly && !variant.available) return false;
        if (!doesEntryVariantMatchSelectedOptionsExceptSize(variant, sizeOptionIndex)) return false;
        const variantSize = String(variant.optionValues && variant.optionValues[sizeOptionIndex] ? variant.optionValues[sizeOptionIndex] : "").trim();
        if (!variantSize) return false;
        return doesEntrySizeValueMatchDimensions(variantSize, widthValue, heightValue);
      });
    }

    function resolveEntryVariantForSizeDimensions(sizeOptionIndex, widthValue, heightValue) {
      const availableMatches = findEntryVariantsBySizeDimensions(sizeOptionIndex, widthValue, heightValue, true);
      if (availableMatches.length > 0) return availableMatches[0];
      const allMatches = findEntryVariantsBySizeDimensions(sizeOptionIndex, widthValue, heightValue, false);
      return allMatches[0] || null;
    }

    function findEntryVariantBySingleDimension(sizeOptionIndex, part, optionValue, availableOnly = true) {
      const entryProduct = state.entryProduct;
      if (!entryProduct || !Array.isArray(entryProduct.variants)) return null;

      const normalizedPart = String(part || "").toLowerCase() === "height" ? "height" : "width";
      const normalizedValue = normalizeSizeDimensionToken(optionValue);
      if (!normalizedValue) return null;

      return entryProduct.variants.find((variant) => {
        if (availableOnly && !variant.available) return false;
        if (!doesEntryVariantMatchSelectedOptionsExceptSize(variant, sizeOptionIndex)) return false;

        const variantSize = String(variant.optionValues && variant.optionValues[sizeOptionIndex] ? variant.optionValues[sizeOptionIndex] : "").trim();
        const parsed = parseEntrySizeDimensionParts(variantSize);
        if (normalizedValue === "Custom") return parsed.isCustom;
        if (parsed.isCustom) return false;

        const actual = normalizedPart === "height" ? parsed.height : parsed.width;
        return Boolean(actual && actual === normalizedValue);
      }) || null;
    }

    function getEntrySizeSplitModel() {
      const entryProduct = state.entryProduct;
      const sizeOptionIndex = Number(entryProduct && entryProduct.sizeOptionIndex);
      if (!entryProduct || !Number.isInteger(sizeOptionIndex) || sizeOptionIndex < 0) return null;

      const sizeValues = getEntryProductOptionValuesForIndex(sizeOptionIndex);
      const hasSplitFriendlySize = sizeValues.some((value) => {
        const parsed = parseEntrySizeDimensionParts(value);
        return parsed.isCustom || (parsed.width && parsed.height);
      });
      if (!hasSplitFriendlySize) return null;

      const derivedOptions = deriveEntrySizeSplitOptions(sizeValues);
      if (!derivedOptions.widthOptions.length || !derivedOptions.heightOptions.length) return null;

      const selectedSizeValue = String(entryProduct.selectedOptions && entryProduct.selectedOptions[sizeOptionIndex] ? entryProduct.selectedOptions[sizeOptionIndex] : "").trim();
      const parsedSelected = parseEntrySizeDimensionParts(selectedSizeValue);
      const customSize = getEntryCustomSizeState();
      const isCustom = Boolean(parsedSelected.isCustom || customSize.active);

      return {
        sizeOptionIndex,
        selectedSizeValue,
        selectedWidth: isCustom ? "Custom" : parsedSelected.width || "",
        selectedHeight: isCustom ? "Custom" : parsedSelected.height || "",
        isCustom,
        customWidth: customSize.widthRaw,
        customHeight: customSize.heightRaw,
        customLabel: customSize.label,
        customMessage: customSize.message,
        customValid: customSize.valid,
        widthOptions: derivedOptions.widthOptions,
        heightOptions: derivedOptions.heightOptions
      };
    }

    function isEntrySizeSplitValueAvailable(model, part, optionValue) {
      if (!model) return false;
      const normalizedPart = String(part || "").toLowerCase();
      if (normalizedPart !== "width" && normalizedPart !== "height") return false;

      const nextValue = normalizeSizeDimensionToken(optionValue);
      if (!nextValue) return false;

      if (nextValue === "Custom") return true;

      const otherValue = normalizedPart === "width" ? model.selectedHeight : model.selectedWidth;
      if (!otherValue || otherValue === "Custom") {
        return Boolean(
          findEntryVariantBySingleDimension(model.sizeOptionIndex, normalizedPart, nextValue, true) ||
            findEntryVariantBySingleDimension(model.sizeOptionIndex, normalizedPart, nextValue, false)
        );
      }

      return findEntryVariantsBySizeDimensions(
        model.sizeOptionIndex,
        normalizedPart === "width" ? nextValue : otherValue,
        normalizedPart === "height" ? nextValue : otherValue,
        true
      ).length > 0;
    }

    function updateEntryVariantSelectionBySizePart(part, optionValue) {
      const model = getEntrySizeSplitModel();
      if (!model) return;

      const normalizedPart = String(part || "").toLowerCase();
      if (normalizedPart !== "width" && normalizedPart !== "height") return;

      const nextValue = normalizeSizeDimensionToken(optionValue);
      if (!nextValue) return;

      let matchedVariant = null;
      if (nextValue === "Custom") {
        const defaults = getEntrySuggestedCustomSizeDefaults(model.selectedSizeValue);
        state.entryContext.isCustomSize = true;
        if (!String(state.entryContext.customWidth || "").trim()) {
          setEntryCustomSizeValue("width", defaults.widthRaw);
        }
        if (!String(state.entryContext.customHeight || "").trim()) {
          setEntryCustomSizeValue("height", defaults.heightRaw);
        }
        if (state.hintTone === "error" && /custom|feet and inches|size\/colour|stock/i.test(state.hintMessage || "")) {
          setHint("");
        }
        return;
      } else {
        const otherValue = normalizedPart === "width" ? model.selectedHeight : model.selectedWidth;
        if (!otherValue || otherValue === "Custom") {
          matchedVariant =
            findEntryVariantBySingleDimension(model.sizeOptionIndex, normalizedPart, nextValue, true) ||
            findEntryVariantBySingleDimension(model.sizeOptionIndex, normalizedPart, nextValue, false);
        } else {
          matchedVariant = resolveEntryVariantForSizeDimensions(
            model.sizeOptionIndex,
            normalizedPart === "width" ? nextValue : otherValue,
            normalizedPart === "height" ? nextValue : otherValue
          );
        }
      }

      if (!matchedVariant) {
        setHint("Selected width/height is out of stock.", "error");
        return;
      }

      const nextSizeValue = String(matchedVariant.optionValues && matchedVariant.optionValues[model.sizeOptionIndex] ? matchedVariant.optionValues[model.sizeOptionIndex] : "").trim();
      if (!nextSizeValue) return;
      updateEntryVariantSelection(model.sizeOptionIndex, nextSizeValue);
      if (nextValue === "Custom") {
        state.entryContext.isCustomSize = true;
      }
    }

    function isEntryOptionValueAvailable(optionIndex, optionValue) {
      const entryProduct = state.entryProduct;
      if (!entryProduct || !Array.isArray(entryProduct.variants)) return false;
      const desiredValue = String(optionValue || "").trim();
      if (!desiredValue) return false;
      const colorOptionIndex = Number(entryProduct.colorOptionIndex);
      if (optionIndex === colorOptionIndex) return true;

      const selectedOptions = Array.isArray(entryProduct.selectedOptions) ? entryProduct.selectedOptions : [];
      return entryProduct.variants.some((variant) => {
        if (!variant.available) return false;
        const variantValue = String(variant.optionValues && variant.optionValues[optionIndex] ? variant.optionValues[optionIndex] : "").trim();
        if (variantValue !== desiredValue) return false;

        for (let index = 0; index < selectedOptions.length; index += 1) {
          if (index === optionIndex || index === colorOptionIndex) continue;
          const expected = String(selectedOptions[index] || "").trim();
          if (!expected) continue;
          const actual = String(variant.optionValues && variant.optionValues[index] ? variant.optionValues[index] : "").trim();
          if (actual !== expected) return false;
        }
        return true;
      });
    }

    function syncEntryVariantToContext(variant, selectedOptionsOverride = null) {
      const entryProduct = state.entryProduct;
      if (!entryProduct || !variant) return;
      const selectedOptions = Array.isArray(selectedOptionsOverride)
        ? selectedOptionsOverride
        : Array.isArray(entryProduct.selectedOptions)
          ? entryProduct.selectedOptions
          : [];
      const variantOptions = Array.isArray(variant.optionValues) ? variant.optionValues : [];

      state.entryContext.variantId = Number(variant.id || 0);
      state.entryContext.variantSku = String(variant.sku || "");
      state.entryContext.variantTitle = String(variant.title || "");
      state.entryContext.productHandle = String(entryProduct.handle || state.entryContext.productHandle || "");
      state.entryContext.productTitle = String(entryProduct.title || state.entryContext.productTitle || "");
      state.entryContext.seriesLabel = String(entryProduct.title || state.entryContext.seriesLabel || "");

      state.preflight.seriesHandle =
        inferSeriesHandle(`${entryProduct.handle} ${entryProduct.title} ${variant.title}`) || state.preflight.seriesHandle;

      const sizeIndex = Number(entryProduct.sizeOptionIndex);
      if (Number.isInteger(sizeIndex) && sizeIndex >= 0) {
        const sizeValue = String(selectedOptions[sizeIndex] || variantOptions[sizeIndex] || "").trim();
        const inferredCustom = getImplicitCustomSizeDefaults(sizeValue);
        const isCustomSize = isCustomSizeDimension(sizeValue) || inferredCustom.active;
        state.entryContext.sizeLabel = sizeValue;
        state.preflight.sizeLabel = sizeValue;
        state.entryContext.isCustomSize = isCustomSize;
        if (isCustomSize) {
          if (!String(state.entryContext.customWidth || "").trim() && inferredCustom.widthRaw) {
            state.entryContext.customWidth = inferredCustom.widthRaw;
          }
          if (!String(state.entryContext.customHeight || "").trim() && inferredCustom.heightRaw) {
            state.entryContext.customHeight = inferredCustom.heightRaw;
          }
        } else {
          clearEntryCustomSize();
        }
      }
    }

    function updateEntryVariantSelection(optionIndex, optionValue) {
      const entryProduct = state.entryProduct;
      if (!entryProduct) return;
      const nextOptions = Array.isArray(entryProduct.selectedOptions) ? entryProduct.selectedOptions.slice() : [];
      while (nextOptions.length < entryProduct.optionNames.length) nextOptions.push("");
      nextOptions[optionIndex] = String(optionValue || "").trim();

      const nextVariant = resolveEntryVariantForOptions(nextOptions);
      if (!nextVariant) return;

      entryProduct.selectedVariantId = Number(nextVariant.id || 0);
      entryProduct.selectedOptions = nextOptions.slice();
      syncEntryVariantToContext(nextVariant, nextOptions);

      if (!state.entryContext.isCustomSize && state.hintTone === "error" && /custom|feet and inches/i.test(state.hintMessage || "")) {
        setHint("");
      }

      if (!nextVariant.available) {
        setHint("Selected size/colour is out of stock.", "error");
      } else if (state.hintTone === "error" && /size\/colour|stock/i.test(state.hintMessage || "")) {
        setHint("");
      }
    }

    function normalizeEntryDropdownKey(optionIndex) {
      return String(optionIndex ?? "").trim();
    }

    function getEntryDropdownMenuId(optionIndex) {
      const normalized = normalizeEntryDropdownKey(optionIndex).replace(/[^a-zA-Z0-9_-]+/g, "-");
      return `GdEntryDropdownMenu-${normalized || "option"}`;
    }

    function isEntryDropdownOpen(optionIndex) {
      const key = normalizeEntryDropdownKey(optionIndex);
      return Boolean(key) && state.entryDropdownOpenKey === key;
    }

    function closeEntryDropdown() {
      state.entryDropdownOpenKey = "";
    }

    function toggleEntryDropdown(optionIndex) {
      const key = normalizeEntryDropdownKey(optionIndex);
      if (!key) return;
      state.entryDropdownOpenKey = state.entryDropdownOpenKey === key ? "" : key;
    }

    let pendingEntryDropdownFocus = null;

    function queueEntryDropdownFocus(optionIndex, target = "trigger") {
      const key = normalizeEntryDropdownKey(optionIndex);
      if (!key) {
        pendingEntryDropdownFocus = null;
        return;
      }
      pendingEntryDropdownFocus = {
        key,
        target: target === "selected-option" ? "selected-option" : "trigger"
      };
    }

    function restorePendingEntryDropdownFocus() {
      const pending = pendingEntryDropdownFocus;
      if (!pending) return;
      pendingEntryDropdownFocus = null;

      requestAnimationFrame(() => {
        if (!railEl) return;

        if (pending.target === "selected-option") {
          const selectedOption = Array.from(railEl.querySelectorAll("[data-gd-entry-dropdown-option-index]")).find((node) => {
            return (
              node instanceof HTMLElement &&
              String(node.getAttribute("data-gd-entry-dropdown-option-index") || "").trim() === pending.key &&
              String(node.getAttribute("aria-checked") || "").trim() === "true"
            );
          });
          if (selectedOption instanceof HTMLElement) {
            selectedOption.focus();
            return;
          }
        }

        const trigger = Array.from(railEl.querySelectorAll("[data-gd-entry-dropdown-toggle]")).find((node) => {
          return node instanceof HTMLElement && String(node.getAttribute("data-gd-entry-dropdown-toggle") || "").trim() === pending.key;
        });
        if (trigger instanceof HTMLElement) {
          trigger.focus();
        }
      });
    }

    function captureEntrySelectionSnapshot() {
      const entryProduct = state.entryProduct;
      const sizeIndex = Number(entryProduct && entryProduct.sizeOptionIndex);
      const rawSizeLabel =
        entryProduct && Number.isInteger(sizeIndex) && sizeIndex >= 0
          ? String(
              entryProduct.selectedOptions && entryProduct.selectedOptions[sizeIndex]
                ? entryProduct.selectedOptions[sizeIndex]
                : state.preflight.sizeLabel || state.entryContext.sizeLabel || ""
            ).trim()
          : String(state.preflight.sizeLabel || state.entryContext.sizeLabel || "").trim();
      const customSize = getEntryCustomSizeState();

      return {
        rawSizeLabel,
        colorLabel: String(state.entryContext.colorLabel || "").trim(),
        panelPreference: getEntryPanelPreferenceValue(),
        explicitCustom: Boolean(state.entryContext.isCustomSize || isCustomSizeDimension(rawSizeLabel)),
        customActive: customSize.active,
        customWidth: String(customSize.widthRaw || "").trim(),
        customHeight: String(customSize.heightRaw || "").trim()
      };
    }

    function applyEntrySelectionSnapshot(snapshot) {
      const entryProduct = state.entryProduct;
      if (!entryProduct || !snapshot || typeof snapshot !== "object") return false;

      const nextOptions = Array.isArray(entryProduct.selectedOptions) ? entryProduct.selectedOptions.slice() : [];
      while (nextOptions.length < entryProduct.optionNames.length) nextOptions.push("");

      const sizeIndex = Number(entryProduct.sizeOptionIndex);
      setEntryPanelPreferenceSelection(snapshot.panelPreference);
      setEntryIndependentColorSelection(snapshot.colorLabel, entryProduct);

      if (snapshot.explicitCustom) {
        state.entryContext.isCustomSize = true;
        state.entryContext.customWidth = snapshot.customWidth;
        state.entryContext.customHeight = snapshot.customHeight;
      }

      let matchedVariant = null;
      const rawSizeLabel = String(snapshot.rawSizeLabel || "").trim();
      const parsedSize = parseEntrySizeDimensionParts(rawSizeLabel);

      entryProduct.selectedOptions = nextOptions.slice();

      if (Number.isInteger(sizeIndex) && sizeIndex >= 0) {
        if (snapshot.explicitCustom) {
          matchedVariant = resolveEntryVariantForSizeDimensions(sizeIndex, "Custom", "Custom");
        }

        if (!matchedVariant && rawSizeLabel) {
          nextOptions[sizeIndex] = rawSizeLabel;
          entryProduct.selectedOptions = nextOptions.slice();
          matchedVariant = resolveEntryVariantForOptions(nextOptions);
        }

        if (!matchedVariant && parsedSize.width && parsedSize.height && !parsedSize.isCustom) {
          matchedVariant = resolveEntryVariantForSizeDimensions(sizeIndex, parsedSize.width, parsedSize.height);
        }

        if (!matchedVariant && snapshot.customActive) {
          matchedVariant = resolveEntryVariantForSizeDimensions(sizeIndex, "Custom", "Custom");
        }

        const matchedSizeValue = String(
          matchedVariant && matchedVariant.optionValues && matchedVariant.optionValues[sizeIndex]
            ? matchedVariant.optionValues[sizeIndex]
            : ""
        ).trim();
        if (matchedSizeValue) {
          nextOptions[sizeIndex] = matchedSizeValue;
        }
      }

      entryProduct.selectedOptions = nextOptions.slice();

      const selectedVariant = matchedVariant || resolveEntryVariantForOptions(nextOptions) || getSelectedEntryVariant();
      if (!selectedVariant) return false;

      entryProduct.selectedVariantId = Number(selectedVariant.id || 0);
      syncEntryVariantToContext(selectedVariant, nextOptions);
      return true;
    }

    function selectEntryCollectionProduct(productHandle) {
      const product = getEntryCollectionProductByHandle(productHandle);
      if (!product) return false;
      const selectionSnapshot = captureEntrySelectionSnapshot();

      state.entryProduct = cloneEntryProduct(product);
      return applyEntrySelectionSnapshot(selectionSnapshot);
    }

    function getEntryProductImageUrl() {
      const entryProduct = state.entryProduct;
      if (!entryProduct) return "";
      const selectedVariant = getSelectedEntryVariant();
      if (selectedVariant && selectedVariant.image) return String(selectedVariant.image);
      if (entryProduct.image) return String(entryProduct.image);
      const variantWithImage = (entryProduct.variants || []).find((variant) => variant && variant.image);
      return variantWithImage ? String(variantWithImage.image || "") : "";
    }

    function getEntryPreviewImages() {
      const entryProduct = state.entryProduct;
      if (!entryProduct) return [];
      const productImages = Array.isArray(entryProduct.images) ? entryProduct.images.map((item) => String(item || "").trim()).filter(Boolean) : [];
      if (productImages.length >= 2) return [productImages[0], productImages[1]];
      if (productImages.length === 1) return [productImages[0], productImages[0]];
      const fallback = String(getEntryProductImageUrl() || "").trim();
      if (fallback) return [fallback, fallback];
      return [];
    }

    function renderEntryOptionButton(optionIndex, optionValue) {
      const entryProduct = state.entryProduct;
      if (!entryProduct) return "";
      const selectedValue = String(entryProduct.selectedOptions && entryProduct.selectedOptions[optionIndex] ? entryProduct.selectedOptions[optionIndex] : "");
      const active = normalizeCompareToken(selectedValue) === normalizeCompareToken(optionValue);
      const available = isEntryOptionValueAvailable(optionIndex, optionValue);

      return `
        <button
          type="button"
          class="gd-entry-product__chip${active ? " is-active" : ""}"
          data-gd-entry-option-index="${escapeHtml(optionIndex)}"
          data-gd-entry-option-value="${escapeHtml(optionValue)}"
          ${available ? "" : "disabled"}
          aria-pressed="${active ? "true" : "false"}"
        >${escapeHtml(optionValue)}</button>
      `;
    }

    function renderEntrySizeSplitButton(part, optionValue, model) {
      if (!model) return "";
      const normalizedPart = String(part || "").toLowerCase();
      if (normalizedPart !== "width" && normalizedPart !== "height") return "";

      const selectedValue = normalizedPart === "width" ? model.selectedWidth : model.selectedHeight;
      const normalizedSelected = normalizeSizeDimensionToken(selectedValue);
      const normalizedValue = normalizeSizeDimensionToken(optionValue);
      const active = normalizeCompareToken(normalizedSelected || selectedValue) === normalizeCompareToken(normalizedValue || optionValue);
      const available = isEntrySizeSplitValueAvailable(model, normalizedPart, optionValue);
      const token = normalizedPart === "width" ? ENTRY_SIZE_WIDTH_TOKEN : ENTRY_SIZE_HEIGHT_TOKEN;

      return `
        <button
          type="button"
          class="gd-entry-product__chip${active ? " is-active" : ""}"
          data-gd-entry-option-index="${escapeHtml(token)}"
          data-gd-entry-option-value="${escapeHtml(optionValue)}"
          ${available ? "" : "disabled"}
          aria-pressed="${active ? "true" : "false"}"
        >${escapeHtml(optionValue)}</button>
      `;
    }

    function renderEntrySelectField(label, optionIndex, values, selectedValue, availabilityResolver) {
      const options = Array.isArray(values) ? values : [];
      if (options.length === 0) return "";
      const normalizedSelected = normalizeCompareToken(selectedValue);
      const dropdownKey = normalizeEntryDropdownKey(optionIndex);
      const menuId = getEntryDropdownMenuId(dropdownKey);
      const expanded = isEntryDropdownOpen(dropdownKey);
      const resolvedValue =
        options.find((optionValue) => normalizeCompareToken(optionValue) === normalizedSelected) || String(selectedValue || "").trim() || options[0];

      return `
        <div class="gd-entry-product__select-field">
          <span class="gd-entry-product__select-label">${escapeHtml(label || "Select")}</span>
          <span class="gd-entry-product__dropdown${expanded ? " is-open" : ""}" data-gd-entry-dropdown="${escapeHtml(dropdownKey)}">
            <button
              type="button"
              class="gd-entry-product__dropdown-trigger"
              data-gd-entry-dropdown-toggle="${escapeHtml(dropdownKey)}"
              aria-haspopup="menu"
              aria-expanded="${expanded ? "true" : "false"}"
              aria-controls="${escapeHtml(menuId)}"
            >
              <span class="gd-entry-product__dropdown-value">${escapeHtml(resolvedValue)}</span>
              <span class="gd-entry-product__dropdown-icon" aria-hidden="true"></span>
            </button>
            <span class="gd-entry-product__dropdown-menu" id="${escapeHtml(menuId)}" role="menu" aria-label="${escapeHtml(label || "Select")}">
              ${options
                .map((optionValue) => {
                  const active = normalizeCompareToken(optionValue) === normalizedSelected;
                  const available = typeof availabilityResolver === "function" ? availabilityResolver(optionValue) : true;
                  return `
                    <button
                      type="button"
                      class="gd-entry-product__dropdown-option${active ? " is-active" : ""}"
                      data-gd-entry-dropdown-option-index="${escapeHtml(dropdownKey)}"
                      data-gd-entry-dropdown-option-value="${escapeHtml(optionValue)}"
                      role="menuitemradio"
                      aria-checked="${active ? "true" : "false"}"
                      ${available ? "" : "disabled"}
                    >
                      <span class="gd-entry-product__dropdown-option-label">${escapeHtml(optionValue)}</span>
                      <span class="gd-entry-product__dropdown-option-check" aria-hidden="true">${active ? "✓" : ""}</span>
                    </button>
                  `;
                })
                .join("")}
            </span>
          </span>
        </div>
      `;
    }

    function renderEntrySizeSplitSelect(part, model) {
      if (!model) return "";
      const normalizedPart = String(part || "").toLowerCase();
      if (normalizedPart !== "width" && normalizedPart !== "height") return "";
      const token = normalizedPart === "width" ? ENTRY_SIZE_WIDTH_TOKEN : ENTRY_SIZE_HEIGHT_TOKEN;
      const label = normalizedPart === "width" ? "Width" : "Height";
      const values = normalizedPart === "width" ? model.widthOptions : model.heightOptions;
      const selectedValue = normalizedPart === "width" ? model.selectedWidth : model.selectedHeight;
      return renderEntrySelectField(label, token, values, selectedValue, (optionValue) => {
        return isEntrySizeSplitValueAvailable(model, normalizedPart, optionValue);
      });
    }

    function renderEntryPanelPreferenceSelect() {
      return renderEntrySelectField(
        "Panel Preference",
        ENTRY_PANEL_PREFERENCE_TOKEN,
        getEntryPanelPreferenceOptionLabels(),
        formatEntryPanelPreferenceLabel(getEntryPanelPreferenceValue()),
        () => true
      );
    }

    function renderEntryDoorQuantityField() {
      const showCustomQuantityInput = isCustomDoorQuantityValue(getDoorQuantityValue());
      return `
        <div class="gd-entry-product__quantity-field">
          ${renderEntrySelectField(
            "Quantity",
            ENTRY_DOOR_QUANTITY_TOKEN,
            getEntryDoorQuantityOptionLabels(),
            getEntryDoorQuantitySelectValue(),
            () => true
          )}
          ${
            showCustomQuantityInput
              ? `
                <div class="gd-entry-product__custom-field gd-entry-product__custom-field--quantity">
                  <label class="gd-entry-product__custom-label" for="GdEntryDoorQuantity">Custom quantity</label>
                  <input
                    type="number"
                    id="GdEntryDoorQuantity"
                    class="gd-entry-product__quantity-input"
                    value="${escapeHtml(getEntryDoorCustomQuantityValue())}"
                    min="${ENTRY_DOOR_QUANTITY_PRESET_MAX + 1}"
                    max="${ENTRY_MAX_DOOR_QUANTITY}"
                    step="1"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    autocomplete="off"
                    enterkeyhint="done"
                    data-gd-entry-quantity-input
                  />
                </div>
              `
              : ""
          }
        </div>
      `;
    }

    function getEntryCollectionCardImageUrl(product) {
      const source = product && typeof product === "object" ? product : {};
      const images = Array.isArray(source.images) ? source.images.map((item) => String(item || "").trim()).filter(Boolean) : [];
      return String(images[0] || source.image || "").trim();
    }

    function inferEntryCollectionSubtitle(product) {
      const source = product && typeof product === "object" ? product : {};
      const titleToken = normalizeCompareToken(source.title || "");
      const matchedRule = ENTRY_SERIES_SUBTITLE_RULES.find((rule) => {
        return titleToken && titleToken.includes(String(rule.token || ""));
      });
      if (matchedRule) return String(matchedRule.label || "").trim();

      const description = String(source.description || "").replace(/\s+/g, " ").trim();
      if (!description) return "";
      if (/wood[-\s]?grain/i.test(description)) return "Wood-Grain";
      if (/carriage/i.test(description)) return "Carriage Style";
      if (/flush/i.test(description)) return "Flush Panel";
      if (/raised/i.test(description)) return "Raised Panel";
      if (/insulat/i.test(description)) return "Insulated";
      if (/modern/i.test(description)) return "Modern";

      const firstSentence = description.split(/[.!?]/).map((item) => item.trim()).filter(Boolean)[0] || "";
      const compact = firstSentence.split(/\s+/).slice(0, 2).join(" ");
      return compact && compact.length <= 20 ? compact : "";
    }

    function renderEntryColorSwatchButton(optionValue) {
      const entryProduct = state.entryProduct;
      if (!entryProduct) return "";
      const selectedValue = getEntryIndependentColorDisplayLabel(state.entryContext.colorLabel || "");
      const active = normalizeCompareToken(selectedValue) === normalizeCompareToken(optionValue);
      const fill = getColorSwatchFill(optionValue);

      return `
        <button
          type="button"
          class="gd-entry-product__swatch${active ? " is-active" : ""}"
          data-gd-entry-option-index="${escapeHtml(ENTRY_COLOR_TOKEN)}"
          data-gd-entry-option-value="${escapeHtml(optionValue)}"
          aria-label="${escapeHtml(optionValue)}"
          aria-pressed="${active ? "true" : "false"}"
          title="${escapeHtml(optionValue)}"
        >
          <span class="gd-entry-product__swatch-dot" style="background:${fill};"></span>
          <span class="gd-entry-product__swatch-label">${escapeHtml(optionValue)}</span>
        </button>
      `;
    }

    function renderEntryCollectionButton(product) {
      const active = state.entryProduct && String(state.entryProduct.handle || "") === String(product && product.handle ? product.handle : "");
      const imageUrl = getEntryCollectionCardImageUrl(product);
      const subtitle = inferEntryCollectionSubtitle(product);

      return `
        <button
          type="button"
          class="gd-entry-product__collection-item gd-entry-product__collection-item--series${active ? " is-active" : ""}"
          data-gd-entry-product-handle="${escapeHtml(product.handle || "")}"
        >
          <span class="gd-entry-product__collection-check" aria-hidden="true">${active ? "✓" : ""}</span>
          <span class="gd-entry-product__collection-media${imageUrl ? "" : " is-placeholder"}">
            ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="" loading="lazy">` : '<span class="gd-entry-product__collection-fallback" aria-hidden="true"></span>'}
          </span>
          <span class="gd-entry-product__collection-body">
            <span class="gd-entry-product__collection-name">${escapeHtml(product.title || "Door")}</span>
            ${subtitle ? `<span class="gd-entry-product__collection-note">${escapeHtml(subtitle)}</span>` : ""}
          </span>
        </button>
      `;
    }

    function renderDesignRailSectionHeading(indexLabel, title, description = "") {
      const indexText = String(indexLabel || "").trim();
      const titleText = String(title || "").trim() || "Section";
      const descriptionText = String(description || "").trim();
      const kicker = [indexText, titleText].filter(Boolean).join(" / ");

      return `
        <div class="gd-entry-product__section-head">
          <p class="gd-entry-product__section-kicker">${escapeHtml(kicker)}</p>
          ${descriptionText ? `<p class="gd-entry-product__section-note">${escapeHtml(descriptionText)}</p>` : ""}
        </div>
      `;
    }

    function buildEntryProductPanelMarkup() {
      if (state.entryProductLoading) {
        return `
          <section class="gd-entry-product gd-entry-product--rail" data-gd-entry-product aria-live="polite">
            <section class="gd-entry-product__card is-loading">
              <p class="gd-entry-product__loading">Loading door collection details...</p>
            </section>
          </section>
        `;
      }

      if (state.entryProductError) {
        return `
          <section class="gd-entry-product gd-entry-product--rail" data-gd-entry-product aria-live="polite">
            <section class="gd-entry-product__card is-error">
              <p class="gd-entry-product__error">${escapeHtml(state.entryProductError)}</p>
            </section>
          </section>
        `;
      }

      if (!hasLiveEntryProduct()) {
        return "";
      }

      const entryProduct = state.entryProduct;
      const entryProfile = getEntryProfile();
      const customSize = getEntryCustomSizeState();
      const sizeSplitModel = getEntrySizeSplitModel();
      const sizeIndex = Number(entryProduct.sizeOptionIndex);
      const sizeValues = Number.isInteger(sizeIndex) && sizeIndex >= 0 ? getEntryProductOptionValuesForIndex(sizeIndex) : [];
      const colorValues = getEntryCollectionColorValues(entryProduct);
      const sizeLabel = sizeIndex >= 0 ? String(entryProduct.optionNames[sizeIndex] || "Size") : "";
      const colorLabel = "Colour";
      const panelPreferenceValue = getEntryPanelPreferenceValue();
      const panelPreferenceNote =
        panelPreferenceValue === "24" ? '24" panel preference is limited to White, Iron Ore and Black.' : "";
      const panelPreferenceMarkup = `
        <div class="gd-entry-product__select-grid gd-entry-product__select-grid--paired-mobile">
          ${renderEntryPanelPreferenceSelect()}
          ${renderEntryDoorQuantityField()}
        </div>
      `;
      const selectedSize = String(entryProfile.sizeLabel || "").trim() || (sizeSplitModel ? String(sizeSplitModel.selectedSizeValue || "").trim() : sizeIndex >= 0 ? String(entryProduct.selectedOptions[sizeIndex] || "").trim() : "");
      const productsMarkup = state.entryCollectionProducts.map((product) => renderEntryCollectionButton(product)).join("");
      const customNote = customSize.valid ? `Custom size: ${customSize.label}` : customSize.message || "Use feet and inches, for example 8'9\".";
      const dimensionsMarkup = sizeSplitModel
        ? `
            <section class="gd-entry-product__section gd-entry-product__section--dimensions" data-gd-design-section="dimensions">
              ${renderDesignRailSectionHeading("01", "Dimensions", panelPreferenceNote)}
              <div class="gd-entry-product__select-grid gd-entry-product__select-grid--paired-mobile">
                ${renderEntrySizeSplitSelect("width", sizeSplitModel)}
                ${renderEntrySizeSplitSelect("height", sizeSplitModel)}
              </div>
              ${
                sizeSplitModel.isCustom
                  ? `
                    <div class="gd-entry-product__custom-fields">
                      <div class="gd-entry-product__custom-field">
                        <label class="gd-entry-product__custom-label" for="GdEntryCustomWidth">Custom width</label>
                        <input
                          type="text"
                          id="GdEntryCustomWidth"
                          class="gd-entry-product__custom-input${customSize.widthRaw && !customSize.width.valid ? " is-invalid" : ""}"
                          value="${escapeHtml(customSize.widthRaw)}"
                          data-gd-entry-custom-input="width"
                          inputmode="numeric"
                          autocomplete="off"
                          autocapitalize="off"
                          autocorrect="off"
                          spellcheck="false"
                          enterkeyhint="done"
                          placeholder="8'9&quot;"
                        />
                      </div>
                      <div class="gd-entry-product__custom-field">
                        <label class="gd-entry-product__custom-label" for="GdEntryCustomHeight">Custom height</label>
                        <input
                          type="text"
                          id="GdEntryCustomHeight"
                          class="gd-entry-product__custom-input${customSize.heightRaw && !customSize.height.valid ? " is-invalid" : ""}"
                          value="${escapeHtml(customSize.heightRaw)}"
                          data-gd-entry-custom-input="height"
                          inputmode="numeric"
                          autocomplete="off"
                          autocapitalize="off"
                          autocorrect="off"
                          spellcheck="false"
                          enterkeyhint="done"
                          placeholder="7'"
                        />
                      </div>
                    </div>
                    <p class="gd-entry-product__custom-note${customSize.valid ? "" : " is-error"}">${escapeHtml(customNote)}</p>
                  `
                  : ""
              }
              ${panelPreferenceMarkup}
            </section>
          `
        : sizeValues.length > 0
          ? `
              <section class="gd-entry-product__section gd-entry-product__section--dimensions" data-gd-design-section="dimensions">
                ${renderDesignRailSectionHeading("01", "Dimensions", panelPreferenceNote)}
                <div class="gd-entry-product__select-grid gd-entry-product__select-grid--single">
                  ${renderEntrySelectField(sizeLabel || "Size", sizeIndex, sizeValues, selectedSize, (optionValue) => {
                    return isEntryOptionValueAvailable(sizeIndex, optionValue);
                  })}
                </div>
                ${panelPreferenceMarkup}
              </section>
            `
          : "";
      const seriesMarkup =
        state.entryCollectionProducts.length > 0
          ? `
              <section class="gd-entry-product__section gd-entry-product__section--series gd-entry-product__collection" data-gd-design-section="series">
                ${renderDesignRailSectionHeading("02", "Series Selection")}
                <p class="gd-entry-product__collection-label">${escapeHtml(entry.doorCollectionTitle || "Door Collection")}</p>
                <div class="gd-entry-product__collection-list" data-gd-scroll-key="entry-series">
                  ${productsMarkup}
                </div>
              </section>
            `
          : "";
      const colorMarkup =
        colorValues.length > 0
          ? `
              <section class="gd-entry-product__section gd-entry-product__section--color gd-entry-product__group" data-gd-design-section="color">
                ${renderDesignRailSectionHeading("04", "Exterior Color")}
                <p class="gd-entry-product__group-label">${escapeHtml(colorLabel)}</p>
                <div class="gd-entry-product__swatches" data-gd-scroll-key="entry-color">
                  ${colorValues.map((value) => renderEntryColorSwatchButton(value)).join("")}
                </div>
              </section>
            `
          : "";

      return `
        <section class="gd-entry-product gd-entry-product--rail" data-gd-entry-product aria-live="polite">
          <section class="gd-entry-product__card">
            ${dimensionsMarkup}
            ${seriesMarkup}
            ${colorMarkup}
          </section>
        </section>
      `;
    }

    function onEntryCollectionProductSelect(productHandle) {
      ensureWindowCellConfigsForActiveDoor();
      const selected = selectEntryCollectionProduct(productHandle);
      if (!selected) return;
      state.designNavSectionId = "series";
      syncWindowChoiceSteps();
      syncWindowCellConfigsToCurrentSelection();
      syncWindowCellsToLayout();
      render();
    }

    function onEntryOptionSelect(optionIndexRaw, optionValue) {
      if (!hasLiveEntryProduct()) return;
      ensureWindowCellConfigsForActiveDoor();
      closeEntryDropdown();
      const optionToken = String(optionIndexRaw || "").trim();
      if (optionToken === ENTRY_COLOR_TOKEN) {
        state.designNavSectionId = "color";
        setEntryIndependentColorSelection(optionValue, state.entryProduct);
        state.windowColorAutoSync = true;
        syncWindowChoiceSteps();
        syncWindowCellConfigsToCurrentSelection();
        syncWindowCellsToLayout();
        render();
        return;
      }
      if (optionToken === ENTRY_PANEL_PREFERENCE_TOKEN) {
        state.designNavSectionId = "dimensions";
        setEntryPanelPreferenceSelection(optionValue);
        syncWindowChoiceSteps();
        syncWindowCellConfigsToCurrentSelection();
        syncWindowCellsToLayout();
        render();
        return;
      }
      if (optionToken === ENTRY_DOOR_QUANTITY_TOKEN) {
        state.designNavSectionId = "dimensions";
        if (normalizeCompareToken(optionValue) === normalizeCompareToken(ENTRY_DOOR_QUANTITY_CUSTOM_OPTION)) {
          const customQuantity = Math.max(ENTRY_DOOR_QUANTITY_PRESET_MAX + 1, getDoorQuantityValue());
          setActiveDoorQuantity(customQuantity);
        } else {
          setActiveDoorQuantity(optionValue);
        }
        render();
        return;
      }
      if (optionToken === ENTRY_SIZE_WIDTH_TOKEN) {
        state.designNavSectionId = "dimensions";
        updateEntryVariantSelectionBySizePart("width", optionValue);
        syncWindowChoiceSteps();
        syncWindowCellConfigsToCurrentSelection();
        syncWindowCellsToLayout();
        render();
        return;
      }
      if (optionToken === ENTRY_SIZE_HEIGHT_TOKEN) {
        state.designNavSectionId = "dimensions";
        updateEntryVariantSelectionBySizePart("height", optionValue);
        syncWindowChoiceSteps();
        syncWindowCellConfigsToCurrentSelection();
        syncWindowCellsToLayout();
        render();
        return;
      }
      const optionIndex = Number(optionIndexRaw);
      if (!Number.isInteger(optionIndex) || optionIndex < 0) return;
      if (hasLiveEntryProduct() && state.entryProduct) {
        if (optionIndex === Number(state.entryProduct.sizeOptionIndex)) {
          state.designNavSectionId = "dimensions";
        }
      }
      updateEntryVariantSelection(optionIndex, optionValue);
      syncWindowChoiceSteps();
      syncWindowCellConfigsToCurrentSelection();
      syncWindowCellsToLayout();
      render();
    }

    function appendQueryParam(url, key, value) {
      const safeUrl = String(url || "").trim();
      const safeKey = String(key || "").trim();
      const safeValue = String(value || "").trim();
      if (!safeUrl || !safeKey || !safeValue) return safeUrl;
      const separator = safeUrl.includes("?") ? "&" : "?";
      return `${safeUrl}${separator}${encodeURIComponent(safeKey)}=${encodeURIComponent(safeValue)}`;
    }

    function isConfiguredWindowCollection(collectionHandle) {
      return normalizeCompareToken(collectionHandle) === normalizeCompareToken(entry.windowCollectionHandle || "window");
    }

    async function requestWindowCollectionRawProducts(collectionHandle) {
      const endpoint = String(entry.windowProductsEndpoint || "").trim();
      if (!endpoint || !isConfiguredWindowCollection(collectionHandle)) return null;

      let url = appendQueryParam(endpoint, "collection", collectionHandle);
      url = appendQueryParam(url, "product_type", entry.windowProductType || "window");

      const response = await fetch(url, {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      return Array.isArray(payload && payload.products) ? payload.products : [];
    }

    async function requestCollectionRawProducts(collectionHandle) {
      if (isConfiguredWindowCollection(collectionHandle)) {
        try {
          const windowProducts = await requestWindowCollectionRawProducts(collectionHandle);
          if (Array.isArray(windowProducts)) return windowProducts;
        } catch (error) {
          console.warn("[gd-configurator] failed to fetch full window products; falling back to Shopify Ajax products", error);
        }
      }

      const response = await fetch(`/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250`, {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      return Array.isArray(payload && payload.products) ? payload.products : [];
    }

    async function requestCollectionProducts(collectionHandle) {
      const rawProducts = await requestCollectionRawProducts(collectionHandle);
      return sortEntryCollectionProducts(rawProducts.map((product) => parseEntryProduct(product)).filter(Boolean));
    }

    function getEntryCollectionProductSortRank(product) {
      const source = product && typeof product === "object" ? product : {};
      const text = normalizeCompareToken(`${source.handle || ""} ${source.title || ""}`);
      if (!text) return Number.MAX_SAFE_INTEGER;

      const matchedRule = ENTRY_SERIES_SORT_RULES.find((rule) => {
        return text.includes(String(rule.token || ""));
      });
      if (matchedRule) return Number(matchedRule.rank || 0);

      const seriesMatch = text.match(/\b(300|400|500|600|800|900)\b/);
      if (seriesMatch) return Number(seriesMatch[1]);

      return 2000;
    }

    function sortEntryCollectionProducts(products) {
      const list = Array.isArray(products) ? products : [];
      return list
        .map((product, index) => {
          return {
            product,
            index,
            rank: getEntryCollectionProductSortRank(product)
          };
        })
        .sort((left, right) => {
          if (left.rank !== right.rank) return left.rank - right.rank;
          return left.index - right.index;
        })
        .map((item) => item.product);
    }

    function extractMediaUrl(value) {
      if (value && typeof value === "object") {
        return String(value.src || value.url || "").trim();
      }
      return String(value || "").trim();
    }

    function isCollectionVariantAvailable(variant) {
      const quantity = Number(variant && variant.inventory_quantity);
      const hasTrackedQty = Number.isFinite(quantity);
      const hasStock = hasTrackedQty ? quantity > 0 : true;
      const availableByShopify =
        variant && variant.available === false ? false : variant && variant.availableForSale === false ? false : true;
      return availableByShopify && hasStock;
    }

    function buildTrackComponentTitle(product, variant) {
      const productTitle = String(product && product.title ? product.title : "").trim() || "Track";
      const variantTitle = String(variant && (variant.public_title || variant.title) ? variant.public_title || variant.title : "").trim();
      if (!variantTitle || /default title/i.test(variantTitle)) return productTitle;
      return `${productTitle} - ${variantTitle}`;
    }

    function normalizeTrackToken(value) {
      return normalizeCompareToken(value).replace(/\s+/g, "");
    }

    function isTrackTokenMatch(value, token) {
      const haystack = normalizeTrackToken(value);
      const needle = normalizeTrackToken(token);
      if (!haystack || !needle) return false;
      if (haystack.includes(needle)) return true;
      if (needle === "lhr") {
        return haystack.includes("lowheadroom") || haystack.includes("lowhead");
      }
      return false;
    }

    function getTrackOptionSearchText(option) {
      if (!option || typeof option !== "object") return "";
      return `${String(option.label || "").trim()} ${String(option.handle || "").trim()}`.trim();
    }

    function getTrackConfiguredSortOrder() {
      const configured = Array.isArray(entry.trackSortOrder) ? entry.trackSortOrder : [];
      const cleaned = configured.map((value) => String(value || "").trim()).filter(Boolean);
      return cleaned.length > 0 ? cleaned : defaultEntryConfig.trackSortOrder.slice();
    }

    function getTrackOptionSortRank(option) {
      const text = typeof option === "string" ? option : getTrackOptionSearchText(option);
      const sortOrder = getTrackConfiguredSortOrder();
      for (let index = 0; index < sortOrder.length; index += 1) {
        if (isTrackTokenMatch(text, sortOrder[index])) return index;
      }
      if (isTrackTokenMatch(text, "notrack")) return 99;
      return 10 + sortOrder.length;
    }

    function isNoTrackOption(option) {
      const text = getTrackOptionSearchText(option);
      return isTrackTokenMatch(text, "notrack") || isTrackTokenMatch(text, "withouttrack") || isTrackTokenMatch(text, "none");
    }

    function findPreferredTrackDefaultOption(options) {
      const list = Array.isArray(options) ? options : [];
      if (list.length === 0) return null;

      const configuredDefault = String(entry.trackDefaultOption || "").trim();
      if (configuredDefault) {
        const preferredConfigured = list.find((option) => isTrackTokenMatch(getTrackOptionSearchText(option), configuredDefault));
        if (preferredConfigured && !isNoTrackOption(preferredConfigured)) return preferredConfigured;
      }

      const sortOrder = getTrackConfiguredSortOrder();
      for (const token of sortOrder) {
        const matched = list.find((option) => {
          if (isNoTrackOption(option)) return false;
          return isTrackTokenMatch(getTrackOptionSearchText(option), token);
        });
        if (matched) return matched;
      }

      const firstRealTrack = list.find((option) => !isNoTrackOption(option));
      return firstRealTrack || list[0];
    }

    function ensureTrackDefaultSelectionIfMissing(step) {
      if (!step || !isTrackSystemStep(step)) return false;
      if (!Array.isArray(step.options) || step.options.length === 0) return false;
      const selectedSet = getSelectionSet(step.id);
      if (selectedSet.size > 0) return false;
      const preferred = findPreferredTrackDefaultOption(step.options);
      if (!preferred) return false;
      selectedSet.clear();
      selectedSet.add(preferred.id);
      return true;
    }

    function buildTrackStepOptions(step, rawProducts) {
      const desiredType = normalizeCompareToken(entry.trackProductType || "track");
      const products = Array.isArray(rawProducts) ? rawProducts : [];
      const filteredProducts = products.filter((product) => {
        const productType = normalizeCompareToken(product && (product.product_type || product.productType || ""));
        if (!desiredType) return Boolean(productType);
        return productType === desiredType || productType.includes(desiredType);
      });

      const trackOptions = filteredProducts
        .map((product) => {
          const rawVariants = Array.isArray(product && product.variants) ? product.variants : [];
          const productImage =
            extractMediaUrl(product && (product.featured_image || product.image)) ||
            extractMediaUrl(Array.isArray(product && product.images) ? product.images[0] : "");

          const variants = rawVariants
            .map((variant) => {
              const id = coerceNumericId(variant && variant.id);
              if (!id) return null;
              const variantImage = extractMediaUrl(variant && (variant.featured_image || variant.image));
              return {
                id,
                sku: String(variant && variant.sku ? variant.sku : "").trim(),
                title: String(variant && (variant.public_title || variant.title) ? variant.public_title || variant.title : "").trim(),
                price_cents: moneyToCents(variant && variant.price),
                available: isCollectionVariantAvailable(variant),
                image: variantImage || productImage
              };
            })
            .filter(Boolean);

          if (variants.length === 0) return null;

          const selectedVariant = variants.find((variant) => variant.available) || variants[0];
          if (!selectedVariant) return null;

          const label = String(product && product.title ? product.title : "").trim() || buildTrackComponentTitle(product, selectedVariant);
          const baseHandle = toHandleToken((product && product.handle) || label || `track-${selectedVariant.id}`);
          const optionHandle = `gd-opt-track-${baseHandle || selectedVariant.id}`;
          const optionImage = String(selectedVariant.image || productImage || "").trim();

          return normalizeOption(step, {
            id: `gd-track-${selectedVariant.id}`,
            handle: optionHandle,
            label,
            image: optionImage,
            components: [
              {
                variant_id: selectedVariant.id,
                sku: selectedVariant.sku,
                title: buildTrackComponentTitle(product, selectedVariant),
                image: optionImage,
                price_cents: selectedVariant.price_cents,
                qty: 1,
                available: selectedVariant.available
              }
            ]
          });
        })
        .filter(Boolean)
        .sort((optionA, optionB) => {
          const rankA = getTrackOptionSortRank(optionA);
          const rankB = getTrackOptionSortRank(optionB);
          if (rankA !== rankB) return rankA - rankB;
          return String(optionA.label || "").localeCompare(String(optionB.label || ""), undefined, {
            numeric: true,
            sensitivity: "base"
          });
        });

      const noTrackOption = normalizeOption(step, {
        id: "gd-track-none",
        handle: "gd-opt-track-none",
        label: "No Track",
        components: []
      });

      if (noTrackOption && !trackOptions.some((option) => String(option.id) === String(noTrackOption.id))) {
        trackOptions.push(noTrackOption);
      }

      return trackOptions;
    }

    function pruneSelectionForStepOptions(step, options) {
      if (!step) return;
      const validOptionIds = new Set((Array.isArray(options) ? options : []).map((option) => String(option.id)));
      const selectedSet = getSelectionSet(step.id);

      for (const selectedId of Array.from(selectedSet.values())) {
        if (!validOptionIds.has(String(selectedId))) {
          selectedSet.delete(selectedId);
        }
      }

      if (String(state.activeStepId || "") === String(step.id) && !validOptionIds.has(String(state.activeOptionId || ""))) {
        state.activeOptionId = "";
      }
    }

    async function replaceTrackSystemOptionsFromCollection() {
      const trackStepHandle = String(entry.trackStepHandle || "").trim();
      const trackStep =
        (trackStepHandle ? findStepByHandle(trackStepHandle) : null) ||
        steps.find((step) => /track\s*system/i.test(String(step && step.title ? step.title : ""))) ||
        null;
      if (!trackStep) return;

      const collectionHandle = String(entry.trackCollectionHandle || "").trim();
      if (!collectionHandle) return;

      try {
        const rawProducts = await requestCollectionRawProducts(collectionHandle);
        const trackOptions = buildTrackStepOptions(trackStep, rawProducts);
        if (typeof trackStep.__gdOriginalRequired !== "boolean") {
          trackStep.__gdOriginalRequired = Boolean(trackStep.required);
        }

        trackStep.options = trackOptions;
        trackStep.required = trackOptions.length > 0 ? Boolean(trackStep.__gdOriginalRequired) : false;
        pruneSelectionForStepOptions(trackStep, trackOptions);
        ensureTrackDefaultSelectionIfMissing(trackStep);

        if (trackOptions.length === 0) {
          console.warn("[gd-configurator] no track options matched configured collection filter", {
            collectionHandle,
            productType: entry.trackProductType
          });
          setHint("No Track products found in Accessories (type: track).", "error");
        }
      } catch (error) {
        console.error("[gd-configurator] failed to replace track system options from collection", error);
      }
    }

    function buildWindowComponentTitle(product, variant) {
      const productTitle = String(product && product.title ? product.title : "").trim() || "Window";
      const variantTitle = String(variant && variant.title ? variant.title : "").trim();
      if (!variantTitle || /default title/i.test(variantTitle)) return productTitle;
      return `${productTitle} - ${variantTitle}`;
    }

    function ensureSyntheticWindowChoiceStep(stepHandle, fallbackHandle, title, position) {
      const resolvedHandle = String(stepHandle || fallbackHandle || "").trim() || String(fallbackHandle || "").trim();
      if (!resolvedHandle) return null;
      const existing = findStepByHandle(resolvedHandle);
      if (existing) return existing;

      const synthetic = normalizeStep({
        id: `__${resolvedHandle}__`,
        handle: resolvedHandle,
        title: title || "Window Option",
        position: Number.isFinite(Number(position)) ? Number(position) : 0,
        required: true,
        multi_select: false,
        options: []
      });
      synthetic.__gdSynthetic = true;
      steps.push(synthetic);
      steps.sort((stepA, stepB) => resolveStepPosition(stepA) - resolveStepPosition(stepB));
      return synthetic;
    }

    function getWindowOptionIndex(product, token, fallback = -1) {
      const optionNames = Array.isArray(product && product.optionNames) ? product.optionNames : [];
      const normalizedToken = normalizeCompareToken(token);
      if (!normalizedToken) return fallback;

      for (let index = 0; index < optionNames.length; index += 1) {
        const name = normalizeCompareToken(optionNames[index]);
        if (!name) continue;
        if (name.includes(normalizedToken)) return index;
      }
      return fallback;
    }

    function getWindowChoiceMeta(product) {
      if (!product) {
        return {
          glassIndex: -1,
          colorIndex: -1,
          insertIndex: -1
        };
      }

      const optionNames = Array.isArray(product.optionNames) ? product.optionNames : [];
      const glassIndexFromName = getWindowOptionIndex(product, "glass", -1);
      const colorIndexFromProduct = Number(product.colorOptionIndex);
      const colorIndexFromName = getWindowOptionIndex(product, "color", -1);
      const colorIndexCandidate = Number.isInteger(colorIndexFromProduct) && colorIndexFromProduct >= 0 ? colorIndexFromProduct : colorIndexFromName;
      const insertIndexFromName = getWindowOptionIndex(product, "insert", -1);

      let glassIndex = glassIndexFromName >= 0 ? glassIndexFromName : optionNames.length > 0 ? 0 : -1;
      let colorIndex = colorIndexCandidate >= 0 ? colorIndexCandidate : -1;
      let insertIndex = insertIndexFromName;

      if (insertIndex < 0 && optionNames.length >= 3) {
        const fallbackIndexes = [0, 1, 2].filter((index) => index !== glassIndex && index !== colorIndex);
        insertIndex = fallbackIndexes.length > 0 ? fallbackIndexes[0] : -1;
      }

      if (glassIndex === colorIndex && colorIndex >= 0) {
        glassIndex = optionNames.length > 0 ? 0 : -1;
      }
      if (insertIndex === glassIndex || insertIndex === colorIndex) {
        insertIndex = -1;
      }

      return {
        glassIndex,
        colorIndex,
        insertIndex
      };
    }

    function getWindowChoiceValueFromVariant(variant, optionIndex) {
      if (!variant || !Number.isInteger(optionIndex) || optionIndex < 0) return "";
      return String(variant.optionValues && variant.optionValues[optionIndex] ? variant.optionValues[optionIndex] : "").trim();
    }

    function getWindowStepSelectedValue(step) {
      const option = getSelectedOptionFromStep(step);
      if (!option) return "";
      return String(option.window_value || option.label || "").trim();
    }

    function setWindowStepSelectionByValue(step, value) {
      if (!step) return;
      const selectedSet = getSelectionSet(step.id);
      selectedSet.clear();
      const desired = normalizeCompareToken(value);
      if (!desired) return;
      const option =
        (Array.isArray(step.options) ? step.options : []).find((candidate) => {
          return normalizeCompareToken(candidate && (candidate.window_value || candidate.label || "")) === desired;
        }) || null;
      if (option) selectedSet.add(option.id);
    }

    function areColorLabelsEquivalent(left, right) {
      const leftLabel = String(left || "").trim();
      const rightLabel = String(right || "").trim();
      if (!leftLabel || !rightLabel) return false;

      const leftHandle = inferColorHandle(leftLabel);
      const rightHandle = inferColorHandle(rightLabel);
      if (leftHandle && rightHandle) return leftHandle === rightHandle;

      return normalizeCompareToken(leftLabel) === normalizeCompareToken(rightLabel);
    }

    function findWindowColorOptionForDoorColor(options, doorColorLabel) {
      const desiredColor = String(doorColorLabel || "").trim();
      const list = Array.isArray(options) ? options : [];
      if (!desiredColor || list.length === 0) return null;

      const byEquivalentLabel =
        list.find((candidate) => {
          const candidateValue = String(candidate && (candidate.window_value || candidate.label || "")).trim();
          return areColorLabelsEquivalent(desiredColor, candidateValue);
        }) || null;
      if (byEquivalentLabel) return byEquivalentLabel;

      const desiredToken = normalizeCompareToken(desiredColor);
      if (!desiredToken) return null;

      return (
        list.find((candidate) => {
          const candidateValue = normalizeCompareToken(candidate && (candidate.window_value || candidate.label || ""));
          return candidateValue === desiredToken;
        }) || null
      );
    }

    function syncWindowColorSelection(step, options) {
      if (!step || !Array.isArray(options) || options.length === 0) return false;

      if (state.windowColorAutoSync !== false && stepHasSelection(step)) {
        const matchedDoorColor = findWindowColorOptionForDoorColor(options, state.entryContext.colorLabel);
        if (matchedDoorColor) {
          setWindowStepSelectionByValue(step, matchedDoorColor.window_value || matchedDoorColor.label || "");
          return true;
        }
      }

      return false;
    }

    function ensureInitialWindowDefaults() {
      const layoutStep = getWindowLayoutStep();
      if (!layoutStep || !Array.isArray(layoutStep.options) || layoutStep.options.length === 0) return false;

      let selectedLayout = getSelectedOptionFromStep(layoutStep);
      if (!selectedLayout) {
        const nonNoWindowOptions = layoutStep.options.filter((option) => !isNoWindowLayoutOption(option));
        const preferred = nonNoWindowOptions.find((option) => {
          const code = normalizeWindowSizeCode(`${option && option.handle ? option.handle : ""} ${option && option.label ? option.label : ""}`);
          return code === WINDOW_DEFAULT_LAYOUT_SIZE_CODE;
        });
        const nextLayout = preferred || nonNoWindowOptions[0] || null;
        if (nextLayout) {
          const selectedSet = getSelectionSet(layoutStep.id);
          selectedSet.clear();
          selectedSet.add(nextLayout.id);
          selectedLayout = nextLayout;
        }
      }

      syncWindowChoiceSteps();
      syncWindowCellsToLayout();
      clearWindowHoverCell();
      return Boolean(selectedLayout) && !isNoWindowLayoutOption(selectedLayout);
    }

    function buildWindowLayoutStepOptions(step, rawProducts) {
      const desiredType = normalizeCompareToken(entry.windowProductType || "window");
      const products = Array.isArray(rawProducts) ? rawProducts : [];
      const filteredRawProducts = products.filter((product) => {
        const productType = normalizeCompareToken(product && (product.product_type || product.productType || ""));
        if (!desiredType) return true;
        return productType === desiredType || productType.includes(desiredType);
      });

      const parsedProducts = filteredRawProducts
        .map((product) => parseEntryProduct(product))
        .filter((product) => {
          return product && Array.isArray(product.variants) && product.variants.length > 0;
        });

      const optionProductById = new Map();
      const noWindowOptions = (Array.isArray(step && step.options) ? step.options : []).filter((option) => isNoWindowLayoutOption(option));

      const dynamicOptions = parsedProducts
        .map((product) => {
          const selectedVariant = product.variants.find((variant) => variant.available) || product.variants[0] || null;
          if (!selectedVariant) return null;

          const optionId = `gd-window-product-${product.id || selectedVariant.id}`;
          const label = String(product.title || "").trim() || `Window ${product.handle || product.id || ""}`.trim();
          const optionImage = String(selectedVariant.image || product.image || "").trim();
          const option = normalizeOption(step, {
            id: optionId,
            handle: `gd-opt-window-product-${toHandleToken(product.handle || label || optionId)}`,
            label,
            image: optionImage,
            components: []
          });
          optionProductById.set(String(option.id), product);
          return option;
        })
        .filter(Boolean);

      const options = [...noWindowOptions, ...dynamicOptions];
      return {
        options,
        parsedProducts,
        optionProductById
      };
    }

    function getWindowGlassStep() {
      const handle = String(entry.windowGlassStepHandle || "").trim();
      return (
        (handle ? findStepByHandle(handle) : null) ||
        steps.find((step) => String(step && step.handle ? step.handle : "") === WINDOW_GLASS_STEP_HANDLE) ||
        steps.find((step) => stepTitleHasTokens(step, ["glass", "type"])) ||
        steps.find((step) => stepTitleHasTokens(step, ["window", "glass"])) ||
        null
      );
    }

    function getWindowColorStep() {
      const handle = String(entry.windowColorStepHandle || "").trim();
      return (
        (handle ? findStepByHandle(handle) : null) ||
        steps.find((step) => String(step && step.handle ? step.handle : "") === WINDOW_COLOR_STEP_HANDLE) ||
        steps.find((step) => stepTitleHasTokens(step, ["window", "color"])) ||
        null
      );
    }

    function getWindowInsertStep() {
      const handle = String(entry.windowInsertStepHandle || "").trim();
      return (
        (handle ? findStepByHandle(handle) : null) ||
        steps.find((step) => String(step && step.handle ? step.handle : "") === WINDOW_INSERT_STEP_HANDLE) ||
        steps.find((step) => stepTitleHasTokens(step, ["window", "insert"])) ||
        null
      );
    }

    function getWindowChoiceStepsInOrder() {
      return [getWindowGlassStep(), getWindowColorStep(), getWindowInsertStep()].filter(Boolean);
    }

    function getWindowPlacementStepLabel(step) {
      if (isWindowGlassStep(step)) return "Glass Type";
      if (isWindowColorStep(step)) return "Frame Color";
      if (isWindowInsertStep(step)) return "Insert";
      return getStepDisplayTitle(step, "Step");
    }

    function formatLabelList(values) {
      const list = (Array.isArray(values) ? values : []).map((value) => String(value || "").trim()).filter(Boolean);
      if (list.length <= 1) return list[0] || "";
      if (list.length === 2) return `${list[0]} and ${list[1]}`;
      return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
    }

    function getWindowPlacementRequiredSteps() {
      const selectedWindowProduct = getSelectedWindowLayoutProduct();
      if (!selectedWindowProduct) return [];

      return [getWindowGlassStep(), getWindowColorStep(), getWindowInsertStep()].filter((step) => {
        if (!step || isStepHiddenFromRail(step) || !Array.isArray(step.options) || step.options.length === 0) {
          return false;
        }
        if (isWindowInsertStep(step)) {
          return shouldShowWindowInsertUi();
        }
        return true;
      });
    }

    function getMissingWindowPlacementSteps() {
      return getWindowPlacementRequiredSteps().filter((step) => !getSelectedOptionFromStep(step));
    }

    function buildWindowPlacementValidationMessage(missingSteps = getMissingWindowPlacementSteps()) {
      const labels = missingSteps.map((step) => getWindowPlacementStepLabel(step));
      const formatted = formatLabelList(labels);
      if (!formatted) return "";
      return `Select ${formatted} before placing a window in preview.`;
    }

    function isWindowPlacementValidationHint(message) {
      return /before placing a window in preview/i.test(String(message || ""));
    }

    function syncWindowPlacementAttentionState(updateHint = false) {
      const activeDoor = getActiveDoorState();
      if (!activeDoor || !activeDoor.windowPlacementAttention) return false;

      const missingSteps = getMissingWindowPlacementSteps();
      if (missingSteps.length === 0) {
        activeDoor.windowPlacementAttention = false;
        if (isWindowPlacementValidationHint(state.hintMessage)) {
          setHint("");
        }
        return false;
      }

      if (updateHint) {
        setHint(buildWindowPlacementValidationMessage(missingSteps), "error");
      }
      return true;
    }

    function revealWindowPlacementValidation(missingSteps = getMissingWindowPlacementSteps()) {
      if (!Array.isArray(missingSteps) || missingSteps.length === 0) return false;

      const activeDoor = getActiveDoorState();
      if (activeDoor) {
        activeDoor.windowPlacementAttention = true;
      }

      const firstMissingStep = missingSteps[0] || null;
      state.designNavSectionId = "windows";
      if (firstMissingStep) {
        state.activeStepId = firstMissingStep.id;
        state.activeOptionId = "";
        state.mobileOpenStepId = firstMissingStep.id;
        state.lastTouchedStepId = firstMissingStep.id;
        state.lastTouchedOptionId = "";
      }

      setHint(buildWindowPlacementValidationMessage(missingSteps), "error");
      render();
      requestAnimationFrame(() => {
        scrollDesignRailToSection("windows");
      });
      return true;
    }

    function getSelectedWindowLayoutProduct() {
      const layoutStep = getWindowLayoutStep();
      if (!layoutStep) return null;
      const selectedLayout = getSelectedOptionFromStep(layoutStep);
      if (!selectedLayout || isNoWindowLayoutOption(selectedLayout)) return null;
      return state.windowProductByLayoutOptionId.get(String(selectedLayout.id)) || null;
    }

    function getWindowSelectedChoiceValues() {
      const glassValue = getWindowStepSelectedValue(getWindowGlassStep());
      const colorValue = getWindowStepSelectedValue(getWindowColorStep());
      const insertValue = getWindowStepSelectedValue(getWindowInsertStep());
      return {
        glass: glassValue,
        color: colorValue,
        insert: normalizeWindowInsertSelectionForGlass(glassValue, insertValue)
      };
    }

    function hasWindowVariantSelections() {
      const selectedWindowProduct = getSelectedWindowLayoutProduct();
      if (!selectedWindowProduct) return false;

      const glassStep = getWindowGlassStep();
      const colorStep = getWindowColorStep();
      const insertStep = getWindowInsertStep();

      if (glassStep && Array.isArray(glassStep.options) && glassStep.options.length > 0 && !stepHasSelection(glassStep)) {
        return false;
      }
      if (colorStep && Array.isArray(colorStep.options) && colorStep.options.length > 0 && !stepHasSelection(colorStep)) {
        return false;
      }
      if (
        insertStep &&
        Array.isArray(insertStep.options) &&
        insertStep.options.length > 0 &&
        shouldShowWindowInsertUi() &&
        !stepHasSelection(insertStep)
      ) {
        return false;
      }

      return true;
    }

    function doesWindowGlassSupportInsert(value) {
      const token = normalizeCompareToken(value);
      if (!token) return false;
      return WINDOW_INSERT_SUPPORTED_GLASS_TOKENS.includes(token);
    }

    function normalizeWindowInsertSelectionForGlass(glassValue, insertValue) {
      if (doesWindowGlassSupportInsert(glassValue)) {
        return String(insertValue || "").trim();
      }
      return WINDOW_NO_INSERT_LABEL;
    }

    function getDisplayWindowInsertLabel(glassValue, insertValue) {
      if (!doesWindowGlassSupportInsert(glassValue)) {
        return "";
      }
      return String(insertValue || "").trim();
    }

    function shouldShowWindowInsertUi() {
      if (!getSelectedWindowLayoutProduct()) return false;
      const selectedValues = getWindowSelectedChoiceValues();
      return doesWindowGlassSupportInsert(selectedValues && selectedValues.glass);
    }

    function isNoWindowInsertChoiceValue(value) {
      const token = normalizeCompareToken(value);
      return token === "no insert" || token === "no inset" || token === "none";
    }

    function isWindowInsertValueEmpty(value) {
      const token = normalizeCompareToken(value);
      return !token || token === "default title" || isNoWindowInsertChoiceValue(token);
    }

    function hasWindowSkuTrailingInsertCode(sku) {
      return WINDOW_INSERT_CODE_SUFFIX_PATTERN.test(String(sku || "").trim());
    }

    function hasWindowTitleStandaloneInsertCode(title) {
      return WINDOW_INSERT_CODE_TITLE_PATTERN.test(String(title || "").trim());
    }

    function isWindowVariantCompatibleWithNoInsertSelection(variant, meta) {
      if (!variant) return false;

      const variantSku = String(variant && variant.sku ? variant.sku : "").trim();
      if (variantSku && hasWindowSkuTrailingInsertCode(variantSku)) {
        return false;
      }

      const variantTitle = String(variant && variant.title ? variant.title : "").trim();
      if (variantTitle && hasWindowTitleStandaloneInsertCode(variantTitle)) {
        return false;
      }

      const insertIndex = Number(meta && meta.insertIndex);
      if (Number.isInteger(insertIndex) && insertIndex >= 0) {
        return isWindowInsertValueEmpty(getWindowChoiceValueFromVariant(variant, insertIndex));
      }

      return Boolean(variantSku || variantTitle);
    }

    function buildNoInsertWindowChoiceOption(step, available = true) {
      return normalizeOption(step, {
        id: "gd-window-insert-none",
        handle: "gd-opt-window-insert-none",
        label: WINDOW_NO_INSERT_LABEL,
        window_value: WINDOW_NO_INSERT_LABEL,
        available,
        components: []
      });
    }

    function variantValueMatches(expected, actual) {
      const expectedToken = normalizeCompareToken(expected);
      if (!expectedToken) return true;
      if (isNoWindowInsertChoiceValue(expectedToken)) {
        return isWindowInsertValueEmpty(actual);
      }
      return expectedToken === normalizeCompareToken(actual);
    }

    function isWindowVariantMatchingSelections(variant, meta, selectedValues, ignoreKey = "") {
      if (!variant) return false;
      const ignoredKeys = new Set(
        (Array.isArray(ignoreKey) ? ignoreKey : [ignoreKey]).map((value) => String(value || "").trim()).filter(Boolean)
      );
      const checks = [
        { key: "glass", index: Number(meta && meta.glassIndex) },
        { key: "color", index: Number(meta && meta.colorIndex) },
        { key: "insert", index: Number(meta && meta.insertIndex) }
      ];

      for (const check of checks) {
        if (!Number.isInteger(check.index) || check.index < 0) continue;
        if (ignoredKeys.has(check.key)) continue;
        const expected = String(selectedValues && selectedValues[check.key] ? selectedValues[check.key] : "").trim();
        if (!expected) continue;
        const actual = getWindowChoiceValueFromVariant(variant, check.index);
        if (!variantValueMatches(expected, actual)) return false;
      }
      return true;
    }

    function findWindowVariantBySelections(product, meta, selectedValues, availableOnly = false, allowInsertFallback = false) {
      if (!product || !Array.isArray(product.variants)) return null;
      const selectingNoInsert = isNoWindowInsertChoiceValue(selectedValues && selectedValues.insert);
      for (const variant of product.variants) {
        if (!variant) continue;
        if (availableOnly && !variant.available) continue;
        if (!isWindowVariantMatchingSelections(variant, meta, selectedValues, "")) continue;
        if (selectingNoInsert && !isWindowVariantCompatibleWithNoInsertSelection(variant, meta)) continue;
        return variant;
      }
      if (allowInsertFallback && selectingNoInsert) {
        for (const variant of product.variants) {
          if (!variant) continue;
          if (availableOnly && !variant.available) continue;
          if (isWindowVariantMatchingSelections(variant, meta, selectedValues, ["insert"])) return variant;
        }
      }
      return null;
    }

    function isWindowChoiceValueAvailable(product, meta, key, optionValue, selectedValues) {
      if (!product || !Array.isArray(product.variants)) return false;
      const glassSupportsInsert = doesWindowGlassSupportInsert(selectedValues && selectedValues.glass);
      const selectingNoInsert = key === "insert" && isNoWindowInsertChoiceValue(optionValue);
      const allowSyntheticNoInsert = isApiOrderMode();
      if (key === "insert" && !glassSupportsInsert) {
        return selectingNoInsert;
      }
      const index = Number(meta && meta[`${key}Index`]);
      const hasOptionIndex = Number.isInteger(index) && index >= 0;
      if (key === "insert" && selectingNoInsert && !hasOptionIndex) {
        return product.variants.some((variant) => {
          if (!variant || !variant.available) return false;
          return isWindowVariantMatchingSelections(variant, meta, selectedValues, ["insert"]);
        });
      }
      if (!hasOptionIndex) return false;

      const ignoreKeys = [key];
      if (allowSyntheticNoInsert && (selectingNoInsert || isNoWindowInsertChoiceValue(selectedValues && selectedValues.insert))) {
        ignoreKeys.push("insert");
      }
      const matching = product.variants.filter((variant) => {
        if (!variant || !variant.available) return false;
        if (!isWindowVariantMatchingSelections(variant, meta, selectedValues, ignoreKeys)) return false;
        const actual = getWindowChoiceValueFromVariant(variant, index);
        if (selectingNoInsert) {
          return isWindowInsertValueEmpty(actual) || allowSyntheticNoInsert;
        }
        return variantValueMatches(optionValue, actual);
      });

      return matching.length > 0;
    }

    function buildWindowChoiceStepOptions(step, product, meta, key, selectedValues) {
      if (!step || !product || !Array.isArray(product.variants)) return [];
      const index = Number(meta && meta[`${key}Index`]);
      const buildingInsertOptions = key === "insert";
      if (buildingInsertOptions && !doesWindowGlassSupportInsert(selectedValues && selectedValues.glass)) {
        return [buildNoInsertWindowChoiceOption(step, true)];
      }
      if (!buildingInsertOptions && (!Number.isInteger(index) || index < 0)) return [];

      const optionValueMap = new Map();
      for (const variant of product.variants) {
        const value = getWindowChoiceValueFromVariant(variant, index);
        if (buildingInsertOptions && isWindowInsertValueEmpty(value)) continue;
        if (!value || /default title/i.test(value)) continue;
        const token = normalizeCompareToken(value);
        if (!token || optionValueMap.has(token)) continue;
        optionValueMap.set(token, value);
      }

      const options = Array.from(optionValueMap.values())
        .map((value) => {
          const token = toHandleToken(value || `${key}-option`);
          const available = isWindowChoiceValueAvailable(product, meta, key, value, selectedValues);
          return normalizeOption(step, {
            id: `gd-window-${key}-${token}`,
            handle: `gd-opt-window-${key}-${token}`,
            label: value,
            window_value: value,
            available,
            components: []
          });
        })
        .sort((optionA, optionB) => {
          return String(optionA.label || "").localeCompare(String(optionB.label || ""), undefined, {
            numeric: true,
            sensitivity: "base"
          });
        });

      if (buildingInsertOptions) {
        const noInsertAvailable = isWindowChoiceValueAvailable(product, meta, key, WINDOW_NO_INSERT_LABEL, selectedValues);
        if (noInsertAvailable) {
          options.unshift(buildNoInsertWindowChoiceOption(step, true));
        }
      }

      return options;
    }

    function syncWindowChoiceStepOptions(step, options) {
      if (!step) return;

      if (typeof step.__gdOriginalRequired !== "boolean") {
        step.__gdOriginalRequired = Boolean(step.required);
      }
      const nextOptions = Array.isArray(options) ? options : [];
      step.options = nextOptions;
      step.required = nextOptions.length > 0 ? Boolean(step.__gdOriginalRequired) : false;
      pruneSelectionForStepOptions(step, nextOptions);
    }

    function syncWindowChoiceSteps() {
      const selectedWindowProduct = getSelectedWindowLayoutProduct();
      const glassStep = getWindowGlassStep();
      const colorStep = getWindowColorStep();
      const insertStep = getWindowInsertStep();

      if (!selectedWindowProduct) {
        syncWindowChoiceStepOptions(glassStep, []);
        syncWindowChoiceStepOptions(colorStep, []);
        syncWindowChoiceStepOptions(insertStep, []);
        syncWindowPlacementAttentionState(false);
        return;
      }

      const meta = getWindowChoiceMeta(selectedWindowProduct);
      let selectedValues = getWindowSelectedChoiceValues();
      const allowInsertFallback =
        !doesWindowGlassSupportInsert(selectedValues && selectedValues.glass) ||
        isNoWindowInsertChoiceValue(selectedValues && selectedValues.insert);

      const firstGlassOptions = buildWindowChoiceStepOptions(glassStep, selectedWindowProduct, meta, "glass", selectedValues);
      const firstColorOptions = buildWindowChoiceStepOptions(colorStep, selectedWindowProduct, meta, "color", selectedValues);
      const firstInsertOptions = buildWindowChoiceStepOptions(insertStep, selectedWindowProduct, meta, "insert", selectedValues);
      syncWindowChoiceStepOptions(glassStep, firstGlassOptions);
      syncWindowChoiceStepOptions(colorStep, firstColorOptions);
      syncWindowChoiceStepOptions(insertStep, firstInsertOptions);

      syncWindowColorSelection(colorStep, firstColorOptions);

      selectedValues = getWindowSelectedChoiceValues();
      if (hasWindowVariantSelections()) {
        let matchedVariant = findWindowVariantBySelections(selectedWindowProduct, meta, selectedValues, true, allowInsertFallback);
        if (!matchedVariant) {
          matchedVariant = findWindowVariantBySelections(selectedWindowProduct, meta, selectedValues, false, allowInsertFallback);
        }
        if (!matchedVariant) {
          matchedVariant = selectedWindowProduct.variants.find((variant) => variant.available) || selectedWindowProduct.variants[0] || null;
        }

        if (matchedVariant) {
          const nextGlass = getWindowChoiceValueFromVariant(matchedVariant, meta.glassIndex);
          const nextColor = getWindowChoiceValueFromVariant(matchedVariant, meta.colorIndex);
          const nextInsert = getWindowChoiceValueFromVariant(matchedVariant, meta.insertIndex);
          setWindowStepSelectionByValue(glassStep, nextGlass);
          setWindowStepSelectionByValue(colorStep, nextColor);
          if (insertStep) {
            const nextGlassSupportsInsert = doesWindowGlassSupportInsert(nextGlass || (selectedValues && selectedValues.glass));
            const selectedInsert = String(selectedValues && selectedValues.insert ? selectedValues.insert : "").trim();
            if (!nextGlassSupportsInsert || isNoWindowInsertChoiceValue(selectedInsert) || isWindowInsertValueEmpty(nextInsert)) {
              setWindowStepSelectionByValue(insertStep, WINDOW_NO_INSERT_LABEL);
            } else {
              setWindowStepSelectionByValue(insertStep, nextInsert);
            }
          }
        }
      }

      const finalSelectedValues = getWindowSelectedChoiceValues();
      const finalGlassOptions = buildWindowChoiceStepOptions(glassStep, selectedWindowProduct, meta, "glass", finalSelectedValues);
      const finalColorOptions = buildWindowChoiceStepOptions(colorStep, selectedWindowProduct, meta, "color", finalSelectedValues);
      const finalInsertOptions = buildWindowChoiceStepOptions(insertStep, selectedWindowProduct, meta, "insert", finalSelectedValues);
      syncWindowChoiceStepOptions(glassStep, finalGlassOptions);
      syncWindowChoiceStepOptions(colorStep, finalColorOptions);
      syncWindowChoiceStepOptions(insertStep, finalInsertOptions);
      syncWindowPlacementAttentionState(false);
    }

    function getSelectedWindowVariant() {
      const selectedWindowProduct = getSelectedWindowLayoutProduct();
      if (!selectedWindowProduct || !hasWindowVariantSelections()) return null;
      const meta = getWindowChoiceMeta(selectedWindowProduct);
      const selectedValues = getWindowSelectedChoiceValues();
      return (
        findWindowVariantBySelections(selectedWindowProduct, meta, selectedValues, true) ||
        findWindowVariantBySelections(selectedWindowProduct, meta, selectedValues, false) ||
        null
      );
    }

    function stripTrailingWindowInsertCode(sku) {
      const text = String(sku || "").trim();
      if (!text) return "";
      return text.replace(WINDOW_INSERT_CODE_SUFFIX_PATTERN, "");
    }

    function buildSyntheticNoInsertWindowTitle(productTitle, sizeCode, glassLabel, colorLabel) {
      const fallbackTitle = String(productTitle || "").trim() || (sizeCode ? `Window ${sizeCode}` : "Window");
      return [fallbackTitle, glassLabel, colorLabel, WINDOW_NO_INSERT_LABEL].filter(Boolean).join(" - ");
    }

    function buildCurrentWindowCellConfig() {
      const layoutStep = getWindowLayoutStep();
      const glassStep = getWindowGlassStep();
      const colorStep = getWindowColorStep();
      const insertStep = getWindowInsertStep();
      const layoutOption = getSelectedOptionFromStep(layoutStep);
      const glassOption = getSelectedOptionFromStep(glassStep);
      const colorOption = getSelectedOptionFromStep(colorStep);
      const insertOption = getSelectedOptionFromStep(insertStep);
      const selectedWindowProduct = getSelectedWindowLayoutProduct();
      const selectedWindowVariant = getSelectedWindowVariant();
      const selectedValues = getWindowSelectedChoiceValues();
      const meta = getWindowChoiceMeta(selectedWindowProduct);
      const selectedWindowDimensions = getSelectedWindowDimensionsInches();

      if (!layoutOption || isNoWindowLayoutOption(layoutOption) || !selectedWindowProduct) {
        return null;
      }
      if (!hasWindowVariantSelections()) {
        return null;
      }

      let resolvedVariant = selectedWindowVariant;
      if (resolvedVariant && isNoWindowInsertChoiceValue(selectedValues.insert) && !isWindowVariantCompatibleWithNoInsertSelection(resolvedVariant, meta)) {
        resolvedVariant = null;
      }
      let syntheticSku = "";
      let syntheticTitle = "";
      let syntheticPriceCents = 0;

      if (!resolvedVariant && isNoWindowInsertChoiceValue(selectedValues.insert)) {
        const fallbackVariant =
          findWindowVariantBySelections(selectedWindowProduct, meta, selectedValues, true, true) ||
          findWindowVariantBySelections(selectedWindowProduct, meta, selectedValues, false, true) ||
          null;

        if (fallbackVariant) {
          syntheticSku = stripTrailingWindowInsertCode(fallbackVariant.sku || "");
          syntheticTitle = buildSyntheticNoInsertWindowTitle(
            selectedWindowProduct.title || layoutOption.label || "",
            getSelectedWindowSizeCode(),
            glassOption ? String(glassOption.label || "") : "",
            colorOption ? String(colorOption.label || "") : ""
          );
          syntheticPriceCents = Number(fallbackVariant.price_cents || 0);
        }
      }

      if (!resolvedVariant && !syntheticSku) return null;

      return normalizeWindowCellConfig({
        layoutLabel: String(layoutOption.label || ""),
        glassLabel: glassOption ? String(glassOption.label || "") : "",
        colorLabel: colorOption ? String(colorOption.label || "") : "",
        insertLabel: getDisplayWindowInsertLabel(
          glassOption ? String(glassOption.label || "") : "",
          insertOption ? String(insertOption.label || "") : ""
        ),
        productTitle: String(selectedWindowProduct.title || ""),
        variantId: Number(resolvedVariant && resolvedVariant.id ? resolvedVariant.id : 0),
        variantTitle: String(resolvedVariant && resolvedVariant.title ? resolvedVariant.title : syntheticTitle),
        variantSku: String(resolvedVariant && resolvedVariant.sku ? resolvedVariant.sku : syntheticSku),
        priceCents: Number(resolvedVariant && resolvedVariant.price_cents ? resolvedVariant.price_cents : syntheticPriceCents),
        sizeCode: getSelectedWindowSizeCode(),
        widthIn: Number(selectedWindowDimensions && selectedWindowDimensions.widthIn ? selectedWindowDimensions.widthIn : 0),
        heightIn: Number(selectedWindowDimensions && selectedWindowDimensions.heightIn ? selectedWindowDimensions.heightIn : 0)
      });
    }

    async function replaceWindowStepsFromCollection() {
      const layoutStepHandle = String(entry.windowLayoutStepHandle || "").trim();
      const windowLayoutStep =
        (layoutStepHandle ? findStepByHandle(layoutStepHandle) : null) ||
        steps.find((step) => String(step && step.handle ? step.handle : "") === WINDOW_LAYOUT_STEP_HANDLE) ||
        steps.find((step) => stepTitleHasTokens(step, ["window", "layout"])) ||
        null;
      if (!windowLayoutStep) return;

      const collectionHandle = String(entry.windowCollectionHandle || "").trim();
      if (!collectionHandle) return;

      try {
        const rawProducts = await requestCollectionRawProducts(collectionHandle);
        const layoutData = buildWindowLayoutStepOptions(windowLayoutStep, rawProducts);
        const layoutOptions = Array.isArray(layoutData.options) ? layoutData.options : [];

        state.windowCollectionProducts = Array.isArray(layoutData.parsedProducts) ? layoutData.parsedProducts : [];
        state.windowProductByLayoutOptionId = layoutData.optionProductById instanceof Map ? layoutData.optionProductById : new Map();

        const fallbackLayoutPosition = Number.isFinite(Number(windowLayoutStep.position)) ? Number(windowLayoutStep.position) : 0;
        const fallbackGlassPosition = fallbackLayoutPosition + 1;
        const fallbackColorPosition = fallbackGlassPosition + 1;
        const fallbackInsertPosition = fallbackColorPosition + 1;

        ensureSyntheticWindowChoiceStep(entry.windowGlassStepHandle, WINDOW_GLASS_STEP_HANDLE, "Product Option", fallbackGlassPosition);
        ensureSyntheticWindowChoiceStep(entry.windowColorStepHandle, WINDOW_COLOR_STEP_HANDLE, "Frame Color", fallbackColorPosition);
        ensureSyntheticWindowChoiceStep(entry.windowInsertStepHandle, WINDOW_INSERT_STEP_HANDLE, "Insert", fallbackInsertPosition);

        if (typeof windowLayoutStep.__gdOriginalRequired !== "boolean") {
          windowLayoutStep.__gdOriginalRequired = Boolean(windowLayoutStep.required);
        }

        windowLayoutStep.options = layoutOptions;
        windowLayoutStep.required = layoutOptions.length > 0 ? Boolean(windowLayoutStep.__gdOriginalRequired) : false;
        pruneSelectionForStepOptions(windowLayoutStep, layoutOptions);

        ensureInitialWindowDefaults();
        syncWindowChoiceSteps();

        if (layoutOptions.length === 0) {
          console.warn("[gd-configurator] no window options matched configured collection filter", {
            collectionHandle,
            productType: entry.windowProductType
          });
          setHint("No Window products found in Window collection.", "error");
        }
      } catch (error) {
        console.error("[gd-configurator] failed to replace window options from collection", error);
      }
    }

    async function requestSingleProduct(productHandle) {
      const response = await fetch(`/products/${encodeURIComponent(productHandle)}.js`, {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      const parsed = parseEntryProduct(payload);
      return parsed ? [parsed] : [];
    }

    async function loadEntryProduct() {
      state.entryProductLoading = true;
      state.entryProductError = "";
      state.entryCollectionProducts = [];
      state.entryProduct = null;
      renderRail();

      try {
        const collectionHandle = String(entry.doorCollectionHandle || "").trim();
        let products = [];

        if (collectionHandle) {
          products = await requestCollectionProducts(collectionHandle);
        }

        if (products.length === 0) {
          const productHandle = String(state.entryContext.productHandle || "").trim();
          if (productHandle) {
            products = await requestSingleProduct(productHandle);
          }
        }

        if (products.length === 0) {
          throw new Error("No products were found in the configured door collection.");
        }

        state.entryCollectionProducts = products;

        const shouldApplyDefaults = shouldAutoSelectConfiguratorDefaults();
        const byHandle = getEntryCollectionProductByHandle(state.entryContext.productHandle);
        const byTitle =
          products.find((product) => normalizeCompareToken(product.title || "") === normalizeCompareToken(state.entryContext.productTitle || "")) ||
          null;
        const defaultProduct = shouldApplyDefaults ? getDefaultEntryCollectionProduct(products) : null;
        const target = shouldApplyDefaults
          ? defaultProduct || products[0]
          : byHandle || byTitle || products[0];

        if (!target) {
          throw new Error("No selectable products were found.");
        }

        selectEntryCollectionProduct(target.handle);

        if (shouldApplyDefaults) {
          applyDefaultEntryVariantSelection();
          state.preflight.completed = true;
        }
      } catch (error) {
        console.error("[gd-configurator] failed to load entry product", error);
        state.entryCollectionProducts = [];
        state.entryProduct = null;
        state.entryProductError = "Door details could not be loaded. Return to the product page and try again.";
      } finally {
        state.entryProductLoading = false;
      }
    }

    function getSelectionSet(stepId) {
      const key = String(stepId || "");
      if (!state.selected.has(key)) {
        state.selected.set(key, new Set());
      }
      return state.selected.get(key);
    }

    function findStepById(stepId) {
      return steps.find((step) => String(step.id) === String(stepId)) || null;
    }

    function findStepByHandle(handle) {
      return steps.find((step) => String(step.handle || "") === String(handle || "")) || null;
    }

    function findOption(step, optionId) {
      if (!step) return null;
      return (step.options || []).find((option) => String(option.id) === String(optionId)) || null;
    }

    function findOptionByHandle(step, handle) {
      if (!step) return null;
      return (step.options || []).find((option) => String(option.handle || "") === String(handle || "")) || null;
    }

    function findOptionByAny(step, anyId) {
      const byId = findOption(step, anyId);
      if (byId) return byId;
      return findOptionByHandle(step, anyId);
    }

    function stepHasSelection(step) {
      const selected = state.selected.get(String(step.id || ""));
      return Boolean(selected && selected.size > 0);
    }

    function getMissingRequiredSteps() {
      const visibleSteps = getVisibleSteps();
      const missing = visibleSteps.filter((step) => {
        if (!step || !step.required || stepHasSelection(step)) return false;
        if (isWindowEmbeddedStep(step) || isWindowPositionStep(step)) return false;
        return true;
      });

      if (hasLiveEntryProduct()) {
        const selectedVariant = getSelectedEntryVariant();
        if (!selectedVariant || !selectedVariant.available) {
          missing.unshift({
            id: "__entry_product__",
            title: entry.doorCollectionTitle || "Door Collection"
          });
        }
      }
      return missing;
    }

    function getMissingRequiredStepsForDoor(door) {
      return withActiveDoorState(door, () => getMissingRequiredSteps().slice());
    }

    function getEntryCustomSizeStateForDoor(door) {
      return withActiveDoorState(door, () => getEntryCustomSizeState());
    }

    function getFirstDoorValidationIssue() {
      for (const door of getCommittedDoors()) {
        const specialTrackIssue = withActiveDoorState(door, () => getSpecialTrackValidationIssue());
        if (specialTrackIssue) {
          return { type: "special_track", door, doorLabel: getDoorLabel(door), specialTrackIssue };
        }
        const missing = getMissingRequiredStepsForDoor(door);
        if (missing.length > 0) {
          return {
            type: "missing_required",
            door,
            doorLabel: getDoorLabel(door),
            missing
          };
        }

        const customSize = getEntryCustomSizeStateForDoor(door);
        if (customSize.active && !customSize.valid) {
          return {
            type: "custom_size",
            door,
            doorLabel: getDoorLabel(door),
            customSize
          };
        }
      }

      return null;
    }

    function optionTotal(option) {
      return (option.components || []).reduce((sum, component) => {
        return sum + Number(component.price_cents || 0) * Number(component.qty || 1);
      }, 0);
    }

    function getActiveDoorTotalCents() {
      let total = 0;

      const entryVariant = getSelectedEntryVariant();
      if (entryVariant) {
        total += Number(entryVariant.price_cents || 0);
      }

      getOrderedWindowCells().forEach((cell) => {
        const config = cell && cell.config ? cell.config : null;
        const priceCents = Number(config && config.priceCents ? config.priceCents : 0);
        if (priceCents > 0) {
          total += priceCents;
        }
      });

      for (const step of getVisibleSteps()) {
        const selectedIds = state.selected.get(step.id);
        if (!selectedIds) continue;

        for (const optionId of selectedIds.values()) {
          const option = findOption(step, optionId);
          if (!option) continue;
          total += optionTotal(option);
        }
      }

      return total;
    }

    function totalCents() {
      return getCommittedDoors().reduce((sum, door) => {
        return sum + withActiveDoorState(door, () => getActiveDoorTotalCents() * getDoorQuantityValue(door));
      }, 0);
    }

    function doorHasCustomPrice(door) {
      if (!door) return false;
      return withActiveDoorState(door, () => {
        const entryVariant = getSelectedEntryVariant();
        if (!entryVariant) return false;
        return Number(entryVariant.price_cents || 0) <= 0;
      });
    }

    function hasAnyDoorWithoutPrice() {
      return getCommittedDoors().some((door) => doorHasCustomPrice(door));
    }

    function summarizeMergedComponents() {
      const merged = new Map();

      for (const step of steps) {
        if (isSpringCycleStep(step)) continue;
        const selectedIds = state.selected.get(step.id);
        if (!selectedIds || selectedIds.size === 0) continue;

        for (const optionId of selectedIds.values()) {
          const option = findOption(step, optionId);
          if (!option) continue;

          for (const component of option.components || []) {
            const variantId = String(component.variant_id || "");
            if (!variantId) continue;
            const qty = Number(component.qty || 1);

            if (!merged.has(variantId)) {
              merged.set(variantId, {
                variantId,
                title: String(component.title || "Component"),
                sku: String(component.sku || ""),
                quantity: 0
              });
            }

            merged.get(variantId).quantity += qty;
          }
        }
      }

      return Array.from(merged.values());
    }

    function getSelectedOptionFromStep(step) {
      if (!step) return null;
      const selectedSet = state.selected.get(step.id);
      if (!selectedSet || selectedSet.size === 0) return null;
      const first = Array.from(selectedSet.values())[0];
      return findOption(step, first);
    }

    function getFirstSelectedOptionAcrossSteps() {
      for (const step of getVisibleSteps()) {
        const option = getSelectedOptionFromStep(step);
        if (option) {
          return { step, option };
        }
      }
      return null;
    }

    function getEntrySeriesStep() {
      return findStepByHandle(entry.seriesStepHandle) || getVisibleSteps()[0] || null;
    }

    function getWindowLayoutStep() {
      const handle = String(entry.windowLayoutStepHandle || "").trim();
      return (
        (handle ? findStepByHandle(handle) : null) ||
        steps.find((step) => String(step && step.handle ? step.handle : "") === WINDOW_LAYOUT_STEP_HANDLE) ||
        steps.find((step) => stepTitleHasTokens(step, ["window", "layout"])) ||
        null
      );
    }

    function getWindowPositionStep() {
      const byHandle = findStepByHandle(WINDOW_POSITION_STEP_HANDLE) || null;
      const byTitle = steps.find((step) => /window.+position|position.+window/i.test(String(step.title || ""))) || null;
      const preferredVisibleWithOptions = getVisibleSteps().find((step) => {
        return isWindowPositionStep(step) && Array.isArray(step.options) && step.options.length > 0;
      });

      if (preferredVisibleWithOptions) return preferredVisibleWithOptions;

      const preferredVisible = getVisibleSteps().find((step) => isWindowPositionStep(step));
      if (preferredVisible) return preferredVisible;

      return byHandle || byTitle || null;
    }

    function isNoWindowLayoutOption(option) {
      if (!option) return false;
      const handle = String(option.handle || "").toLowerCase();
      const label = String(option.label || "").toLowerCase();
      return handle.includes("window-none") || label.includes("no window") || label.includes("solid");
    }

    function requiresWindowPositionSelection() {
      const layoutStep = getWindowLayoutStep();
      if (!layoutStep) return false;
      const selectedLayout = getSelectedOptionFromStep(layoutStep);
      if (!selectedLayout) return false;
      return !isNoWindowLayoutOption(selectedLayout);
    }

    function getLegacyWindowPositionColumnCount(layoutOption) {
      if (!layoutOption || isNoWindowLayoutOption(layoutOption)) return 4;
      const text = `${layoutOption.handle || ""} ${layoutOption.label || ""}`.toLowerCase();
      if (text.includes("2-lite") || text.includes("2 lite") || text.includes("short")) return 2;
      if (text.includes("4-lite") || text.includes("4 lite") || text.includes("long")) return 4;
      return 4;
    }

    function normalizeWindowSizeCode(value) {
      const text = String(value || "").trim();
      if (!text) return "";

      const skuLikeMatch = text.match(/\b(?:wp|wg|wf|wi)[-_ ]?(\d{4})\b/i);
      if (skuLikeMatch) return String(skuLikeMatch[1] || "");

      const sizeMatch = text.match(/(?:^|[^0-9])(\d{1,2})\s*[x×]\s*(\d{1,2})(?:[^0-9]|$)/i);
      if (sizeMatch) {
        const width = Number(sizeMatch[1]);
        const height = Number(sizeMatch[2]);
        if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
          return `${String(width).padStart(2, "0")}${String(height).padStart(2, "0")}`;
        }
      }

      const rawCodeMatch = text.match(/\b(\d{4})\b/);
      if (rawCodeMatch) return String(rawCodeMatch[1] || "");

      return "";
    }

    function getSelectedWindowSizeCode() {
      const layoutStep = getWindowLayoutStep();
      const selectedLayout = getSelectedOptionFromStep(layoutStep);
      if (!selectedLayout || isNoWindowLayoutOption(selectedLayout)) return "";

      const selectedProduct = getSelectedWindowLayoutProduct();
      const selectedVariant = getSelectedWindowVariant();
      const candidates = [
        selectedVariant && selectedVariant.sku,
        selectedVariant && selectedVariant.title,
        selectedProduct && selectedProduct.handle,
        selectedProduct && selectedProduct.title,
        selectedLayout && selectedLayout.handle,
        selectedLayout && selectedLayout.label
      ];

      for (const candidate of candidates) {
        const code = normalizeWindowSizeCode(candidate);
        if (code) return code;
      }

      return "";
    }

    function getCurrentDoorWidthClass() {
      const entryProfile = getEntryProfile();
      const parsedSize = parseDoorSizeLabel(entryProfile.sizeLabel || state.preflight.sizeLabel || "", {
        allowOrientationSwap: entryProfile.sizeMode !== "custom"
      });
      const widthFeet = Number(parsedSize.widthFeet || 0);
      if (!Number.isFinite(widthFeet) || widthFeet <= 0) return "wide";
      return widthFeet <= WINDOW_NARROW_DOOR_MAX_WIDTH_FEET ? "narrow" : "wide";
    }

    function getCurrentDoorVisualConfig() {
      const entryProfile = getEntryProfile();
      const sizeLabel = String(entryProfile && entryProfile.sizeLabel ? entryProfile.sizeLabel : state.preflight.sizeLabel || "").trim();
      return getDoorVisualConfig(sizeLabel, {
        allowOrientationSwap: entryProfile.sizeMode !== "custom",
        panelPreference: entryProfile.panelPreference
      });
    }

    function getEffectiveWindowRowCount(doorConfig) {
      const panelHeights = Array.isArray(doorConfig && doorConfig.panelHeightsIn)
        ? doorConfig.panelHeightsIn
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        : [];
      const panelCountFromHeights = panelHeights.length;
      const panelCount = Number(doorConfig && doorConfig.panelCount ? doorConfig.panelCount : 0);
      const resolved = panelCountFromHeights > 0 ? panelCountFromHeights : panelCount;
      return Math.max(1, Math.min(8, Number.isFinite(resolved) ? resolved : 4));
    }

    function resolveWindowRowBandKey(rowIndex, rowCount) {
      const index = Number(rowIndex);
      const total = Math.max(1, Number(rowCount) || 1);
      if (total === 1) return "top";
      if (index <= 0) return "top";
      if (index >= total - 1) return "bottom";
      return "middle";
    }

    function resolveWindowRowBandLabel(value) {
      const key = resolveWindowPositionKey(value);
      if (key === "top") return "Top";
      if (key === "bottom") return "Bottom";
      if (key === "upper" || key === "middle" || key === "lower") return "Middle";
      return "";
    }

    function buildDynamicWindowRowLabel(rowIndex, rowCount) {
      const sequence = Math.max(1, Number(rowIndex) + 1 || 1);
      return `Row ${sequence}`;
    }

    function getWindowRowModel(doorConfig) {
      const rowCount = getEffectiveWindowRowCount(doorConfig);
      if (rowCount === 4) {
        return WINDOW_ROW_MODEL_4.map((row, index) => {
          return {
            key: String(row.key || ""),
            label: String(row.label || ""),
            index,
            rowNumber: index + 1
          };
        });
      }

      if (rowCount === 5) {
        return WINDOW_ROW_MODEL_5.map((row, index) => {
          return {
            key: String(row.key || ""),
            label: String(row.label || ""),
            index,
            rowNumber: index + 1
          };
        });
      }

      return Array.from({ length: rowCount }, (_, index) => {
        return {
          key: `row${index + 1}`,
          label: buildDynamicWindowRowLabel(index, rowCount),
          index,
          rowNumber: index + 1
        };
      });
    }

    function buildDefaultWindowColumnLabels(cols) {
      const count = Math.max(1, Number(cols) || 1);
      return Array.from({ length: count }, (_, index) => String(index + 1));
    }

    function buildWindowPositionRuleForSizeCode(sizeCode, doorWidthClass, fallbackCols) {
      const normalizedDoorWidthClass = String(doorWidthClass || "").trim() || "wide";
      const resolvedSizeCode = String(sizeCode || "").trim();
      const sizeRule = resolvedSizeCode ? WINDOW_POSITION_RULES_BY_SIZE_CODE[resolvedSizeCode] : null;
      const variantRule = sizeRule && sizeRule[normalizedDoorWidthClass] ? sizeRule[normalizedDoorWidthClass] : null;
      const cols = Math.max(1, Number(variantRule && variantRule.cols ? variantRule.cols : fallbackCols) || 1);
      const defaultLabels = buildDefaultWindowColumnLabels(cols);
      const customLabels = Array.isArray(variantRule && variantRule.columnLabels) ? variantRule.columnLabels : [];
      const columnLabels = customLabels.length === cols ? customLabels.slice() : defaultLabels;
      const centerColumn = Number(variantRule && variantRule.centerColumn);
      const sideColumns = Array.isArray(variantRule && variantRule.sideColumns)
        ? variantRule.sideColumns.map((col) => Number(col)).filter((col) => Number.isFinite(col) && col >= 1 && col <= cols)
        : [];

      return {
        cols,
        columnLabels,
        centerColumn: Number.isFinite(centerColumn) && centerColumn >= 1 && centerColumn <= cols ? centerColumn : 0,
        sideColumns,
        blockCenterWithBothSides: Boolean(variantRule && variantRule.blockCenterWithBothSides),
        sizeCode: resolvedSizeCode,
        doorWidthClass: normalizedDoorWidthClass
      };
    }

    function getWindowPositionRuleForConfig(config, fallbackRule) {
      const resolvedFallback = fallbackRule || getWindowPositionRule();
      const value = config && typeof config === "object" ? config : {};
      const sizeCode = String(value.sizeCode || "").trim();
      if (!sizeCode) return resolvedFallback;

      const fallbackCols = Math.max(1, Number(resolvedFallback && resolvedFallback.cols ? resolvedFallback.cols : 4));
      const doorWidthClass = String(
        (resolvedFallback && resolvedFallback.doorWidthClass) || getCurrentDoorWidthClass() || "wide"
      ).trim() || "wide";
      return buildWindowPositionRuleForSizeCode(sizeCode, doorWidthClass, fallbackCols);
    }

    function getWindowPositionRule() {
      const layoutStep = getWindowLayoutStep();
      const selectedLayout = getSelectedOptionFromStep(layoutStep);
      if (!selectedLayout || isNoWindowLayoutOption(selectedLayout)) {
        return buildWindowPositionRuleForSizeCode("", "wide", 4);
      }

      const sizeCode = getSelectedWindowSizeCode();
      const doorWidthClass = getCurrentDoorWidthClass();
      const fallbackCols = getLegacyWindowPositionColumnCount(selectedLayout);
      return buildWindowPositionRuleForSizeCode(sizeCode, doorWidthClass, fallbackCols);
    }

    function getWindowPositionColumnCount() {
      return getWindowPositionRule().cols;
    }

    function getWindowColumnDisplayLabel(columnNumber, rule) {
      const resolvedRule = rule || getWindowPositionRule();
      const labels = Array.isArray(resolvedRule && resolvedRule.columnLabels) ? resolvedRule.columnLabels : [];
      const index = Number(columnNumber) - 1;
      if (index >= 0 && index < labels.length && labels[index]) {
        return String(labels[index]);
      }
      return String(columnNumber);
    }

    function resolveWindowRowLabel(rowMeta, rowIndex, rowCount) {
      return buildDynamicWindowRowLabel(rowIndex, rowCount);
    }

    function buildWindowPositionDisplayLabel(rowNumber, columnNumber, rows, rule) {
      const safeRowNumber = Math.max(1, Number(rowNumber) || 1);
      return `Row ${safeRowNumber}`;
    }

    function applyWindowCellSelectionRules(cellSet, cellConfigMap, rule, toggledCell, doorConfig) {
      const next = new Set();
      const resolvedDoorConfig = doorConfig || getCurrentDoorVisualConfig();
      const resolvedRule = rule || getWindowPositionRule();
      const maxRows = getEffectiveWindowRowCount(resolvedDoorConfig);
      const toggledCellKey = String(toggledCell && toggledCell.key ? toggledCell.key : "").trim();
      const pendingConfig = toggledCellKey ? buildCurrentWindowCellConfig() : null;

      for (const value of Array.from(cellSet || [])) {
        const parsed = parseWindowCellKey(value);
        if (!parsed) continue;
        const config =
          (cellConfigMap instanceof Map && cellConfigMap.has(parsed.key) ? cellConfigMap.get(parsed.key) : null) ||
          (pendingConfig && parsed.key === toggledCellKey ? pendingConfig : null) ||
          null;
        const cellRule = getWindowPositionRuleForConfig(config, resolvedRule);
        const maxCols = Math.max(1, Number(cellRule && cellRule.cols ? cellRule.cols : 1));
        if (parsed.row < 1 || parsed.row > maxRows || parsed.col < 1 || parsed.col > maxCols) continue;
        next.add(parsed.key);
      }

      return removeOverlappingWindowCells(next, cellConfigMap, resolvedRule, toggledCell, resolvedDoorConfig);
    }

    function getWindowGridRows(step, doorConfig) {
      const resolvedDoorConfig = doorConfig || getCurrentDoorVisualConfig();
      const rowModel = getWindowRowModel(resolvedDoorConfig);
      const allowedKeys = new Set(rowModel.map((row) => row.key));
      const labelsByKey = new Map();
      const sourceStep = step && isWindowPositionStep(step) ? step : getWindowPositionStep();
      const options = sourceStep && Array.isArray(sourceStep.options) ? sourceStep.options : [];

      for (const option of options) {
        let key = resolveWindowPositionKey(`${option && option.label ? option.label : ""} ${option && option.handle ? option.handle : ""}`);
        if (key === "lower" && !allowedKeys.has("lower") && allowedKeys.has("bottom")) {
          key = "bottom";
        }
        if (!key || !allowedKeys.has(key) || labelsByKey.has(key)) continue;
        labelsByKey.set(key, String(WINDOW_POSITION_ROW_DEFAULTS[key] || key));
      }

      return rowModel.map((row) => {
        return {
          key: row.key,
          label:
            labelsByKey.get(row.key) ||
            resolveWindowRowLabel(row, row.index, rowModel.length) ||
            WINDOW_POSITION_ROW_DEFAULTS[row.key] ||
            row.key
        };
      });
    }

    function makeWindowCellKey(rowIndex, colIndex) {
      return `r${rowIndex}c${colIndex}`;
    }

    function parseWindowCellKey(value) {
      const match = /^r(\d+)c(\d+)$/i.exec(String(value || "").trim());
      if (!match) return null;
      const row = Number(match[1]);
      const col = Number(match[2]);
      if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
      return {
        row,
        col,
        key: makeWindowCellKey(row, col)
      };
    }

    function normalizeWindowHoverCell(value, fallbackStepId, rule, doorConfig) {
      if (!value || typeof value !== "object") return null;
      const parsed = parseWindowCellKey(value.cellKey || value.key || "");
      if (!parsed) return null;

      const resolvedDoorConfig = doorConfig || getCurrentDoorVisualConfig();
      const maxRows = getEffectiveWindowRowCount(resolvedDoorConfig);
      const maxCols = Math.max(1, Number(rule && rule.cols ? rule.cols : 1));
      if (parsed.row < 1 || parsed.row > maxRows || parsed.col < 1 || parsed.col > maxCols) return null;

      const stepId = String(fallbackStepId || value.stepId || "").trim();
      if (!stepId) return null;

      return {
        stepId,
        cellKey: parsed.key,
        row: parsed.row,
        col: parsed.col
      };
    }

    function setWindowHoverCell(value, rule, fallbackStepId, doorConfig) {
      const next = value ? normalizeWindowHoverCell(value, fallbackStepId, rule, doorConfig) : null;
      const current = state.windowHoverCell;
      const unchanged =
        (!next && !current) ||
        (next &&
          current &&
          String(next.stepId) === String(current.stepId) &&
          String(next.cellKey) === String(current.cellKey) &&
          Number(next.row) === Number(current.row) &&
          Number(next.col) === Number(current.col));
      if (unchanged) return false;
      state.windowHoverCell = next;
      return true;
    }

    function clearWindowHoverCell() {
      if (!state.windowHoverCell) return false;
      state.windowHoverCell = null;
      return true;
    }

    function getActiveWindowHoverCell(doorConfig) {
      const step = getWindowPositionStep();
      if (!step || !isWindowPositionStep(step) || !state.preflight.completed || !requiresWindowPositionSelection()) return null;
      const rule = getWindowPositionRule();
      return normalizeWindowHoverCell(state.windowHoverCell, step.id, rule, doorConfig || getCurrentDoorVisualConfig());
    }

    function parseWindowSizePairFromText(value) {
      const text = String(value || "").trim();
      if (!text) return null;
      const match = text.match(/(?:^|[^0-9])(\d{1,2})\s*[x×]\s*(\d{1,2})(?:[^0-9]|$)/i);
      if (!match) return null;
      const first = Number(match[1]);
      const second = Number(match[2]);
      if (!Number.isFinite(first) || !Number.isFinite(second) || first <= 0 || second <= 0) return null;
      return { first, second };
    }

    function parseWindowDimensionsFromSizeCode(sizeCode) {
      const code = String(sizeCode || "").trim();
      if (!/^\d{4}$/.test(code)) return null;
      const first = Number(code.slice(0, 2));
      const second = Number(code.slice(2, 4));
      if (!Number.isFinite(first) || !Number.isFinite(second) || first <= 0 || second <= 0) return null;
      return {
        widthIn: second,
        heightIn: first
      };
    }

    function getSelectedWindowDimensionsInches() {
      const sizeCode = getSelectedWindowSizeCode();
      const byCode = parseWindowDimensionsFromSizeCode(sizeCode);
      if (byCode) return byCode;

      const layoutStep = getWindowLayoutStep();
      const layoutOption = getSelectedOptionFromStep(layoutStep);
      const selectedProduct = getSelectedWindowLayoutProduct();
      const selectedVariant = getSelectedWindowVariant();
      const candidates = [
        selectedVariant && selectedVariant.title,
        selectedVariant && selectedVariant.sku,
        selectedProduct && selectedProduct.title,
        selectedProduct && selectedProduct.handle,
        layoutOption && layoutOption.label,
        layoutOption && layoutOption.handle
      ];

      for (const candidate of candidates) {
        const pair = parseWindowSizePairFromText(candidate);
        if (!pair) continue;
        return {
          widthIn: pair.second,
          heightIn: pair.first
        };
      }

      return {
        widthIn: 40,
        heightIn: 14
      };
    }

    function getWindowDimensionsFromConfig(config) {
      const value = config && typeof config === "object" ? config : {};
      const widthIn = Number(value.widthIn || 0);
      const heightIn = Number(value.heightIn || 0);
      if (Number.isFinite(widthIn) && widthIn > 0 && Number.isFinite(heightIn) && heightIn > 0) {
        return { widthIn, heightIn };
      }

      const byCode = parseWindowDimensionsFromSizeCode(value.sizeCode);
      if (byCode) return byCode;

      return getSelectedWindowDimensionsInches();
    }

    function getWindowCellConfig(cellOrKey) {
      const cellKey =
        typeof cellOrKey === "string"
          ? String(cellOrKey || "").trim()
          : String(cellOrKey && cellOrKey.key ? cellOrKey.key : "");
      if (!cellKey) return null;
      const storedConfig = state.windowCellConfigs instanceof Map ? state.windowCellConfigs.get(cellKey) : null;
      if (storedConfig) return normalizeWindowCellConfig(storedConfig);
      return buildCurrentWindowCellConfig();
    }

    function filterWindowCellConfigMap(cellConfigMap, cellSet) {
      const next = new Map();
      const map = cellConfigMap instanceof Map ? cellConfigMap : new Map();
      const activeKeys = cellSet instanceof Set ? cellSet : new Set();
      activeKeys.forEach((cellKey) => {
        const normalizedKey = String(cellKey || "").trim();
        if (!normalizedKey || !map.has(normalizedKey)) return;
        next.set(normalizedKey, normalizeWindowCellConfig(map.get(normalizedKey)));
      });
      return next;
    }

    function ensureWindowCellConfigsForActiveDoor() {
      if (!(state.windowCells instanceof Set) || state.windowCells.size === 0) return false;
      const currentConfig = buildCurrentWindowCellConfig();
      if (!currentConfig) return false;

      const nextConfigs = cloneWindowCellConfigMap(state.windowCellConfigs);
      let changed = false;
      state.windowCells.forEach((cellKey) => {
        const normalizedKey = String(cellKey || "").trim();
        if (!normalizedKey || nextConfigs.has(normalizedKey)) return;
        nextConfigs.set(normalizedKey, normalizeWindowCellConfig(currentConfig));
        changed = true;
      });

      if (changed) {
        state.windowCellConfigs = nextConfigs;
      }
      return changed;
    }

    function syncWindowCellConfigsToCurrentSelection() {
      // Preserve existing placed window configs. The current window choice should
      // only apply to newly added cells, unless the user explicitly re-clicks an
      // existing cell to update it.
      return ensureWindowCellConfigsForActiveDoor();
    }

    function buildWindowAxisCenters(count, spanPct, clampToEdges = true, edgePaddingPct = 0) {
      const total = Math.max(1, Number(count) || 1);
      const span = Math.max(1, Number(spanPct) || 1);
      if (total === 1) return [50];

      if (!clampToEdges) {
        return Array.from({ length: total }, (_, index) => {
          return ((index + 0.5) / total) * 100;
        });
      }

      const half = span / 2;
      const edgePadding = Math.max(0, Number(edgePaddingPct) || 0);
      const start = half + edgePadding;
      const end = 100 - half - edgePadding;
      if (!(end > start)) {
        return Array.from({ length: total }, (_, index) => {
          return ((index + 0.5) / total) * 100;
        });
      }

      return Array.from({ length: total }, (_, index) => {
        return start + ((end - start) * index) / (total - 1);
      });
    }

    function buildPanelCentersFromHeights(panelHeightsIn) {
      const heights = (Array.isArray(panelHeightsIn) ? panelHeightsIn : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
      if (heights.length === 0) return [];

      const totalHeight = heights.reduce((sum, value) => sum + value, 0);
      if (!(totalHeight > 0)) return [];

      let offset = 0;
      return heights.map((height) => {
        const center = offset + height / 2;
        offset += height;
        return (center / totalHeight) * 100;
      });
    }

    function mapRowModelToPanelCenters(rowModel, panelCenters) {
      const targetCount = Math.max(1, Array.isArray(rowModel) ? rowModel.length : 0);
      const source = (Array.isArray(panelCenters) ? panelCenters : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));
      if (source.length === 0) return buildWindowAxisCenters(targetCount, 1, false);
      if (source.length === targetCount) return source.slice();
      if (source.length === 1) return Array.from({ length: targetCount }, () => source[0]);

      return Array.from({ length: targetCount }, (_, index) => {
        if (targetCount === 1) return source[Math.floor((source.length - 1) / 2)];
        const position = (index / (targetCount - 1)) * (source.length - 1);
        const lowerIndex = Math.floor(position);
        const upperIndex = Math.min(source.length - 1, Math.ceil(position));
        const ratio = position - lowerIndex;
        const lowerValue = source[lowerIndex];
        const upperValue = source[upperIndex];
        return lowerValue + (upperValue - lowerValue) * ratio;
      });
    }

    function convertFullDoorYToWindowZoneY(yFullPct, zoneTopPct, zoneBottomPct) {
      const y = Number(yFullPct);
      if (!Number.isFinite(y)) return 50;
      const top = clampNumber(Number(zoneTopPct) || 0, 0, 49.9);
      const bottom = clampNumber(Number(zoneBottomPct) || 0, 0, 49.9);
      const zoneHeight = 100 - top - bottom;
      if (!(zoneHeight > 0)) return clampNumber(y, 0, 100);
      const withinZone = ((y - top) / zoneHeight) * 100;
      return clampNumber(withinZone, 0, 100);
    }

    function getWindowPreviewMetrics(doorConfig, positionRule, windowDimensionsOverride) {
      const doorWidthIn = Math.max(1, Number(doorConfig && doorConfig.widthIn ? doorConfig.widthIn : 96));
      const doorHeightIn = Math.max(1, Number(doorConfig && doorConfig.heightIn ? doorConfig.heightIn : 84));
      const windowDims = windowDimensionsOverride || getSelectedWindowDimensionsInches();

      let widthPct = (Number(windowDims.widthIn || 0) / doorWidthIn) * 100;
      let heightPct = (Number(windowDims.heightIn || 0) / doorHeightIn) * 100;
      if (!Number.isFinite(widthPct) || widthPct <= 0) widthPct = 12;
      if (!Number.isFinite(heightPct) || heightPct <= 0) heightPct = 9;

      const maxPct = 90;
      const resize = Math.min(1, maxPct / Math.max(widthPct, 0.0001), maxPct / Math.max(heightPct, 0.0001));
      widthPct *= resize;
      heightPct *= resize;

      const windowEdgeInsetIn = 4;
      const horizontalEdgePaddingPct = (windowEdgeInsetIn / doorWidthIn) * 100;
      const cols = Math.max(1, Number(positionRule && positionRule.cols ? positionRule.cols : getWindowPositionColumnCount()));
      const rowModel = getWindowRowModel(doorConfig);
      const rows = Math.max(1, rowModel.length);
      const entryProfile = getEntryProfile();
      const entryTitle = String(entryProfile.seriesLabel || entryProfile.productTitle || "").trim();
      const seriesProfile = getDoorSeriesRenderProfile(entryTitle);
      const colCenters = seriesProfile
        ? getFixedSeriesWindowColumnCenters(doorConfig, seriesProfile, cols, Number(windowDims.widthIn || 0))
        : buildWindowAxisCenters(cols, widthPct, true, horizontalEdgePaddingPct);
      const panelCount = Math.max(1, Number(doorConfig && doorConfig.panelCount ? doorConfig.panelCount : rows));
      const panelHeights = Array.isArray(doorConfig && doorConfig.panelHeightsIn)
        ? doorConfig.panelHeightsIn
        : Array.from({ length: panelCount }, () => 1);
      const panelCentersFullDoor = buildPanelCentersFromHeights(panelHeights);
      const mappedCentersFullDoor = mapRowModelToPanelCenters(rowModel, panelCentersFullDoor);
      const rowCentersRaw = mappedCentersFullDoor.map((center) => {
        return convertFullDoorYToWindowZoneY(center, WINDOW_ZONE_TOP_PCT, WINDOW_ZONE_BOTTOM_PCT);
      });
      const halfHeight = heightPct / 2;
      const minCenter = Math.min(50, halfHeight);
      const maxCenter = Math.max(minCenter, 100 - halfHeight);
      const rowCenters = (rowCentersRaw.length === rows ? rowCentersRaw : buildWindowAxisCenters(rows, heightPct, false)).map((center) => {
        return clampNumber(center, minCenter, maxCenter);
      });

      return {
        widthPct,
        heightPct,
        cols,
        rows,
        rowModel,
        colCenters,
        rowCenters,
        widthIn: Number(windowDims.widthIn || 0),
        heightIn: Number(windowDims.heightIn || 0)
      };
    }

    function buildWindowRectFromCell(cell, metrics) {
      if (!cell || !metrics) return null;
      const x = Number(metrics.colCenters && metrics.colCenters[cell.col - 1]);
      const y = Number(metrics.rowCenters && metrics.rowCenters[cell.row - 1]);
      const width = Number(metrics.widthPct || 0);
      const height = Number(metrics.heightPct || 0);
      if (!Number.isFinite(x) || !Number.isFinite(y) || width <= 0 || height <= 0) return null;
      const halfW = width / 2;
      const halfH = height / 2;
      return {
        left: x - halfW,
        right: x + halfW,
        top: y - halfH,
        bottom: y + halfH
      };
    }

    function buildWindowAxisSegments(centers) {
      const values = (Array.isArray(centers) ? centers : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));
      if (values.length === 0) return [];

      return values.map((center, index) => {
        const previous = index > 0 ? values[index - 1] : null;
        const next = index < values.length - 1 ? values[index + 1] : null;
        const start = previous === null ? 0 : (previous + center) / 2;
        const end = next === null ? 100 : (center + next) / 2;
        return {
          center,
          start: clampNumber(start, 0, 100),
          end: clampNumber(end, 0, 100),
          size: Math.max(0, end - start)
        };
      });
    }

    function rectsOverlap(a, b) {
      if (!a || !b) return false;
      return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
    }

    function getWindowMetricsForOverlap(rule, doorConfig, config) {
      const resolvedDoorConfig = doorConfig || getCurrentDoorVisualConfig();
      return getWindowPreviewMetrics(resolvedDoorConfig, rule, getWindowDimensionsFromConfig(config));
    }

    function getWindowInteractionMetricsForOverlay(rule, doorConfig, config) {
      const metrics = getWindowMetricsForOverlap(rule, doorConfig, config);
      const cols = Math.max(1, Number(metrics && metrics.cols ? metrics.cols : rule && rule.cols ? rule.cols : 1));
      const rows = Math.max(1, Number(metrics && metrics.rows ? metrics.rows : 1));
      const slotWidthPct = 100 / cols;
      const slotHeightPct = 100 / rows;
      const widthPct = Math.min(Number(metrics && metrics.widthPct ? metrics.widthPct : 0) || slotWidthPct, slotWidthPct * 0.92);
      const heightPct = Math.min(Number(metrics && metrics.heightPct ? metrics.heightPct : 0) || slotHeightPct, slotHeightPct * 0.86);
      const hoverWidthPct = Number(metrics && metrics.widthPct ? metrics.widthPct : 0) || widthPct;
      const hoverHeightPct = Number(metrics && metrics.heightPct ? metrics.heightPct : 0) || heightPct;
      const halfHeight = heightPct / 2;
      const minCenter = Math.min(50, halfHeight);
      const maxCenter = Math.max(minCenter, 100 - halfHeight);
      const rowCentersSource =
        Array.isArray(metrics && metrics.rowCenters) && metrics.rowCenters.length === rows
          ? metrics.rowCenters
          : buildWindowAxisCenters(rows, 1, false);

      return {
        ...metrics,
        widthPct,
        heightPct,
        hoverWidthPct,
        hoverHeightPct,
        hoverColCenters:
          Array.isArray(metrics && metrics.colCenters) && metrics.colCenters.length === cols
            ? metrics.colCenters
            : buildWindowAxisCenters(cols, hoverWidthPct, true),
        hoverRowCenters:
          Array.isArray(metrics && metrics.rowCenters) && metrics.rowCenters.length === rows
            ? metrics.rowCenters
            : rowCentersSource.map((center) => clampNumber(center, hoverHeightPct / 2, 100 - hoverHeightPct / 2)),
        colCenters: buildWindowAxisCenters(cols, 1, false),
        rowCenters: rowCentersSource.map((center) => clampNumber(center, minCenter, maxCenter))
      };
    }

    function buildWindowRectForPlacedCell(cell, cellConfigMap, rule, doorConfig, pendingConfig, toggledCellKey) {
      if (!cell) return null;
      const cellKey = String(cell.key || "").trim();
      const config =
        (cell && cell.config) ||
        (cellConfigMap instanceof Map && cellKey ? cellConfigMap.get(cellKey) : null) ||
        (pendingConfig && cellKey && cellKey === String(toggledCellKey || "").trim() ? pendingConfig : null) ||
        null;
      const cellRule = getWindowPositionRuleForConfig(config, rule);
      const metrics = getWindowMetricsForOverlap(cellRule, doorConfig, config);
      return buildWindowRectFromCell(cell, metrics);
    }

    function removeOverlappingWindowCells(cellSet, cellConfigMap, rule, toggledCell, doorConfig) {
      const set = cellSet instanceof Set ? new Set(cellSet) : new Set();
      if (set.size <= 1) return set;

      const pendingConfig = toggledCell && set.has(String(toggledCell && toggledCell.key ? toggledCell.key : "")) ? buildCurrentWindowCellConfig() : null;
      const ordered = Array.from(set.values())
        .map((key) => parseWindowCellKey(key))
        .filter(Boolean)
        .sort((a, b) => {
          if (a.row !== b.row) return a.row - b.row;
          return a.col - b.col;
        });
      if (ordered.length <= 1) return set;

      if (toggledCell && set.has(String(toggledCell.key || ""))) {
        const next = new Set(set);
        const newRect = buildWindowRectForPlacedCell(toggledCell, cellConfigMap, rule, doorConfig, pendingConfig, toggledCell.key);
        for (const existing of ordered) {
          if (existing.key === toggledCell.key) continue;
          const existingRect = buildWindowRectForPlacedCell(existing, cellConfigMap, rule, doorConfig, pendingConfig, toggledCell.key);
          if (rectsOverlap(newRect, existingRect)) {
            next.delete(existing.key);
          }
        }
        return next;
      }

      const keep = [];
      for (const cell of ordered) {
        const cellRect = buildWindowRectForPlacedCell(cell, cellConfigMap, rule, doorConfig, pendingConfig, toggledCell && toggledCell.key);
        const hit = keep.some((existing) => {
          return rectsOverlap(
            cellRect,
            buildWindowRectForPlacedCell(existing, cellConfigMap, rule, doorConfig, pendingConfig, toggledCell && toggledCell.key)
          );
        });
        if (!hit) keep.push(cell);
      }
      return new Set(keep.map((cell) => cell.key));
    }

    function getOrderedWindowCells(doorConfig) {
      const resolvedDoorConfig = doorConfig || getCurrentDoorVisualConfig();
      const maxRows = getEffectiveWindowRowCount(resolvedDoorConfig);
      return Array.from(state.windowCells.values())
        .map((cellKey) => {
          const parsed = parseWindowCellKey(cellKey);
          if (!parsed) return null;
          return {
            ...parsed,
            config: getWindowCellConfig(parsed.key)
          };
        })
        .filter((cell) => {
          return cell && cell.row >= 1 && cell.row <= maxRows && cell.col >= 1;
        })
        .sort((a, b) => {
          if (a.row !== b.row) return a.row - b.row;
          return a.col - b.col;
        });
    }

    function hasSelectedWindowOpenings(doorConfig) {
      return getOrderedWindowCells(doorConfig).length > 0;
    }

    function syncWindowCellsToLayout() {
      if (!requiresWindowPositionSelection()) {
        if (state.windowCells.size > 0) {
          state.windowCells.clear();
        }
        state.windowCellConfigs = new Map();
        clearWindowHoverCell();
        return;
      }

      const windowPositionStep = getWindowPositionStep();
      if (!windowPositionStep || !isWindowPositionStep(windowPositionStep)) {
        clearWindowHoverCell();
      }

      const doorConfig = getCurrentDoorVisualConfig();
      const positionRule = getWindowPositionRule();
      state.windowCells = applyWindowCellSelectionRules(state.windowCells, state.windowCellConfigs, positionRule, null, doorConfig);
      state.windowCellConfigs = filterWindowCellConfigMap(state.windowCellConfigs, state.windowCells);
      state.windowHoverCell = normalizeWindowHoverCell(state.windowHoverCell, windowPositionStep ? windowPositionStep.id : "", positionRule, doorConfig);
    }

    function buildWindowCellDisplayLabel(cell, rows) {
      const columnRule = getWindowPositionRuleForConfig(cell && cell.config, getWindowPositionRule());
      return buildWindowPositionDisplayLabel(cell.row, cell.col, rows, columnRule);
    }

    function getWindowPositionRuleHint(rule) {
      const resolvedRule = rule || getWindowPositionRule();
      if (!resolvedRule) return "";
      return "If the new window overlaps existing ones, overlapping existing windows are removed automatically.";
    }

    function applyEntryContextSelections() {
      if (!state.preflight.completed) return;

      const seriesStep = getEntrySeriesStep();
      let preselectedStepId = "";
      let preselectedOptionId = "";

      if (!hasLiveEntryProduct() && seriesStep && Array.isArray(seriesStep.options) && seriesStep.options.length > 0) {
        let seriesOption = findOptionByAny(seriesStep, state.preflight.seriesHandle);
        if (!seriesOption && state.entryContext.seriesLabel) {
          seriesOption =
            seriesStep.options.find((option) => {
              return (
                String(option.label || "").trim().toLowerCase() ===
                String(state.entryContext.seriesLabel || "").trim().toLowerCase()
              );
            }) || null;
        }

        if (seriesOption) {
          const seriesSet = getSelectionSet(seriesStep.id);
          seriesSet.clear();
          seriesSet.add(seriesOption.id);
          state.preflight.seriesHandle = String(seriesOption.handle || seriesOption.id || "");
          if (!state.entryContext.seriesLabel) {
            state.entryContext.seriesLabel = String(seriesOption.label || "");
          }
          preselectedStepId = seriesStep.id;
          preselectedOptionId = seriesOption.id;
        }
      }

      const visibleSteps = getVisibleSteps();
      const railSteps = getRailSteps();
      const startStep = findStepByHandle(entry.startStepHandle) || railSteps[0] || visibleSteps[0] || null;
      const fallbackStepId = startStep ? String(startStep.id) : "";

      state.activeStepId = preselectedStepId || fallbackStepId;
      state.mobileOpenStepId = preselectedStepId || fallbackStepId;
      state.activeOptionId = preselectedOptionId || "";
    }

    function ensureInitialFocus() {
      const visibleSteps = getVisibleSteps();
      const railSteps = getRailSteps();
      const firstStep = railSteps[0] || visibleSteps[0] || null;

      if (!state.activeStepId && firstStep) {
        state.activeStepId = firstStep.id;
      }
      if (!state.mobileOpenStepId && firstStep) {
        state.mobileOpenStepId = state.activeStepId || firstStep.id;
      }
      const activeStep = findStepById(state.activeStepId);
      if ((!activeStep || !isStepRenderedAsRailGroup(activeStep)) && firstStep) {
        state.activeStepId = firstStep.id;
        state.mobileOpenStepId = firstStep.id;
        state.activeOptionId = "";
      }
    }

    function getEntryProfile() {
      const customSize = getEntryCustomSizeState();
      const panelPreference = getEntryPanelPreferenceValue();
      const panelPreferenceLabel = formatEntryPanelPreferenceLabel(panelPreference);
      const quantity = getDoorQuantityValue();
      const quantityLabel = formatDoorQuantityLabel(quantity);
      if (hasLiveEntryProduct()) {
        const entryProduct = state.entryProduct;
        const variant = getSelectedEntryVariant();
        const sizeIndex = entryProduct ? Number(entryProduct.sizeOptionIndex) : -1;
        const rawSizeLabel =
          entryProduct && sizeIndex >= 0
            ? String(
                entryProduct.selectedOptions && entryProduct.selectedOptions[sizeIndex]
                  ? entryProduct.selectedOptions[sizeIndex]
                  : variant && variant.optionValues && variant.optionValues[sizeIndex]
                    ? variant.optionValues[sizeIndex]
                  : ""
              )
            : state.preflight.sizeLabel || state.entryContext.sizeLabel || "";
        const colorLabel = supportsEntryIndependentColors(entryProduct) ? state.entryContext.colorLabel || "" : "";

        return {
          seriesLabel: String((entryProduct && entryProduct.title) || state.entryContext.seriesLabel || ""),
          colorLabel: String(colorLabel || ""),
          sizeLabel: String(getEffectiveEntrySizeLabel(rawSizeLabel) || ""),
          quantity,
          quantityLabel,
          panelPreference,
          panelPreferenceLabel,
          sizeMode: customSize.active ? "custom" : "standard",
          customWidth: customSize.width.valid ? customSize.width.normalized : "",
          customHeight: customSize.height.valid ? customSize.height.normalized : "",
          variantSku: String((variant && variant.sku) || ""),
          variantTitle: String((variant && variant.title) || ""),
          productTitle: String((entryProduct && entryProduct.title) || state.entryContext.productTitle || "")
        };
      }

      const seriesStep = getEntrySeriesStep();
      const seriesFromStep = getSelectedOptionFromStep(seriesStep);

      const seriesLabel = state.entryContext.seriesLabel || (seriesFromStep ? seriesFromStep.label : "");
      const colorLabel = state.entryContext.colorLabel || "";
      const sizeLabel = state.preflight.sizeLabel || state.entryContext.sizeLabel || "";

      return {
        seriesLabel: String(seriesLabel || ""),
        colorLabel: String(colorLabel || ""),
        sizeLabel: String(getEffectiveEntrySizeLabel(sizeLabel) || ""),
        quantity,
        quantityLabel,
        panelPreference,
        panelPreferenceLabel,
        sizeMode: customSize.active ? "custom" : "standard",
        customWidth: customSize.width.valid ? customSize.width.normalized : "",
        customHeight: customSize.height.valid ? customSize.height.normalized : "",
        variantSku: String(state.entryContext.variantSku || ""),
        variantTitle: String(state.entryContext.variantTitle || ""),
        productTitle: String(state.entryContext.productTitle || "")
      };
    }

    function getWindowSelectionProfile() {
      const windowLayoutStep = getWindowLayoutStep();
      const windowGlassStep = getWindowGlassStep();
      const windowColorStep = getWindowColorStep();
      const windowInsertStep = getWindowInsertStep();
      const windowPositionStep = getWindowPositionStep();
      const doorConfig = getCurrentDoorVisualConfig();
      const layoutOption = getSelectedOptionFromStep(windowLayoutStep);
      const glassOption = getSelectedOptionFromStep(windowGlassStep);
      const colorOption = getSelectedOptionFromStep(windowColorStep);
      const insertOption = getSelectedOptionFromStep(windowInsertStep);
      const rows = getWindowGridRows(windowPositionStep, doorConfig);
      const selectedCells = getOrderedWindowCells(doorConfig);
      if (selectedCells.length === 0) {
        return {
          layoutLabel: "",
          glassLabel: "",
          colorLabel: "",
          insertLabel: "",
          positionLabel: ""
        };
      }
      const positionLabel = selectedCells.map((cell) => buildWindowCellDisplayLabel(cell, rows)).join(", ");
      const layoutLabels = dedupeTextValues(selectedCells.map((cell) => cell && cell.config && cell.config.layoutLabel));
      const glassLabels = dedupeTextValues(selectedCells.map((cell) => cell && cell.config && cell.config.glassLabel));
      const colorLabels = dedupeTextValues(selectedCells.map((cell) => cell && cell.config && cell.config.colorLabel));
      const insertLabels = dedupeTextValues(selectedCells.map((cell) => cell && cell.config && cell.config.insertLabel));
      const resolvedGlassLabel = glassLabels.length > 0 ? glassLabels.join(" | ") : glassOption ? String(glassOption.label || "") : "";
      const resolvedInsertLabel = getDisplayWindowInsertLabel(
        resolvedGlassLabel,
        insertLabels.length > 0 ? insertLabels.join(" | ") : insertOption ? String(insertOption.label || "") : ""
      );
      return {
        layoutLabel: layoutLabels.length > 0 ? layoutLabels.join(" | ") : layoutOption ? String(layoutOption.label || "") : "",
        glassLabel: resolvedGlassLabel,
        colorLabel: colorLabels.length > 0 ? colorLabels.join(" | ") : colorOption ? String(colorOption.label || "") : "",
        insertLabel: resolvedInsertLabel,
        positionLabel: positionLabel
      };
    }

    function getPreviewModel() {
      const visibleSteps = getVisibleSteps();
      const railSteps = getRailSteps();
      let activeStep = findStepById(state.activeStepId);
      if (!activeStep || !isStepRenderedAsRailGroup(activeStep)) {
        activeStep = railSteps[0] || visibleSteps[0] || null;
      }
      if (!activeStep) return null;

      let option = null;
      let selected = false;

      if (state.activeOptionId) {
        option = findOption(activeStep, state.activeOptionId);
        if (option) {
          selected = getSelectionSet(activeStep.id).has(option.id);
        }
      }

      if (!option && state.lastTouchedStepId && state.lastTouchedOptionId) {
        const touchedStep = findStepById(state.lastTouchedStepId);
        const touchedOption = findOption(touchedStep, state.lastTouchedOptionId);
        if (touchedStep && touchedOption && getSelectionSet(touchedStep.id).has(touchedOption.id)) {
          activeStep = touchedStep;
          option = touchedOption;
          selected = true;
        }
      }

      if (!option) {
        const selectedPair = getFirstSelectedOptionAcrossSteps();
        if (selectedPair) {
          activeStep = selectedPair.step;
          option = selectedPair.option;
          selected = true;
        }
      }

      if (!option) {
        return {
          step: activeStep,
          option: null,
          selected: false
        };
      }

      return {
        step: activeStep,
        option,
        selected
      };
    }

    function buildWindowGridTableMarkup(step, classPrefix, locked, doorConfig) {
      const resolvedDoorConfig = doorConfig || getCurrentDoorVisualConfig();
      const rows = getWindowGridRows(step, resolvedDoorConfig);
      const rule = getWindowPositionRule();
      const cols = Math.max(1, Number(rule.cols || 1));
      const selectedSet = new Set(getOrderedWindowCells(resolvedDoorConfig).map((cell) => cell.key));

      const bodyMarkup = rows
        .map((row, rowIndex) => {
          const rowNumber = rowIndex + 1;
          const cellsMarkup = Array.from({ length: cols }, (_, columnIndex) => {
            const columnNumber = columnIndex + 1;
            const cellKey = makeWindowCellKey(rowNumber, columnNumber);
            const active = selectedSet.has(cellKey);
            const positionLabel = buildWindowPositionDisplayLabel(rowNumber, columnNumber, rows, rule);
            return `
              <button
                type="button"
                class="${classPrefix}__cell${active ? " is-active" : ""}"
                data-gd-window-cell="${escapeHtml(cellKey)}"
                data-gd-window-step="${escapeHtml(step.id)}"
                aria-label="${escapeHtml(positionLabel)}"
                aria-pressed="${active ? "true" : "false"}"
                ${locked ? "disabled" : ""}
              ></button>
            `;
          }).join("");

          return `
            <div class="${classPrefix}__row-head">${escapeHtml(row.label)}</div>
            ${cellsMarkup}
          `;
        })
        .join("");

      return `
        <div class="${classPrefix}__table" style="--gd-window-cols:${Math.max(cols, 1)};" role="grid" aria-label="Window opening grid">
          ${bodyMarkup}
        </div>
      `;
    }

    function buildWindowPreviewOverlayMarkup(step, doorConfig) {
      const resolvedDoorConfig = doorConfig || getCurrentDoorVisualConfig();
      const locked = false;
      const rows = getWindowGridRows(step, resolvedDoorConfig);
      const rule = getWindowPositionRule();
      const cols = Math.max(1, Number(rule.cols || 1));
      const metrics = getWindowInteractionMetricsForOverlay(rule, resolvedDoorConfig);
      const selectedSet = new Set(getOrderedWindowCells(resolvedDoorConfig).map((cell) => cell.key));
      const rowCount = Math.max(rows.length, 1);
      const hoverCellWidthPct = Number(metrics.hoverWidthPct || metrics.widthPct || 0);
      const hoverCellHeightPct = Number(metrics.hoverHeightPct || metrics.heightPct || 0);
      const columnSegments = buildWindowAxisSegments(
        Array.isArray(metrics.colCenters) && metrics.colCenters.length === cols ? metrics.colCenters : buildWindowAxisCenters(cols, 1, false)
      );
      const rowSegments = buildWindowAxisSegments(
        Array.isArray(metrics.rowCenters) && metrics.rowCenters.length === rowCount ? metrics.rowCenters : buildWindowAxisCenters(rowCount, 1, false)
      );
      const cellButtons = [];

      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        const row = rows[rowIndex] || { label: `Row ${rowIndex + 1}` };
        const rowSegment = rowSegments[rowIndex] || {
          start: (rowIndex / rowCount) * 100,
          end: ((rowIndex + 1) / rowCount) * 100,
          size: 100 / rowCount
        };
        for (let colIndex = 0; colIndex < cols; colIndex += 1) {
          const rowNumber = rowIndex + 1;
          const columnNumber = colIndex + 1;
          const cellKey = makeWindowCellKey(rowNumber, columnNumber);
          const active = selectedSet.has(cellKey);
          const positionLabel = buildWindowPositionDisplayLabel(rowNumber, columnNumber, rows, rule);
          const columnSegment = columnSegments[colIndex] || {
            start: (colIndex / cols) * 100,
            end: ((colIndex + 1) / cols) * 100,
            size: 100 / cols
          };
          const left = Math.max(0, columnSegment.start);
          const top = Math.max(0, rowSegment.start);
          const width = Math.max(0, columnSegment.size);
          const height = Math.max(0, rowSegment.size);
          const actualCenterX = Number(metrics.hoverColCenters && metrics.hoverColCenters[colIndex]);
          const actualCenterY = Number(metrics.hoverRowCenters && metrics.hoverRowCenters[rowIndex]);
          const visualWidth = width > 0 ? (hoverCellWidthPct / width) * 100 : 100;
          const visualHeight = height > 0 ? (hoverCellHeightPct / height) * 100 : 100;
          const visualCenterX = width > 0 && Number.isFinite(actualCenterX) ? ((actualCenterX - left) / width) * 100 : 50;
          const visualCenterY = height > 0 && Number.isFinite(actualCenterY) ? ((actualCenterY - top) / height) * 100 : 50;
          const style = ` style="left:${left.toFixed(3)}%;top:${top.toFixed(3)}%;width:${width.toFixed(3)}%;height:${height.toFixed(3)}%;--gd-window-visual-w:${visualWidth.toFixed(3)}%;--gd-window-visual-h:${visualHeight.toFixed(3)}%;--gd-window-visual-x:${visualCenterX.toFixed(3)}%;--gd-window-visual-y:${visualCenterY.toFixed(3)}%;"`;

          cellButtons.push(`
            <button
              type="button"
              class="gd-preview-gallery__window-cell${active ? " is-active" : ""}"
              data-gd-window-cell="${escapeHtml(cellKey)}"
              data-gd-window-step="${escapeHtml(step.id)}"
              aria-label="${escapeHtml(positionLabel)}"
              aria-pressed="${active ? "true" : "false"}"
              ${style}
              ${locked ? "disabled" : ""}
            ></button>
          `);
        }
      }

      return `
        <div class="gd-preview-gallery__window-overlay${locked ? " is-locked" : ""}">
          <div
            class="gd-preview-gallery__window-grid"
            data-gd-window-grid-step="${escapeHtml(step.id)}"
            data-gd-window-grid-cols="${Math.max(cols, 1)}"
            data-gd-window-grid-rows="${rowCount}"
            style="--gd-window-cols:${Math.max(cols, 1)};--gd-window-rows:${rowCount};--gd-window-preview-cell-w:${hoverCellWidthPct.toFixed(3)};--gd-window-preview-cell-h:${hoverCellHeightPct.toFixed(3)};"
            role="grid"
            aria-label="Window positions on door"
          >
            ${cellButtons.join("")}
          </div>
          <span class="gd-preview-gallery__hover-window" data-gd-hover-window aria-hidden="true"></span>
        </div>
      `;
    }

    function updateWindowHoverGhost(doorConfig) {
      const resolvedDoorConfig = doorConfig || getCurrentDoorVisualConfig();
      const ghost = previewEl.querySelector("[data-gd-hover-window]");
      if (!ghost) return;

      const activeHover = getActiveWindowHoverCell(resolvedDoorConfig);
      if (!activeHover) {
        ghost.classList.remove("is-visible");
        return;
      }

      const rule = getWindowPositionRule();
      const metrics = getWindowInteractionMetricsForOverlay(rule, resolvedDoorConfig);
      const cols = Math.max(1, Number(rule && rule.cols ? rule.cols : 1));
      const rows = Math.max(1, Number(metrics && metrics.rows ? metrics.rows : getEffectiveWindowRowCount(resolvedDoorConfig)));

      const x = Number((metrics.hoverColCenters || metrics.colCenters) && (metrics.hoverColCenters || metrics.colCenters)[activeHover.col - 1]);
      const y = Number((metrics.hoverRowCenters || metrics.rowCenters) && (metrics.hoverRowCenters || metrics.rowCenters)[activeHover.row - 1]);
      const widthPct = Number(metrics.hoverWidthPct || metrics.widthPct || 0);
      const heightPct = Number(metrics.hoverHeightPct || metrics.heightPct || 0);

      const safeX = Number.isFinite(x) ? x : ((activeHover.col - 0.5) / cols) * 100;
      const safeY = Number.isFinite(y) ? y : ((activeHover.row - 0.5) / rows) * 100;
      const safeW = Number.isFinite(widthPct) && widthPct > 0 ? widthPct : 12;
      const safeH = Number.isFinite(heightPct) && heightPct > 0 ? heightPct : 9;

      ghost.style.setProperty("--gd-window-x", `${safeX.toFixed(3)}%`);
      ghost.style.setProperty("--gd-window-y", `${safeY.toFixed(3)}%`);
      ghost.style.setProperty("--gd-window-w", `${safeW.toFixed(3)}%`);
      ghost.style.setProperty("--gd-window-h", `${safeH.toFixed(3)}%`);
      ghost.classList.add("is-visible");
    }

    function renderWindowPositionPreview(step) {
      const locked = !requiresWindowPositionSelection();
      const doorConfig = getCurrentDoorVisualConfig();
      const designDoor = getDesignEditingDoor() || getDraftDoorState();
      const presentation = getDoorPreviewPresentation(designDoor);
      const surfaceMarkup = presentation
        ? buildDoorPreviewSectionMarkup(presentation, "gd-preview-door--design gd-preview-door--window-stage", { showMeasurements: true })
        : buildWindowConfigurationSurfaceMarkup(step, doorConfig);
      const summaryMarkup = "";

      previewEl.innerHTML = `
        <section class="gd-design-preview gd-design-preview--window" aria-label="Window configuration preview">
          <div class="gd-design-preview__body gd-design-preview__body--window">
            <section class="gd-window-config-preview${locked ? " is-locked" : ""}">
              <div class="gd-window-config-preview__stage">
                ${surfaceMarkup}
              </div>
            </section>
          </div>
        </section>
      `;
      setPreviewSummaryMarkup(summaryMarkup);
    }

    const DOOR_PANEL_SHORT_IN = 18;
    const DOOR_PANEL_TALL_IN = 21;
    const DOOR_PANEL_LARGE_IN = 24;
    const PREVIEW_DOOR_PX_PER_IN = 8;
    const PREVIEW_DOOR_MIN_HEIGHT_PX = 480;
    const PREVIEW_DOOR_MIN_WIDTH_PX = 480;
    const PREVIEW_CARD_CHROME_PX = 140;
    const PREVIEW_CARD_MIN_BASIS_PX = 620;
    const PREVIEW_CARD_MAX_BASIS_PX = 2600;
    const PREVIEW_CAPTURE_MIN_OUTPUT_WIDTH = 1600;
    const PREVIEW_CAPTURE_MAX_OUTPUT_WIDTH = 1800;
    const PREVIEW_CAPTURE_DATA_URL_SOFT_LIMIT = 700000;

    function parseDoorDimensionTokenInches(value, assumeFeet = true) {
      const raw = normalizeFeetInchesInputValue(value);
      if (!raw || isCustomSizeDimension(raw)) return 0;

      const text = raw.toLowerCase();
      const feetMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:ft|feet|foot|')/i);
      const inchesMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:in|inch|inches|")/i);

      const feet = feetMatch ? Number(feetMatch[1]) : NaN;
      const inches = inchesMatch ? Number(inchesMatch[1]) : 0;
      if (Number.isFinite(feet) && feet > 0) {
        const total = feet * 12 + (Number.isFinite(inches) && inches > 0 ? inches : 0);
        return total > 0 ? total : 0;
      }

      const numeric = Number(text.replace(/[^0-9.]+/g, ""));
      if (!Number.isFinite(numeric) || numeric <= 0) return 0;

      if (/(?:in|inch|inches|")/.test(text)) return numeric;
      return assumeFeet ? numeric * 12 : numeric;
    }

    function parseDoorSizeLabel(sizeLabel, options = {}) {
      const raw = String(sizeLabel || "").trim();
      const allowOrientationSwap = options.allowOrientationSwap !== false;
      const parts = raw
        .split(/[x×]/i)
        .map((part) => String(part || "").trim())
        .filter(Boolean);

      let widthIn = 0;
      let heightIn = 0;

      if (parts.length >= 2) {
        widthIn = parseDoorDimensionTokenInches(parts[0], true);
        heightIn = parseDoorDimensionTokenInches(parts[1], true);
      }

      if (widthIn <= 0 || heightIn <= 0) {
        const values = (raw.match(/\d+(?:\.\d+)?/g) || [])
          .map((part) => Number(part))
          .filter((value) => Number.isFinite(value) && value > 0);
        if (widthIn <= 0) widthIn = values.length >= 1 ? values[0] * 12 : 96;
        if (heightIn <= 0) heightIn = values.length >= 2 ? values[1] * 12 : 84;
      }

      let safeWidthIn = Math.max(1, widthIn);
      let safeHeightIn = Math.max(1, heightIn);

      // Some standard size labels arrive as "8x16" for a 16' x 8' door.
      // Preserve explicitly-entered custom sizes by letting callers disable the swap heuristic.
      if (allowOrientationSwap && safeHeightIn > safeWidthIn && safeHeightIn >= 120 && safeWidthIn <= 120) {
        [safeWidthIn, safeHeightIn] = [safeHeightIn, safeWidthIn];
      }

      return {
        label: raw,
        widthFeet: safeWidthIn / 12,
        heightFeet: safeHeightIn / 12,
        widthIn: safeWidthIn,
        heightIn: safeHeightIn
      };
    }

    function getDoorPanelPreferredHeightIn(panelPreference) {
      const normalized = normalizeEntryPanelPreferenceValue(panelPreference);
      if (normalized === "24") return DOOR_PANEL_LARGE_IN;
      if (normalized === "21") return DOOR_PANEL_TALL_IN;
      return DOOR_PANEL_SHORT_IN;
    }

    function getDoorPanelAllowedHeights(panelPreference) {
      const normalized = normalizeEntryPanelPreferenceValue(panelPreference);
      return normalized === "24" ? [DOOR_PANEL_SHORT_IN, DOOR_PANEL_TALL_IN, DOOR_PANEL_LARGE_IN] : [DOOR_PANEL_SHORT_IN, DOOR_PANEL_TALL_IN];
    }

    function buildDoorPanelHeightCountMap(panelHeightsIn) {
      const counts = new Map();
      (Array.isArray(panelHeightsIn) ? panelHeightsIn : []).forEach((height) => {
        const value = Math.round(Number(height));
        if (!Number.isFinite(value) || value <= 0) return;
        counts.set(value, Number(counts.get(value) || 0) + 1);
      });
      return counts;
    }

    function buildDoorPanelHeightCountRecord(countMap) {
      const record = {};
      if (!(countMap instanceof Map)) return record;
      Array.from(countMap.entries())
        .sort((left, right) => Number(left[0]) - Number(right[0]))
        .forEach(([height, count]) => {
          const safeHeight = Math.round(Number(height));
          const safeCount = Math.max(0, Number(count) || 0);
          if (!(safeHeight > 0) || !(safeCount > 0)) return;
          record[String(safeHeight)] = safeCount;
        });
      return record;
    }

    function buildDoorPanelPriorityIndices(totalSections) {
      const count = Math.max(1, Number(totalSections) || 1);
      const priority = [];
      const used = new Set();

      function pushIndex(index) {
        const safeIndex = Number(index);
        if (!Number.isInteger(safeIndex) || safeIndex < 0 || safeIndex >= count || used.has(safeIndex)) return;
        used.add(safeIndex);
        priority.push(safeIndex);
      }

      pushIndex(0);
      pushIndex(count - 1);
      pushIndex(Math.floor((count - 1) / 2));

      for (let offset = 1; priority.length < count && offset < count; offset += 1) {
        pushIndex(offset);
        pushIndex(count - 1 - offset);
      }

      return priority;
    }

    function pickPrioritizedDoorPanelIndices(indices, desiredCount, totalSections) {
      const slots = new Set(
        (Array.isArray(indices) ? indices : [])
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value >= 0)
      );
      const pickCount = Math.max(0, Math.min(slots.size, Number(desiredCount) || 0));
      if (pickCount <= 0) return [];

      return buildDoorPanelPriorityIndices(totalSections)
        .filter((index) => slots.has(index))
        .slice(0, pickCount)
        .sort((left, right) => left - right);
    }

    function distributeDoorPanelHeights(countMap) {
      const entries = Array.from((countMap instanceof Map ? countMap : new Map()).entries())
        .map(([height, count]) => {
          return {
            height: Math.round(Number(height)),
            count: Math.max(0, Number(count) || 0)
          };
        })
        .filter((item) => item.height > 0 && item.count > 0)
        .sort((left, right) => left.height - right.height);
      if (entries.length === 0) return [];

      const totalSections = entries.reduce((sum, item) => sum + item.count, 0);
      const baseHeight = entries[0].height;
      const assigned = Array.from({ length: totalSections }, () => null);

      entries
        .slice(1)
        .sort((left, right) => right.height - left.height)
        .forEach((item) => {
          const availableIndices = assigned
            .map((value, index) => {
              return value === null ? index : null;
            })
            .filter((value) => value !== null);
          pickPrioritizedDoorPanelIndices(availableIndices, item.count, totalSections).forEach((slotIndex) => {
            assigned[slotIndex] = item.height;
          });
        });

      return assigned.map((value) => (value === null ? baseHeight : value));
    }

    function buildDoorPanelLayoutResult(panelHeightsIn, options = {}) {
      const heights = (Array.isArray(panelHeightsIn) ? panelHeightsIn : [])
        .map((height) => Math.round(Number(height)))
        .filter((height) => Number.isFinite(height) && height > 0);
      const heightCounts = buildDoorPanelHeightCountMap(heights);
      const preferredHeightIn = getDoorPanelPreferredHeightIn(options.panelPreference);

      return {
        sectionCount: heights.length,
        shortCount: Number(heightCounts.get(DOOR_PANEL_SHORT_IN) || 0),
        tallCount: Number(heightCounts.get(DOOR_PANEL_TALL_IN) || 0),
        largeCount: Number(heightCounts.get(DOOR_PANEL_LARGE_IN) || 0),
        preferredCount: Number(heightCounts.get(preferredHeightIn) || 0),
        preferredHeightIn,
        panelHeightCounts: buildDoorPanelHeightCountRecord(heightCounts),
        panelHeightsIn: heights,
        isExact: Boolean(options.isExact)
      };
    }

    function enumerateDoorPanelLayoutSolutions(targetHeightIn, allowedHeights, startIndex = 0, currentHeights = [], solutions = []) {
      const remaining = Math.round(Number(targetHeightIn || 0));
      if (remaining === 0) {
        solutions.push(currentHeights.slice());
        return solutions;
      }
      if (remaining < 0) return solutions;

      const heights = Array.isArray(allowedHeights) ? allowedHeights : [];
      for (let index = startIndex; index < heights.length; index += 1) {
        const panelHeight = Math.round(Number(heights[index]));
        if (!Number.isFinite(panelHeight) || panelHeight <= 0 || panelHeight > remaining) continue;
        currentHeights.push(panelHeight);
        enumerateDoorPanelLayoutSolutions(remaining - panelHeight, heights, index, currentHeights, solutions);
        currentHeights.pop();
      }
      return solutions;
    }

    function solveDoorPanelLayoutByHeight(heightIn, panelPreference) {
      const targetHeightIn = Math.round(Number(heightIn || 0));
      if (!Number.isFinite(targetHeightIn) || targetHeightIn <= 0) return null;

      const preferredHeightIn = getDoorPanelPreferredHeightIn(panelPreference);
      const prefersNoShortPanels = preferredHeightIn === DOOR_PANEL_LARGE_IN;
      const allowedHeights = getDoorPanelAllowedHeights(panelPreference).slice().sort((left, right) => left - right);
      const rawSolutions = enumerateDoorPanelLayoutSolutions(targetHeightIn, allowedHeights);
      if (rawSolutions.length === 0) return null;

      const rankedSolutions = rawSolutions
        .map((panelHeightsIn) => {
          const heightCounts = buildDoorPanelHeightCountMap(panelHeightsIn);
          const preferredCount = Number(heightCounts.get(preferredHeightIn) || 0);
          const shortCount = Number(heightCounts.get(DOOR_PANEL_SHORT_IN) || 0);
          const sectionCount = panelHeightsIn.length;
          const distancePenalty = Array.from(heightCounts.entries()).reduce((sum, entry) => {
            const height = Math.round(Number(entry[0]));
            const count = Math.max(0, Number(entry[1]) || 0);
            if (!(height > 0) || !(count > 0)) return sum;
            return sum + Math.abs(height - preferredHeightIn) * count;
          }, 0);

          return {
            panelHeightsIn,
            preferredCount,
            shortCount,
            avoidsShortPanels: shortCount === 0,
            sectionCount,
            distancePenalty,
            heightCounts
          };
        })
        .sort((left, right) => {
          if (prefersNoShortPanels && left.avoidsShortPanels !== right.avoidsShortPanels) {
            return left.avoidsShortPanels ? -1 : 1;
          }
          if (left.preferredCount !== right.preferredCount) return right.preferredCount - left.preferredCount;
          if (left.sectionCount !== right.sectionCount) return left.sectionCount - right.sectionCount;
          if (prefersNoShortPanels && left.shortCount !== right.shortCount) return left.shortCount - right.shortCount;
          if (left.distancePenalty !== right.distancePenalty) return left.distancePenalty - right.distancePenalty;

          const heightsDescending = Array.from(new Set([...allowedHeights, preferredHeightIn])).sort((a, b) => b - a);
          for (const height of heightsDescending) {
            const leftCount = Number(left.heightCounts.get(height) || 0);
            const rightCount = Number(right.heightCounts.get(height) || 0);
            if (leftCount !== rightCount) return rightCount - leftCount;
          }
          return 0;
        });

      const best = rankedSolutions[0];
      return buildDoorPanelLayoutResult(distributeDoorPanelHeights(best.heightCounts), {
        panelPreference,
        isExact: true
      });
    }

    function buildDoorPanelLayout(heightIn, panelPreference) {
      const exact = solveDoorPanelLayoutByHeight(heightIn, panelPreference);
      if (exact) return exact;

      const targetHeightIn = Math.max(1, Number(heightIn) || 84);
      const preferredHeightIn = getDoorPanelPreferredHeightIn(panelPreference);
      const fallbackSections = Math.max(3, Math.min(8, Math.round(targetHeightIn / preferredHeightIn)));
      return buildDoorPanelLayoutResult(Array.from({ length: fallbackSections }, () => preferredHeightIn), {
        panelPreference,
        isExact: false
      });
    }

    function clampNumber(value, min, max) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return min;
      return Math.min(max, Math.max(min, numeric));
    }

    function getDoorVisualConfig(sizeLabel, options = {}) {
      const size = parseDoorSizeLabel(sizeLabel, options);
      const widthIn = Math.max(1, Number(size.widthIn || 0));
      const heightIn = Math.max(1, Number(size.heightIn || 0));
      const aspect = Math.max(0.1, widthIn / heightIn);
      const panelPreference = normalizeEntryPanelPreferenceValue(options.panelPreference);
      const panelLayout = buildDoorPanelLayout(heightIn, panelPreference);
      const panelCount = Math.max(3, Number(panelLayout && panelLayout.sectionCount ? panelLayout.sectionCount : 4));
      const sectionCount = size.widthFeet >= 16 ? 6 : size.widthFeet >= 12 ? 5 : size.widthFeet >= 8 ? 4 : 3;
      return {
        ...size,
        aspect,
        panelPreference,
        panelPreferenceLabel: formatEntryPanelPreferenceLabel(panelPreference),
        panelCount,
        panelLayout,
        panelHeightsIn: Array.isArray(panelLayout && panelLayout.panelHeightsIn) ? panelLayout.panelHeightsIn.slice() : [],
        panelRuleMatched: Boolean(panelLayout && panelLayout.isExact),
        panelShortCount: Number(panelLayout && panelLayout.shortCount ? panelLayout.shortCount : 0),
        panelTallCount: Number(panelLayout && panelLayout.tallCount ? panelLayout.tallCount : 0),
        panelLargeCount: Number(panelLayout && panelLayout.largeCount ? panelLayout.largeCount : 0),
        panelHeightCounts: panelLayout && panelLayout.panelHeightCounts ? { ...panelLayout.panelHeightCounts } : {},
        sectionCount,
        widthIn,
        heightIn
      };
    }

    function getPreviewDoorMetrics(doorConfig) {
      const widthIn = Math.max(1, Number(doorConfig && doorConfig.widthIn ? doorConfig.widthIn : 96));
      const heightIn = Math.max(1, Number(doorConfig && doorConfig.heightIn ? doorConfig.heightIn : 84));
      const renderWidthPx = Math.max(PREVIEW_DOOR_MIN_WIDTH_PX, Math.round(widthIn * PREVIEW_DOOR_PX_PER_IN));
      const renderHeightPx = Math.max(PREVIEW_DOOR_MIN_HEIGHT_PX, Math.round(heightIn * PREVIEW_DOOR_PX_PER_IN));
      const cardBasisPx = clampNumber(
        renderWidthPx + PREVIEW_CARD_CHROME_PX,
        PREVIEW_CARD_MIN_BASIS_PX,
        PREVIEW_CARD_MAX_BASIS_PX
      );

      return {
        renderWidthPx,
        renderHeightPx,
        cardBasisPx
      };
    }

    function shouldUseClassic300PanelTexture(entryTitle) {
      const token = normalizeCompareToken(entryTitle);
      if (!token) return false;
      return token.includes("classic 300") || token.includes("classic300") || token.includes("a300");
    }

    function shouldUseCoachHouse400PanelTexture(entryTitle) {
      const token = normalizeCompareToken(entryTitle);
      if (!token) return false;
      return token.includes("coach house 400") || token.includes("coachhouse400") || token.includes("a400");
    }

    const DOOR_SERIES_RENDER_PROFILES = Object.freeze([
      Object.freeze({
        key: "classic-300",
        surfaceClass: "is-classic-300-texture",
        aliases: ["classic 300", "classic300", "a300"],
        moduleWidthIn: 96,
        panelWidthIn: 19,
        panelHeightIn: 13,
        panelsPerModule: 4,
        edgeIn: 3.75,
        reliefShadow: "gd-a300-panel-relief-shadow.png",
        reliefHighlight: "gd-a300-panel-relief-highlight.png"
      }),
      Object.freeze({
        key: "coach-house-400",
        surfaceClass: "is-coach-house-400-texture",
        aliases: ["coach house 400", "coachhouse400", "a400"],
        moduleWidthIn: 96,
        panelWidthIn: 19,
        panelHeightIn: 13,
        panelsPerModule: 4,
        edgeIn: 4,
        usesLegacyCoachRenderer: true
      }),
      Object.freeze({
        key: "heritage-500",
        surfaceClass: "is-heritage-500-texture",
        aliases: ["heritage 500", "heritage500", "a500"],
        moduleWidthIn: 96,
        panelWidthIn: 41,
        panelHeightIn: 13.75,
        panelsPerModule: 2,
        edgeIn: 4.5,
        reliefShadow: "gd-a500-panel-relief-shadow.png",
        reliefHighlight: "gd-a500-panel-relief-highlight.png"
      }),
      Object.freeze({
        key: "carriage-600",
        surfaceClass: "is-carriage-600-texture",
        aliases: ["carriage 600", "carriage600", "a600"],
        moduleWidthIn: 96,
        panelWidthIn: 41,
        panelHeightIn: 13,
        panelsPerModule: 2,
        edgeIn: 5,
        reliefShadow: "gd-a600-panel-relief-v2-shadow.png",
        reliefHighlight: "gd-a600-panel-relief-v2-highlight.png"
      }),
      Object.freeze({
        key: "modern-flush-800",
        surfaceClass: "is-modern-flush-800-texture",
        aliases: ["modern flush 800", "modern flush", "modern re defined", "x finity", "xfinity", "a800"],
        moduleWidthIn: 96,
        windowColumnsPerModule: 4,
        surfaceTexture: "gd-a800-steel-microtexture-v2.jpg"
      }),
      Object.freeze({
        key: "commercial-grooved-900",
        surfaceClass: "is-commercial-grooved-900-texture",
        aliases: ["commercial grooved 900", "commercialgrooved900", "a900"],
        moduleWidthIn: 96,
        windowColumnsPerModule: 4,
        surfaceTexture: "gd-a900-steel-microtexture-v2.jpg"
      })
    ]);

    function getDoorSeriesRenderProfile(entryTitle) {
      const token = normalizeCompareToken(entryTitle);
      if (!token) return null;
      return DOOR_SERIES_RENDER_PROFILES.find((profile) => {
        return profile.aliases.some((alias) => token.includes(alias));
      }) || null;
    }

    function getCoachHouse400TextureTone(colorLabel) {
      const key = resolveKnownColorKey(colorLabel);
      if (["black", "basalt black", "iron ore", "charcoal"].includes(key)) return "dark";
      if (["brown", "dark brown", "basalt brown", "mahogany", "dark walnut"].includes(key)) return "dark";
      if (["woodgrain", "teak", "plank"].includes(key)) return "warm";
      return "light";
    }

    function getCoachHouse400CellFill(colorLabel) {
      const key = resolveKnownColorKey(colorLabel);
      if (["woodgrain", "teak", "plank"].includes(key)) {
        const stops = resolvePreviewDoorFillStops(colorLabel);
        return `linear-gradient(155deg, ${stops[0]} 0%, ${stops[1]} 100%)`;
      }
      return getColorSwatchFill(colorLabel || "White");
    }

    function getCoachHouse400TextureColorClass(colorLabel) {
      const key = resolveKnownColorKey(colorLabel) || "white";
      return `is-a400-color-${key.replace(/\s+/g, "-")}`;
    }

    function getFixedSeriesModuleCount(doorConfig) {
      const widthIn = Math.max(1, Number(doorConfig && doorConfig.widthIn ? doorConfig.widthIn : 96));
      return Math.max(1, Math.round(widthIn / 96));
    }

    function getCoachHouse400ModuleCount(doorConfig) {
      return getFixedSeriesModuleCount(doorConfig);
    }

    function getCoachHouse400RowTileWidthPct(doorConfig) {
      return clampNumber(100 / getCoachHouse400ModuleCount(doorConfig), 25, 100);
    }

    function getFixedSeriesPatternWidthPct(doorConfig, seriesProfile) {
      const widthIn = Math.max(1, Number(doorConfig && doorConfig.widthIn ? doorConfig.widthIn : 96));
      const moduleWidthIn = Math.max(1, Number(seriesProfile && seriesProfile.moduleWidthIn) || 96);
      const patternWidthIn = Math.min(widthIn, getFixedSeriesModuleCount(doorConfig) * moduleWidthIn);
      return clampNumber((patternWidthIn / widthIn) * 100, 1, 100);
    }

    function getCoachHouse400RowPatternWidthPct(doorConfig) {
      return getFixedSeriesPatternWidthPct(doorConfig, getDoorSeriesRenderProfile("Coach House 400"));
    }

    function getFixedSeriesPanelCentersInModule(seriesProfile) {
      const panelCount = Math.max(0, Number(seriesProfile && seriesProfile.panelsPerModule) || 0);
      const moduleWidthIn = Math.max(1, Number(seriesProfile && seriesProfile.moduleWidthIn) || 96);
      const panelWidthIn = Math.max(0, Number(seriesProfile && seriesProfile.panelWidthIn) || 0);
      const edgeIn = Math.max(0, Number(seriesProfile && seriesProfile.edgeIn) || 0);
      if (!(panelCount > 0) || !(panelWidthIn > 0)) return [];

      const gapCount = Math.max(0, panelCount - 1);
      const availableGapIn = Math.max(0, moduleWidthIn - edgeIn * 2 - panelWidthIn * panelCount);
      const gapIn = gapCount > 0 ? availableGapIn / gapCount : 0;
      return Array.from({ length: panelCount }, (_, index) => {
        return edgeIn + panelWidthIn / 2 + index * (panelWidthIn + gapIn);
      });
    }

    function getFixedSeriesMoldCellSlots(doorConfig, seriesProfile) {
      const doorWidthIn = Math.max(1, Number(doorConfig && doorConfig.widthIn ? doorConfig.widthIn : 96));
      const moduleCount = getFixedSeriesModuleCount(doorConfig);
      const moduleWidthIn = Math.max(1, Number(seriesProfile && seriesProfile.moduleWidthIn) || 96);
      const panelWidthIn = Math.max(0, Number(seriesProfile && seriesProfile.panelWidthIn) || 0);
      const panelCentersInModule = getFixedSeriesPanelCentersInModule(seriesProfile);
      if (!(panelWidthIn > 0) || panelCentersInModule.length === 0) return [];

      const naturalPatternWidthIn = moduleCount * moduleWidthIn;
      const patternWidthIn = Math.min(doorWidthIn, naturalPatternWidthIn);
      const patternOffsetIn = (doorWidthIn - patternWidthIn) / 2;
      const patternScale = patternWidthIn / naturalPatternWidthIn;

      return Array.from({ length: moduleCount }, (_, moduleIndex) => {
        return panelCentersInModule.map((centerInModule, panelIndex) => {
          const centerIn = patternOffsetIn + (moduleIndex * moduleWidthIn + centerInModule) * patternScale;
          const renderedPanelWidthIn = panelWidthIn * patternScale;
          return {
            index: moduleIndex * panelCentersInModule.length + panelIndex,
            leftIn: centerIn - renderedPanelWidthIn / 2,
            rightIn: centerIn + renderedPanelWidthIn / 2
          };
        });
      }).flat();
    }

    function getOverlappingFixedSeriesMoldCellIndexes(doorConfig, seriesProfile, windowRectsIn) {
      const slots = getFixedSeriesMoldCellSlots(doorConfig, seriesProfile);
      const rects = (Array.isArray(windowRectsIn) ? windowRectsIn : [])
        .map((rect) => ({
          leftIn: Number(rect && rect.leftIn),
          rightIn: Number(rect && rect.rightIn)
        }))
        .filter((rect) => Number.isFinite(rect.leftIn) && Number.isFinite(rect.rightIn) && rect.rightIn > rect.leftIn);
      if (slots.length === 0 || rects.length === 0) return [];

      return slots
        .filter((slot) => {
          return rects.some((rect) => {
            return Math.min(slot.rightIn, rect.rightIn) - Math.max(slot.leftIn, rect.leftIn) > 0.001;
          });
        })
        .map((slot) => slot.index);
    }

    function getFixedSeriesWindowColumnCenters(doorConfig, seriesProfile, columnCount, windowWidthIn) {
      const doorWidthIn = Math.max(1, Number(doorConfig && doorConfig.widthIn ? doorConfig.widthIn : 96));
      const moduleCount = getFixedSeriesModuleCount(doorConfig);
      const moduleWidthIn = Math.max(1, Number(seriesProfile && seriesProfile.moduleWidthIn) || 96);
      const naturalPatternWidthIn = moduleCount * moduleWidthIn;
      const patternWidthIn = Math.min(doorWidthIn, naturalPatternWidthIn);
      const patternOffsetIn = (doorWidthIn - patternWidthIn) / 2;
      const patternScale = patternWidthIn / naturalPatternWidthIn;
      const cols = Math.max(1, Number(columnCount) || 1);
      const windowWidth = Math.max(0, Number(windowWidthIn) || 0);
      const panelCentersInModule = getFixedSeriesPanelCentersInModule(seriesProfile);
      let centersInPattern;

      if (
        panelCentersInModule.length > 0 &&
        cols === moduleCount * panelCentersInModule.length &&
        windowWidth <= Number(seriesProfile.panelWidthIn || 0)
      ) {
        centersInPattern = Array.from({ length: moduleCount }, (_, moduleIndex) => {
          return panelCentersInModule.map((centerInModule) => moduleIndex * moduleWidthIn + centerInModule);
        }).flat();
      } else {
        const widthPctInPattern = (windowWidth / naturalPatternWidthIn) * 100;
        const edgePaddingPctInPattern = (4 / naturalPatternWidthIn) * 100;
        centersInPattern = buildWindowAxisCenters(cols, widthPctInPattern, true, edgePaddingPctInPattern).map((centerPct) => {
          return (centerPct / 100) * naturalPatternWidthIn;
        });
      }

      return centersInPattern.map((centerInPattern) => {
        const centerInDoor = patternOffsetIn + centerInPattern * patternScale;
        return clampNumber((centerInDoor / doorWidthIn) * 100, 0, 100);
      });
    }

    function getCoachHouse400WindowColumnCenters(doorConfig, columnCount, windowWidthIn) {
      return getFixedSeriesWindowColumnCenters(
        doorConfig,
        getDoorSeriesRenderProfile("Coach House 400"),
        columnCount,
        windowWidthIn
      );
    }

    function getFixedSeriesWindowReplacementIndexesByRow(doorConfig, seriesProfile, selectedCells, fallbackRule) {
      const replacementsByRow = new Map();
      if (!seriesProfile || !seriesProfile.panelsPerModule || !Array.isArray(selectedCells)) return replacementsByRow;

      const doorWidthIn = Math.max(1, Number(doorConfig && doorConfig.widthIn ? doorConfig.widthIn : 96));
      selectedCells.forEach((cell) => {
        const rowIndex = Math.max(0, Math.floor(Number(cell && cell.row ? cell.row : 1)) - 1);
        const cellRule = getWindowPositionRuleForConfig(cell && cell.config, fallbackRule);
        const metrics = getWindowPreviewMetrics(doorConfig, cellRule, getWindowDimensionsFromConfig(cell && cell.config));
        const xPct = Number(metrics && metrics.colCenters && metrics.colCenters[Number(cell && cell.col ? cell.col : 1) - 1]);
        const widthPct = Math.max(0, Number(metrics && metrics.widthPct ? metrics.widthPct : 0));
        if (!Number.isFinite(xPct) || !(widthPct > 0)) return;

        const centerIn = (xPct / 100) * doorWidthIn;
        const windowWidthIn = (widthPct / 100) * doorWidthIn;
        const overlappingIndexes = getOverlappingFixedSeriesMoldCellIndexes(doorConfig, seriesProfile, [
          {
            leftIn: centerIn - windowWidthIn / 2,
            rightIn: centerIn + windowWidthIn / 2
          }
        ]);
        if (overlappingIndexes.length === 0) return;

        const rowReplacements = replacementsByRow.get(rowIndex) || new Set();
        overlappingIndexes.forEach((index) => rowReplacements.add(index));
        replacementsByRow.set(rowIndex, rowReplacements);
      });

      return replacementsByRow;
    }

    function buildFixedSeriesPanelGridMarkup(doorConfig, rowHeightIn, seriesProfile, replacedPanelIndexes = new Set()) {
      if (!seriesProfile || !seriesProfile.panelsPerModule) return "";
      const moduleCount = getFixedSeriesModuleCount(doorConfig);
      const moduleWidthIn = Math.max(1, Number(seriesProfile.moduleWidthIn) || 96);
      const edgeIn = Math.max(0, Number(seriesProfile.edgeIn) || 0);
      const panelIn = Math.max(1, Number(seriesProfile.panelWidthIn) || 1);
      const panelHeightIn = Math.max(1, Number(seriesProfile.panelHeightIn) || 1);
      const panelsPerModule = Math.max(1, Number(seriesProfile.panelsPerModule) || 1);
      const gapCount = Math.max(0, panelsPerModule - 1);
      const gapIn = gapCount > 0
        ? Math.max(0, moduleWidthIn - edgeIn * 2 - panelIn * panelsPerModule) / gapCount
        : 0;
      const safeRowHeightIn = Math.max(panelHeightIn, Number(rowHeightIn) || panelHeightIn);
      const panelHeightPct = clampNumber((panelHeightIn / safeRowHeightIn) * 100, 35, 100);
      const columns = [];
      const cellColumns = [];

      for (let moduleIndex = 0; moduleIndex < moduleCount; moduleIndex += 1) {
        columns.push(edgeIn);
        for (let panelIndex = 0; panelIndex < panelsPerModule; panelIndex += 1) {
          const gridColumnIndex = columns.length + 1;
          columns.push(panelIn);
          cellColumns.push(gridColumnIndex);
          if (panelIndex < panelsPerModule - 1) columns.push(gapIn);
        }
        columns.push(edgeIn);
      }

      const template = columns.map((width) => `${Math.max(0.001, width).toFixed(3)}fr`).join(" ");
      const cells = cellColumns.map((columnIndex, panelIndex) => {
        if (replacedPanelIndexes.has(panelIndex)) return "";
        return `<span class="gd-preview-door__mold-cell" style="grid-column:${columnIndex};" aria-hidden="true"></span>`;
      }).join("");
      const style = [
        `grid-template-columns:${template}`,
        `--gd-series-cell-h:${panelHeightPct.toFixed(3)}%`,
        `--gd-series-cell-shadow:url(${seriesProfile.reliefShadow})`,
        `--gd-series-cell-highlight:url(${seriesProfile.reliefHighlight})`
      ].join(";");
      return `<span class="gd-preview-door__mold-grid is-${seriesProfile.key}" style="${style}" aria-hidden="true">${cells}</span>`;
    }

    function buildCoachHouse400PanelGridMarkup(doorConfig, rowHeightIn, replacedPanelIndexes = new Set()) {
      const moduleCount = getCoachHouse400ModuleCount(doorConfig);
      const moduleWidthIn = 96;
      const edgeIn = 4;
      const panelIn = 19;
      const panelHeightIn = 13;
      const gapIn = 4;
      const safeRowHeightIn = Math.max(panelHeightIn, Number(rowHeightIn) || panelHeightIn);
      const panelHeightPct = clampNumber((panelHeightIn / safeRowHeightIn) * 100, 35, 100);
      const centerGapIn = Math.max(4, moduleWidthIn - edgeIn * 2 - panelIn * 4 - gapIn * 2);
      const columns = [];
      const cellColumns = [];

      for (let moduleIndex = 0; moduleIndex < moduleCount; moduleIndex += 1) {
        [
          edgeIn,
          panelIn,
          gapIn,
          panelIn,
          centerGapIn,
          panelIn,
          gapIn,
          panelIn,
          edgeIn
        ].forEach((columnWidthIn, columnIndex) => {
          const gridColumnIndex = columns.length + 1;
          columns.push(Math.max(1, Number(columnWidthIn) || 1));
          if (columnIndex === 1 || columnIndex === 3 || columnIndex === 5 || columnIndex === 7) {
            cellColumns.push(gridColumnIndex);
          }
        });
      }

      const template = columns.map((width) => `${width.toFixed(3)}fr`).join(" ");
      const cells = cellColumns
        .map((columnIndex, panelIndex) => {
          if (replacedPanelIndexes.has(panelIndex)) return "";
          return `<span class="gd-preview-door__coach-cell" style="grid-column:${columnIndex};" aria-hidden="true"></span>`;
        })
        .join("");

      return `<span class="gd-preview-door__coach-grid" style="grid-template-columns:${template};--gd-a400-cell-h:${panelHeightPct.toFixed(3)}%;" aria-hidden="true">${cells}</span>`;
    }

    function buildDoorPanelRowsMarkup(panelCount, panelHeightsIn, options = {}) {
      const heights = Array.isArray(panelHeightsIn)
        ? panelHeightsIn.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
        : [];
      const count = heights.length > 0 ? heights.length : Math.max(3, Math.min(8, Number(panelCount) || 4));
      const seriesProfile = options.seriesProfile || null;
      const replacementsByRow = getFixedSeriesWindowReplacementIndexesByRow(
        options.doorConfig,
        seriesProfile,
        options.selectedCells,
        options.windowRule
      );
      return Array.from({ length: count }, (_, index) => {
        const rowHeightIn =
          heights.length > 0
            ? Number(heights[index] || heights[heights.length - 1] || 0)
            : Number(options.doorConfig && options.doorConfig.heightIn ? options.doorConfig.heightIn : 0) / count;
        const replacedPanelIndexes = replacementsByRow.get(index) || new Set();
        const panelGridMarkup = seriesProfile && seriesProfile.usesLegacyCoachRenderer
          ? buildCoachHouse400PanelGridMarkup(options.doorConfig, rowHeightIn, replacedPanelIndexes)
          : buildFixedSeriesPanelGridMarkup(options.doorConfig, rowHeightIn, seriesProfile, replacedPanelIndexes);
        return `<span class="gd-preview-door__panel-row${index === 0 ? " is-first" : ""}" aria-hidden="true">${panelGridMarkup}</span>`;
      }).join("");
    }

    function buildDoorSectionLinesMarkup(sectionCount) {
      const count = Math.max(2, Math.min(8, Number(sectionCount) || 4));
      if (count <= 1) return "";
      return Array.from({ length: count - 1 }, (_, index) => {
        const left = ((index + 1) / count) * 100;
        return `<span class="gd-preview-door__section-line" style="left:${left.toFixed(2)}%;" aria-hidden="true"></span>`;
      }).join("");
    }

    function getWindowInsertPreviewToken(value) {
      const text = String(value || "").trim();
      if (!text) return "";
      if (isNoWindowInsertChoiceValue(text)) return "";
      const codeMatch = text.match(/\b([a-z]\d{1,2})\b/i);
      if (codeMatch) return String(codeMatch[1] || "").toUpperCase();
      const firstToken = text.split(/\s+/).find(Boolean);
      return String(firstToken || "").trim().toUpperCase();
    }

    function getWindowInsertVisualToken(value) {
      const token = normalizeCompareToken(value);
      if (!token || isNoWindowInsertChoiceValue(token)) return "none";
      const codeMatch = token.match(/\b(l0[1-9]|l10|l15|s0[1-7]|st0[12])\b/);
      if (codeMatch) return String(codeMatch[1] || "").toLowerCase();
      if (token.includes("stockton 10")) return "l01";
      if (token.includes("arched stockton") || token.includes("arched stocktone")) return "l02";
      if (token.includes("straight stockbridge")) return "l05";
      if (token.includes("arched stockbridge")) return "l06";
      if (token.includes("wagon wheel") || token.includes("wagonwheel")) {
        return token.includes("2 piece") ? "l07" : "s06";
      }
      if (token.includes("sunburst")) return "s07";
      if (token.includes("prairie")) return "s03";
      if (token.includes("waterton")) return token.includes("long") || token.includes("2 piece") ? "l04" : "s04";
      if (token.includes("cascade")) return token.includes("long") || token.includes("2 piece") ? "l03" : "s02";
      if (token.includes("stockton")) return "s01";
      return "none";
    }

    function getWindowInsertDisplayLabel(value) {
      const label = String(value || "").trim();
      const standardLabels = {
        l01: "L01 Stockton 10 Square",
        l02: "L02 2-Piece Arched Stockton",
        l03: "L03 Cascade",
        l04: "L04 Waterton",
        l05: "L05 Straight Stockbridge",
        l06: "L06 2-Piece Arched Stockbridge",
        l07: "L07 2 Piece Wagon Wheel",
        s01: "S01 Stockton",
        s02: "S02 Cascade",
        s03: "S03 Prairie",
        s04: "S04 Waterton",
        s06: "S06 Wagon Wheel",
        s07: "S07 4-Piece Sunburst"
      };
      return standardLabels[getWindowInsertVisualToken(label)] || label;
    }

    function getWindowInsertPreviewColor(value) {
      const token = normalizeCompareToken(value);
      if (token.includes("almond")) return "#e5dac2";
      if (token.includes("sandstone")) return "#c5beb1";
      if (token.includes("charcoal")) return "#606771";
      if (token.includes("iron ore")) return "#454a50";
      if (token.includes("basalt black") || token === "black") return "#22272e";
      if (token.includes("dark brown") || token.includes("dark walnut")) return "#49352b";
      if (token.includes("brown") || token.includes("mahogany")) return "#765241";
      if (token.includes("woodgrain") || token.includes("teak") || token.includes("plank")) return "#745b48";
      if (token.includes("white")) return "#f2f2ed";
      return "#20262d";
    }

    function renderWindowInsertPatternMarkup(value, mirrored = false) {
      const token = getWindowInsertVisualToken(value);
      if (token === "none") return "";

      const pathsByToken = {
        l01: '<path d="M16.67 0V64M33.33 0V64M50 0V64M66.67 0V64M83.33 0V64M0 32H100"/>',
        l02: '<path d="M16.67 0V64M33.33 0V64M50 0V64M66.67 0V64M83.33 0V64M0 34H100M0 20Q44 5 100 4"/>',
        l03: '<path d="M20 0V64M40 0V64M60 0V64M80 0V64M0 34H100M0 19Q45 2 100 5"/>',
        l04: '<path d="M0 32H8L25 8L42 32L25 56L8 32H58L75 8L92 32L75 56L58 32H100"/>',
        l05: '<path d="M25 0V64M50 0V64M75 0V64"/>',
        l06: '<path d="M25 0V64M50 0V64M75 0V64M0 20Q44 5 100 4"/>',
        l07: '<path d="M0 54Q40 8 100 7M0 54L100 64M24 31L100 64M58 13L100 64"/>',
        s01: '<path d="M50 0V64M0 32H100"/>',
        s02: '<path d="M0 22Q50 -3 100 22M50 0V64M0 32H100"/>',
        s03: '<path d="M24 0V64M76 0V64M0 21H100M0 43H100"/>',
        s04: '<path d="M0 32H24L50 7L76 32L50 57L24 32M76 32H100"/>',
        s06: '<path d="M0 54Q40 8 100 7M0 54L100 64M24 31L100 64M58 13L100 64"/>',
        s07: '<path d="M0 50Q44 8 100 8M24 31L100 64"/>'
      };
      const pathMarkup = pathsByToken[token] || "";
      if (!pathMarkup) return "";
      const transform = mirrored ? ' transform="translate(100 0) scale(-1 1)"' : "";

      return `<svg class="gd-window-insert-pattern gd-window-insert-pattern--${token}" viewBox="0 0 100 64" preserveAspectRatio="none" aria-hidden="true" focusable="false"><g${transform}>${pathMarkup}</g></svg>`;
    }

    function getWindowInsertPreviewPaneCount(value, windowWidthIn = 0) {
      const token = getWindowInsertVisualToken(value);
      if (/^l\d/.test(token)) return 2;
      if (/^(s\d|st\d)/.test(token)) return 4;
      return Number(windowWidthIn || 0) >= 30 ? 2 : 4;
    }

    function shouldMirrorWindowInsert(cells, currentCell) {
      if (!currentCell) return false;
      const currentRow = Number(currentCell.row || 0);
      const currentKey = String(currentCell.key || "").trim();
      const rowCells = (Array.isArray(cells) ? cells : [])
        .filter((cell) => cell && Number(cell.row || 0) === currentRow)
        .slice()
        .sort((a, b) => Number(a.col || 0) - Number(b.col || 0));
      const visualIndex = rowCells.findIndex((cell) => {
        if (cell === currentCell) return true;
        const cellKey = String(cell && cell.key ? cell.key : "").trim();
        return Boolean(currentKey && cellKey === currentKey);
      });
      return visualIndex >= 0 && visualIndex % 2 === 1;
    }

    function buildWindowPreviewLabel(config) {
      const value = config && typeof config === "object" ? config : {};
      const sizeLabel = formatCaptureWindowSizeLabel(getWindowDimensionsFromConfig(value));
      const glassLabel = String(value.glassLabel || "").trim();
      const colorLabel = String(value.colorLabel || "").trim();
      const insertLabel = getWindowInsertPreviewToken(value.insertLabel);
      return {
        sizeLabel,
        glassLabel,
        colorLabel,
        insertLabel,
        primary: [sizeLabel, glassLabel].filter(Boolean).join(" | "),
        secondary: [colorLabel, insertLabel].filter(Boolean).join(" | ")
      };
    }

    function drawCaptureWindowLabel(context, centerX, centerY, windowWidthPx, windowHeightPx, windowLabel) {
      if (!context || !windowLabel || typeof windowLabel !== "object") return;

      const sizeLabel = String(windowLabel.sizeLabel || "").trim();
      const glassLabel = String(windowLabel.glassLabel || "").trim();
      const colorLabel = String(windowLabel.colorLabel || "").trim();
      const insertLabel = String(windowLabel.insertLabel || "").trim();
      const secondaryLabel = [colorLabel, insertLabel].filter(Boolean).join(" | ");
      const candidates = [];
      const seen = new Set();
      const requiresFullDetail = Boolean(colorLabel && insertLabel);

      const pushCandidate = (lines) => {
        const normalized = (Array.isArray(lines) ? lines : [])
          .map((line) => String(line || "").trim())
          .filter(Boolean);
        if (normalized.length === 0) return;
        const signature = normalized.join("\n");
        if (seen.has(signature)) return;
        seen.add(signature);
        candidates.push(normalized);
      };

      if (insertLabel && colorLabel) {
        pushCandidate([windowLabel.primary, windowLabel.secondary]);
        pushCandidate([sizeLabel, glassLabel, secondaryLabel]);
        pushCandidate([sizeLabel, glassLabel, colorLabel, insertLabel]);
      } else {
        pushCandidate([windowLabel.primary, windowLabel.secondary]);
        pushCandidate([sizeLabel, glassLabel, secondaryLabel]);
        pushCandidate([sizeLabel, glassLabel, insertLabel || colorLabel]);
      }

      let bestLayout = null;
      const maxWidth = Math.max(12, windowWidthPx - 8);
      const maxHeight = Math.max(12, windowHeightPx - 6);
      const minFontSize = requiresFullDetail ? 5 : 7;

      candidates.forEach((lines) => {
        const lineGap = Math.max(
          1,
          Math.round(windowHeightPx * (lines.length >= 4 ? 0.012 : lines.length >= 3 ? 0.025 : 0.05))
        );
        const usableHeight = Math.max(8, maxHeight - lineGap * Math.max(0, lines.length - 1));
        const baseSize = usableHeight / Math.max(lines.length, 1);
        const sizes = lines.map((line, index) => {
          const emphasis =
            lines.length >= 4
              ? index === 0
                ? 0.84
                : 0.74
              : lines.length === 2
                ? index === 0
                  ? requiresFullDetail
                    ? 0.94
                    : 1.06
                  : requiresFullDetail
                    ? 0.8
                    : 0.92
                : index === 0
                  ? requiresFullDetail
                    ? 0.9
                    : 0.98
                  : requiresFullDetail
                    ? 0.78
                    : 0.88;
          const preferredSize = Math.max(minFontSize, Math.round(baseSize * emphasis));
          return fitCaptureTextSize(context, line, preferredSize, maxWidth, minFontSize);
        });
        const totalHeight = sizes.reduce((sum, size) => sum + size, 0) + lineGap * Math.max(0, lines.length - 1);
        const overflowPenalty = Math.max(0, totalHeight - maxHeight) * 24;
        const minResolvedSize = Math.min(...sizes);
        const avgResolvedSize = sizes.reduce((sum, size) => sum + size, 0) / Math.max(sizes.length, 1);
        const fullDetailBonus =
          requiresFullDetail && lines.some((line) => line.includes(colorLabel)) && lines.some((line) => line.includes(insertLabel))
            ? 2000
            : 0;
        const score = fullDetailBonus + minResolvedSize * 100 + avgResolvedSize * 4 - overflowPenalty - lines.length * 3;

        if (!bestLayout || score > bestLayout.score) {
          bestLayout = {
            lines,
            sizes,
            lineGap,
            totalHeight,
            score
          };
        }
      });

      if (!bestLayout || !Array.isArray(bestLayout.lines) || bestLayout.lines.length === 0) return;

      let currentY = centerY - bestLayout.totalHeight / 2;
      bestLayout.lines.forEach((line, index) => {
        const fontSize = Number(bestLayout.sizes[index] || 0);
        if (!(fontSize > 0)) return;

        context.font = `${index === 0 ? 650 : 560} ${fontSize}px Arial, sans-serif`;
        context.textAlign = "center";
        context.textBaseline = "top";
        context.lineJoin = "round";
        context.lineWidth = Math.max(1, Math.round(fontSize * 0.2));
        context.strokeStyle = "rgba(255, 255, 255, 0.96)";
        context.strokeText(line, centerX, currentY);
        context.fillStyle = "rgba(18, 26, 36, 0.94)";
        context.fillText(line, centerX, currentY);

        currentY += fontSize + (index < bestLayout.lines.length - 1 ? bestLayout.lineGap : 0);
      });
    }

    function buildDoorWindowLayerMarkup(hasWindowLayout, doorConfig, positionRule) {
      if (!hasWindowLayout) return "";
      const selectedLayout = getSelectedOptionFromStep(getWindowLayoutStep());
      if (selectedLayout && isNoWindowLayoutOption(selectedLayout)) return "";

      const fallbackRule = positionRule || getWindowPositionRule();
      const rowCount = Math.max(1, Number(getEffectiveWindowRowCount(doorConfig)));
      const cells = getOrderedWindowCells(doorConfig).filter((cell) => {
        if (!cell || cell.row < 1 || cell.row > rowCount || cell.col < 1) return false;
        const cellRule = getWindowPositionRuleForConfig(cell.config, fallbackRule);
        const cellColCount = Math.max(1, Number(cellRule && cellRule.cols ? cellRule.cols : 1));
        return cell.col <= cellColCount;
      });

      if (cells.length === 0) return "";

      const colCount = cells.reduce((max, cell) => {
        const cellRule = getWindowPositionRuleForConfig(cell && cell.config, fallbackRule);
        const cellCols = Math.max(1, Number(cellRule && cellRule.cols ? cellRule.cols : 1));
        return Math.max(max, cellCols);
      }, Math.max(1, Number(fallbackRule && fallbackRule.cols ? fallbackRule.cols : getWindowPositionColumnCount())));

      const windowsMarkup = cells
        .map((cell) => {
          const cellRule = getWindowPositionRuleForConfig(cell && cell.config, fallbackRule);
          const cellColCount = Math.max(1, Number(cellRule && cellRule.cols ? cellRule.cols : 1));
          const metrics = getWindowPreviewMetrics(doorConfig, cellRule, getWindowDimensionsFromConfig(cell && cell.config));
          const x = metrics.colCenters[cell.col - 1] || ((cell.col - 0.5) / cellColCount) * 100;
          const y = metrics.rowCenters[cell.row - 1] || ((cell.row - 0.5) / rowCount) * 100;
          const insertMarkup = renderWindowInsertPatternMarkup(
            cell && cell.config ? cell.config.insertLabel : "",
            shouldMirrorWindowInsert(cells, cell)
          );
          const insertColor = getWindowInsertPreviewColor(cell && cell.config ? cell.config.colorLabel : "");
          const insertLayerMarkup = insertMarkup
            ? `<span class="gd-preview-door__window-insert" style="--gd-window-insert-color:${insertColor};">${insertMarkup}</span>`
            : "";
          return `<span class="gd-preview-door__window${insertMarkup ? " has-window-insert" : ""}" style="--gd-window-x:${x.toFixed(3)}%;--gd-window-y:${y.toFixed(3)}%;--gd-window-w:${metrics.widthPct.toFixed(3)}%;--gd-window-h:${metrics.heightPct.toFixed(3)}%;" aria-hidden="true">${insertLayerMarkup}</span>`;
        })
        .join("");

      return `
        <div class="gd-preview-door__windows" style="--gd-window-cols:${colCount};--gd-window-rows:${rowCount};">
          ${windowsMarkup}
        </div>
      `;
    }

    function buildWindowConfigurationSurfaceMarkup(step, doorConfig) {
      const resolvedDoorConfig = doorConfig || getCurrentDoorVisualConfig();
      const rule = getWindowPositionRule();
      const sectionCount = Math.max(4, Number(resolvedDoorConfig && resolvedDoorConfig.sectionCount ? resolvedDoorConfig.sectionCount : 4));
      const surfaceStyle = [
        `--gd-door-aspect:${Math.max(2.15, sectionCount * 0.52).toFixed(3)}`,
        "--gd-window-zone-top:8%",
        "--gd-window-zone-right:5.5%",
        "--gd-window-zone-bottom:6%",
        "--gd-window-zone-left:5.5%"
      ].join(";");
      const activeWindowMarkup = buildDoorWindowLayerMarkup(true, resolvedDoorConfig, rule);
      const overlayMarkup = buildWindowPreviewOverlayMarkup(step, resolvedDoorConfig);

      return `
        <div class="gd-window-config-preview__surface" style="${escapeHtml(surfaceStyle)}">
          <div class="gd-window-config-preview__surface-door" aria-hidden="true"></div>
          ${activeWindowMarkup}
          ${overlayMarkup}
        </div>
      `;
    }

    function getDoorPreviewPresentation(door) {
      if (!door) return null;
      return withActiveDoorState(door, () => {
        const entryProfile = getEntryProfile();
        const windowProfile = getWindowSelectionProfile();
        const entryTitle = String(entryProfile.seriesLabel || entryProfile.productTitle || "Garage Door").trim() || "Garage Door";
        const sizeLabel = String(entryProfile.sizeLabel || "").trim();
        const colorLabel = String(entryProfile.colorLabel || "").trim() || String(windowProfile.colorLabel || "").trim();
        const doorConfig = getDoorVisualConfig(sizeLabel, {
          allowOrientationSwap: entryProfile.sizeMode !== "custom",
          panelPreference: entryProfile.panelPreference
        });
        const selectedCells = getOrderedWindowCells(doorConfig);
        const seriesProfile = getDoorSeriesRenderProfile(entryTitle);
        const useClassic300Texture = Boolean(seriesProfile && seriesProfile.key === "classic-300");
        const useCoachHouse400Texture = Boolean(seriesProfile && seriesProfile.key === "coach-house-400");
        const useRealisticSeriesTexture = Boolean(seriesProfile && !seriesProfile.usesLegacyCoachRenderer);
        const coachHouse400TextureTone = useCoachHouse400Texture ? getCoachHouse400TextureTone(colorLabel || "White") : "";
        const coachHouse400TextureColorClass = useCoachHouse400Texture ? getCoachHouse400TextureColorClass(colorLabel || "White") : "";
        const seriesTextureTone = useRealisticSeriesTexture ? getCoachHouse400TextureTone(colorLabel || "White") : "";
        const seriesTextureKey = (resolveKnownColorKey(colorLabel || "White") || "white").replace(/\s+/g, "-");
        const seriesSurfaceTexture =
          seriesProfile && seriesProfile.surfaceTexture
            ? seriesProfile.surfaceTexture
            : `gd-a400-ai-texture-${seriesTextureKey}.jpg`;
        const windowLayoutStep = getWindowLayoutStep();
        const windowPositionStep = getWindowPositionStep();
        const layoutOption = getSelectedOptionFromStep(windowLayoutStep);
        const hasWindowLayout = selectedCells.length > 0 || Boolean(layoutOption && !isNoWindowLayoutOption(layoutOption));
        const windowRule = hasWindowLayout ? getWindowPositionRule() : null;
        const doorPanelCount = Math.max(3, Number(doorConfig.panelCount || 4));
        const doorFill = getColorSwatchFill(colorLabel || "White");
        const coachHouse400CellFill = useCoachHouse400Texture ? getCoachHouse400CellFill(colorLabel || "White") : doorFill;
        const coachHouse400RowTileWidthPct = useCoachHouse400Texture ? getCoachHouse400RowTileWidthPct(doorConfig) : 100;
        const coachHouse400RowPatternWidthPct = useCoachHouse400Texture ? getCoachHouse400RowPatternWidthPct(doorConfig) : 100;
        const seriesPatternWidthPct = seriesProfile ? getFixedSeriesPatternWidthPct(doorConfig, seriesProfile) : 100;
        const doorMetrics = getPreviewDoorMetrics(doorConfig);
        const doorStyle = [
          `--gd-door-fill:${doorFill}`,
          `--gd-a400-cell-fill:${coachHouse400CellFill}`,
          `--gd-door-aspect:${doorConfig.aspect.toFixed(3)}`,
          `--gd-door-render-width:${doorMetrics.renderWidthPx}px`,
          `--gd-door-render-height:${doorMetrics.renderHeightPx}px`,
          `--gd-door-panels:${doorPanelCount}`,
          `--gd-series-pattern-w:${seriesPatternWidthPct.toFixed(3)}%`,
          `--gd-series-surface-texture:url(${seriesSurfaceTexture})`,
          `--gd-a400-row-pattern-w:${coachHouse400RowPatternWidthPct.toFixed(3)}%`,
          `--gd-a400-row-tile-w:${coachHouse400RowTileWidthPct.toFixed(3)}%`
        ].join(";");
        const panelRowsMarkup = buildDoorPanelRowsMarkup(doorPanelCount, doorConfig.panelHeightsIn, {
          doorConfig,
          seriesProfile,
          selectedCells,
          windowRule
        });
        const panelRowsTemplate = Array.isArray(doorConfig.panelHeightsIn)
          ? doorConfig.panelHeightsIn
              .map((height) => Number(height))
              .filter((height) => Number.isFinite(height) && height > 0)
              .map((height) => `${height.toFixed(3)}fr`)
              .join(" ")
          : "";
        const panelRowsInlineStyle = panelRowsTemplate ? `grid-template-rows:${panelRowsTemplate};` : "";
        const staticWindowMarkup = selectedCells.length > 0 ? buildDoorWindowLayerMarkup(true, doorConfig, windowRule) : "";
        const windowOverlayMarkup =
          state.designModalOpen &&
          windowPositionStep &&
          isWindowPositionStep(windowPositionStep) &&
          requiresWindowPositionSelection()
            ? buildWindowPreviewOverlayMarkup(windowPositionStep, doorConfig)
            : "";
        const trackSystemLabel = getSelectedTrackPackageLabel();
        const showTrackSystem = hasTrackSystemStep();
        const summaryText = [sizeLabel, colorLabel || "", windowProfile.layoutLabel || "", showTrackSystem ? trackSystemLabel : ""]
          .filter(Boolean)
          .join(" / ");
        const chips = [
          `<span class="gd-preview-door__chip">Qty: ${escapeHtml(String(entryProfile.quantity || ENTRY_DEFAULT_DOOR_QUANTITY))}</span>`,
          sizeLabel ? `<span class="gd-preview-door__chip">Size: ${escapeHtml(sizeLabel)}</span>` : "",
          `<span class="gd-preview-door__chip"><span class="gd-preview-door__chip-dot" style="background:${escapeHtml(doorFill)};"></span>Color: ${escapeHtml(colorLabel || "Default")}</span>`,
          hasWindowLayout && windowProfile.layoutLabel
            ? `<span class="gd-preview-door__chip">Window: ${escapeHtml(windowProfile.layoutLabel)}</span>`
            : "",
          showTrackSystem && trackSystemLabel
            ? `<span class="gd-preview-door__chip">Track: ${escapeHtml(trackSystemLabel)}</span>`
            : ""
        ]
          .filter(Boolean)
          .join("");

        return {
          doorId: String(door.id || ""),
          doorLabel: getDoorLabel(door),
          topSummary: summaryText,
          previewTitle: entryTitle,
          previewEyebrow: "Door Summary",
          previewDesc: summaryText,
          cardStyle: `--gd-preview-card-basis:${doorMetrics.cardBasisPx}px`,
          doorStyle,
          panelRowsMarkup,
          panelRowsInlineStyle,
          panelHeightsIn: Array.isArray(doorConfig.panelHeightsIn) ? doorConfig.panelHeightsIn.slice() : [],
          overallSizeLabel: sizeLabel,
          widthIn: Number(doorConfig.widthIn || 0),
          heightIn: Number(doorConfig.heightIn || 0),
          staticWindowMarkup,
          windowOverlayMarkup,
          chips,
          preflightCompleted: Boolean(state.preflight.completed),
          useClassic300Texture,
          useCoachHouse400Texture,
          useRealisticSeriesTexture,
          seriesSurfaceClass: seriesProfile ? seriesProfile.surfaceClass : "",
          seriesTextureTone,
          coachHouse400TextureTone,
          coachHouse400TextureColorClass
        };
      });
    }

    function buildDoorPreviewMetaContentMarkup(presentation) {
      if (!presentation) return "";
      return `
        <p class="gd-preview-door__eyebrow">${escapeHtml(presentation.previewEyebrow)}</p>
        <h3 class="gd-preview-door__title">${escapeHtml(presentation.previewTitle)}</h3>
        ${presentation.previewDesc ? `<p class="gd-preview-door__desc">${escapeHtml(presentation.previewDesc)}</p>` : ""}
        ${presentation.chips ? `<div class="gd-preview-door__chips">${presentation.chips}</div>` : ""}
      `;
    }

    function buildDoorPreviewMetaMarkup(presentation) {
      if (!presentation) return "";
      return `
        <div class="gd-preview-door__meta">
          ${buildDoorPreviewMetaContentMarkup(presentation)}
        </div>
      `;
    }

    function buildDesignPreviewMetaMarkup(presentation) {
      if (!presentation) return "";
      return `
        <div class="gd-configurator__preview-summary-inner">
          <div class="gd-configurator__preview-summary-card">
            ${buildDoorPreviewMetaContentMarkup(presentation)}
          </div>
        </div>
      `;
    }

    function formatDoorPreviewInchesLabel(valueIn) {
      const numeric = Number(valueIn || 0);
      if (!Number.isFinite(numeric) || numeric <= 0) return "";
      const rounded = Math.round(numeric * 1000) / 1000;
      const label = Number.isInteger(rounded) ? String(Math.round(rounded)) : String(rounded).replace(/\.0+$/, "");
      return `${label}"`;
    }

    function formatDoorPreviewFeetInchesLabel(valueIn) {
      const totalInches = Math.max(0, Math.round(Number(valueIn || 0)));
      if (totalInches <= 0) return "";

      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      if (feet <= 0) return `${inches}"`;
      if (inches <= 0) return `${feet}'`;
      return `${feet}'${inches}"`;
    }

    function buildDoorPreviewMeasurementMarkup(presentation) {
      if (!presentation) return "";

      const panelHeights = Array.isArray(presentation.panelHeightsIn)
        ? presentation.panelHeightsIn.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
        : [];
      const sideStyle = presentation.panelRowsInlineStyle ? ` style="${escapeHtml(presentation.panelRowsInlineStyle)}"` : "";
      const sideLabels = panelHeights
        .map((height, index) => {
          const label = formatDoorPreviewInchesLabel(height);
          if (!label) return "";
          return `<span class="gd-preview-door__measure-side-row${index === 0 ? " is-first" : ""}">${escapeHtml(label)}</span>`;
        })
        .join("");
      const overallLabel = formatDoorPreviewFeetInchesLabel(presentation.widthIn);

      if (!sideLabels && !overallLabel) return "";

      return `
        <div class="gd-preview-door__measure-shell">
          ${sideLabels ? `<div class="gd-preview-door__measure-side"${sideStyle}>${sideLabels}</div>` : ""}
          ${overallLabel ? `<div class="gd-preview-door__measure-bottom">Width: ${escapeHtml(overallLabel)}</div>` : ""}
        </div>
      `;
    }

    function buildDoorPreviewSectionMarkup(presentation, extraClass = "", options = {}) {
      if (!presentation) return "";
      const metaMarkup = String(options.metaMarkup || "");
      const showMeta = Boolean(metaMarkup);
      const showMeasurements = Boolean(options.showMeasurements);
      const panelRowsStyle = presentation.panelRowsInlineStyle ? ` style="${escapeHtml(presentation.panelRowsInlineStyle)}"` : "";
      const measurementMarkup = showMeasurements ? buildDoorPreviewMeasurementMarkup(presentation) : "";
      const surfaceWrapStyle = ` style="${escapeHtml(presentation.doorStyle)}"`; 
      const coachHouse400ToneClass =
        presentation.useCoachHouse400Texture && presentation.coachHouse400TextureTone
          ? ` is-a400-tone-${escapeHtml(presentation.coachHouse400TextureTone)}`
          : "";
      const coachHouse400ColorClass =
        presentation.useCoachHouse400Texture && presentation.coachHouse400TextureColorClass
          ? ` ${escapeHtml(presentation.coachHouse400TextureColorClass)}`
          : "";
      const seriesSurfaceClass = presentation.seriesSurfaceClass
        ? ` ${escapeHtml(presentation.seriesSurfaceClass)}`
        : "";
      const realisticSeriesClass = presentation.useRealisticSeriesTexture ? " is-realistic-series" : "";
      const seriesToneClass = presentation.useRealisticSeriesTexture && presentation.seriesTextureTone
        ? ` is-series-tone-${escapeHtml(presentation.seriesTextureTone)}`
        : "";
      return `
        <section class="gd-preview-door${presentation.preflightCompleted ? "" : " is-entry-pending"}${showMeta ? "" : " is-meta-hidden"}${extraClass ? ` ${extraClass}` : ""}" data-gd-door-id="${escapeHtml(presentation.doorId)}">
          <div class="gd-preview-door__canvas">
            <div class="gd-preview-door__surface-wrap${showMeasurements ? " has-measurements" : ""}"${surfaceWrapStyle}>
              <div class="gd-preview-door__surface${seriesSurfaceClass}${realisticSeriesClass}${seriesToneClass}${coachHouse400ToneClass}${coachHouse400ColorClass}" style="${escapeHtml(presentation.doorStyle)}">
                <div class="gd-preview-door__panel-rows"${panelRowsStyle}>
                  ${presentation.panelRowsMarkup}
                </div>
                ${presentation.staticWindowMarkup}
                ${presentation.windowOverlayMarkup || ""}
              </div>
              ${measurementMarkup}
            </div>
          </div>
          ${metaMarkup}
        </section>
      `;
    }

    function buildOverviewAddDoorCardMarkup(options = {}) {
      const compact = Boolean(options && options.compact);
      const title = compact ? "Add Door" : "Add New Door";
      const description = "";

      return `
        <button
          type="button"
          class="gd-workspace-card${compact ? " is-compact" : ""}"
          data-gd-open-design-modal
        >
          <span class="gd-workspace-card__icon" aria-hidden="true">+</span>
          <span class="gd-workspace-card__title">${escapeHtml(title)}</span>
          ${description ? `<span class="gd-workspace-card__desc">${escapeHtml(description)}</span>` : ""}
        </button>
      `;
    }

    function buildOverviewPreflightCardMarkup() {
      const collectionUrl = resolveDoorCollectionUrl();
      return `
        <a class="gd-workspace-card gd-workspace-card--entry" href="${escapeHtml(collectionUrl)}">
          <span class="gd-workspace-card__icon" aria-hidden="true">&#8594;</span>
          <span class="gd-workspace-card__title">Start from Product Page</span>
          <span class="gd-workspace-card__desc">Open a garage door product first, then launch the configurator from there.</span>
        </a>
      `;
    }

    function buildPreviewDoorCardMarkup(door, index, active) {
      const presentation = getDoorPreviewPresentation(door);
      if (!presentation) return "";
      const deleteDisabled = getCommittedDoors().length <= 1;
      const metaMarkup = buildDoorPreviewMetaMarkup(presentation);

      return `
        <article
          class="gd-preview-card${active ? " is-active" : ""}"
          data-gd-door-card="${escapeHtml(presentation.doorId)}"
          aria-label="${escapeHtml(presentation.doorLabel)}"
          style="${escapeHtml(presentation.cardStyle)}"
        >
          <button
            type="button"
            class="gd-preview-card__select"
            data-gd-door-select="${escapeHtml(presentation.doorId)}"
            aria-label="Edit ${escapeHtml(presentation.doorLabel)}"
            aria-pressed="${active ? "true" : "false"}"
          >
            <span class="sr-only">${escapeHtml(presentation.doorLabel)}</span>
          </button>
          <button
            type="button"
            class="gd-preview-card__duplicate"
            data-gd-door-duplicate="${escapeHtml(presentation.doorId)}"
            aria-label="Duplicate ${escapeHtml(presentation.doorLabel)}"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true" focusable="false">
              <rect x="8" y="8" width="12" height="12" rx="2"></rect>
              <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3"></path>
            </svg>
            <span>Duplicate</span>
          </button>
          <button
            type="button"
            class="gd-preview-card__delete"
            data-gd-door-delete="${escapeHtml(presentation.doorId)}"
            aria-label="Delete ${escapeHtml(presentation.doorLabel)}"
            ${deleteDisabled ? "disabled" : ""}
          >
            ×
          </button>
          ${buildDoorPreviewSectionMarkup(presentation, "gd-preview-door--card")}
          ${metaMarkup}
        </article>
      `;
    }

    function renderOverviewPreview() {
      const previewDoors = getPreviewDoors();
      const focusedDoor = getPreviewFocusedDoor();
      setPreviewSummaryMarkup("");

      if (!state.preflight.completed) {
        previewEl.innerHTML = `
          <section class="gd-preview-stack gd-preview-stack--workspace" aria-label="Door workspace">
            <div class="gd-preview-stack__empty gd-preview-stack__empty--workspace">
              <div class="gd-preview-stack__empty-card-wrap">
                ${buildOverviewPreflightCardMarkup()}
              </div>
              <div class="gd-preview-stack__empty-copy-block">
                <h2 class="gd-preview-stack__empty-title">Your workspace is waiting.</h2>
                <p class="gd-preview-stack__empty-copy">
                  Begin from a garage door product page. Configuration options will appear once the product context is loaded.
                </p>
              </div>
            </div>
          </section>
        `;
        return;
      }

      if (previewDoors.length === 0) {
        previewEl.innerHTML = `
          <section class="gd-preview-stack gd-preview-stack--workspace" aria-label="Door workspace">
            <div class="gd-preview-stack__empty gd-preview-stack__empty--workspace gd-preview-stack__empty--workspace-centered">
              <div class="gd-preview-stack__empty-card-wrap">
                ${buildOverviewAddDoorCardMarkup()}
              </div>
            </div>
          </section>
        `;
        return;
      }

      const selectedDoorId = String(state.previewDoorId || (focusedDoor && focusedDoor.id) || "");
      const doorCardsMarkup = previewDoors
        .map((door, index) => buildPreviewDoorCardMarkup(door, index, String(door && door.id ? door.id : "") === selectedDoorId))
        .join("");

      previewEl.innerHTML = `
        <section class="gd-preview-stack gd-preview-stack--workspace gd-preview-stack--content-only" aria-label="Door workspace">
          <div class="gd-preview-stack__viewport gd-preview-stack__viewport--workspace" data-gd-door-scroll>
            ${doorCardsMarkup}
            ${buildOverviewAddDoorCardMarkup({ compact: true })}
          </div>
        </section>
      `;
      if (clearWindowHoverCell()) updateWindowHoverGhost();
    }

    function renderDesignPreview() {
      const activeStep = findStepById(state.activeStepId);
      const windowPositionStep = getWindowPositionStep();
      const selectedWindowProduct = getSelectedWindowLayoutProduct();
      const windowSectionActive =
        state.designNavSectionId === "windows" ||
        (activeStep &&
          (isWindowLayoutStep(activeStep) ||
            isWindowGlassStep(activeStep) ||
            isWindowColorStep(activeStep) ||
            isWindowInsertStep(activeStep) ||
            isWindowPositionStep(activeStep)));

      if (windowSectionActive && windowPositionStep) {
        renderWindowPositionPreview(windowPositionStep);
        return;
      }

      const designDoor = getDesignEditingDoor() || getDraftDoorState();
      const presentation = getDoorPreviewPresentation(designDoor);
      if (!presentation) {
        previewEl.innerHTML = "<p class='text-sm text-opacity'>Door design is unavailable.</p>";
        setPreviewSummaryMarkup("");
        return;
      }

      previewEl.innerHTML = `
        <section class="gd-design-preview" aria-label="Door design preview">
          <div class="gd-design-preview__body">
            ${buildDoorPreviewSectionMarkup(presentation, "gd-preview-door--design", { showMeasurements: true })}
          </div>
        </section>
      `;
      setPreviewSummaryMarkup("");
      if (clearWindowHoverCell()) updateWindowHoverGhost();
    }

    function renderPreview() {
      if (!previewEl) return;
      if (state.designModalOpen) {
        renderDesignPreview();
        return;
      }
      renderOverviewPreview();
    }

    function buildStepIntro(step) {
      const selectionRule = step.multi_select ? "Select one or more options." : "Select one option.";
      const requirement = step.required ? "This section is required." : "This section is optional.";
      return `${selectionRule} ${requirement}`;
    }

    const COLOR_SWATCH_FILL_BY_KEY = Object.freeze({
      white: "linear-gradient(145deg, #f8f9fb 0%, #eceef1 55%, #d8dbe0 100%)",
      almond: "linear-gradient(145deg, #f4efe4 0%, #e8dfcf 56%, #d6cbb8 100%)",
      sandstone: "linear-gradient(145deg, #d8d5ce 0%, #c6c2b9 60%, #b1ada3 100%)",
      charcoal: "linear-gradient(145deg, #7f848e 0%, #666b74 58%, #4b5058 100%)",
      "iron ore": "linear-gradient(145deg, #65696f 0%, #4d5157 56%, #34393f 100%)",
      "basalt black": "linear-gradient(145deg, #4f5359 0%, #383d43 56%, #25292e 100%)",
      "dark brown": "linear-gradient(145deg, #524a42 0%, #3d362f 58%, #2c2621 100%)",
      "basalt brown": "linear-gradient(145deg, #6c6057 0%, #544941 58%, #3e352f 100%)",
      brown: "linear-gradient(145deg, #6f554d 0%, #614942 58%, #4f3933 100%)",
      woodgrain:
        "linear-gradient(155deg, #8f715f 0%, #6f5548 60%, #5a4338 100%), repeating-linear-gradient(170deg, rgba(255,255,255,0.08) 0 3px, rgba(0,0,0,0.06) 3px 6px)",
      teak:
        "linear-gradient(155deg, #9f7f40 0%, #816233 60%, #634825 100%), repeating-linear-gradient(170deg, rgba(255,255,255,0.1) 0 3px, rgba(0,0,0,0.08) 3px 6px)",
      mahogany:
        "linear-gradient(155deg, #5f4738 0%, #4c372c 60%, #34241d 100%), repeating-linear-gradient(170deg, rgba(255,255,255,0.08) 0 3px, rgba(0,0,0,0.08) 3px 6px)",
      "dark walnut":
        "linear-gradient(155deg, #5a4738 0%, #453426 60%, #302218 100%), repeating-linear-gradient(170deg, rgba(255,255,255,0.08) 0 3px, rgba(0,0,0,0.08) 3px 6px)",
      plank:
        "linear-gradient(155deg, #b59069 0%, #9a744e 60%, #785937 100%), repeating-linear-gradient(170deg, rgba(255,255,255,0.1) 0 3px, rgba(0,0,0,0.07) 3px 6px)",
      black: "linear-gradient(145deg, #2d333b 0%, #1f2329 58%, #12151a 100%)"
    });
    const COLOR_OPTION_ORDER = Object.freeze([
      "white",
      "almond",
      "sandstone",
      "charcoal",
      "iron ore",
      "basalt black",
      "dark brown",
      "basalt brown",
      "brown",
      "woodgrain",
      "teak",
      "mahogany",
      "dark walnut",
      "plank",
      "black"
    ]);
    const ENTRY_INDEPENDENT_COLOR_LABELS_BY_KEY = Object.freeze({
      white: "White",
      almond: "Almond",
      sandstone: "Sandstone",
      charcoal: "Charcoal",
      "iron ore": "Iron Ore",
      "basalt black": "Basalt Black",
      "dark brown": "Dark Brown",
      "basalt brown": "Basalt Dark Brown",
      brown: "Brown",
      woodgrain: "Woodgrain",
      teak: "Teak",
      mahogany: "Mahogany",
      "dark walnut": "Dark Walnut",
      plank: "Plank",
      black: "Black"
    });
    const WINDOW_ZONE_TOP_PCT = 2.8;
    const WINDOW_ZONE_BOTTOM_PCT = 2.8;
    const WINDOW_ROW_MODEL_4 = Object.freeze([
      Object.freeze({ key: "top", label: "Row 1" }),
      Object.freeze({ key: "upper", label: "Row 2" }),
      Object.freeze({ key: "middle", label: "Row 3" }),
      Object.freeze({ key: "bottom", label: "Row 4" })
    ]);
    const WINDOW_ROW_MODEL_5 = Object.freeze([
      Object.freeze({ key: "top", label: "Row 1" }),
      Object.freeze({ key: "upper", label: "Row 2" }),
      Object.freeze({ key: "middle", label: "Row 3" }),
      Object.freeze({ key: "lower", label: "Row 4" }),
      Object.freeze({ key: "bottom", label: "Row 5" })
    ]);
    const WINDOW_POSITION_ROW_DEFAULTS = Object.freeze({
      top: "Row 1",
      upper: "Row 2",
      middle: "Row 3",
      lower: "Row 4",
      bottom: "Row 5"
    });
    const WINDOW_NARROW_DOOR_MAX_WIDTH_FEET = 10;
    const WINDOW_POSITION_RULE_1240 = Object.freeze({
      narrow: Object.freeze({
        cols: 3,
        columnLabels: Object.freeze(["1", "2", "3"]),
        centerColumn: 2,
        sideColumns: Object.freeze([1, 3]),
        centerBlocksSides: true,
        blockCenterWithBothSides: false
      }),
      wide: Object.freeze({
        cols: 7,
        columnLabels: Object.freeze(["1", "2", "3", "4", "5", "6", "7"]),
        centerColumn: 4
      })
    });
    const WINDOW_POSITION_RULES_BY_SIZE_CODE = Object.freeze({
      "0741": WINDOW_POSITION_RULE_1240,
      "0761": WINDOW_POSITION_RULE_1240,
      "0781": WINDOW_POSITION_RULE_1240,
      "1217": Object.freeze({
        narrow: Object.freeze({ cols: 4 }),
        wide: Object.freeze({ cols: 8 })
      }),
      "1240": WINDOW_POSITION_RULE_1240,
      "1440": WINDOW_POSITION_RULE_1240
    });

    function normalizeColorKey(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    }

    function resolveKnownColorKey(value) {
      const key = normalizeColorKey(value);
      if (!key) return "";
      if (COLOR_SWATCH_FILL_BY_KEY[key]) return key;
      if (key.includes("white")) return "white";
      if (key.includes("almond")) return "almond";
      if (key.includes("sand")) return "sandstone";
      if (key.includes("iron ore")) return "iron ore";
      if (key.includes("basalt black")) return "basalt black";
      if (key.includes("basalt dark brown") || key.includes("basalt brown")) return "basalt brown";
      if (key.includes("charcoal")) return "charcoal";
      if (key.includes("dark brown")) return "dark brown";
      if (key.includes("wood")) return "woodgrain";
      if (key.includes("teak")) return "teak";
      if (key.includes("mahogany")) return "mahogany";
      if (key.includes("dark walnut")) return "dark walnut";
      if (key.includes("plank")) return "plank";
      if (key.includes("black")) return "black";
      if (key.includes("brown")) return "brown";
      return "";
    }

    function resolveWindowPositionKey(value) {
      const key = normalizeColorKey(value);
      if (!key) return "";
      if (key.includes("top") || key.includes("first")) return "top";
      if (key.includes("upper") || key.includes("second")) return "upper";
      if (key.includes("middle") || key.includes("center") || key.includes("third")) return "middle";
      if (key.includes("lower") || key.includes("fourth")) return "lower";
      if (key.includes("bottom") || key.includes("fifth")) return "bottom";
      if (/\b1\b/.test(key)) return "top";
      if (/\b2\b/.test(key)) return "upper";
      if (/\b3\b/.test(key)) return "middle";
      if (/\b4\b/.test(key)) return "lower";
      if (/\b5\b/.test(key)) return "bottom";
      return "";
    }

    function stepTitleHasTokens(step, tokens) {
      if (!step || !Array.isArray(tokens) || tokens.length === 0) return false;
      const title = normalizeCompareToken(step && step.title ? step.title : "");
      if (!title) return false;
      return tokens.every((token) => {
        const normalizedToken = normalizeCompareToken(token);
        return normalizedToken ? title.includes(normalizedToken) : false;
      });
    }

    function isColorStep(step) {
      if (!step) return false;
      const handle = String(step.handle || "").toLowerCase();
      const title = String(step.title || "").toLowerCase();
      const expectedHandle = String(entry.colorStepHandle || "").toLowerCase();
      return handle === expectedHandle || /color|finish/.test(title);
    }

    function isWindowLayoutStep(step) {
      if (!step) return false;
      const handle = String(step.handle || "").toLowerCase();
      const expectedHandle = String(entry.windowLayoutStepHandle || WINDOW_LAYOUT_STEP_HANDLE).toLowerCase();
      return handle === expectedHandle || handle === WINDOW_LAYOUT_STEP_HANDLE || stepTitleHasTokens(step, ["window", "layout"]);
    }

    function isWindowPositionStep(step) {
      if (!step) return false;
      const handle = String(step.handle || "").toLowerCase();
      const title = String(step.title || "").toLowerCase();
      return handle === WINDOW_POSITION_STEP_HANDLE || /window.+position|position.+window/.test(title);
    }

    function isWindowGlassStep(step) {
      if (!step) return false;
      const handle = String(step.handle || "").toLowerCase();
      const expectedHandle = String(entry.windowGlassStepHandle || WINDOW_GLASS_STEP_HANDLE).toLowerCase();
      return (
        handle === expectedHandle ||
        handle === WINDOW_GLASS_STEP_HANDLE ||
        stepTitleHasTokens(step, ["glass", "type"]) ||
        stepTitleHasTokens(step, ["window", "glass"])
      );
    }

    function isWindowColorStep(step) {
      if (!step) return false;
      const handle = String(step.handle || "").toLowerCase();
      const expectedHandle = String(entry.windowColorStepHandle || WINDOW_COLOR_STEP_HANDLE).toLowerCase();
      return handle === expectedHandle || handle === WINDOW_COLOR_STEP_HANDLE || stepTitleHasTokens(step, ["window", "color"]);
    }

    function getStepDisplayTitle(step, fallback = "Step") {
      if (!step) return fallback;
      if (isWindowColorStep(step)) return "Frame Color";
      const title = String(step.title || "").trim();
      return title || fallback;
    }

    function isWindowInsertStep(step) {
      if (!step) return false;
      const handle = String(step.handle || "").toLowerCase();
      const expectedHandle = String(entry.windowInsertStepHandle || WINDOW_INSERT_STEP_HANDLE).toLowerCase();
      return handle === expectedHandle || handle === WINDOW_INSERT_STEP_HANDLE || stepTitleHasTokens(step, ["window", "insert"]);
    }

    function isTrackSystemStep(step) {
      if (!step) return false;
      const handle = String(step.handle || "").toLowerCase();
      const expectedHandle = String(entry.trackStepHandle || defaultEntryConfig.trackStepHandle || "").toLowerCase();
      return (
        handle === expectedHandle ||
        handle === String(defaultEntryConfig.trackStepHandle || "").toLowerCase() ||
        stepTitleHasTokens(step, ["track", "system"])
      );
    }

    function isSpringCycleStep(step) {
      if (!step) return false;
      const handle = String(step.handle || "").toLowerCase();
      if (handle === SPRING_CYCLE_STEP_HANDLE) return true;
      return stepTitleHasTokens(step, ["spring", "cycle"]);
    }

    function getColorSwatchFill(label) {
      const key = resolveKnownColorKey(label);
      if (key && COLOR_SWATCH_FILL_BY_KEY[key]) return COLOR_SWATCH_FILL_BY_KEY[key];
      return "linear-gradient(145deg, #9ba1aa 0%, #7f8690 100%)";
    }

    function getSwatchImageUrl(step, option) {
      if (isColorStep(step)) return "";
      const raw = String(option && option.image ? option.image : "").trim();
      if (!raw) return "";
      if (/dummyimage\\.com/i.test(raw)) return "";
      return raw;
    }

    function getColorDisplayOptions(step) {
      const options = Array.isArray(step && step.options) ? step.options : [];
      if (options.length <= 1) return options;

      const optionByKey = new Map();
      for (const option of options) {
        const key = resolveKnownColorKey(option && option.label ? option.label : option && option.handle ? option.handle : "");
        if (!key || optionByKey.has(key)) continue;
        optionByKey.set(key, option);
      }

      const ordered = COLOR_OPTION_ORDER.map((key) => optionByKey.get(key)).filter(Boolean);
      return ordered.length > 0 ? ordered : options;
    }

    function getWindowPositionDisplayOptions(step, doorConfig) {
      const options = Array.isArray(step && step.options) ? step.options : [];
      if (options.length <= 1) return options;

      const rowModel = getWindowRowModel(doorConfig || getCurrentDoorVisualConfig());
      const optionByKey = new Map();
      for (const option of options) {
        const key = resolveWindowPositionKey(option && option.label ? option.label : option && option.handle ? option.handle : "");
        if (!key || optionByKey.has(key)) continue;
        optionByKey.set(key, option);
      }

      const ordered = rowModel.map((row) => optionByKey.get(row.key)).filter(Boolean);
      return ordered.length > 0 ? ordered : options;
    }

    function buildOptionDescription(step, option) {
      if (isTrackSystemStep(step)) return "";
      const componentsCount = (option.components || []).length;
      if (componentsCount === 0) return "No additional components included.";
      return `${componentsCount} component${componentsCount === 1 ? "" : "s"} included in this package.`;
    }

    function isWindowOptionAvailable(option) {
      if (option && typeof option.available === "boolean") {
        return option.available;
      }
      const components = Array.isArray(option && option.components) ? option.components : [];
      if (components.length === 0) return true;
      return components.every((component) => component && component.available !== false);
    }

    function renderColorSwatchOption(step, option, selectedIds) {
      const active = selectedIds.has(option.id);
      const previewed = String(state.activeStepId) === String(step.id) && String(state.activeOptionId) === String(option.id);
      const total = optionTotal(option);
      const status = total > 0 ? `+${formatMoney(total)}` : "Included";
      const available = isWindowColorStep(step) ? isWindowOptionAvailable(option) : true;
      const imageUrl = getSwatchImageUrl(step, option);
      const fallbackFill = getColorSwatchFill(option.label || "");
      const mediaMarkup = imageUrl
        ? `<img src="${escapeHtml(imageUrl)}" alt="" loading="lazy">`
        : `<span class="gd-color-swatch__fill" style="background:${fallbackFill};"></span>`;

      return `
        <button
          type="button"
          class="gd-color-swatch${active ? " is-active" : ""}${previewed ? " is-previewed" : ""}"
          data-gd-option="${escapeHtml(option.id)}"
          data-gd-step-id="${escapeHtml(step.id)}"
          aria-label="${escapeHtml(`${option.label || "Color"} ${status}`)}"
          aria-pressed="${active ? "true" : "false"}"
          title="${escapeHtml(option.label || "Color")}"
          ${available ? "" : "disabled"}
        >
          <span class="gd-color-swatch__ring">
            <span class="gd-color-swatch__dot">${mediaMarkup}</span>
          </span>
          <span class="gd-color-swatch__label">${escapeHtml(option.label || "Color")}</span>
        </button>
      `;
    }

    function renderColorPicker(step, selectedIds) {
      const selectedOption = getSelectedOptionFromStep(step);
      const selectedTotal = selectedOption ? optionTotal(selectedOption) : 0;
      const selectedStatus = selectedOption ? (selectedTotal > 0 ? `+${formatMoney(selectedTotal)}` : "Included") : "Select";
      const fallbackName = isWindowColorStep(step) ? "Frame Color" : "Color";
      const selectedName = selectedOption ? String(selectedOption.label || fallbackName) : fallbackName;
      const swatchesMarkup = getColorDisplayOptions(step)
        .map((option) => renderColorSwatchOption(step, option, selectedIds))
        .join("");

      return `
        <div class="gd-color-picker">
          <div class="gd-color-picker__swatches" data-gd-scroll-key="step:${escapeHtml(step.id)}:color">
            ${swatchesMarkup}
          </div>
          <p class="gd-color-picker__status">${escapeHtml(selectedStatus)}</p>
          <p class="gd-color-picker__name">${escapeHtml(selectedName)}</p>
        </div>
      `;
    }

    function getWindowOptionDimensionsInches(option) {
      const candidates = [
        option && option.label,
        option && option.handle,
        option && option.window_value
      ];

      for (const candidate of candidates) {
        const byCode = parseWindowDimensionsFromSizeCode(normalizeWindowSizeCode(candidate));
        if (byCode) return byCode;
        const byText = parseWindowSizePairFromText(candidate);
        if (byText) {
          return {
            widthIn: Number(byText.second || 0),
            heightIn: Number(byText.first || 0)
          };
        }
      }

      return null;
    }

    function renderWindowLayoutVisualMarkup(option) {
      if (option && isNoWindowLayoutOption(option)) {
        return `
          <span class="gd-window-choice__visual-media gd-window-choice__visual-media--layout">
            <span class="gd-window-choice__layout-solid" aria-hidden="true"></span>
          </span>
        `;
      }

      const dims = getWindowOptionDimensionsInches(option);
      const widthIn = Math.max(1, Number(dims && dims.widthIn ? dims.widthIn : 40));
      const heightIn = Math.max(1, Number(dims && dims.heightIn ? dims.heightIn : 14));
      const ratio = widthIn / heightIn;
      let widthPct = 56;
      let heightPct = 32;

      if (ratio >= 4.25) {
        widthPct = 78;
        heightPct = 18;
      } else if (ratio >= 2.35) {
        widthPct = 70;
        heightPct = 24;
      } else if (ratio <= 0.72) {
        widthPct = 24;
        heightPct = 70;
      } else if (ratio <= 1.1) {
        widthPct = 38;
        heightPct = 44;
      }

      return `
        <span class="gd-window-choice__visual-media gd-window-choice__visual-media--layout">
          <span class="gd-window-choice__layout-frame" style="width:${widthPct}%;height:${heightPct}%;" aria-hidden="true"></span>
        </span>
      `;
    }

    function getWindowGlassVisualToken(option) {
      const text = normalizeCompareToken(option && (option.label || option.window_value || option.handle || ""));
      if (text.includes("frost")) return "frosted";
      if (text.includes("grey") || text.includes("gray") || text.includes("smoke")) return "grey";
      if (text.includes("bronze")) return "bronze";
      if (text.includes("black")) return "black";
      return "clear";
    }

    function renderWindowGlassVisualMarkup(step, option) {
      const token = getWindowGlassVisualToken(option);
      const optionLabel = String(option && (option.label || option.window_value || "Glass Type")).trim() || "Glass Type";
      return `
        <span class="gd-window-choice__visual-media gd-window-choice__visual-media--glass">
          <span class="gd-window-choice__file-card gd-window-choice__file-card--glass gd-window-choice__file-card--${escapeHtml(token)}" aria-hidden="true">
            <span class="gd-window-choice__file-kicker">Glass Type</span>
            <span class="gd-window-choice__file-name">${escapeHtml(optionLabel)}</span>
          </span>
        </span>
      `;
    }

    function renderWindowInsertVisualMarkup(step, option) {
      const optionLabel = String(option && (option.label || option.window_value || "Insert")).trim() || "Insert";
      const insertToken = getWindowInsertVisualToken(optionLabel);
      const selectedWindowDimensions = getSelectedWindowDimensionsInches();
      const paneCount = getWindowInsertPreviewPaneCount(
        optionLabel,
        Number(selectedWindowDimensions && selectedWindowDimensions.widthIn ? selectedWindowDimensions.widthIn : 0)
      );
      const panesMarkup = Array.from({ length: paneCount }, (_, index) => {
        const patternMarkup = renderWindowInsertPatternMarkup(optionLabel, index % 2 === 1);
        return `<span class="gd-window-choice__insert-pane${insertToken === "none" ? " is-plain" : ""}">${patternMarkup}</span>`;
      }).join("");

      return `
        <span class="gd-window-choice__visual-media gd-window-choice__visual-media--insert">
          <span class="gd-window-choice__insert-preview gd-window-choice__insert-preview--${escapeHtml(insertToken)}${paneCount === 2 ? " is-long" : " is-short"}" style="--gd-insert-pane-count:${paneCount};" aria-hidden="true">
            ${panesMarkup}
          </span>
        </span>
      `;
    }

    function renderWindowVisualOption(step, option, selectedIds, kind) {
      const active = selectedIds.has(option.id);
      const previewed = String(state.activeStepId) === String(step.id) && String(state.activeOptionId) === String(option.id);
      const total = optionTotal(option);
      const available = isWindowOptionAvailable(option);
      const meta = total > 0 ? formatMoney(total) : "";
      let mediaMarkup = "";

      if (kind === "layout") {
        mediaMarkup = renderWindowLayoutVisualMarkup(option);
      } else if (kind === "insert") {
        mediaMarkup = renderWindowInsertVisualMarkup(step, option);
      }

      return `
        <button
          type="button"
          class="gd-window-choice__visual${active ? " is-active" : ""}${previewed ? " is-previewed" : ""}${kind ? ` gd-window-choice__visual--${escapeHtml(kind)}` : ""}"
          data-gd-option="${escapeHtml(option.id)}"
          data-gd-step-id="${escapeHtml(step.id)}"
          aria-pressed="${active ? "true" : "false"}"
          ${available ? "" : "disabled"}
        >
          ${mediaMarkup}
          <span class="gd-window-choice__visual-label">${escapeHtml(kind === "insert" ? getWindowInsertDisplayLabel(option.label || "Option") : option.label || "Option")}</span>
          ${available ? (meta ? `<span class="gd-window-choice__visual-meta gd-window-choice__visual-price">${escapeHtml(meta)}</span>` : "") : '<span class="gd-window-choice__visual-meta">Out of stock</span>'}
        </button>
      `;
    }

    function renderWindowLayoutOption(step, option, selectedIds) {
      const product = state.windowProductByLayoutOptionId.get(String(option.id));
      const displayVariant = product && Array.isArray(product.variants) ? product.variants.find((variant) => variant.available) || product.variants[0] || null : null;
      const total = displayVariant ? Number(displayVariant.price_cents || 0) : optionTotal(option);
      const optionWithPrice = {
        ...option,
        components: total > 0 ? [{ price_cents: total }] : []
      };
      return renderWindowVisualOption(step, optionWithPrice, selectedIds, "layout");
    }

    function renderWindowLayoutPicker(step, selectedIds) {
      const selectedOption = getSelectedOptionFromStep(step);
      const selectedName = selectedOption ? String(selectedOption.label || "Window size") : "Window Size";
      const selectedTotal = selectedOption ? optionTotal(selectedOption) : 0;
      const selectedStatus = selectedOption ? (selectedTotal > 0 ? `+${formatMoney(selectedTotal)}` : "Included") : "Select";
      const optionsMarkup = (step.options || []).map((option) => renderWindowLayoutOption(step, option, selectedIds)).join("");

      return `
        <div class="gd-window-choice gd-window-choice--layout">
          <div class="gd-window-choice__sizes" data-gd-scroll-key="step:${escapeHtml(step.id)}:layout">
            ${optionsMarkup}
          </div>
          <p class="gd-window-choice__status">${escapeHtml(selectedStatus)}</p>
          <p class="gd-window-choice__name">${escapeHtml(selectedName)}</p>
        </div>
      `;
    }

    function renderWindowGlassOption(step, option, selectedIds) {
      return renderWindowVisualOption(step, option, selectedIds, "glass");
    }

    function renderWindowGlassPicker(step, selectedIds) {
      return renderWindowVisualPicker(step, selectedIds, "glass", "Product Option");
    }

    function renderWindowInsertPicker(step, selectedIds) {
      if (!shouldShowWindowInsertUi()) return "";
      return renderWindowVisualPicker(step, selectedIds, "insert", "Insert");
    }

    function renderWindowChipPicker(step, selectedIds, fallbackName) {
      const selectedOption = getSelectedOptionFromStep(step);
      const selectedName = selectedOption
        ? String(kind === "insert" ? getWindowInsertDisplayLabel(selectedOption.label || fallbackName) : selectedOption.label || fallbackName)
        : fallbackName;
      const selectedTotal = selectedOption ? optionTotal(selectedOption) : 0;
      const selectedStatus = selectedOption ? (selectedTotal > 0 ? `+${formatMoney(selectedTotal)}` : "Included") : "Select";
      const options = Array.isArray(step.options) ? step.options : [];

      if (options.length === 0) {
        return `
          <div class="gd-window-choice gd-window-choice--glass">
            <p class="gd-window-choice__hint">No options available.</p>
          </div>
        `;
      }

      const optionsMarkup = options.map((option) => renderWindowGlassOption(step, option, selectedIds)).join("");

      return `
        <div class="gd-window-choice gd-window-choice--glass">
          <div class="gd-entry-product__chips gd-window-choice__chips" data-gd-scroll-key="step:${escapeHtml(step.id)}:chip">
            ${optionsMarkup}
          </div>
          <p class="gd-window-choice__status">${escapeHtml(selectedStatus)}</p>
          <p class="gd-window-choice__name">${escapeHtml(selectedName)}</p>
        </div>
      `;
    }

    function renderWindowVisualPicker(step, selectedIds, kind, fallbackName) {
      const selectedOption = getSelectedOptionFromStep(step);
      const selectedName = selectedOption ? String(selectedOption.label || fallbackName) : fallbackName;
      const selectedTotal = selectedOption ? optionTotal(selectedOption) : 0;
      const selectedStatus = selectedOption ? (selectedTotal > 0 ? `+${formatMoney(selectedTotal)}` : "Included") : "Select";
      const options = Array.isArray(step.options) ? step.options : [];

      if (options.length === 0) {
        return `
          <div class="gd-window-choice gd-window-choice--${escapeHtml(kind)}">
            <p class="gd-window-choice__hint">No options available.</p>
          </div>
        `;
      }

      const optionsMarkup = options.map((option) => renderWindowVisualOption(step, option, selectedIds, kind)).join("");

      return `
        <div class="gd-window-choice gd-window-choice--${escapeHtml(kind)}">
          <div class="gd-window-choice__chips gd-window-choice__chips--visual" data-gd-scroll-key="step:${escapeHtml(step.id)}:${escapeHtml(kind)}">
            ${optionsMarkup}
          </div>
          <p class="gd-window-choice__status">${escapeHtml(selectedStatus)}</p>
          <p class="gd-window-choice__name">${escapeHtml(selectedName)}</p>
        </div>
      `;
    }

    function renderEmbeddedWindowPanel() {
      const layoutStep = getWindowLayoutStep();
      const glassStep = getWindowGlassStep();
      const colorStep = getWindowColorStep();
      const insertStep = getWindowInsertStep();
      const activeDoor = getActiveDoorState();
      const windowPlacementAttention = Boolean(activeDoor && activeDoor.windowPlacementAttention);
      const missingPlacementSteps = windowPlacementAttention ? getMissingWindowPlacementSteps() : [];
      const missingPlacementStepIds = new Set(missingPlacementSteps.map((step) => String(step.id || "")));
      const selectedWindowProduct = getSelectedWindowLayoutProduct();
      const showInsertSection = Boolean(
        selectedWindowProduct &&
        insertStep &&
        !isStepHiddenFromRail(insertStep) &&
        shouldShowWindowInsertUi()
      );
      const sections = [];

      if (layoutStep && !isStepHiddenFromRail(layoutStep)) {
        const selectedIds = getSelectionSet(layoutStep.id);
        sections.push(`
          <div class="gd-window-embed__section${missingPlacementStepIds.has(String(layoutStep.id || "")) ? " is-missing" : ""}" data-gd-window-embed-step="${escapeHtml(layoutStep.id)}">
            <p class="gd-window-embed__label">${escapeHtml(layoutStep.title || "Window Size")}</p>
            ${renderWindowLayoutPicker(layoutStep, selectedIds)}
          </div>
        `);
      }

      if (selectedWindowProduct && glassStep && !isStepHiddenFromRail(glassStep)) {
        const selectedIds = getSelectionSet(glassStep.id);
        sections.push(`
          <div class="gd-window-embed__section${missingPlacementStepIds.has(String(glassStep.id || "")) ? " is-missing" : ""}" data-gd-window-embed-step="${escapeHtml(glassStep.id)}">
            <p class="gd-window-embed__label">${escapeHtml(glassStep.title || "Product Option")}</p>
            ${missingPlacementStepIds.has(String(glassStep.id || "")) ? '<p class="gd-window-embed__missing-note">Required before placing a window in preview.</p>' : ""}
            ${renderWindowGlassPicker(glassStep, selectedIds)}
          </div>
        `);
      }

      if (selectedWindowProduct && colorStep && !isStepHiddenFromRail(colorStep)) {
        const selectedIds = getSelectionSet(colorStep.id);
        sections.push(`
          <div class="gd-window-embed__section${missingPlacementStepIds.has(String(colorStep.id || "")) ? " is-missing" : ""}" data-gd-window-embed-step="${escapeHtml(colorStep.id)}">
            <p class="gd-window-embed__label">${escapeHtml(getStepDisplayTitle(colorStep, "Frame Color"))}</p>
            ${missingPlacementStepIds.has(String(colorStep.id || "")) ? '<p class="gd-window-embed__missing-note">Required before placing a window in preview.</p>' : ""}
            ${renderColorPicker(colorStep, selectedIds)}
          </div>
        `);
      }

      if (showInsertSection) {
        const selectedIds = getSelectionSet(insertStep.id);
        sections.push(`
          <div class="gd-window-embed__section${missingPlacementStepIds.has(String(insertStep.id || "")) ? " is-missing" : ""}" data-gd-window-embed-step="${escapeHtml(insertStep.id)}">
            <p class="gd-window-embed__label">${escapeHtml(insertStep.title || "Insert")}</p>
            ${missingPlacementStepIds.has(String(insertStep.id || "")) ? '<p class="gd-window-embed__missing-note">Required before placing a window in preview.</p>' : ""}
            ${renderWindowInsertPicker(insertStep, selectedIds)}
          </div>
        `);
      }

      if (sections.length === 0) return "";

      return `
        <section class="gd-window-embed" data-gd-window-embed data-gd-design-section="windows">
          ${renderDesignRailSectionHeading("03", "Windows")}
          ${sections.join("")}
        </section>
      `;
    }

    function renderWindowPositionPicker(step, locked) {
      const positionRule = getWindowPositionRule();
      const ruleHint = getWindowPositionRuleHint(positionRule);
      const doorConfig = getCurrentDoorVisualConfig();
      const rows = getWindowGridRows(step, doorConfig);
      const selectedCells = getOrderedWindowCells(doorConfig);
      const selectedCount = selectedCells.length;
      const selectedName =
        selectedCount > 0 ? `${selectedCount} position${selectedCount === 1 ? "" : "s"} selected` : "Positions";
      const selectedSummary =
        selectedCount > 0
          ? selectedCells
              .slice(0, 4)
              .map((cell) => buildWindowCellDisplayLabel(cell, rows))
              .join(", ")
          : "";
      const hiddenCount = selectedCount > 4 ? selectedCount - 4 : 0;
      const hint = locked
        ? "Select a window layout first, then choose opening cells."
        : ruleHint
          ? `Tap any cell to place windows. ${ruleHint}`
          : "Tap any cell in this table to place windows in arbitrary positions.";
      const tableMarkup = buildWindowGridTableMarkup(step, "gd-window-position", locked, doorConfig);

      return `
        <div class="gd-window-position${locked ? " is-locked" : ""}">
          <p class="gd-window-position__hint">${escapeHtml(hint)}</p>
          <p class="gd-window-position__name">${escapeHtml(selectedName)}</p>
          ${selectedSummary ? `<p class="gd-window-position__selected">${escapeHtml(selectedSummary)}${hiddenCount > 0 ? ` +${hiddenCount} more` : ""}</p>` : ""}
          ${tableMarkup}
        </div>
      `;
    }

    function renderOptionButton(step, option, selectedIds) {
      const active = selectedIds.has(option.id);
      const previewed = String(state.activeStepId) === String(step.id) && String(state.activeOptionId) === String(option.id);
      const total = optionTotal(option);
      const trackStep = isTrackSystemStep(step);
      const description = buildOptionDescription(step, option);

      return `
        <button
          type="button"
          class="gd-rail-option${active ? " is-active" : ""}${previewed ? " is-previewed" : ""}${trackStep ? " gd-rail-option--track" : ""}"
          data-gd-option="${escapeHtml(option.id)}"
          data-gd-step-id="${escapeHtml(step.id)}"
          aria-pressed="${active ? "true" : "false"}"
        >
          <span class="gd-rail-option__check" aria-hidden="true"></span>
          <span class="gd-rail-option__thumb" aria-hidden="true">
            <img src="${escapeHtml(option.image)}" alt="" loading="lazy">
          </span>
          <span class="gd-rail-option__body">
            ${trackStep ? "" : `<span class="gd-rail-option__price-row">${formatMoney(total)}</span>`}
            <span class="gd-rail-option__label">${escapeHtml(option.label || "Option")}</span>
            ${description ? `<span class="gd-rail-option__meta">${escapeHtml(description)}</span>` : ""}
          </span>
        </button>
      `;
    }

    function renderStepGroup(step, index, mobile) {
      const selectedIds = getSelectionSet(step.id);
      const colorStep = isColorStep(step);
      const windowLayoutStep = isWindowLayoutStep(step);
      const windowGlassStep = isWindowGlassStep(step);
      const windowColorStep = isWindowColorStep(step);
      const windowInsertStep = isWindowInsertStep(step);
      const windowPositionStep = isWindowPositionStep(step);
      const trackStep = isTrackSystemStep(step);
      const showWindowInsertUi = !windowInsertStep || shouldShowWindowInsertUi();
      const selectedCount = selectedIds.size;
      const windowCellCount = windowPositionStep ? getOrderedWindowCells().length : 0;
      const hasSelection = windowPositionStep ? windowCellCount > 0 : selectedCount > 0;
      const expanded = !mobile || state.mobileOpenStepId === step.id || (!state.mobileOpenStepId && index === 0);
      const windowPositionLocked = windowPositionStep && !requiresWindowPositionSelection();
      const selectedOption =
        colorStep || windowLayoutStep || windowGlassStep || windowColorStep || windowInsertStep ? getSelectedOptionFromStep(step) : null;

      let status = "Optional";
      if (colorStep) {
        if (selectedOption) {
          const selectedTotal = optionTotal(selectedOption);
          status = selectedTotal > 0 ? `+${formatMoney(selectedTotal)}` : "Included";
        } else if (step.required) {
          status = "Select";
        }
      } else if (windowLayoutStep || windowGlassStep || windowColorStep || windowInsertStep) {
        if (windowInsertStep && !showWindowInsertUi) {
          status = "Not needed";
        } else
        if (!step.required && (!Array.isArray(step.options) || step.options.length === 0)) {
          status = "Not needed";
        } else if (selectedOption) {
          status = "Selected";
        } else if (step.required) {
          status = "Select";
        }
      } else if (windowPositionStep) {
        if (windowPositionLocked) {
          status = "Not needed";
        } else if (windowCellCount > 0) {
          status = `${windowCellCount} selected`;
        } else {
          status = "Required";
        }
      } else if (hasSelection) {
        status = selectedCount === 1 ? "1 selected" : `${selectedCount} selected`;
      } else if (step.required) {
        status = "Required";
      }
      const intro = buildStepIntro(step);
      const showIntro = !(colorStep || windowLayoutStep || windowGlassStep || windowColorStep || windowInsertStep || windowPositionStep || trackStep);

      const optionsMarkup = expanded
        ? colorStep
          ? renderColorPicker(step, selectedIds)
          : windowLayoutStep
            ? renderWindowLayoutPicker(step, selectedIds)
            : windowGlassStep
              ? renderWindowGlassPicker(step, selectedIds)
              : windowColorStep
                ? renderColorPicker(step, selectedIds)
                : windowInsertStep
                  ? (showWindowInsertUi ? renderWindowInsertPicker(step, selectedIds) : "")
          : windowPositionStep
            ? renderWindowPositionPicker(step, windowPositionLocked)
            : (step.options || []).map((option) => renderOptionButton(step, option, selectedIds)).join("")
        : "";
      const learnMoreMarkup = colorStep || windowLayoutStep || windowGlassStep || windowColorStep || windowInsertStep || windowPositionStep || trackStep
        ? ""
        : `
            <button
              type="button"
              class="gd-step-group__learn"
              data-gd-step-learn="${escapeHtml(step.id)}"
            >Learn More</button>
          `;

      return `
        <section class="gd-step-group${hasSelection ? " is-complete" : ""}${colorStep ? " gd-step-group--color" : ""}${windowLayoutStep ? " gd-step-group--window-layout" : ""}${windowGlassStep ? " gd-step-group--window-glass" : ""}${windowColorStep ? " gd-step-group--window-color" : ""}${windowInsertStep ? " gd-step-group--window-insert" : ""}${windowPositionStep ? " gd-step-group--window-position" : ""}${trackStep ? " gd-step-group--track-system" : ""}" data-gd-step-group="${escapeHtml(step.id)}"${colorStep && !windowColorStep ? ' data-gd-design-section="color"' : ""}${trackStep ? ' data-gd-design-section="track"' : ""}>
          <button
            type="button"
            class="gd-step-group__toggle"
            data-gd-step-toggle="${escapeHtml(step.id)}"
            aria-expanded="${expanded ? "true" : "false"}"
          >
            <span class="gd-step-group__head-copy">
              <span class="gd-step-group__title">${escapeHtml(getStepDisplayTitle(step, "Step"))}</span>
              <span class="gd-step-group__status">${escapeHtml(status)}</span>
            </span>
            <span class="gd-step-group__chevron" aria-hidden="true">▾</span>
          </button>

          <div class="gd-step-group__options" data-gd-step-options="${escapeHtml(step.id)}" ${expanded ? "" : "hidden"}>
            ${showIntro ? `<p class="gd-step-group__intro">${escapeHtml(intro)}</p>` : ""}
            ${optionsMarkup}
            ${trackStep && expanded ? renderSpecialTrackFields() : ""}
            ${learnMoreMarkup}
          </div>
        </section>
      `;
    }

    function renderPreflightRail() {
      const visibleSteps = getVisibleSteps();
      const collectionUrl = resolveDoorCollectionUrl();
      railEl.innerHTML = `
        <section class="gd-step-group is-locked">
          <div class="gd-step-group__lock-head">
            <h3 class="h5">Start from product page</h3>
            <p class="text-sm text-opacity">This configurator requires entry from the product template.</p>
            <a class="button button--secondary" href="${escapeHtml(collectionUrl)}">Go to products</a>
          </div>
          <div class="gd-step-group__lock-list">
            ${visibleSteps
              .map((step, index) => {
                return `
                  <div class="gd-step-group__lock-row">
                    <span class="gd-step-group__index">${index + 1}</span>
                    <span>${escapeHtml(getStepDisplayTitle(step, "Step"))}${step.required ? " *" : ""}</span>
                  </div>
                `;
              })
              .join("")}
          </div>
        </section>
      `;
    }

    function renderOrderDetailsPanel(options = {}) {
      const flat = options && Object.prototype.hasOwnProperty.call(options, "flat") ? Boolean(options.flat) : false;
      const poNumber = getPoNumber();
      const orderDate = getOrderDate();
      const pickupDate = getPickupDate();
      const customerId = getManualCustomerId();
      const note = getCustomerNote();
      const panelClassName = ["gd-order-note", flat ? "gd-order-note--flat" : ""].filter(Boolean).join(" ");
      return `
        <div class="${panelClassName}" aria-label="Quote details">
          <div class="gd-order-note__stack">
            <div class="gd-order-note__inline-grid gd-order-note__inline-grid--quad">
              <div class="gd-order-note__inline-field">
                <label class="gd-order-note__inline-label" for="GdOrderCustomerId">Customer ID</label>
                <input
                  class="gd-order-note__input gd-order-note__input--single-line gd-order-note__input--inline"
                  id="GdOrderCustomerId"
                  data-gd-order-customer-id
                  type="text"
                  maxlength="${ORDER_CUSTOMER_ID_MAX_LENGTH}"
                  value="${escapeHtml(customerId)}"
                >
              </div>
              <div class="gd-order-note__inline-field">
                <label class="gd-order-note__inline-label" for="GdOrderPoNumber">PO Number</label>
                <input
                  class="gd-order-note__input gd-order-note__input--single-line gd-order-note__input--inline"
                  id="GdOrderPoNumber"
                  data-gd-order-po
                  type="text"
                  maxlength="${ORDER_PO_NUMBER_MAX_LENGTH}"
                  value="${escapeHtml(poNumber)}"
                >
              </div>
              <div class="gd-order-note__inline-field">
                <label class="gd-order-note__inline-label" for="GdOrderDate">Date</label>
                <input
                  class="gd-order-note__input gd-order-note__input--single-line gd-order-note__input--inline"
                  id="GdOrderDate"
                  data-gd-order-date
                  type="date"
                  value="${escapeHtml(orderDate)}"
                >
              </div>
              <div class="gd-order-note__inline-field">
                <label class="gd-order-note__inline-label" for="GdOrderPickupDate">Pickup Date</label>
                <input
                  class="gd-order-note__input gd-order-note__input--single-line gd-order-note__input--inline"
                  id="GdOrderPickupDate"
                  data-gd-order-pickup-date
                  type="date"
                  value="${escapeHtml(pickupDate)}"
                >
              </div>
            </div>
            <div class="gd-order-note__inline-field">
              <label class="gd-order-note__inline-label" for="GdOrderNote">Notes</label>
              <textarea
                class="gd-order-note__input gd-order-note__input--notes"
                id="GdOrderNote"
                data-gd-order-note
                rows="4"
                maxlength="${ORDER_NOTE_MAX_LENGTH}"
              >${escapeHtml(note)}</textarea>
              <div class="gd-order-note__meta">
                <span data-gd-order-note-count>${note.length}/${ORDER_NOTE_MAX_LENGTH}</span>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    function renderOrderNotePanel() {
      return "";
    }

    function renderOverviewActionPanel() {
      return `
        <section class="gd-door-actions gd-door-actions--minimal" aria-label="Add door">
          <button
            type="button"
            class="gd-design-rail-save__button gd-door-actions__primary-action"
            data-gd-open-design-modal
            ${state.preflight.completed ? "" : "disabled"}
          >
            Add Door
          </button>
        </section>
      `;
    }

    function renderDesignActionPanel() {
      const customSize = getEntryCustomSizeState();
      const disabled = !state.preflight.completed || (customSize.active && !customSize.valid);
      const count = getCommittedDoorUnitCount();
      const editingDoor = getDesignEditingDoor();
      const description = customSize.active && !customSize.valid
        ? customSize.message || "Enter both custom width and height before saving this door."
        : editingDoor
          ? "Update this saved door, then click Save Door to apply the changes."
          : "Complete this single door, including window and track system selections, then save it back into the preview list.";
      const designDoor = editingDoor || getDraftDoorState();
      const designDoorLabel = designDoor ? getDoorLabel(designDoor) : "Door";

      return `
        <section class="gd-door-actions gd-door-actions--design" aria-label="Door design actions">
          <div class="gd-door-actions__copy">
            <h3 class="gd-door-actions__title">Design ${escapeHtml(designDoorLabel)}</h3>
            <p class="gd-door-actions__desc">${escapeHtml(description)}</p>
          </div>
          <div class="gd-door-actions__row gd-door-actions__row--design">
            <span class="gd-door-actions__count">${count} door${count === 1 ? "" : "s"} in preview</span>
            <div class="gd-door-actions__buttons">
              <button
                type="button"
                class="gd-door-actions__close"
                data-gd-design-close
              >
                Back
              </button>
              <button
                type="button"
                class="gd-door-actions__add"
                data-gd-rail-add-door
                ${disabled ? "disabled" : ""}
              >
                Save Door
              </button>
            </div>
          </div>
        </section>
      `;
    }

    function renderDesignRailSaveButton() {
      const customSize = getEntryCustomSizeState();
      const disabled = !state.preflight.completed || (customSize.active && !customSize.valid);

      return `
        <div class="gd-design-rail-save" aria-label="Door design save action">
          <button
            type="button"
            class="gd-design-rail-save__button"
            data-gd-rail-add-door
            ${disabled ? "disabled" : ""}
          >
            Save Door
          </button>
        </div>
      `;
    }

    function getDesignSidebarNavItems() {
      const items = [];
      const windowLayoutStep = getWindowLayoutStep();
      const windowGlassStep = getWindowGlassStep();
      const windowColorStep = getWindowColorStep();
      const windowInsertStep = getWindowInsertStep();
      const colorStep = getVisibleSteps().find((step) => isColorStep(step) && !isWindowColorStep(step)) || null;
      const trackStep = getRailSteps().find((step) => isTrackSystemStep(step)) || null;
      const hasDimensions = hasLiveEntryProduct() || Boolean(getEntrySizeSplitModel());
      const hasSeries = hasLiveEntryProduct() || Boolean(getEntrySeriesStep());
      const hasWindows = Boolean(windowLayoutStep || windowGlassStep || windowColorStep || windowInsertStep);
      const hasDoorColor =
        Boolean(getEntryCollectionColorValues(state.entryProduct).length > 0) || Boolean(colorStep);

      if (hasDimensions) {
        items.push({ id: "dimensions", label: "Dimensions", stepId: "" });
      }
      if (hasSeries) {
        items.push({ id: "series", label: "Series", stepId: "" });
      }
      if (hasWindows) {
        items.push({
          id: "windows",
          label: "Windows",
          stepId: String((windowLayoutStep || windowGlassStep || windowColorStep || windowInsertStep || {}).id || "")
        });
      }
      if (hasDoorColor) {
        items.push({
          id: "color",
          label: "Color",
          stepId: String((colorStep || {}).id || "")
        });
      }
      if (trackStep) {
        items.push({
          id: "track",
          label: "Track",
          stepId: String(trackStep.id || "")
        });
      }

      return items;
    }

    function getDesignSectionIdForStep(step) {
      if (!step) return "";
      if (isWindowLayoutStep(step) || isWindowGlassStep(step) || isWindowColorStep(step) || isWindowInsertStep(step) || isWindowPositionStep(step)) {
        return "windows";
      }
      if (isTrackSystemStep(step)) return "track";
      if (isColorStep(step)) return "color";
      return "";
    }

    function getActiveDesignSectionId(navItems) {
      if (state.designNavSectionId && navItems.some((item) => item.id === state.designNavSectionId)) {
        return state.designNavSectionId;
      }
      const activeStep = findStepById(state.activeStepId);
      const fromStep = getDesignSectionIdForStep(activeStep);
      if (fromStep && navItems.some((item) => item.id === fromStep)) return fromStep;
      return navItems[0] ? navItems[0].id : "";
    }

    function renderDesignSidebar() {
      if (!designSidebarEl) return;
      if (!state.designModalOpen || !state.preflight.completed || isMobileLayout()) {
        designSidebarEl.innerHTML = "";
        return;
      }

      const navItems = getDesignSidebarNavItems();
      const activeSectionId = getActiveDesignSectionId(navItems);
      const count = getCommittedDoorUnitCount();

      designSidebarEl.innerHTML = `
        <div class="gd-design-sidebar">
          <div class="gd-design-sidebar__head">
            <p class="gd-design-sidebar__count">${count} door${count === 1 ? "" : "s"} in preview</p>
          </div>
          <nav class="gd-design-sidebar__nav" aria-label="Door design sections">
            ${navItems
              .map((item, index) => {
                const active = item.id === activeSectionId;
                return `
                  <button
                    type="button"
                    class="gd-design-sidebar__link${active ? " is-active" : ""}"
                    data-gd-design-nav="${escapeHtml(item.id)}"
                    data-gd-design-step-id="${escapeHtml(item.stepId || "")}"
                    aria-current="${active ? "true" : "false"}"
                  >
                    <span class="gd-design-sidebar__index">${String(index + 1).padStart(2, "0")}</span>
                    <span>${escapeHtml(item.label)}</span>
                  </button>
                `;
              })
              .join("")}
          </nav>
          <div class="gd-design-sidebar__actions">
            <button type="button" class="gd-design-sidebar__secondary" data-gd-design-close>Back</button>
          </div>
        </div>
      `;
    }

    function scrollDesignRailToSection(sectionId) {
      const targetId = String(sectionId || "").trim();
      if (!targetId || !railEl) return;
      const target = railEl.querySelector(`[data-gd-design-section="${escapeHtml(targetId)}"]`);
      if (!(target instanceof HTMLElement)) return;
      target.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    }

    function focusDesignSection(sectionId, stepId = "") {
      const targetId = String(sectionId || "").trim();
      if (!targetId) return;
      state.designNavSectionId = targetId;
      const targetStepId = String(stepId || "").trim();
      if (targetStepId) {
        focusStep(targetStepId);
        requestAnimationFrame(() => {
          scrollDesignRailToSection(targetId);
        });
        return;
      }
      renderDesignSidebar();
      requestAnimationFrame(() => {
        scrollDesignRailToSection(targetId);
      });
    }

    function renderOverviewRail() {
      railEl.innerHTML = "";
    }

    function renderDesignRail() {
      if (!state.preflight.completed) {
        renderPreflightRail();
        return;
      }

      const mobile = isMobileLayout();
      const railSteps = getRailSteps();
      const entryPanelMarkup = buildEntryProductPanelMarkup();
      const doorActionMarkup = "";
      const railSaveMarkup = renderDesignRailSaveButton();
      const windowPanelMarkup = renderEmbeddedWindowPanel();
      const stepGroupsMarkup = railSteps
        .map((step, index) => {
          return renderStepGroup(step, index, mobile);
        })
        .join("");

      railEl.innerHTML = `${doorActionMarkup}${entryPanelMarkup}${windowPanelMarkup}${stepGroupsMarkup}${railSaveMarkup}`;
    }

    function renderSidebar() {
      renderDesignSidebar();
    }

    function renderRail() {
      if (state.designModalOpen) {
        renderDesignRail();
        return;
      }
      renderOverviewRail();
    }

    function captureRailScrollSnapshot() {
      if (!railEl) return null;
      const stepOptionsScroll = Array.from(railEl.querySelectorAll("[data-gd-step-options]"))
        .map((node) => {
          const stepId = String(node.getAttribute("data-gd-step-options") || "").trim();
          if (!stepId) return null;
          return {
            stepId,
            scrollLeft: Number(node.scrollLeft || 0),
            scrollTop: Number(node.scrollTop || 0)
          };
        })
        .filter(Boolean);
      const keyedScroll = Array.from(railEl.querySelectorAll("[data-gd-scroll-key]"))
        .map((node) => {
          const key = String(node.getAttribute("data-gd-scroll-key") || "").trim();
          if (!key) return null;
          return {
            key,
            scrollLeft: Number(node.scrollLeft || 0),
            scrollTop: Number(node.scrollTop || 0)
          };
        })
        .filter(Boolean);

      return {
        railScrollLeft: Number(railEl.scrollLeft || 0),
        railScrollTop: Number(railEl.scrollTop || 0),
        stepOptionsScroll,
        keyedScroll
      };
    }

    function restoreRailScrollSnapshot(snapshot) {
      if (!snapshot || !railEl) return;

      railEl.scrollLeft = Number(snapshot.railScrollLeft || 0);
      railEl.scrollTop = Number(snapshot.railScrollTop || 0);

      const stepOptionNodeById = new Map();
      railEl.querySelectorAll("[data-gd-step-options]").forEach((node) => {
        const stepId = String(node.getAttribute("data-gd-step-options") || "").trim();
        if (!stepId) return;
        stepOptionNodeById.set(stepId, node);
      });

      for (const item of Array.isArray(snapshot.stepOptionsScroll) ? snapshot.stepOptionsScroll : []) {
        const stepId = String(item && item.stepId ? item.stepId : "").trim();
        if (!stepId) continue;
        const node = stepOptionNodeById.get(stepId);
        if (!node) continue;
        node.scrollLeft = Number(item.scrollLeft || 0);
        node.scrollTop = Number(item.scrollTop || 0);
      }

      const keyedNodeById = new Map();
      railEl.querySelectorAll("[data-gd-scroll-key]").forEach((node) => {
        const key = String(node.getAttribute("data-gd-scroll-key") || "").trim();
        if (!key || keyedNodeById.has(key)) return;
        keyedNodeById.set(key, node);
      });

      for (const item of Array.isArray(snapshot.keyedScroll) ? snapshot.keyedScroll : []) {
        const key = String(item && item.key ? item.key : "").trim();
        if (!key) continue;
        const node = keyedNodeById.get(key);
        if (!node) continue;
        node.scrollLeft = Number(item.scrollLeft || 0);
        node.scrollTop = Number(item.scrollTop || 0);
      }
    }

    function getSelectedHorizontalScrollItem(container) {
      if (!(container instanceof HTMLElement)) return null;
      return (
        Array.from(container.children).find((node) => {
          if (!(node instanceof HTMLElement)) return false;
          return node.classList.contains("is-active") || node.getAttribute("aria-pressed") === "true";
        }) || null
      );
    }

    function revealHorizontalScrollItem(container, item) {
      if (!(container instanceof HTMLElement) || !(item instanceof HTMLElement)) return false;
      const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
      if (maxScrollLeft <= 0) return false;

      const padding = 12;
      const itemLeft = Number(item.offsetLeft || 0);
      const itemRight = itemLeft + Number(item.offsetWidth || 0);
      const visibleLeft = Number(container.scrollLeft || 0);
      const visibleRight = visibleLeft + Number(container.clientWidth || 0);
      const fullyVisible = itemLeft >= visibleLeft + padding && itemRight <= visibleRight - padding;
      if (fullyVisible) return false;

      const centeredLeft = itemLeft - Math.max(0, (container.clientWidth - item.offsetWidth) / 2);
      const nextLeft = Math.min(maxScrollLeft, Math.max(0, centeredLeft));
      container.scrollTo({
        left: nextLeft,
        behavior: "smooth"
      });
      return true;
    }

    function revealSelectedRailScrollTargets(snapshot) {
      if (!railEl) return;
      const preservedKeys = new Set(
        Array.isArray(snapshot && snapshot.keyedScroll)
          ? snapshot.keyedScroll
              .map((item) => String(item && item.key ? item.key : "").trim())
              .filter(Boolean)
          : []
      );

      railEl.querySelectorAll("[data-gd-scroll-key]").forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        const key = String(node.getAttribute("data-gd-scroll-key") || "").trim();
        if (key && preservedKeys.has(key)) return;
        const selectedItem = getSelectedHorizontalScrollItem(node);
        if (!selectedItem) return;
        revealHorizontalScrollItem(node, selectedItem);
      });
    }

    function getPreviewScrollContainer() {
      if (!previewEl) return null;
      return (
        previewEl.querySelector("[data-gd-door-scroll]") ||
        previewEl.querySelector(".gd-preview-door--design .gd-preview-door__canvas") ||
        previewEl.querySelector(".gd-window-config-preview__stage .gd-preview-door__canvas") ||
        previewEl.querySelector(".gd-preview-window__panel") ||
        null
      );
    }

    function capturePreviewScrollSnapshot() {
      const scrollContainer = getPreviewScrollContainer();
      if (!(scrollContainer instanceof HTMLElement)) return null;

      return {
        scrollLeft: Number(scrollContainer.scrollLeft || 0),
        scrollTop: Number(scrollContainer.scrollTop || 0)
      };
    }

    function restorePreviewScrollSnapshot(snapshot) {
      if (!snapshot) return;
      const scrollContainer = getPreviewScrollContainer();
      if (!(scrollContainer instanceof HTMLElement)) return;

      scrollContainer.scrollLeft = Number(snapshot.scrollLeft || 0);
      scrollContainer.scrollTop = Number(snapshot.scrollTop || 0);
    }

    let pendingPreviewScrollRestoreFrame = 0;
    function cancelPendingPreviewScrollRestore() {
      if (pendingPreviewScrollRestoreFrame) {
        cancelAnimationFrame(pendingPreviewScrollRestoreFrame);
        pendingPreviewScrollRestoreFrame = 0;
      }
    }

    function schedulePreviewScrollRestore(snapshot, remainingPasses = 2) {
      if (!snapshot) return;
      cancelPendingPreviewScrollRestore();
      restorePreviewScrollSnapshot(snapshot);

      const nextRemainingPasses = Math.max(0, Number(remainingPasses) || 0);
      if (nextRemainingPasses <= 0) return;

      pendingPreviewScrollRestoreFrame = requestAnimationFrame(() => {
        pendingPreviewScrollRestoreFrame = 0;
        if (pendingPreviewScrollDoorId) return;
        schedulePreviewScrollRestore(snapshot, nextRemainingPasses - 1);
      });
    }

    function renderDock() {
      const committedDoors = getCommittedDoors();
      const activeDoorPreflightCompleted = Boolean(state.preflight.completed);
      const firstDoorIssue = activeDoorPreflightCompleted ? getFirstDoorValidationIssue() : null;
      const total = totalCents();
      const financials = getOrderFinancialSummary(total);
      const hasCustomPrice = hasAnyDoorWithoutPrice();
      dockTotalEl.textContent = hasCustomPrice && !financials.subtotalOverridden ? "Custom Price" : financials.balanceLabel;
      const customerIdentity = getCustomerIdentity();
      const customerContextChecking = isApiOrderMode() && !state.customerContext.loaded;
      const customerContextUnavailable =
        isApiOrderMode() && state.customerContext.loaded && String(state.customerContext.error || "").trim() !== "";
      const requiresCustomerLogin =
        isApiOrderMode() &&
        state.customerContext.loaded &&
        !customerContextUnavailable &&
        !customerIdentity.shopifyCustomerId;

      let hint = state.hintMessage;
      let tone = state.hintTone || "info";
      let dockState = "ready";

      if (!state.preflight.completed) {
        hint = "Start from a product page to begin configuration.";
        tone = "error";
        dockState = "entry";
      } else if (committedDoors.length === 0) {
        hint = state.designModalOpen
          ? "Finish this door in the modal, then click Save Door."
          : "Click Add Door to open the single-door design modal.";
        tone = "info";
        dockState = "incomplete";
      } else if (customerContextChecking) {
        hint = "Checking account session...";
        tone = "info";
        dockState = "loading";
      } else if (state.adding) {
        hint = "";
        tone = "info";
        dockState = "loading";
      } else if (firstDoorIssue && firstDoorIssue.type === "custom_size") {
        hint = `${firstDoorIssue.doorLabel}: ${firstDoorIssue.customSize.message || "Enter both custom width and height."}`;
        tone = "error";
        dockState = "error";
      } else if (firstDoorIssue && firstDoorIssue.type === "missing_required") {
        hint = `${firstDoorIssue.doorLabel}: complete ${firstDoorIssue.missing.length} required selection${firstDoorIssue.missing.length === 1 ? "" : "s"} to enable Quote Now.`;
        tone = "error";
        dockState = "incomplete";
      } else if (customerContextUnavailable) {
        hint = "Unable to verify account session. Please refresh and try again.";
        tone = "error";
        dockState = "error";
      } else if (requiresCustomerLogin) {
        hint = "Please sign in to your customer account before requesting this quote.";
        tone = "error";
        dockState = "login";
      } else if (!hint) {
        const committedDoorCount = getCommittedDoorUnitCount();
        hint = committedDoorCount > 1 ? `Ready to quote ${committedDoorCount} doors.` : "Ready to request your quote.";
        tone = "success";
      }

      if (!state.preflight.completed) {
        dockState = "entry";
      } else if (committedDoors.length === 0) {
        dockState = "incomplete";
      } else if (customerContextChecking) {
        dockState = "loading";
      } else if (state.adding) {
        dockState = "loading";
      } else if (firstDoorIssue && firstDoorIssue.type === "custom_size") {
        dockState = "error";
      } else if (firstDoorIssue && firstDoorIssue.type === "missing_required") {
        dockState = "incomplete";
      } else if (customerContextUnavailable) {
        dockState = "error";
      } else if (requiresCustomerLogin) {
        dockState = "login";
      } else if (tone === "error") {
        dockState = "error";
      } else {
        dockState = "ready";
      }

      const hasHint = String(hint || "").trim() !== "";
      dockHintEl.textContent = hasHint ? hint : "";
      dockHintEl.setAttribute("data-tone", tone);
      dockHintEl.toggleAttribute("hidden", !hasHint);
      dockEl.setAttribute("data-state", dockState);
      dockEl.toggleAttribute("hidden", committedDoors.length === 0);

      dockAddButton.disabled =
        state.adding ||
        !activeDoorPreflightCompleted ||
        committedDoors.length === 0 ||
        Boolean(firstDoorIssue) ||
        customerContextChecking ||
        customerContextUnavailable;
      const dockButtonLabel = state.adding
        ? isApiOrderMode()
          ? "Submitting..."
          : "Quoting..."
        : customerContextChecking
          ? "Checking..."
        : requiresCustomerLogin
          ? "Login to Quote"
          : "Review Quote";
      const dockButtonLabelEl = dockAddButton.querySelector("[data-gd-dock-add-label]");
      if (dockButtonLabelEl) {
        dockButtonLabelEl.textContent = dockButtonLabel;
      } else {
        dockAddButton.textContent = dockButtonLabel;
      }
      if (dockTotalEl) {
        dockTotalEl.hidden = false;
      }
      dockAddButton.setAttribute("data-show-total", "false");
    }

    function renderEmptyState() {
      previewEl.innerHTML = "<p class='text-sm text-opacity'>No steps found in the selected configurator.</p>";
      setPreviewSummaryMarkup("");
      if (designSidebarEl) designSidebarEl.innerHTML = "";
      railEl.innerHTML = "";
      dockEl.hidden = true;
      dockTotalEl.textContent = formatMoney(0);
      dockTotalEl.hidden = false;
      const dockButtonLabelEl = dockAddButton.querySelector("[data-gd-dock-add-label]");
      if (dockButtonLabelEl) {
        dockButtonLabelEl.textContent = "Review Quote";
      } else {
        dockAddButton.textContent = "Review Quote";
      }
      dockAddButton.setAttribute("data-show-total", "false");
      dockAddButton.disabled = true;
      dockHintEl.textContent = "No steps are available for this configurator.";
      dockHintEl.setAttribute("data-tone", "error");
    }

    function render() {
      const visibleSteps = getVisibleSteps();
      syncDesignModalUiState();
      const viewMode = state.designModalOpen ? "design" : "overview";

      if (visibleSteps.length === 0) {
        renderEmptyState();
        lastRenderedViewMode = viewMode;
        return;
      }

      const railScrollSnapshot = lastRenderedViewMode === viewMode ? captureRailScrollSnapshot() : null;
      const previewScrollSnapshot = lastRenderedViewMode === viewMode ? capturePreviewScrollSnapshot() : null;
      ensureInitialFocus();
      syncWindowCellsToLayout();
      renderPreview();
      cancelPendingPreviewScrollRestore();
      if (pendingPreviewScrollDoorId) {
        requestAnimationFrame(() => {
          syncPreviewScrollToDoor(pendingPreviewScrollDoorId);
        });
      } else if (previewScrollSnapshot) {
        schedulePreviewScrollRestore(previewScrollSnapshot);
      }
      renderRail();
      renderSidebar();
      if (railScrollSnapshot) {
        restoreRailScrollSnapshot(railScrollSnapshot);
      } else if (railEl) {
        railEl.scrollLeft = 0;
        railEl.scrollTop = 0;
      }
      if (pendingRailSelectedReveal && state.designModalOpen) {
        const snapshot = railScrollSnapshot;
        pendingRailSelectedReveal = false;
        requestAnimationFrame(() => {
          revealSelectedRailScrollTargets(snapshot);
        });
      }
      lastRenderedViewMode = viewMode;
      renderDock();
      restorePendingEntryDropdownFocus();
    }

    let pendingPreviewScrollDoorId = "";
    let pendingRailSelectedReveal = false;
    let lastRenderedViewMode = state.designModalOpen ? "design" : "overview";

    function queuePreviewScrollToDoor(doorId) {
      cancelPendingPreviewScrollRestore();
      pendingPreviewScrollDoorId = String(doorId || "").trim();
    }

    function syncPreviewScrollToDoor(doorId) {
      const targetDoorId = String(doorId || "").trim();
      if (!targetDoorId || !previewEl) return;
      const scrollContainer = previewEl.querySelector("[data-gd-door-scroll]");
      const targetCard = Array.from(previewEl.querySelectorAll("[data-gd-door-card]")).find((node) => {
        return String(node.getAttribute("data-gd-door-card") || "").trim() === targetDoorId;
      });
      if (!(scrollContainer instanceof HTMLElement) || !(targetCard instanceof HTMLElement)) return;
      targetCard.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start"
      });
      if (pendingPreviewScrollDoorId === targetDoorId) {
        pendingPreviewScrollDoorId = "";
      }
    }

    function addDoorFromCurrent() {
      const specialTrackIssue = getSpecialTrackValidationIssue();
      if (specialTrackIssue) {
        showSpecialTrackValidationIssue(specialTrackIssue);
        return;
      }
      const editingDoor = getDesignEditingDoor();
      if (editingDoor) {
        state.previewDoorId = editingDoor.id;
        queuePreviewScrollToDoor(editingDoor.id);
        state.designEditingDoorId = "";
        state.designEditingSnapshot = null;
        state.designModalOpen = false;
        setHint("");
        ensureInitialFocus();
        render();
        return;
      }

      const draftDoor = getDraftDoorState();
      if (!draftDoor) return;

      draftDoor.committed = true;
      state.previewDoorId = draftDoor.id;
      queuePreviewScrollToDoor(draftDoor.id);

      const nextDoor = createNextDraftDoorState(draftDoor || {});
      nextDoor.committed = false;
      state.doors.push(nextDoor);
      state.activeDoorId = nextDoor.id;
      ensureInitialWindowDefaults();
      state.designModalOpen = false;
      setHint("");
      ensureInitialFocus();
      render();
    }

    function duplicateDoorById(doorId) {
      const sourceDoor = getDoorStateById(doorId);
      if (!isCommittedDoor(sourceDoor)) return;

      const nextDoor = cloneDoorState(sourceDoor);
      nextDoor.committed = true;
      state.doors.splice(getDoorIndex(sourceDoor) + 1, 0, nextDoor);
      state.previewDoorId = nextDoor.id;
      queuePreviewScrollToDoor(nextDoor.id);
      setHint("");
      render();

      const duplicateButton = previewEl.querySelector(`[data-gd-door-duplicate="${nextDoor.id}"]`);
      if (duplicateButton) duplicateButton.focus({ preventScroll: true });
    }

    function deleteDoorById(doorId) {
      const targetId = String(doorId || "").trim();
      const index = getDoorIndex(targetId);
      const committedDoors = getCommittedDoors();
      const committedIndex = getCommittedDoorIndex(targetId);
      if (index < 0 || committedDoors.length <= 1) return;

      state.doors.splice(index, 1);

      if (String(state.previewDoorId || "") === targetId) {
        const nextCommittedDoors = getCommittedDoors();
        const fallbackDoor =
          nextCommittedDoors[Math.max(0, committedIndex - 1)] ||
          nextCommittedDoors[Math.max(0, Math.min(nextCommittedDoors.length - 1, committedIndex))] ||
          nextCommittedDoors[0] ||
          null;
        state.previewDoorId = fallbackDoor ? fallbackDoor.id : "";
        if (fallbackDoor) {
          queuePreviewScrollToDoor(fallbackDoor.id);
        }
      } else if (state.previewDoorId) {
        queuePreviewScrollToDoor(state.previewDoorId);
      }

      setHint("");
      ensureInitialFocus();
      render();
    }

    function setActiveFromStep(step) {
      if (!step) return;
      state.activeStepId = step.id;
      const selectedOption = getSelectedOptionFromStep(step);
      state.activeOptionId = selectedOption ? selectedOption.id : "";
      const designSectionId = getDesignSectionIdForStep(step);
      if (designSectionId) {
        state.designNavSectionId = designSectionId;
      }
    }

    function onStepToggle(stepId) {
      const step = findStepById(stepId);
      if (!step || !state.preflight.completed) return;

      if (isMobileLayout()) {
        if (String(state.mobileOpenStepId) === String(step.id)) {
          state.mobileOpenStepId = "";
        } else {
          state.mobileOpenStepId = step.id;
        }
      } else {
        state.mobileOpenStepId = step.id;
      }

      setActiveFromStep(step);
      setHint("");
      render();
    }

    function focusStep(stepId) {
      const step = findStepById(stepId);
      if (!step || !state.preflight.completed) return;
      state.activeStepId = step.id;
      state.mobileOpenStepId = step.id;
      const selectedOption = getSelectedOptionFromStep(step);
      state.activeOptionId = selectedOption ? selectedOption.id : "";
      const designSectionId = getDesignSectionIdForStep(step);
      if (designSectionId) {
        state.designNavSectionId = designSectionId;
      }
      setHint("");
      render();
    }

    function onOptionSelect(stepId, optionId) {
      const step = findStepById(stepId);
      const option = findOption(step, optionId);
      if (!step || !option || !state.preflight.completed) return;

      const windowLayoutStep = getWindowLayoutStep();
      const windowPositionStep = getWindowPositionStep();
      const selectingWindowLayout = windowLayoutStep && String(step.id) === String(windowLayoutStep.id);
      const selectingWindowChoice = isWindowLayoutStep(step) || isWindowGlassStep(step) || isWindowColorStep(step) || isWindowInsertStep(step);
      const selectingWindowColor = isWindowColorStep(step);
      if (selectingWindowChoice) {
        ensureWindowCellConfigsForActiveDoor();
      }

      const selectedSet = getSelectionSet(step.id);

      if (step.multi_select) {
        if (selectedSet.has(option.id)) selectedSet.delete(option.id);
        else selectedSet.add(option.id);
      } else {
        selectedSet.clear();
        selectedSet.add(option.id);
      }

      let nextOpenStepId = step.id;

      if (selectingWindowChoice) {
        if (selectingWindowColor) {
          state.windowColorAutoSync = areColorLabelsEquivalent(state.entryContext.colorLabel, option.window_value || option.label || "");
        }
        if (selectingWindowLayout && isNoWindowLayoutOption(option)) {
          state.windowCells.clear();
          state.windowCellConfigs = new Map();
          if (windowPositionStep && String(state.activeStepId) === String(windowPositionStep.id)) {
            state.activeOptionId = "";
          }
        }

        syncWindowChoiceSteps();
        syncWindowCellConfigsToCurrentSelection();
        syncWindowCellsToLayout();

        const missingChoiceStep = getWindowChoiceStepsInOrder().find((choiceStep) => {
          if (!choiceStep || !Array.isArray(choiceStep.options) || choiceStep.options.length === 0) return false;
          return !getSelectedOptionFromStep(choiceStep);
        });

        if (missingChoiceStep) {
          nextOpenStepId = missingChoiceStep.id;
        } else if (
          windowPositionStep &&
          isStepRenderedAsRailGroup(windowPositionStep) &&
          selectingWindowLayout &&
          !isNoWindowLayoutOption(option) &&
          state.windowCells.size === 0
        ) {
          nextOpenStepId = windowPositionStep.id;
        }
      }

      state.activeStepId = step.id;
      state.activeOptionId = option.id;
      state.mobileOpenStepId = nextOpenStepId;
      state.lastTouchedStepId = step.id;
      state.lastTouchedOptionId = option.id;
      const designSectionId = getDesignSectionIdForStep(step);
      if (designSectionId) {
        state.designNavSectionId = designSectionId;
      }

      setHint("");
      render();
    }

    function onWindowCellToggle(stepId, cellKey) {
      let step = findStepById(stepId);
      if (!step || !isWindowPositionStep(step)) {
        step = getWindowPositionStep();
      }
      if (!step || !isWindowPositionStep(step) || !state.preflight.completed) return;

      const parsed = parseWindowCellKey(cellKey);
      if (!parsed) return;

      const doorConfig = getCurrentDoorVisualConfig();
      const positionRule = getWindowPositionRule();
      const maxRows = getEffectiveWindowRowCount(doorConfig);
      const maxCols = Math.max(1, Number(positionRule.cols || 1));
      if (parsed.row < 1 || parsed.row > maxRows || parsed.col < 1 || parsed.col > maxCols) return;
      const cellAlreadySelected = state.windowCells.has(parsed.key);
      if (!cellAlreadySelected) {
        const missingPlacementSteps = getMissingWindowPlacementSteps();
        if (missingPlacementSteps.length > 0) {
          revealWindowPlacementValidation(missingPlacementSteps);
          return;
        }
      }

      const next = new Set(state.windowCells);
      const nextConfigs = cloneWindowCellConfigMap(state.windowCellConfigs);
      const nextConfig = buildCurrentWindowCellConfig();
      if (next.has(parsed.key)) {
        const existingConfig = nextConfigs.get(parsed.key) || getWindowCellConfig(parsed.key);
        if (nextConfig && !areWindowCellConfigsEquivalent(existingConfig, nextConfig)) {
          nextConfigs.set(parsed.key, nextConfig);
        } else {
          next.delete(parsed.key);
          nextConfigs.delete(parsed.key);
        }
      } else {
        if (!nextConfig) return;
        next.add(parsed.key);
        nextConfigs.set(parsed.key, nextConfig);
      }
      state.windowCells = applyWindowCellSelectionRules(next, nextConfigs, positionRule, parsed, doorConfig);
      state.windowCellConfigs = filterWindowCellConfigMap(nextConfigs, state.windowCells);
      const activeDoor = getActiveDoorState();
      if (activeDoor) {
        activeDoor.windowPlacementAttention = false;
      }

      state.activeStepId = step.id;
      state.activeOptionId = "";
      state.mobileOpenStepId = step.id;
      state.lastTouchedStepId = step.id;
      state.lastTouchedOptionId = "";
      state.designNavSectionId = "windows";

      setHint("");
      render();
    }

    function resolveWindowCellKeyFromGridClick(gridElement, event) {
      if (!gridElement || !event) return "";
      const rect = gridElement.getBoundingClientRect();
      if (!(rect.width > 0) || !(rect.height > 0)) return "";

      const cols = Math.max(1, Number(gridElement.getAttribute("data-gd-window-grid-cols") || 1));
      const rows = Math.max(1, Number(gridElement.getAttribute("data-gd-window-grid-rows") || 1));
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      if (!Number.isFinite(x) || !Number.isFinite(y)) return "";
      if (x < 0 || x > 1 || y < 0 || y > 1) return "";

      const col = Math.min(cols, Math.max(1, Math.floor(x * cols) + 1));
      const row = Math.min(rows, Math.max(1, Math.floor(y * rows) + 1));
      return makeWindowCellKey(row, col);
    }

    function parsePercentCssVariable(value) {
      const numeric = Number(String(value || "").replace(/[^0-9.+-]/g, ""));
      if (!Number.isFinite(numeric)) return 0;
      return numeric;
    }

    function getNearestWindowCellIndex(centers, value) {
      const list = (Array.isArray(centers) ? centers : [])
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item));
      if (list.length === 0) return 1;

      let bestIndex = 0;
      let bestDistance = Infinity;
      for (let index = 0; index < list.length; index += 1) {
        const distance = Math.abs(list[index] - value);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      }

      return bestIndex + 1;
    }

    function resolveWindowCellFromSurfacePointer(surfaceElement, eventLike, clampToZone = true) {
      if (!surfaceElement || !eventLike || !state.preflight.completed || !requiresWindowPositionSelection()) return null;
      const step = getWindowPositionStep();
      if (!step || !isWindowPositionStep(step)) return null;

      const doorConfig = getCurrentDoorVisualConfig();
      const rule = getWindowPositionRule();
      const metrics = getWindowMetricsForOverlap(rule, doorConfig);
      const rect = surfaceElement.getBoundingClientRect();
      if (!(rect.width > 0) || !(rect.height > 0)) return null;

      const clientX = Number(eventLike.clientX);
      const clientY = Number(eventLike.clientY);
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;

      const styles = window.getComputedStyle(surfaceElement);
      const zoneTopPct = parsePercentCssVariable(styles.getPropertyValue("--gd-window-zone-top"));
      const zoneRightPct = parsePercentCssVariable(styles.getPropertyValue("--gd-window-zone-right"));
      const zoneBottomPct = parsePercentCssVariable(styles.getPropertyValue("--gd-window-zone-bottom"));
      const zoneLeftPct = parsePercentCssVariable(styles.getPropertyValue("--gd-window-zone-left"));

      const zoneLeftPx = rect.left + (zoneLeftPct / 100) * rect.width;
      const zoneTopPx = rect.top + (zoneTopPct / 100) * rect.height;
      const zoneWidthPx = rect.width * (1 - (zoneLeftPct + zoneRightPct) / 100);
      const zoneHeightPx = rect.height * (1 - (zoneTopPct + zoneBottomPct) / 100);
      if (!(zoneWidthPx > 0) || !(zoneHeightPx > 0)) return null;

      const rawXPct = ((clientX - zoneLeftPx) / zoneWidthPx) * 100;
      const rawYPct = ((clientY - zoneTopPx) / zoneHeightPx) * 100;
      if (!Number.isFinite(rawXPct) || !Number.isFinite(rawYPct)) return null;

      const xPct = clampToZone ? clampNumber(rawXPct, 0, 100) : rawXPct;
      const yPct = clampToZone ? clampNumber(rawYPct, 0, 100) : rawYPct;
      if (!clampToZone && (xPct < 0 || xPct > 100 || yPct < 0 || yPct > 100)) return null;

      const cols = Math.max(1, Number(rule && rule.cols ? rule.cols : 1));
      const rows = Math.max(1, Number(metrics && metrics.rows ? metrics.rows : getEffectiveWindowRowCount(doorConfig)));
      const colCenters =
        Array.isArray(metrics.colCenters) && metrics.colCenters.length >= cols
          ? metrics.colCenters.slice(0, cols)
          : buildWindowAxisCenters(cols, 1, false);
      const rowCenters =
        Array.isArray(metrics.rowCenters) && metrics.rowCenters.length >= rows
          ? metrics.rowCenters.slice(0, rows)
          : buildWindowAxisCenters(rows, 1, false);

      const col = Math.min(cols, Math.max(1, getNearestWindowCellIndex(colCenters, xPct)));
      const row = Math.min(rows, Math.max(1, getNearestWindowCellIndex(rowCenters, yPct)));
      return {
        stepId: step.id,
        cellKey: makeWindowCellKey(row, col),
        row,
        col
      };
    }

    function resolveWindowCellFromSurfacePointerMove(surfaceElement, event) {
      return resolveWindowCellFromSurfacePointer(surfaceElement, event, true);
    }

    function resolveWindowCellFromSurfaceClick(surfaceElement, event) {
      return resolveWindowCellFromSurfacePointer(surfaceElement, event, true);
    }

    function pushUniqueMetaValue(values, rawValue) {
      if (!Array.isArray(values)) return;
      const value = String(rawValue || "").trim();
      if (!value) return;
      if (!values.includes(value)) values.push(value);
    }

    function ensureMergedItemRecord(merged, recordKey, seed = null) {
      const key = String(recordKey || "").trim();
      if (!key) return null;
      const initial = seed && typeof seed === "object" ? seed : {};
      if (!merged.has(key)) {
        merged.set(key, {
          id: Number(initial.id || 0),
          sku: String(initial.sku || "").trim(),
          title: String(initial.title || "").trim(),
          price: initial.price === undefined || initial.price === null ? undefined : String(initial.price).trim(),
          requiresShipping: initial.requiresShipping === undefined ? undefined : Boolean(initial.requiresShipping),
          taxable: initial.taxable === undefined ? undefined : Boolean(initial.taxable),
          quantity: 0,
          properties: {},
          _meta: {
            sources: [],
            productTypes: [],
            stepHandles: [],
            stepTitles: [],
            optionLabels: [],
            componentTitles: [],
            componentSkus: [],
            windowLayouts: [],
            windowGlasses: [],
            windowColors: [],
            windowInserts: [],
            windowPositions: []
          }
        });
      }
      const record = merged.get(key);
      if (!record.title && initial.title) record.title = String(initial.title).trim();
      if (!record.sku && initial.sku) record.sku = String(initial.sku).trim();
      if ((record.price === undefined || record.price === null || record.price === "") && initial.price !== undefined && initial.price !== null) {
        record.price = String(initial.price).trim();
      }
      if (record.requiresShipping === undefined && initial.requiresShipping !== undefined) {
        record.requiresShipping = Boolean(initial.requiresShipping);
      }
      if (record.taxable === undefined && initial.taxable !== undefined) {
        record.taxable = Boolean(initial.taxable);
      }
      return record;
    }

    function annotateMergedItemRecord(item, metadata) {
      if (!item || !item._meta || !metadata || typeof metadata !== "object") return;
      const meta = item._meta;
      pushUniqueMetaValue(meta.sources, metadata.source);
      pushUniqueMetaValue(meta.productTypes, metadata.productType);
      pushUniqueMetaValue(meta.stepHandles, metadata.stepHandle);
      pushUniqueMetaValue(meta.stepTitles, metadata.stepTitle);
      pushUniqueMetaValue(meta.optionLabels, metadata.optionLabel);
      pushUniqueMetaValue(meta.componentTitles, metadata.componentTitle);
      pushUniqueMetaValue(meta.componentSkus, metadata.componentSku);
      pushUniqueMetaValue(meta.windowLayouts, metadata.windowLayout);
      pushUniqueMetaValue(meta.windowGlasses, metadata.windowGlass);
      pushUniqueMetaValue(meta.windowColors, metadata.windowColor);
      pushUniqueMetaValue(meta.windowInserts, metadata.windowInsert);
      pushUniqueMetaValue(meta.windowPositions, metadata.windowPosition);
      if (metadata.isTrackSystem) {
        pushUniqueMetaValue(meta.sources, "track_system");
        pushUniqueMetaValue(meta.productTypes, "track_system");
      }
    }

    function finalizeMergedItemRecord(item) {
      const properties = item && item.properties && typeof item.properties === "object" ? { ...item.properties } : {};
      const meta = item && item._meta && typeof item._meta === "object" ? item._meta : {};

      const setCsv = (key, values) => {
        if (!Array.isArray(values) || values.length === 0) return;
        properties[key] = values.join(",");
      };
      const setLabels = (key, values) => {
        if (!Array.isArray(values) || values.length === 0) return;
        properties[key] = values.join(" | ");
      };

      setCsv("_gd_line_sources", meta.sources);
      setCsv("_gd_line_product_types", meta.productTypes);
      setCsv("_gd_line_step_handles", meta.stepHandles);
      setLabels("_gd_line_step_titles", meta.stepTitles);
      setLabels("_gd_line_option_labels", meta.optionLabels);
      setLabels("_gd_line_component_titles", meta.componentTitles);
      setCsv("_gd_line_component_skus", meta.componentSkus);
      setLabels("_gd_window_layout", meta.windowLayouts);
      setLabels("_gd_window_glass", meta.windowGlasses);
      setLabels("_gd_window_color", meta.windowColors);
      setLabels("_gd_window_insert", meta.windowInserts);
      setLabels("_gd_window_position", meta.windowPositions);

      if (
        (Array.isArray(meta.sources) && meta.sources.includes("track_system")) ||
        (Array.isArray(meta.productTypes) && meta.productTypes.includes("track_system"))
      ) {
        properties._gd_is_track_system = "true";
      }

      const result = {
        quantity: item.quantity,
        properties
      };
      if (Number(item && item.id ? item.id : 0) > 0) {
        result.id = Number(item.id);
      } else {
        if (String(item && item.title ? item.title : "").trim()) result.title = String(item.title).trim();
        if (String(item && item.sku ? item.sku : "").trim()) result.sku = String(item.sku).trim();
        if (item && item.price !== undefined && item.price !== null && String(item.price).trim() !== "") {
          result.price = String(item.price).trim();
        }
        if (item && item.requiresShipping !== undefined) result.requiresShipping = Boolean(item.requiresShipping);
        if (item && item.taxable !== undefined) result.taxable = Boolean(item.taxable);
      }
      return result;
    }

    function formatSyntheticLineItemPrice(priceCents) {
      const cents = Number(priceCents || 0);
      if (!Number.isFinite(cents)) return "0.00";
      return (cents / 100).toFixed(2);
    }

    function buildSyntheticWindowLineItemTitle(config) {
      const value = config && typeof config === "object" ? config : {};
      return buildSyntheticNoInsertWindowTitle(value.productTitle, value.sizeCode, value.glassLabel, value.colorLabel);
    }

    function buildMergedItems() {
      const invalid = [];
      const items = [];

      getCommittedDoors().forEach((door) => {
        const doorQuantity = getDoorQuantityValue(door);
        const result = withActiveDoorState(door, () => {
          const merged = new Map();
          const nextInvalid = [];
          const doorConfig = getCurrentDoorVisualConfig();
          const windowPositionStep = getWindowPositionStep();
          const windowRows = windowPositionStep ? getWindowGridRows(windowPositionStep, doorConfig) : [];

          const entryVariant = getSelectedEntryVariant();
          const entryVariantId = Number(entryVariant && entryVariant.id ? entryVariant.id : state.entryContext.variantId || 0);
          const entryVariantAvailable = entryVariant ? entryVariant.available !== false : entryVariantId > 0;
          if (entryVariantId > 0) {
            if (!entryVariantAvailable) {
              nextInvalid.push({
                optionLabel: entry.doorCollectionTitle || "Door Collection",
                stepTitle: entry.doorCollectionTitle || "Door Collection",
                title: (entryVariant && entryVariant.title) || ""
              });
            } else {
              const mergedItem = ensureMergedItemRecord(merged, `variant:${entryVariantId}`, {
                id: entryVariantId
              });
              mergedItem.quantity += doorQuantity;
              annotateMergedItemRecord(mergedItem, {
                source: "entry_product",
                productType: "garage_door",
                optionLabel: entry.doorCollectionTitle || "Door Collection",
                componentTitle: (entryVariant && entryVariant.title) || state.entryContext.variantTitle || state.entryContext.productTitle || "",
                componentSku: (entryVariant && entryVariant.sku) || state.entryContext.variantSku || ""
              });
            }
          }

          getOrderedWindowCells().forEach((cell) => {
            const config = cell && cell.config ? cell.config : null;
            const variantId = Number(config && config.variantId ? config.variantId : 0);
            const syntheticWindowSku = String(config && config.variantSku ? config.variantSku : "").trim();
            const allowSyntheticWindowLine = isApiOrderMode() && !variantId && syntheticWindowSku;
            if (!(variantId > 0) && !allowSyntheticWindowLine) {
              nextInvalid.push({
                optionLabel: (config && config.productTitle) || (config && config.layoutLabel) || "Window",
                stepTitle: "Window",
                title: (config && config.variantTitle) || ""
              });
              return;
            }

            const mergedItem = variantId > 0
              ? ensureMergedItemRecord(merged, `variant:${variantId}`, {
                  id: variantId
                })
              : ensureMergedItemRecord(merged, `sku:${syntheticWindowSku}`, {
                  id: 0,
                  sku: syntheticWindowSku,
                  title: buildSyntheticWindowLineItemTitle(config),
                  price: formatSyntheticLineItemPrice(config && config.priceCents),
                  requiresShipping: true,
                  taxable: true
                });
            mergedItem.quantity += doorQuantity;
            annotateMergedItemRecord(mergedItem, {
              source: "window_layout",
              productType: "window_layout",
              stepTitle: "Window",
              optionLabel: (config && config.layoutLabel) || (config && config.productTitle) || "Window",
              componentTitle: (config && config.variantTitle) || "",
              componentSku: (config && config.variantSku) || "",
              windowLayout: (config && config.layoutLabel) || "",
              windowGlass: (config && config.glassLabel) || "",
              windowColor: (config && config.colorLabel) || "",
              windowInsert: (config && config.insertLabel) || "",
              windowPosition: buildWindowCellDisplayLabel(cell, windowRows)
            });
          });

          for (const step of getVisibleSteps()) {
            if (isWindowEmbeddedStep(step)) continue;
            const selectedIds = state.selected.get(step.id);
            if (!selectedIds || selectedIds.size === 0) continue;

            for (const optionId of selectedIds.values()) {
              const option = findOption(step, optionId);
              if (!option) continue;

              for (const component of option.components || []) {
                const variantId = Number(component.variant_id || 0);
                const quantity = Number(component.qty || 1);
                const available = component.available !== false;

                if (!variantId || quantity <= 0 || !available) {
                  nextInvalid.push({
                    optionLabel: option.label || "Option",
                    stepTitle: step.title || "Step",
                    title: component.title || ""
                  });
                  continue;
                }

                const mergedItem = ensureMergedItemRecord(merged, `variant:${variantId}`, {
                  id: variantId
                });
                mergedItem.quantity += quantity * doorQuantity;
                annotateMergedItemRecord(mergedItem, {
                  source: "config_step",
                  productType: "component",
                  stepHandle: step.handle || step.id || "",
                  stepTitle: step.title || "",
                  optionLabel: option.label || "",
                  componentTitle: component.title || "",
                  componentSku: component.sku || "",
                  isTrackSystem: isTrackSystemStep(step)
                });
              }
            }
          }

          return {
            invalid: nextInvalid,
            items: Array.from(merged.values()).map((item) => finalizeMergedItemRecord(item))
          };
        });

        const doorLabel = getDoorLabel(door);
        const doorIndex = getDoorIndex(door) + 1;
        result.invalid.forEach((item) => {
          invalid.push({
            ...item,
            doorId: door.id,
            doorIndex,
            doorLabel
          });
        });
        result.items.forEach((item) => {
          const lineProperties = item && item.properties && typeof item.properties === "object" ? item.properties : {};
          const specialTrack = lineProperties._gd_is_track_system === "true"
            ? withActiveDoorState(door, () => getSpecialTrackProfile()) : null;
          items.push({
            ...item,
            properties: {
              ...lineProperties,
              ...(specialTrack ? {
                _gd_special_track: JSON.stringify(specialTrack),
                ...Object.fromEntries(getSpecialTrackSummaryFields(specialTrack).map((field) => [field.label, field.value]))
              } : {}),
              _gd_door_id: door.id,
              _gd_door_index: String(doorIndex),
              _gd_door_label: doorLabel,
              _gd_door_quantity: String(doorQuantity)
            }
          });
        });
      });

      return {
        invalid,
        items
      };
    }

    function generateConfigId() {
      const randomPart = Math.random().toString(36).slice(2, 8);
      return `gd_${Date.now().toString(36)}_${randomPart}`;
    }

    function resolveOrderApiEndpointUrl() {
      const endpoint = String(entry.orderApiEndpoint || "").trim();
      if (!endpoint) return "";

      if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
        return endpoint;
      }

      if (endpoint.startsWith("/")) {
        return `${window.location.origin}${endpoint}`;
      }

      return `${window.location.origin}/${endpoint}`;
    }

    function normalizeOrderSuccessRedirectUrl(value) {
      const raw = String(value || "").trim();
      if (!raw) return "";
      if (/^https?:\/\//i.test(raw)) return raw;
      if (raw.startsWith("//")) return `${window.location.protocol}${raw}`;
      if (raw.startsWith("/")) return `${window.location.origin}${raw}`;
      return "";
    }

    function resolveOrderSuccessRedirectUrl(parsedState) {
      const configured = normalizeOrderSuccessRedirectUrl(entry.orderSuccessRedirectUrl || "");
      if (configured) return configured;

      const fromResponse = normalizeOrderSuccessRedirectUrl(
        (parsedState && parsedState.redirectUrl) ||
          (parsedState && parsedState.order && parsedState.order.url) ||
          (parsedState && parsedState.draftOrder && parsedState.draftOrder.invoiceUrl) ||
          ""
      );
      if (fromResponse) return fromResponse;

      return `${window.location.origin}/account/orders`;
    }

    function truncateOrderAttributeValue(value, maxLength = 900) {
      const text = String(value || "").trim();
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.slice(0, maxLength);
    }

    function getOrderPreviewCaptureTargetElement() {
      if (!previewEl) return null;
      return (
        previewEl.querySelector(".gd-preview-door__canvas") ||
        previewEl.querySelector(".gd-preview-window__panel") ||
        previewEl.querySelector(".gd-preview-door__surface") ||
        previewEl.querySelector(".gd-preview-door") ||
        previewEl.querySelector(".gd-preview-window") ||
        null
      );
    }

    function encodeCanvasDataUrl(canvas) {
      if (!(canvas instanceof HTMLCanvasElement)) return "";
      let pngData = "";
      try {
        pngData = canvas.toDataURL("image/png");
        if (pngData && pngData.length <= PREVIEW_CAPTURE_DATA_URL_SOFT_LIMIT) {
          return pngData;
        }
      } catch (error) {}

      let fallbackJpeg = "";
      for (const quality of [0.96, 0.92, 0.88, 0.84]) {
        try {
          const jpegData = canvas.toDataURL("image/jpeg", quality);
          if (!jpegData) continue;
          fallbackJpeg = jpegData;
          if (jpegData.length <= PREVIEW_CAPTURE_DATA_URL_SOFT_LIMIT) {
            return jpegData;
          }
        } catch (error) {}
      }

      return fallbackJpeg || pngData || "";
    }

    function resolvePreviewCaptureSourceSize(target) {
      const candidates = [];

      if (target instanceof Element) {
        const rect = target.getBoundingClientRect();
        candidates.push([
          Math.round(rect.width || target.clientWidth || 0),
          Math.round(rect.height || target.clientHeight || 0)
        ]);
      }

      const surface = previewEl ? previewEl.querySelector(".gd-preview-door__surface") : null;
      if (surface instanceof Element) {
        const rect = surface.getBoundingClientRect();
        candidates.push([
          Math.round(rect.width || surface.clientWidth || 0),
          Math.round(rect.height || surface.clientHeight || 0)
        ]);
      }

      if (previewEl instanceof Element) {
        const rect = previewEl.getBoundingClientRect();
        candidates.push([
          Math.round(rect.width || previewEl.clientWidth || 0),
          Math.round(rect.height || previewEl.clientHeight || 0)
        ]);
      }

      for (const [width, height] of candidates) {
        if (width > 0 && height > 0) {
          return {
            sourceWidth: Math.max(1, width),
            sourceHeight: Math.max(1, height)
          };
        }
      }

      return {
        sourceWidth: 980,
        sourceHeight: 560
      };
    }

    function getPreviewCaptureOutputSize(sourceWidth, sourceHeight) {
      const safeSourceWidth = Math.max(1, Math.round(Number(sourceWidth) || 0));
      const safeSourceHeight = Math.max(1, Math.round(Number(sourceHeight) || 0));
      const targetWidth = clampNumber(
        safeSourceWidth,
        PREVIEW_CAPTURE_MIN_OUTPUT_WIDTH,
        PREVIEW_CAPTURE_MAX_OUTPUT_WIDTH
      );
      const scale = targetWidth / safeSourceWidth;
      return {
        outputWidth: Math.max(1, Math.round(safeSourceWidth * scale)),
        outputHeight: Math.max(1, Math.round(safeSourceHeight * scale))
      };
    }

    function getSingleDoorPreviewCaptureOutputSize(model, sourceWidth, sourceHeight) {
      const base = getPreviewCaptureOutputSize(sourceWidth, sourceHeight);
      const outputWidth = base.outputWidth;
      const aspect = Math.max(0.5, Number(model && model.doorConfig && model.doorConfig.aspect ? model.doorConfig.aspect : 1.14));
      const outerPadding = Math.max(12, Math.round(outputWidth * 0.032));
      const tileWidth = Math.max(80, outputWidth - outerPadding * 2);
      const headerBlock = Math.max(72, Math.round(outputWidth * 0.062));
      const footerBlock = Math.max(26, Math.round(outputWidth * 0.028));
      const doorPaddingX = Math.max(10, Math.round(tileWidth * 0.055));
      const effectiveDoorWidth = Math.max(40, tileWidth - doorPaddingX * 2);
      const estimatedDoorHeight = effectiveDoorWidth / aspect;
      const outputHeight = Math.round(
        outerPadding * 2 +
          headerBlock +
          estimatedDoorHeight +
          footerBlock
      );

      return {
        outputWidth,
        outputHeight: Math.max(520, Math.min(1700, outputHeight))
      };
    }

    function drawRoundedRectPath(context, x, y, width, height, radius) {
      const safeRadius = Math.max(0, Math.min(Number(radius) || 0, width / 2, height / 2));
      context.beginPath();
      context.moveTo(x + safeRadius, y);
      context.lineTo(x + width - safeRadius, y);
      context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
      context.lineTo(x + width, y + height - safeRadius);
      context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
      context.lineTo(x + safeRadius, y + height);
      context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
      context.lineTo(x, y + safeRadius);
      context.quadraticCurveTo(x, y, x + safeRadius, y);
      context.closePath();
    }

    function formatCaptureWindowSizeLabel(windowDims) {
      const widthIn = Number(windowDims && windowDims.widthIn ? windowDims.widthIn : 0);
      const heightIn = Number(windowDims && windowDims.heightIn ? windowDims.heightIn : 0);
      if (!(widthIn > 0) || !(heightIn > 0)) return "";
      return `${Math.round(heightIn)}x${Math.round(widthIn)}`;
    }

    function fitCaptureTextSize(context, text, preferredSize, maxWidth, minSize = 8) {
      if (!context || !text) return Math.max(minSize, Number(preferredSize) || minSize);
      const safeMaxWidth = Math.max(1, Number(maxWidth) || 0);
      let size = Math.max(minSize, Number(preferredSize) || minSize);
      while (size > minSize) {
        context.font = `600 ${size}px Arial, sans-serif`;
        if (context.measureText(text).width <= safeMaxWidth) break;
        size -= 1;
      }
      return Math.max(minSize, size);
    }

    function resolvePreviewDoorFillStops(colorLabel) {
      const key = resolveKnownColorKey(colorLabel);
      if (key === "black") return ["#404852", "#1f262e"];
      if (key === "charcoal") return ["#8e96a2", "#59626d"];
      if (key === "iron ore") return ["#7c8187", "#454a51"];
      if (key === "basalt black") return ["#697079", "#343b43"];
      if (key === "dark brown") return ["#766359", "#4c3f38"];
      if (key === "basalt brown") return ["#87776d", "#544942"];
      if (key === "brown") return ["#87675c", "#5f463e"];
      if (key === "woodgrain") return ["#a5816f", "#6f5548"];
      if (key === "teak") return ["#b28f4f", "#72552c"];
      if (key === "mahogany") return ["#745748", "#433228"];
      if (key === "dark walnut") return ["#735d4c", "#3c2d23"];
      if (key === "plank") return ["#b89470", "#7f613f"];
      if (key === "almond") return ["#f4efe5", "#ddd3c1"];
      if (key === "sandstone") return ["#d9d5cc", "#b6b2a7"];
      return ["#f5f7fa", "#dfe4ea"];
    }

    function buildDoorCaptureModel(door) {
      return withActiveDoorState(door, () => {
        const entryProfile = getEntryProfile();
        const doorConfig = getCurrentDoorVisualConfig();
        const positionRule = getWindowPositionRule();
        const rows = Math.max(1, Number(getEffectiveWindowRowCount(doorConfig)));
        const selectedCells = getOrderedWindowCells(doorConfig).filter((cell) => {
          if (!cell || cell.row < 1 || cell.row > rows || cell.col < 1) return false;
          const cellRule = getWindowPositionRuleForConfig(cell.config, positionRule);
          const cols = Math.max(1, Number(cellRule && cellRule.cols ? cellRule.cols : 1));
          return cell.col <= cols;
        });

        return {
          id: door && door.id ? String(door.id) : "",
          label: getDoorLabel(door),
          entryProfile,
          doorConfig,
          positionRule,
          selectedCells
        };
      });
    }

    function drawDoorCaptureSideMeasurements(context, metrics) {
      if (!context || !metrics) return;
      const panelHeights = Array.isArray(metrics.panelHeights)
        ? metrics.panelHeights.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
        : [];
      if (panelHeights.length === 0) return;

      const doorX = Number(metrics.doorX || 0);
      const doorY = Number(metrics.doorY || 0);
      const doorHeight = Math.max(1, Number(metrics.doorHeight || 0));
      const labelRightX = Number(metrics.labelRightX || 0);
      const lineEndX = Math.max(labelRightX + 6, Number(metrics.lineEndX || labelRightX));
      const fontSize = Math.max(10, Number(metrics.fontSize || 10));
      const totalHeight = panelHeights.reduce((sum, value) => sum + value, 0) || panelHeights.length;

      context.save();
      context.fillStyle = "rgba(73, 84, 97, 0.9)";
      context.strokeStyle = "rgba(111, 122, 136, 0.82)";
      context.lineWidth = 1;
      context.font = `600 ${fontSize}px Arial, sans-serif`;
      context.textAlign = "right";
      context.textBaseline = "middle";

      let rowTop = doorY;
      panelHeights.forEach((height, index) => {
        const rowHeight =
          index === panelHeights.length - 1 ? doorY + doorHeight - rowTop : (doorHeight * height) / totalHeight;
        const label = formatDoorPreviewInchesLabel(height);
        if (label) {
          const centerY = rowTop + rowHeight / 2;
          context.fillText(label, labelRightX, centerY);
          context.beginPath();
          context.moveTo(labelRightX + 6, centerY);
          context.lineTo(lineEndX, centerY);
          context.stroke();
        }
        rowTop += rowHeight;
      });

      context.restore();
    }

    function drawDoorCaptureBottomMeasurement(context, metrics) {
      if (!context || !metrics) return;
      const widthLabel = formatDoorPreviewFeetInchesLabel(metrics.widthIn);
      if (!widthLabel) return;

      const doorX = Number(metrics.doorX || 0);
      const doorY = Number(metrics.doorY || 0);
      const doorWidth = Math.max(1, Number(metrics.doorWidth || 0));
      const doorHeight = Math.max(1, Number(metrics.doorHeight || 0));
      const offsetY = Math.max(18, Number(metrics.offsetY || 18));
      const fontSize = Math.max(10, Number(metrics.fontSize || 10));
      const centerX = doorX + doorWidth / 2;
      const centerY = doorY + doorHeight + offsetY;
      const labelText = `Width: ${widthLabel}`;

      context.save();
      context.fillStyle = "rgba(73, 84, 97, 0.94)";
      context.strokeStyle = "rgba(111, 122, 136, 0.82)";
      context.lineWidth = 1;
      context.font = `700 ${fontSize}px Arial, sans-serif`;
      context.textAlign = "center";
      context.textBaseline = "middle";

      const textWidth = context.measureText(labelText).width;
      const gap = Math.max(16, fontSize * 0.85);
      const lineY = centerY;
      const availableHalf = Math.max(22, Math.min(doorWidth * 0.25, 54));

      context.beginPath();
      context.moveTo(centerX - textWidth / 2 - gap - availableHalf, lineY);
      context.lineTo(centerX - textWidth / 2 - gap, lineY);
      context.moveTo(centerX + textWidth / 2 + gap, lineY);
      context.lineTo(centerX + textWidth / 2 + gap + availableHalf, lineY);
      context.stroke();
      context.fillText(labelText, centerX, centerY);
      context.restore();
    }

    function drawDoorCaptureTile(context, tileRect, model, index, totalCount) {
      if (!context || !tileRect || !model) return;
      const tileX = Number(tileRect.x || 0);
      const tileY = Number(tileRect.y || 0);
      const tileWidth = Math.max(40, Number(tileRect.width || 0));
      const tileHeight = Math.max(40, Number(tileRect.height || 0));
      const headerHeight = Math.max(24, Math.round(tileHeight * 0.11));
      const baseDoorPaddingX = Math.max(10, Math.round(tileWidth * 0.055));
      const panelHeights = Array.isArray(model.doorConfig && model.doorConfig.panelHeightsIn) && model.doorConfig.panelHeightsIn.length
        ? model.doorConfig.panelHeightsIn.map((height) => Number(height)).filter((height) => Number.isFinite(height) && height > 0)
        : [];
      const measurementSideWidth = panelHeights.length > 0 ? Math.max(34, Math.round(tileWidth * 0.09)) : 0;
      const measurementSideGap = panelHeights.length > 0 ? Math.max(8, Math.round(tileWidth * 0.02)) : 0;
      const measurementBottomHeight =
        Number(model.doorConfig && model.doorConfig.widthIn ? model.doorConfig.widthIn : 0) > 0
          ? Math.max(24, Math.round(tileHeight * 0.075))
          : 0;
      const doorPaddingLeft = baseDoorPaddingX + measurementSideWidth + measurementSideGap;
      const doorPaddingRight = baseDoorPaddingX;
      const doorPaddingBottom = Math.max(10, Math.round(tileHeight * 0.05)) + measurementBottomHeight;
      const doorAvailableWidth = Math.max(40, tileWidth - doorPaddingLeft - doorPaddingRight);
      const doorAvailableHeight = Math.max(40, tileHeight - headerHeight - doorPaddingBottom);
      const aspect = Math.max(0.5, Number(model.doorConfig && model.doorConfig.aspect ? model.doorConfig.aspect : 1.14));

      let doorWidth = doorAvailableWidth;
      let doorHeight = doorWidth / aspect;
      if (doorHeight > doorAvailableHeight) {
        doorHeight = doorAvailableHeight;
        doorWidth = doorHeight * aspect;
      }
      doorWidth = Math.max(28, doorWidth);
      doorHeight = Math.max(28, doorHeight);

      const doorX = tileX + doorPaddingLeft + (doorAvailableWidth - doorWidth) / 2;
      const doorY = tileY + headerHeight + Math.max(6, Math.round(tileHeight * 0.014));
      const cornerRadius = Math.max(2, Math.round(Math.min(doorWidth, doorHeight) * 0.01));

      context.fillStyle = "#23303d";
      context.font = `${Math.max(11, Math.round(tileHeight * 0.048))}px Arial, sans-serif`;
      context.textAlign = "left";
      context.textBaseline = "top";
      context.fillText(String(model.label || `Door ${index + 1}`), tileX + 12, tileY + 10);

      const summaryText = String(model.entryProfile && model.entryProfile.sizeLabel ? model.entryProfile.sizeLabel : "").trim();
      if (summaryText) {
        const sizeFont = fitCaptureTextSize(
          context,
          summaryText,
          Math.max(12, Math.round(tileHeight * 0.05)),
          tileWidth * 0.72,
          10
        );
        context.fillStyle = "#5d6977";
        context.font = `600 ${sizeFont}px Arial, sans-serif`;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(summaryText, tileX + tileWidth / 2, tileY + headerHeight * 0.44);
      }

      const fillStops = resolvePreviewDoorFillStops(String(model.entryProfile && model.entryProfile.colorLabel ? model.entryProfile.colorLabel : ""));
      const doorGradient = context.createLinearGradient(doorX, doorY, doorX, doorY + doorHeight);
      doorGradient.addColorStop(0, fillStops[0]);
      doorGradient.addColorStop(1, fillStops[1]);

      drawRoundedRectPath(context, doorX, doorY, doorWidth, doorHeight, cornerRadius);
      context.fillStyle = doorGradient;
      context.fill();
      context.lineWidth = Math.max(1, Math.round(tileWidth * 0.006));
      context.strokeStyle = "rgba(26, 33, 43, 0.62)";
      context.stroke();

      const panelCount =
        panelHeights.length > 0 ? panelHeights.length : Math.max(3, Number(model.doorConfig && model.doorConfig.panelCount ? model.doorConfig.panelCount : 4));
      const effectivePanelHeights = panelHeights.length > 0 ? panelHeights : Array.from({ length: panelCount }, () => 1);
      const panelTotal = effectivePanelHeights.reduce((sum, value) => sum + value, 0) || panelCount;

      let rowY = doorY;
      for (let panelIndex = 0; panelIndex < panelCount; panelIndex += 1) {
        const ratio = effectivePanelHeights[panelIndex] / panelTotal;
        const rowHeight = panelIndex === panelCount - 1 ? doorY + doorHeight - rowY : doorHeight * ratio;
        const rowGradient = context.createLinearGradient(doorX, rowY, doorX, rowY + rowHeight);
        rowGradient.addColorStop(0, "rgba(255,255,255,0.16)");
        rowGradient.addColorStop(0.55, "rgba(255,255,255,0.04)");
        rowGradient.addColorStop(1, "rgba(0,0,0,0.1)");
        context.fillStyle = rowGradient;
        context.fillRect(doorX, rowY, doorWidth, rowHeight);

        if (panelIndex > 0) {
          context.strokeStyle = "rgba(31, 40, 52, 0.44)";
          context.lineWidth = 1;
          context.beginPath();
          context.moveTo(doorX, rowY);
          context.lineTo(doorX + doorWidth, rowY);
          context.stroke();
        }

        rowY += rowHeight;
      }

      const sectionCount = Math.max(1, Number(model.doorConfig && model.doorConfig.sectionCount ? model.doorConfig.sectionCount : 4));
      for (let sectionIndex = 1; sectionIndex < sectionCount; sectionIndex += 1) {
        const x = doorX + (doorWidth * sectionIndex) / sectionCount;
        context.strokeStyle = "rgba(24, 32, 42, 0.34)";
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(x, doorY);
        context.lineTo(x, doorY + doorHeight);
        context.stroke();
      }

      if (Array.isArray(model.selectedCells) && model.selectedCells.length > 0) {
        const fallbackRule = model.positionRule || getWindowPositionRule();
        const rows = Math.max(1, Number(getEffectiveWindowRowCount(model.doorConfig)));
        const zoneTopPx = (doorHeight * WINDOW_ZONE_TOP_PCT) / 100;
        const zoneBottomPx = (doorHeight * WINDOW_ZONE_BOTTOM_PCT) / 100;
        const zoneHeightPx = Math.max(1, doorHeight - zoneTopPx - zoneBottomPx);

        model.selectedCells.forEach((cell) => {
          const cellRule = getWindowPositionRuleForConfig(cell && cell.config, fallbackRule);
          const cols = Math.max(1, Number(cellRule && cellRule.cols ? cellRule.cols : 1));
          const metrics = getWindowPreviewMetrics(model.doorConfig, cellRule, getWindowDimensionsFromConfig(cell && cell.config));
          const xPct = Number(metrics.colCenters && metrics.colCenters[cell.col - 1]);
          const yPct = Number(metrics.rowCenters && metrics.rowCenters[cell.row - 1]);
          const windowWidthPx = doorWidth * (Math.max(0, Number(metrics && metrics.widthPct ? metrics.widthPct : 0)) / 100);
          const windowHeightPx = zoneHeightPx * (Math.max(0, Number(metrics && metrics.heightPct ? metrics.heightPct : 0)) / 100);
          if (!Number.isFinite(xPct) || !Number.isFinite(yPct)) return;
          if (!(windowWidthPx > 0) || !(windowHeightPx > 0)) return;
          if (cell.row < 1 || cell.row > rows || cell.col < 1 || cell.col > cols) return;

          const centerX = doorX + (doorWidth * xPct) / 100;
          const centerY = doorY + zoneTopPx + (zoneHeightPx * yPct) / 100;
          const windowX = centerX - windowWidthPx / 2;
          const windowY = centerY - windowHeightPx / 2;
          const windowRadius = Math.max(1, Math.round(tileWidth * 0.004));

          drawRoundedRectPath(context, windowX, windowY, windowWidthPx, windowHeightPx, windowRadius);
          const windowGradient = context.createLinearGradient(windowX, windowY, windowX, windowY + windowHeightPx);
          windowGradient.addColorStop(0, "rgba(198, 214, 233, 0.96)");
          windowGradient.addColorStop(1, "rgba(140, 162, 187, 0.92)");
          context.fillStyle = windowGradient;
          context.fill();
          context.lineWidth = 1;
          context.strokeStyle = "rgba(21, 28, 38, 0.82)";
          context.stroke();

          const windowLabel = buildWindowPreviewLabel(cell && cell.config);
          drawCaptureWindowLabel(context, centerX, centerY, windowWidthPx, windowHeightPx, windowLabel);
        });
      }

      drawDoorCaptureSideMeasurements(context, {
        panelHeights,
        doorX,
        doorY,
        doorHeight,
        labelRightX: doorX - Math.max(10, measurementSideGap * 0.9),
        lineEndX: doorX - 2,
        fontSize: Math.max(11, Math.round(tileHeight * 0.034))
      });
      drawDoorCaptureBottomMeasurement(context, {
        widthIn: Number(model.doorConfig && model.doorConfig.widthIn ? model.doorConfig.widthIn : 0),
        doorX,
        doorY,
        doorWidth,
        doorHeight,
        offsetY: Math.max(18, measurementBottomHeight * 0.72),
        fontSize: Math.max(11, Math.round(tileHeight * 0.034))
      });

      if (totalCount > 1) {
        context.fillStyle = "rgba(89, 100, 112, 0.88)";
        context.font = `${Math.max(10, Math.round(tileHeight * 0.045))}px Arial, sans-serif`;
        context.textAlign = "right";
        context.textBaseline = "alphabetic";
        context.fillText(`${index + 1}/${totalCount}`, tileX + tileWidth - 12, tileY + tileHeight - 18);
        context.textAlign = "left";
      }
    }

    function fillOrderPreviewCaptureBackground(context, outputWidth, outputHeight) {
      if (!context) return;
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, outputWidth, outputHeight);
    }

    function captureDoorPreviewImageDataUrlFromModel(model, sourceWidth, sourceHeight) {
      if (!model) return "";
      const outputSize = getSingleDoorPreviewCaptureOutputSize(model, sourceWidth, sourceHeight);
      const outputWidth = outputSize.outputWidth;
      const outputHeight = outputSize.outputHeight;
      const canvas = document.createElement("canvas");
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const context = canvas.getContext("2d");
      if (!context) return "";

      fillOrderPreviewCaptureBackground(context, outputWidth, outputHeight);

      const outerPadding = Math.max(8, Math.round(outputWidth * 0.035));
      drawDoorCaptureTile(
        context,
        {
          x: outerPadding,
          y: outerPadding,
          width: Math.max(80, outputWidth - outerPadding * 2),
          height: Math.max(120, outputHeight - outerPadding * 2)
        },
        model,
        0,
        1
      );

      return encodeCanvasDataUrl(canvas);
    }

    function captureOrderPreviewImageDataUrlFromLayout(sourceWidth, sourceHeight) {
      const outputSize = getPreviewCaptureOutputSize(sourceWidth, sourceHeight);
      const outputWidth = outputSize.outputWidth;
      const outputHeight = outputSize.outputHeight;
      const canvas = document.createElement("canvas");
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const context = canvas.getContext("2d");
      if (!context) return "";

      fillOrderPreviewCaptureBackground(context, outputWidth, outputHeight);

      const models = getCommittedDoors().map((door) => buildDoorCaptureModel(door)).filter(Boolean);
      const count = Math.max(1, models.length);
      const outerPadding = Math.max(8, Math.round(outputWidth * 0.035));
      const gap = Math.max(10, Math.round(outputWidth * 0.024));
      const columns = count <= 1 ? 1 : count === 2 ? 2 : count <= 4 ? 2 : 3;
      const rows = Math.max(1, Math.ceil(count / columns));
      const tileWidth = Math.max(80, (outputWidth - outerPadding * 2 - gap * (columns - 1)) / columns);
      const tileHeight = Math.max(120, (outputHeight - outerPadding * 2 - gap * (rows - 1)) / rows);

      models.forEach((model, index) => {
        const col = index % columns;
        const row = Math.floor(index / columns);
        drawDoorCaptureTile(
          context,
          {
            x: outerPadding + col * (tileWidth + gap),
            y: outerPadding + row * (tileHeight + gap),
            width: tileWidth,
            height: tileHeight
          },
          model,
          index,
          count
        );
      });

      return encodeCanvasDataUrl(canvas);
    }

    async function captureOrderPreviewImages() {
      const target = getOrderPreviewCaptureTargetElement();
      const sourceSize = resolvePreviewCaptureSourceSize(target);
      const models = getCommittedDoors().map((door) => buildDoorCaptureModel(door)).filter(Boolean);
      const previewImages = models
        .map((model) => {
          const dataUrl = captureDoorPreviewImageDataUrlFromModel(model, sourceSize.sourceWidth, sourceSize.sourceHeight);
          if (!dataUrl) return null;
          return {
            doorId: String(model.id || ""),
            label: String(model.label || ""),
            dataUrl,
            mimeType: dataUrl.startsWith("data:image/png") ? "image/png" : "image/jpeg"
          };
        })
        .filter(Boolean);

      if (previewImages.length === 0) {
        console.warn("[gd-configurator] preview capture failed", {
          sourceWidth: sourceSize.sourceWidth,
          sourceHeight: sourceSize.sourceHeight,
          hasTarget: target instanceof Element
        });
      }

      return previewImages;
    }

    function buildLineItemCustomAttributes(properties) {
      if (!properties || typeof properties !== "object") return [];
      return Object.entries(properties)
        .map(([key, value]) => {
          return {
            key: String(key || "").trim(),
            value: String(value ?? "").trim()
          };
        })
        .filter((item) => item.key);
    }

    function parseLineMetaValues(value, delimiterPattern) {
      const text = String(value || "").trim();
      if (!text) return [];
      return text
        .split(delimiterPattern)
        .map((item) => String(item || "").trim())
        .filter(Boolean);
    }

    function buildDoorSummaryAttributeValue(doorSummaries) {
      const summaries = Array.isArray(doorSummaries) ? doorSummaries : [];
      return truncateOrderAttributeValue(
        summaries
          .map((summary) => {
            const fields = Array.isArray(summary && summary.fields) ? summary.fields : [];
            const compactFields = fields
              .map((field) => {
                const label = String(field && field.label ? field.label : "").trim();
                const value = String(field && field.value ? field.value : "").trim();
                if (!label || !value) return "";
                return `${label}: ${value}`;
              })
              .filter(Boolean)
              .join(" | ");
            return `${String(summary && summary.label ? summary.label : "Door").trim()}: ${compactFields}`.trim();
          })
          .filter(Boolean)
          .join(" || "),
        900
      );
    }

    function buildDoorSummaryNoteLines(doorSummaries) {
      const summaries = Array.isArray(doorSummaries) ? doorSummaries : [];
      return summaries
        .map((summary) => {
          const fieldList = Array.isArray(summary && summary.fields) ? summary.fields : [];
          const fieldMap = new Map();
          fieldList.forEach((field) => {
            const label = String(field && field.label ? field.label : "").trim();
            const value = String(field && field.value ? field.value : "").trim();
            if (!label || !value) return;
            fieldMap.set(label, value);
          });

          const compact = [
            fieldMap.get("Product") ? `Product: ${fieldMap.get("Product")}` : "",
            fieldMap.get("Quantity") ? `Quantity: ${fieldMap.get("Quantity")}` : "",
            fieldMap.get("Size") ? `Size: ${fieldMap.get("Size")}` : "",
            fieldMap.get("Panel Preference") ? `Panel Preference: ${fieldMap.get("Panel Preference")}` : "",
            fieldMap.get("Color") ? `Color: ${fieldMap.get("Color")}` : "",
            fieldMap.get("Window Count") ? `Window Count: ${fieldMap.get("Window Count")}` : "",
            fieldMap.get("Window Layout") ? `Window Layout: ${fieldMap.get("Window Layout")}` : "",
            fieldMap.get("Track System") ? `Track System: ${fieldMap.get("Track System")}` : ""
          ]
            .filter(Boolean)
            .join(" | ");

          if (!compact) return "";
          return `${String(summary && summary.label ? summary.label : "Door").trim()}: ${compact}`;
        })
        .filter(Boolean);
    }

    function buildAggregatedOrderApiLineItems(lineItems) {
      const grouped = new Map();

      (Array.isArray(lineItems) ? lineItems : []).forEach((lineItem) => {
        if (!lineItem) return;
        const variantId = Number(lineItem.variantId || 0);
        const manualSku = String(lineItem.sku || "").trim();
        const manualTitle = String(lineItem.title || "").trim();
        const manualPrice = lineItem.price === undefined || lineItem.price === null ? "" : String(lineItem.price).trim();
        const specialTrackAttributes = (Array.isArray(lineItem.customAttributes) ? lineItem.customAttributes : [])
          .filter((attribute) => attribute && ["_gd_special_track", "_gd_door_id"].includes(attribute.key));
        const hasSpecialTrack = specialTrackAttributes.some((attribute) => attribute.key === "_gd_special_track");
        const specialTrackKey = hasSpecialTrack ? `::special:${JSON.stringify(specialTrackAttributes)}` : "";
        const key = (variantId > 0 ? `variant:${variantId}` : `manual:${manualSku}::${manualTitle}::${manualPrice}`) + specialTrackKey;
        if (!(variantId > 0) && !manualSku && !manualTitle) return;
        if (!grouped.has(key)) {
          grouped.set(key, {
            variantId,
            sku: manualSku,
            title: manualTitle,
            price: manualPrice,
            requiresShipping: lineItem.requiresShipping === undefined ? undefined : Boolean(lineItem.requiresShipping),
            taxable: lineItem.taxable === undefined ? undefined : Boolean(lineItem.taxable),
            quantity: 0,
            sourceTypes: new Set(),
            productTypes: new Set(),
            stepHandles: new Set(),
            stepTitles: new Set(),
            optionLabels: new Set(),
            componentTitles: new Set(),
            customAttributeMap: new Map(),
            isTrackSystem: false
          });
        }

        const group = grouped.get(key);
        group.quantity += Math.max(0, Number(lineItem.quantity || 0));
        (Array.isArray(lineItem.sourceTypes) ? lineItem.sourceTypes : []).forEach((value) => group.sourceTypes.add(String(value || "").trim()));
        (Array.isArray(lineItem.productTypes) ? lineItem.productTypes : []).forEach((value) => group.productTypes.add(String(value || "").trim()));
        (Array.isArray(lineItem.stepHandles) ? lineItem.stepHandles : []).forEach((value) => group.stepHandles.add(String(value || "").trim()));
        (Array.isArray(lineItem.stepTitles) ? lineItem.stepTitles : []).forEach((value) => group.stepTitles.add(String(value || "").trim()));
        (Array.isArray(lineItem.optionLabels) ? lineItem.optionLabels : []).forEach((value) => group.optionLabels.add(String(value || "").trim()));
        (Array.isArray(lineItem.componentTitles) ? lineItem.componentTitles : []).forEach((value) => group.componentTitles.add(String(value || "").trim()));
        (Array.isArray(lineItem.customAttributes) ? lineItem.customAttributes : []).forEach((attribute) => {
          appendUniqueAttributeValue(group.customAttributeMap, attribute && attribute.key, attribute && attribute.value);
        });
        if (lineItem.isTrackSystem) {
          group.isTrackSystem = true;
        }
      });

      return Array.from(grouped.values())
        .filter((group) => group.quantity > 0)
        .map((group) => {
          const result = {
            quantity: group.quantity,
            sourceTypes: Array.from(group.sourceTypes.values()).filter(Boolean),
            productTypes: Array.from(group.productTypes.values()).filter(Boolean),
            stepHandles: Array.from(group.stepHandles.values()).filter(Boolean),
            stepTitles: Array.from(group.stepTitles.values()).filter(Boolean),
            optionLabels: Array.from(group.optionLabels.values()).filter(Boolean),
            componentTitles: Array.from(group.componentTitles.values()).filter(Boolean),
            isTrackSystem: group.isTrackSystem,
            customAttributes: finalizeAggregatedCustomAttributes(group.customAttributeMap)
          };
          if (group.variantId > 0) {
            result.variantId = group.variantId;
          } else {
            if (group.sku) result.sku = group.sku;
            if (group.title) result.title = group.title;
            if (group.price) result.price = group.price;
            if (group.requiresShipping !== undefined) result.requiresShipping = Boolean(group.requiresShipping);
            if (group.taxable !== undefined) result.taxable = Boolean(group.taxable);
          }
          return result;
        });
    }

    function buildOrderApiPayload(
      items,
      configId,
      entryProfile,
      windowProfile,
      previewImages,
      poNumber,
      orderDate,
      pickupDate,
      customerId,
      customerNote,
      doorSummaries = [],
      financials = null,
      depositMethod = ""
    ) {
      const customerIdentity = getCustomerIdentity();
      const safePreviewImages = (Array.isArray(previewImages) ? previewImages : [])
        .map((image, index) => {
          const dataUrl = truncateOrderAttributeValue(image && image.dataUrl ? image.dataUrl : "", 750000);
          if (!dataUrl) return null;
          return {
            label: buildPreviewImageUploadLabel(image && image.label ? image.label : "", index),
            dataUrl,
            mimeType: String(image && image.mimeType ? image.mimeType : dataUrl.startsWith("data:image/png") ? "image/png" : "image/jpeg").trim()
          };
        })
        .filter(Boolean);
      const safePoNumber = truncateOrderAttributeValue(poNumber, ORDER_PO_NUMBER_MAX_LENGTH);
      const safeOrderDate = normalizeOrderDateValue(orderDate, getTodayDateInputValue());
      const safePickupDate = normalizeOrderDateValue(pickupDate, "");
      const safeCustomerId = truncateOrderAttributeValue(customerId, ORDER_CUSTOMER_ID_MAX_LENGTH);
      const safeCustomerNote = truncateOrderAttributeValue(customerNote, ORDER_NOTE_MAX_LENGTH);
      const safeDoorSummaries = Array.isArray(doorSummaries) ? doorSummaries : [];
      const safeDoorSummaryValue = buildDoorSummaryAttributeValue(safeDoorSummaries);
      const totalDoorCount = countDoorSummaryUnits(safeDoorSummaries);
      const doorStyleCount = safeDoorSummaries.length;
      const safeFinancials =
        financials && typeof financials === "object" ? financials : getOrderFinancialSummary(totalCents());
      const safeDepositMethod = normalizeOrderDepositMethodValue(depositMethod);
      const financialNoteLines = buildOrderFinancialNoteLines(safeFinancials, safeDepositMethod);
      const qboPoFieldName = String(entry.orderApiQboPoFieldName || "").trim();
      const qboPaymentFieldName = String(entry.orderApiQboPaymentFieldName || "").trim();
      const qboPaymentFieldValue = truncateOrderAttributeValue(
        String(entry.orderApiQboPaymentFieldValue || "").trim(),
        ORDER_PO_NUMBER_MAX_LENGTH
      );
      if (safePreviewImages.length === 0) {
        throw new Error("Missing previewImages");
      }
      const customAttributes = [
        { key: "_gd_config_id", value: configId },
        { key: "_gd_config_title", value: String(config.title || "Garage door configuration") },
        { key: "_shopify_customer_id", value: customerIdentity.shopifyCustomerId || "" },
        { key: "_shopify_customer_email", value: customerIdentity.email || "" },
        { key: "_gd_entry_series", value: entryProfile.seriesLabel || "" },
        { key: "_gd_entry_size", value: entryProfile.sizeLabel || "" },
        { key: "_gd_entry_panel_preference", value: entryProfile.panelPreferenceLabel || "" },
        { key: "_gd_entry_size_mode", value: entryProfile.sizeMode === "custom" ? "custom" : "" },
        { key: "_gd_entry_custom_width", value: entryProfile.customWidth || "" },
        { key: "_gd_entry_custom_height", value: entryProfile.customHeight || "" },
        { key: "_gd_entry_color", value: entryProfile.colorLabel || "" },
        { key: "_gd_entry_variant_sku", value: entryProfile.variantSku || "" },
        { key: "_gd_entry_variant_title", value: entryProfile.variantTitle || "" },
        { key: "_gd_entry_product_title", value: entryProfile.productTitle || "" },
        { key: "_gd_window_layout", value: windowProfile.layoutLabel || "" },
        { key: "_gd_window_glass", value: windowProfile.glassLabel || "" },
        { key: "_gd_window_color", value: windowProfile.colorLabel || "" },
        { key: "_gd_window_insert", value: windowProfile.insertLabel || "" },
        { key: "_gd_window_position", value: windowProfile.positionLabel || "" },
        { key: "_gd_door_count", value: String(totalDoorCount || 0) },
        { key: "_gd_door_style_count", value: String(doorStyleCount || 0) },
        { key: "_gd_preview_image_count", value: String(safePreviewImages.length || 0) },
        { key: "_gd_doors_summary", value: safeDoorSummaryValue || "" },
        { key: "_gd_po_number", value: safePoNumber || "" },
        { key: "_gd_pickup_date", value: safePickupDate || "" },
        { key: "_gd_customer_id", value: safeCustomerId || "" },
        { key: "_gd_deposit_method", value: safeDepositMethod || "" }
      ].filter((attribute) => String(attribute.value || "").trim() !== "");

      if (safeCustomerNote) {
        customAttributes.push({ key: "_gd_customer_note", value: safeCustomerNote });
      }

      const note = [
        safeCustomerNote ? `Notes:\n${safeCustomerNote}` : "",
        safePickupDate ? `Pickup Date: ${safePickupDate}` : "",
        ...financialNoteLines
      ]
        .filter(Boolean)
        .join("\n");

      const rawLineItems = items.map((item) => {
        const lineProperties = item && item.properties && typeof item.properties === "object" ? item.properties : {};
        const sourceTypes = parseLineMetaValues(lineProperties._gd_line_sources, /\s*,\s*/);
        const productTypes = parseLineMetaValues(lineProperties._gd_line_product_types, /\s*,\s*/);
        const stepHandles = parseLineMetaValues(lineProperties._gd_line_step_handles, /\s*,\s*/);
        const stepTitles = parseLineMetaValues(lineProperties._gd_line_step_titles, /\s*\|\s*/);
        const optionLabels = parseLineMetaValues(lineProperties._gd_line_option_labels, /\s*\|\s*/);
        const componentTitles = parseLineMetaValues(lineProperties._gd_line_component_titles, /\s*\|\s*/);
        const isTrackSystem =
          sourceTypes.includes("track_system") ||
          productTypes.includes("track_system") ||
          String(lineProperties._gd_is_track_system || "")
            .trim()
            .toLowerCase() === "true";

        return {
          variantId: item.id,
          title: item.title,
          sku: item.sku,
          price: item.price,
          requiresShipping: item.requiresShipping,
          taxable: item.taxable,
          quantity: item.quantity,
          sourceTypes,
          productTypes,
          stepHandles,
          stepTitles,
          optionLabels,
          componentTitles,
          isTrackSystem,
          customAttributes: buildLineItemCustomAttributes(lineProperties)
        };
      });

      const lineItems = buildAggregatedOrderApiLineItems(rawLineItems);

      const trackSystemLineItems = lineItems
        .filter((lineItem) => lineItem && lineItem.isTrackSystem)
        .map((lineItem) => {
          return {
            variantId: lineItem.variantId,
            quantity: lineItem.quantity,
            optionLabels: Array.isArray(lineItem.optionLabels) ? lineItem.optionLabels : [],
            componentTitles: Array.isArray(lineItem.componentTitles) ? lineItem.componentTitles : []
          };
        });

      if (trackSystemLineItems.length > 0) {
        const labels = [];
        trackSystemLineItems.forEach((lineItem) => {
          (lineItem.optionLabels || []).forEach((label) => {
            if (!labels.includes(label)) labels.push(label);
          });
        });

        customAttributes.push({ key: "_gd_track_system_count", value: String(trackSystemLineItems.length) });
        if (labels.length > 0) {
          customAttributes.push({
            key: "_gd_track_system_options",
            value: truncateOrderAttributeValue(labels.join(" | "), 900)
          });
        }
      }

      const qboCustomFields = [];
      if (qboPoFieldName && safePoNumber) {
        qboCustomFields.push({
          name: qboPoFieldName,
          value: safePoNumber
        });
      }
      if (qboPaymentFieldName && qboPaymentFieldValue) {
        qboCustomFields.push({
          name: qboPaymentFieldName,
          value: qboPaymentFieldValue
        });
      }

      const payload = {
        email: customerIdentity.email || "",
        customerId: safeCustomerId || "",
        date: safeOrderDate || "",
        pickupDate: safePickupDate || "",
        shopifyCustomerId: customerIdentity.shopifyCustomerId || "",
        sourceName: "gd-configurator",
        paymentTermsName: entry.orderApiPaymentTermsName || "Due on receipt",
        pickup: true,
        pickupTitle: entry.orderApiPickupTitle || "Local pickup",
        visibleToCustomer: true,
        allowDiscountCodesInCheckout: false,
        sendInvoice: Boolean(entry.orderApiSendInvoice),
        redirectTo: entry.orderApiRedirect || "invoice",
        tags: ["gd-configurator", "invoice-only"],
        note,
        ...(safePoNumber ? { poNumber: safePoNumber } : {}),
        ...(qboCustomFields.length > 0 ? { qboCustomFields } : {}),
        customAttributes,
        doorSummaries: safeDoorSummaries,
        previewImages: safePreviewImages,
        lineItems,
        trackSystemLineItems,
        hasTrackSystemLineItem: trackSystemLineItems.length > 0
      };

      return payload;
    }

    function syncPreparedSubmissionOrderDetails(submission) {
      const prepared = submission && typeof submission === "object" ? submission : null;
      if (!prepared) return null;

      const poNumber = getPoNumber();
      const orderDate = getOrderDate();
      const pickupDate = getPickupDate();
      const customerId = getManualCustomerId();
      const customerNote = getCustomerNote();
      const depositMethod = getOrderDepositMethod();
      const financials = getOrderFinancialSummary(prepared.totalCents);

      prepared.poNumber = poNumber;
      prepared.orderDate = orderDate;
      prepared.pickupDate = pickupDate;
      prepared.customerId = customerId;
      prepared.customerNote = customerNote;
      prepared.depositMethod = depositMethod;
      prepared.financials = financials;

      if (Array.isArray(prepared.items)) {
        prepared.items = prepared.items.map((item) => {
          if (!item || typeof item !== "object") return item;
          const nextProperties = item.properties && typeof item.properties === "object" ? { ...item.properties } : {};

          if (poNumber) {
            nextProperties[SHOPIFY_PO_FIELD_NAME] = poNumber;
            nextProperties._gd_po_number = poNumber;
          } else {
            delete nextProperties[SHOPIFY_PO_FIELD_NAME];
            delete nextProperties._gd_po_number;
          }

          if (customerId) {
            nextProperties._gd_customer_id = customerId;
          } else {
            delete nextProperties._gd_customer_id;
          }

          if (pickupDate) {
            nextProperties[SHOPIFY_PICKUP_DATE_FIELD_NAME] = pickupDate;
            nextProperties._gd_pickup_date = pickupDate;
          } else {
            delete nextProperties[SHOPIFY_PICKUP_DATE_FIELD_NAME];
            delete nextProperties._gd_pickup_date;
          }

          if (customerNote) {
            nextProperties._gd_customer_note = customerNote;
          } else {
            delete nextProperties._gd_customer_note;
          }

          return {
            ...item,
            properties: nextProperties
          };
        });
      }

      return prepared;
    }

    async function requestJson(url, requestConfig) {
      const response = await fetch(url, requestConfig);
      const contentType =
        response && response.headers && typeof response.headers.get === "function"
          ? String(response.headers.get("content-type") || "")
          : "";
      let payload = null;

      try {
        if (contentType.includes("application/json")) {
          payload = await response.json();
        } else {
          const text = await response.text();
          if (text) {
            try {
              payload = JSON.parse(text);
            } catch (error) {
              payload = { message: text };
            }
          }
        }
      } catch (error) {
        payload = null;
      }

      if (!response.ok) {
        const message =
          payload && typeof payload === "object"
            ? payload.message || payload.description || payload.error || `Request failed with status ${response.status}.`
            : `Request failed with status ${response.status}.`;
        const requestError = new Error(String(message));
        requestError.status = response.status;
        requestError.payload = payload;
        throw requestError;
      }

      return payload;
    }

    async function syncShopifyOrderAttributes(poNumber, pickupDate) {
      const updateUrl =
        window.theme && window.theme.routes && window.theme.routes.cart_update_url
          ? String(window.theme.routes.cart_update_url)
          : "/cart/update.js";

      await requestJson(updateUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          attributes: {
            [SHOPIFY_PO_FIELD_NAME]: String(poNumber || "").trim(),
            [SHOPIFY_PICKUP_DATE_FIELD_NAME]: normalizeOrderDateValue(pickupDate, "")
          }
        }),
        credentials: "same-origin"
      });
    }

    async function submitPreparedOrder(submission, options = {}) {
      const prepared = submission && typeof submission === "object" ? submission : null;
      if (!prepared) {
        return {
          ok: false,
          message: "Unable to prepare this quote."
        };
      }
      if (state.adding) {
        return {
          ok: false,
          message: "This quote is already being submitted."
        };
      }

      const items = Array.isArray(prepared.items) ? prepared.items : [];
      const configId = String(prepared.configId || "").trim();
      const entryProfile = prepared.entryProfile && typeof prepared.entryProfile === "object" ? prepared.entryProfile : {};
      const windowProfile =
        prepared.windowProfile && typeof prepared.windowProfile === "object" ? prepared.windowProfile : {};
      const doorSummaries = Array.isArray(prepared.doorSummaries) ? prepared.doorSummaries : [];
      const onSuccess = options && typeof options.onSuccess === "function" ? options.onSuccess : null;

      if (prepared.apiOrderMode) {
        const endpointUrl = String(prepared.endpointUrl || "").trim();
        if (!endpointUrl) {
          setHint("Quote API endpoint is not configured.", "error");
          renderDock();
          return {
            ok: false,
            message: "Quote API endpoint is not configured."
          };
        }

        const previewImages = await captureOrderPreviewImages();

        console.log("[gd-configurator] preview capture result", {
          previewImageCount: previewImages.length,
          previewImageLabels: previewImages.map((image, index) =>
            buildPreviewImageUploadLabel(image && image.label ? image.label : "", index)
          ),
          previewImageLengths: previewImages.map((image) => String(image && image.dataUrl ? image.dataUrl.length : 0))
        });
        if (previewImages.length === 0) {
          setHint("Preview capture failed. Please refresh and try again.", "error");
          renderDock();
          return {
            ok: false,
            message: "Preview capture failed. Please refresh and try again."
          };
        }

        const orderPayload = buildOrderApiPayload(
          items,
          configId,
          entryProfile,
          windowProfile,
          previewImages,
          prepared.poNumber,
          prepared.orderDate,
          prepared.pickupDate,
          prepared.customerId,
          prepared.customerNote,
          doorSummaries,
          prepared.financials,
          prepared.depositMethod
        );
        console.log("[gd-configurator] quote payload ready", {
          date: String(orderPayload.date || ""),
          pickupDate: String(orderPayload.pickupDate || ""),
          customerId: String(orderPayload.customerId || ""),
          shopifyCustomerId: String(orderPayload.shopifyCustomerId || ""),
          customerEmail: String(orderPayload.email || ""),
          hasPoNumber: Boolean(String(orderPayload.poNumber || "").trim()),
          qboCustomFields: Array.isArray(orderPayload.qboCustomFields) ? orderPayload.qboCustomFields : [],
          lineItems: Array.isArray(orderPayload.lineItems)
            ? orderPayload.lineItems.map((item) => ({
                variantId: item.variantId,
                sku: item.sku,
                title: item.title,
                quantity: item.quantity
              }))
            : [],
          hasCustomerNote: Boolean(
            Array.isArray(orderPayload.customAttributes) &&
              orderPayload.customAttributes.find((item) => item && item.key === "_gd_customer_note")
          ),
          previewImageCount: Array.isArray(orderPayload.previewImages) ? orderPayload.previewImages.length : 0,
          previewImageLabels: Array.isArray(orderPayload.previewImages)
            ? orderPayload.previewImages.map((image) => String(image && image.label ? image.label : ""))
            : []
        });

        state.adding = true;
        setHint("Submitting quote...", "info");
        renderDock();

        try {
          const parsedState = await requestJson(endpointUrl, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json"
            },
            body: JSON.stringify(orderPayload),
            credentials: "same-origin"
          });

          if (!parsedState || parsedState.ok === false) {
            const message = parsedState && parsedState.message ? parsedState.message : "Unable to submit quote.";
            setHint(message, "error");
            return {
              ok: false,
              message
            };
          }

          setHint("Quote submitted successfully.", "success");
          renderDock();
          if (onSuccess) {
            await onSuccess();
          }
          await showOrderSuccessModal(
            "Your quote was submitted successfully. Quote details will be sent to your email.",
            parsedState && parsedState.docNumber ? parsedState.docNumber : ""
          );
          window.location.reload();
          return { ok: true };
        } catch (error) {
          console.error("[gd-configurator] quote-api error", error);
          const message =
            error && error.message ? String(error.message) : "Something went wrong while submitting your quote.";
          setHint(message, "error");
          return {
            ok: false,
            message
          };
        } finally {
          state.adding = false;
          renderDock();
        }
      }

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      const addUrl = window.theme && window.theme.routes ? window.theme.routes.cart_add_url : "/cart/add.js";

      const addConfig =
        window.theme && window.theme.utils && typeof window.theme.utils.fetchConfig === "function"
          ? window.theme.utils.fetchConfig("javascript")
          : {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json"
              }
            };

      addConfig.method = "POST";
      addConfig.headers = addConfig.headers || {};
      addConfig.headers["Content-Type"] = "application/json";
      addConfig.headers.Accept = "application/json";
      addConfig.body = JSON.stringify({
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname
      });

      state.adding = true;
      setHint("Preparing your quote...", "info");
      renderDock();

      try {
        const parsedState = await requestJson(addUrl, addConfig);

        if (parsedState && parsedState.status) {
          const message = parsedState.description || "Unable to prepare this quote.";
          setHint(message, "error");
          return {
            ok: false,
            message
          };
        }

        await syncShopifyOrderAttributes(prepared.poNumber, prepared.pickupDate);

        const checkoutPath =
          window.theme && window.theme.routes && window.theme.routes.checkout_url
            ? String(window.theme.routes.checkout_url)
            : "/checkout";
        const checkoutUrl = checkoutPath.startsWith("http")
          ? checkoutPath
          : `${window.location.origin}${checkoutPath.startsWith("/") ? checkoutPath : `/${checkoutPath}`}`;

        setHint("Redirecting to checkout...", "success");
        renderDock();
        if (onSuccess) {
          await onSuccess();
        }
        window.location.assign(checkoutUrl);
        return { ok: true };
      } catch (error) {
        console.error("[gd-configurator] add-to-cart error", error);
        const message = error && error.message ? String(error.message) : "Something went wrong while preparing this quote.";
        setHint(message, "error");
        return {
          ok: false,
          message
        };
      } finally {
        state.adding = false;
        renderDock();
      }
    }

    async function addToCart() {
      const customerIdentityBeforeSubmit = getCustomerIdentity();
      console.log("[gd-configurator] Quote Now handler start", {
        adding: state.adding,
        preflightCompleted: state.preflight.completed,
        missingRequiredCount: state.preflight.completed ? getMissingRequiredStepsForDoor(getActiveDoorState()).length : null,
        apiOrderMode: isApiOrderMode(),
        endpointUrl: isApiOrderMode() ? resolveOrderApiEndpointUrl() : "",
        customerContextEndpoint: isApiOrderMode() ? resolveCustomerContextEndpointUrl() : "",
        customerContextLoaded: state.customerContext.loaded,
        customerContextError: String(state.customerContext.error || ""),
        customerId: customerIdentityBeforeSubmit.shopifyCustomerId,
        customerEmail: customerIdentityBeforeSubmit.email,
        hasPreviewTarget: Boolean(getOrderPreviewCaptureTargetElement())
      });

      if (state.adding) return;
      if (!state.preflight.completed) {
        setHint("Start from a product page first.", "error");
        render();
        return;
      }

      const firstDoorIssue = getFirstDoorValidationIssue();
      if (firstDoorIssue && firstDoorIssue.type === "special_track") {
        openDesignModal(firstDoorIssue.door.id);
        showSpecialTrackValidationIssue(firstDoorIssue.specialTrackIssue);
        return;
      }
      if (firstDoorIssue && firstDoorIssue.type === "missing_required") {
        state.activeDoorId = firstDoorIssue.door.id;
        const firstMissing = firstDoorIssue.missing[0];
        const focusableStep = findStepById(firstMissing && firstMissing.id ? firstMissing.id : "");
        if (focusableStep && !isStepHiddenFromRail(focusableStep)) {
          state.mobileOpenStepId = focusableStep.id;
          state.activeStepId = focusableStep.id;
          state.activeOptionId = "";
        } else if (focusableStep && isWindowPositionStep(focusableStep)) {
          state.designNavSectionId = "windows";
        }
        setHint(`${firstDoorIssue.doorLabel}: please complete required step "${firstMissing.title}".`, "error");
        render();
        return;
      }

      if (firstDoorIssue && firstDoorIssue.type === "custom_size") {
        state.activeDoorId = firstDoorIssue.door.id;
        setHint(`${firstDoorIssue.doorLabel}: ${firstDoorIssue.customSize.message || "Enter both custom width and height."}`, "error");
        render();
        return;
      }

      const merged = buildMergedItems();
      const poNumber = getPoNumber();
      const orderDate = getOrderDate();
      const pickupDate = getPickupDate();
      const customerId = getManualCustomerId();
      const customerNote = getCustomerNote();

      if (merged.invalid.length > 0) {
        const firstInvalid = merged.invalid[0];
        if (firstInvalid && firstInvalid.doorId) {
          state.activeDoorId = firstInvalid.doorId;
        }
        setHint(
          `${firstInvalid && firstInvalid.doorLabel ? `${firstInvalid.doorLabel}: ` : ""}one or more selected components are unavailable. Update selections and try again.`,
          "error"
        );
        render();
        return;
      }

      if (merged.items.length === 0) {
        setHint("No components selected. Choose at least one option.", "error");
        renderDock();
        return;
      }

      const configId = generateConfigId();
      const doorSummaries = getCommittedDoors().map((door) => buildDoorReviewSummary(door));
      const firstDoorSummary = doorSummaries[0] || {};
      const entryProfile =
        firstDoorSummary.entryProfile && typeof firstDoorSummary.entryProfile === "object" ? firstDoorSummary.entryProfile : {};
      const windowProfile =
        firstDoorSummary.windowProfile && typeof firstDoorSummary.windowProfile === "object" ? firstDoorSummary.windowProfile : {};
      const orderTotalCents = totalCents();
      const financials = getOrderFinancialSummary(orderTotalCents);
      const depositMethod = getOrderDepositMethod();
      const doorSummaryById = new Map(
        doorSummaries
          .filter((summary) => summary && summary.id)
          .map((summary) => [String(summary.id), summary])
      );
      const doorSummaryValue = buildDoorSummaryAttributeValue(doorSummaries);
      const totalDoorCount = countDoorSummaryUnits(doorSummaries);
      const doorStyleCount = doorSummaries.length;
      const items = merged.items.map((item) => {
        const lineProperties = item && item.properties && typeof item.properties === "object" ? item.properties : {};
        const lineDoorId = String(lineProperties._gd_door_id || "").trim();
        const lineDoorSummary = doorSummaryById.get(lineDoorId) || null;
        const lineEntryProfile =
          lineDoorSummary && lineDoorSummary.entryProfile && typeof lineDoorSummary.entryProfile === "object"
            ? lineDoorSummary.entryProfile
            : {};
        const preparedItem = {
          quantity: item.quantity,
          properties: {
            ...lineProperties,
            _gd_config_id: configId,
            _gd_config_title: String(config.title || "Garage door configuration"),
            _gd_door_count: String(totalDoorCount || 0),
            _gd_door_style_count: String(doorStyleCount || 0),
            _gd_doors_summary: doorSummaryValue || "",
            _gd_door_quantity: String(
              lineDoorSummary && typeof lineDoorSummary === "object" ? normalizeDoorQuantityValue(lineDoorSummary.quantity) : 1
            ),
            _gd_entry_series: lineEntryProfile.seriesLabel || "",
            _gd_entry_size: lineEntryProfile.sizeLabel || "",
            _gd_entry_panel_preference: lineEntryProfile.panelPreferenceLabel || "",
            _gd_entry_size_mode: lineEntryProfile.sizeMode === "custom" ? "custom" : "",
            _gd_entry_custom_width: lineEntryProfile.customWidth || "",
            _gd_entry_custom_height: lineEntryProfile.customHeight || "",
            _gd_entry_color: lineEntryProfile.colorLabel || "",
            _gd_entry_variant_sku: lineEntryProfile.variantSku || "",
            _gd_entry_variant_title: lineEntryProfile.variantTitle || "",
            _gd_entry_product_title: lineEntryProfile.productTitle || "",
            _gd_track_system: lineDoorSummary && lineDoorSummary.trackSystemLabel ? lineDoorSummary.trackSystemLabel : "",
            ...(poNumber ? { [SHOPIFY_PO_FIELD_NAME]: poNumber } : {}),
            ...(poNumber ? { _gd_po_number: poNumber } : {}),
            ...(pickupDate ? { [SHOPIFY_PICKUP_DATE_FIELD_NAME]: pickupDate } : {}),
            ...(pickupDate ? { _gd_pickup_date: pickupDate } : {}),
            ...(customerId ? { _gd_customer_id: customerId } : {}),
            ...(customerNote ? { _gd_customer_note: customerNote } : {})
          }
        };
        if (Number(item && item.id ? item.id : 0) > 0) {
          preparedItem.id = item.id;
        }
        if (String(item && item.title ? item.title : "").trim()) {
          preparedItem.title = String(item.title).trim();
        }
        if (String(item && item.sku ? item.sku : "").trim()) {
          preparedItem.sku = String(item.sku).trim();
        }
        if (item && item.price !== undefined && item.price !== null && String(item.price).trim() !== "") {
          preparedItem.price = String(item.price).trim();
        }
        if (item && item.requiresShipping !== undefined) {
          preparedItem.requiresShipping = Boolean(item.requiresShipping);
        }
        if (item && item.taxable !== undefined) {
          preparedItem.taxable = Boolean(item.taxable);
        }
        return preparedItem;
      });

      let endpointUrl = "";
      if (isApiOrderMode()) {
        endpointUrl = resolveOrderApiEndpointUrl();
        if (!endpointUrl) {
          setHint("Quote API endpoint is not configured.", "error");
          renderDock();
          return;
        }

        const customerIdentity = await fetchCustomerContext(true);
        if (String(state.customerContext.error || "").trim()) {
          setHint("Unable to verify account session. Please refresh and try again.", "error");
          renderDock();
          return;
        }
        logCustomerIdentity("order-click-after-context");
        if (!customerIdentity.shopifyCustomerId) {
          const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          const loginUrl = `/customer_authentication/login?return_to=${encodeURIComponent(returnUrl)}`;
          console.log("[gd-configurator] missing customer identity", { loginUrl });
          setHint("Please sign in to your customer account before requesting this quote.", "error");
          renderDock();
          window.location.assign(loginUrl);
          return;
        }
      }

      const preparedSubmission = {
        apiOrderMode: isApiOrderMode(),
        endpointUrl,
        configId,
        entryProfile,
        windowProfile,
        doorSummaries,
        items,
        poNumber,
        orderDate,
        pickupDate,
        customerId,
        customerNote,
        financials,
        depositMethod,
        hasCustomPrice: hasAnyDoorWithoutPrice(),
        totalCents: orderTotalCents
      };
      const reviewSnapshot = buildOrderReviewSnapshot(preparedSubmission);

      setHint("");
      renderDock();

      await showOrderReviewModal(reviewSnapshot, async ({ close }) => {
        syncPreparedSubmissionOrderDetails(preparedSubmission);
        return submitPreparedOrder(preparedSubmission, {
          onSuccess: close
        });
      });
    }

    railEl.addEventListener("click", (event) => {
      const clickedEntryDropdown = event.target.closest("[data-gd-entry-dropdown]");
      const closedEntryDropdownFromRailClick = Boolean(
        state.entryDropdownOpenKey && (!clickedEntryDropdown || !railEl.contains(clickedEntryDropdown))
      );
      if (closedEntryDropdownFromRailClick) {
        closeEntryDropdown();
      }

      const openDesignButton = event.target.closest("[data-gd-open-design-modal]");
      if (openDesignButton && railEl.contains(openDesignButton)) {
        openDesignModal();
        return;
      }

      const closeDesignButton = event.target.closest("[data-gd-design-close]");
      if (closeDesignButton && railEl.contains(closeDesignButton)) {
        closeDesignModal();
        return;
      }

      const productButton = event.target.closest("[data-gd-entry-product-handle]");
      if (productButton && railEl.contains(productButton)) {
        const productHandle = productButton.getAttribute("data-gd-entry-product-handle");
        onEntryCollectionProductSelect(productHandle);
        return;
      }

      const addDoorButton = event.target.closest("[data-gd-rail-add-door]");
      if (addDoorButton && railEl.contains(addDoorButton)) {
        addDoorFromCurrent();
        return;
      }

      const entryDropdownToggle = event.target.closest("[data-gd-entry-dropdown-toggle]");
      if (entryDropdownToggle && railEl.contains(entryDropdownToggle)) {
        const dropdownKey = entryDropdownToggle.getAttribute("data-gd-entry-dropdown-toggle");
        const willOpen = !isEntryDropdownOpen(dropdownKey);
        toggleEntryDropdown(dropdownKey);
        queueEntryDropdownFocus(dropdownKey, willOpen ? "selected-option" : "trigger");
        render();
        return;
      }

      const entryDropdownOption = event.target.closest("[data-gd-entry-dropdown-option-index]");
      if (entryDropdownOption && railEl.contains(entryDropdownOption)) {
        const optionIndex = entryDropdownOption.getAttribute("data-gd-entry-dropdown-option-index");
        const optionValue = entryDropdownOption.getAttribute("data-gd-entry-dropdown-option-value");
        queueEntryDropdownFocus(optionIndex, "trigger");
        onEntryOptionSelect(optionIndex, optionValue);
        return;
      }

      const entryOptionButton = event.target.closest("button[data-gd-entry-option-index][data-gd-entry-option-value]");
      if (entryOptionButton && railEl.contains(entryOptionButton)) {
        const optionIndex = entryOptionButton.getAttribute("data-gd-entry-option-index");
        const optionValue = entryOptionButton.getAttribute("data-gd-entry-option-value");
        onEntryOptionSelect(optionIndex, optionValue);
        return;
      }

      const windowCellButton = event.target.closest("[data-gd-window-cell]");
      if (windowCellButton && railEl.contains(windowCellButton)) {
        const stepId = windowCellButton.getAttribute("data-gd-window-step");
        const cellKey = windowCellButton.getAttribute("data-gd-window-cell");
        onWindowCellToggle(stepId, cellKey);
        return;
      }

      const optionButton = event.target.closest("[data-gd-option]");
      if (optionButton && railEl.contains(optionButton)) {
        const stepId = optionButton.getAttribute("data-gd-step-id");
        const optionId = optionButton.getAttribute("data-gd-option");
        onOptionSelect(stepId, optionId);
        return;
      }

      const learnButton = event.target.closest("[data-gd-step-learn]");
      if (learnButton && railEl.contains(learnButton)) {
        const stepId = learnButton.getAttribute("data-gd-step-learn");
        focusStep(stepId);
        return;
      }

      const toggleButton = event.target.closest("[data-gd-step-toggle]");
      if (toggleButton && railEl.contains(toggleButton)) {
        const stepId = toggleButton.getAttribute("data-gd-step-toggle");
        onStepToggle(stepId);
        return;
      }

      if (closedEntryDropdownFromRailClick) {
        const openDropdown = railEl.querySelector(".gd-entry-product__dropdown.is-open");
        if (openDropdown instanceof HTMLElement) {
          openDropdown.classList.remove("is-open");
          const openToggle = openDropdown.querySelector("[data-gd-entry-dropdown-toggle]");
          if (openToggle instanceof HTMLElement) {
            openToggle.setAttribute("aria-expanded", "false");
          }
        } else {
          render();
        }
      }
    });

    railEl.addEventListener("input", (event) => {
      if (handleSpecialTrackInput(event)) return;
      if (handleOrderFormInput(event, railEl)) {
        return;
      }

      const quantityInput = event.target.closest("[data-gd-entry-quantity-input]");
      if (quantityInput && railEl.contains(quantityInput)) {
        const normalizedInputValue = normalizeDoorQuantityInputValue(quantityInput.value, { allowEmpty: true });
        if (quantityInput.value !== normalizedInputValue) {
          quantityInput.value = normalizedInputValue;
        }
        state.designNavSectionId = "dimensions";
        if (normalizedInputValue) {
          setActiveDoorQuantity(normalizedInputValue);
        }
        renderDock();
        return;
      }

      const customInput = event.target.closest("[data-gd-entry-custom-input]");
      if (!customInput || !railEl.contains(customInput)) return;

      const part = customInput.getAttribute("data-gd-entry-custom-input");
      const normalizedInputValue = normalizeFeetInchesQuoteMarks(customInput.value);
      if (customInput.value !== normalizedInputValue) {
        customInput.value = normalizedInputValue;
      }
      state.designNavSectionId = "dimensions";
      state.entryContext.isCustomSize = true;
      setEntryCustomSizeValue(part, customInput.value);
      if (getEntryCustomSizeState().valid && state.hintTone === "error" && /custom|feet and inches/i.test(state.hintMessage || "")) {
        setHint("");
      }
      renderDock();
    });

    railEl.addEventListener("change", (event) => {
      if (handleSpecialTrackInput(event)) return;
      const entrySelect = event.target.closest("[data-gd-entry-select]");
      if (entrySelect && railEl.contains(entrySelect)) {
        const optionIndex = entrySelect.getAttribute("data-gd-entry-option-index");
        onEntryOptionSelect(optionIndex, entrySelect.value || "");
        return;
      }

      if (handleOrderFormChange(event, railEl)) {
        return;
      }

      const quantityInput = event.target.closest("[data-gd-entry-quantity-input]");
      if (quantityInput && railEl.contains(quantityInput)) {
        const normalizedInputValue = normalizeDoorQuantityInputValue(quantityInput.value);
        if (quantityInput.value !== normalizedInputValue) {
          quantityInput.value = normalizedInputValue;
        }
        state.designNavSectionId = "dimensions";
        setActiveDoorQuantity(normalizedInputValue);
        render();
        return;
      }

      const customInput = event.target.closest("[data-gd-entry-custom-input]");
      if (!customInput || !railEl.contains(customInput)) return;

      const part = customInput.getAttribute("data-gd-entry-custom-input");
      const normalizedInputValue = normalizeFeetInchesInputValue(customInput.value);
      if (customInput.value !== normalizedInputValue) {
        customInput.value = normalizedInputValue;
      }
      state.designNavSectionId = "dimensions";
      const parsed = parseFeetInchesInput(customInput.value);
      state.entryContext.isCustomSize = true;
      setEntryCustomSizeValue(part, parsed.valid ? parsed.normalized : customInput.value);
      if (getEntryCustomSizeState().valid && state.hintTone === "error" && /custom|feet and inches/i.test(state.hintMessage || "")) {
        setHint("");
      }
      render();
    });

    if (designSidebarEl) {
      designSidebarEl.addEventListener("click", (event) => {
        const closeDesignButton = event.target.closest("[data-gd-design-close]");
        if (closeDesignButton && designSidebarEl.contains(closeDesignButton)) {
          closeDesignModal();
          return;
        }

        const saveButton = event.target.closest("[data-gd-design-save]");
        if (saveButton && designSidebarEl.contains(saveButton)) {
          addDoorFromCurrent();
          return;
        }

        const navButton = event.target.closest("[data-gd-design-nav]");
        if (navButton && designSidebarEl.contains(navButton)) {
          const sectionId = navButton.getAttribute("data-gd-design-nav");
          const stepId = navButton.getAttribute("data-gd-design-step-id");
          focusDesignSection(sectionId, stepId);
        }
      });
    }

    document.addEventListener("click", (event) => {
      if (!state.entryDropdownOpenKey) return;
      const target = event.target;
      if (target instanceof Element && target.closest("[data-gd-entry-dropdown]")) return;
      closeEntryDropdown();
      render();
    });

    function suppressWindowCellMouseFocus(event, root) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const windowCellButton = target.closest("[data-gd-window-cell]");
      if (!windowCellButton || !root.contains(windowCellButton)) return;
      event.preventDefault();
    }

    railEl.addEventListener("mousedown", (event) => {
      suppressWindowCellMouseFocus(event, railEl);
    });

    previewEl.addEventListener("mousedown", (event) => {
      suppressWindowCellMouseFocus(event, previewEl);
    });

    previewEl.addEventListener("click", (event) => {
      const windowCellButton = event.target.closest("[data-gd-window-cell]");
      if (windowCellButton && previewEl.contains(windowCellButton)) {
        const stepId = windowCellButton.getAttribute("data-gd-window-step");
        const cellKey = windowCellButton.getAttribute("data-gd-window-cell");
        onWindowCellToggle(stepId, cellKey);
        return;
      }

      const openDesignButton = event.target.closest("[data-gd-open-design-modal]");
      if (openDesignButton && previewEl.contains(openDesignButton)) {
        openDesignModal();
        return;
      }

      const closeDesignButton = event.target.closest("[data-gd-design-close]");
      if (closeDesignButton && previewEl.contains(closeDesignButton)) {
        closeDesignModal();
        return;
      }

      const doorSelectButton = event.target.closest("[data-gd-door-select]");
      if (doorSelectButton && previewEl.contains(doorSelectButton)) {
        const doorId = doorSelectButton.getAttribute("data-gd-door-select");
        openDesignModal(doorId);
        return;
      }

      const duplicateDoorButton = event.target.closest("[data-gd-door-duplicate]");
      if (duplicateDoorButton && previewEl.contains(duplicateDoorButton)) {
        const doorId = duplicateDoorButton.getAttribute("data-gd-door-duplicate");
        duplicateDoorById(doorId);
        return;
      }

      const deleteDoorButton = event.target.closest("[data-gd-door-delete]");
      if (deleteDoorButton && previewEl.contains(deleteDoorButton)) {
        const doorId = deleteDoorButton.getAttribute("data-gd-door-delete");
        deleteDoorById(doorId);
        return;
      }

      const previewDoorCard = event.target.closest("[data-gd-door-card]");
      const previewDoorId = previewDoorCard ? String(previewDoorCard.getAttribute("data-gd-door-card") || "").trim() : "";
      if (previewDoorId) {
        openDesignModal(previewDoorId);
        return;
      }
    });

    const finePointerMq = typeof window.matchMedia === "function" ? window.matchMedia("(hover: hover) and (pointer: fine)") : null;
    function supportsPreviewHover(event) {
      if (event && event.pointerType === "touch") return false;
      if (event && (event.pointerType === "mouse" || event.pointerType === "pen")) return true;
      return finePointerMq ? finePointerMq.matches : true;
    }

    previewEl.addEventListener("pointermove", (event) => {
      if (!supportsPreviewHover(event)) return;
      if (clearWindowHoverCell()) updateWindowHoverGhost();
    });

    previewEl.addEventListener("pointerleave", () => {
      if (clearWindowHoverCell()) updateWindowHoverGhost();
    });

    previewEl.addEventListener("pointercancel", () => {
      if (clearWindowHoverCell()) updateWindowHoverGhost();
    });

    previewEl.addEventListener("pointerdown", (event) => {
      if (supportsPreviewHover(event)) return;
      if (clearWindowHoverCell()) updateWindowHoverGhost();
    });

    dockAddButton.addEventListener("click", (event) => {
      console.log("[gd-configurator] Quote Now clicked", {
        disabled: dockAddButton.disabled,
        buttonText: dockAddButton.textContent
      });
      addToCart(event);
    });

    if (layoutEl) {
      layoutEl.addEventListener("click", (event) => {
        if (!state.designModalOpen) return;
        if (event.target !== layoutEl) return;
        closeDesignModal();
      });
    }

    document.addEventListener("keydown", (event) => {
      if (state.entryDropdownOpenKey && event.key === "Escape") {
        event.preventDefault();
        const openKey = state.entryDropdownOpenKey;
        closeEntryDropdown();
        queueEntryDropdownFocus(openKey, "trigger");
        render();
        return;
      }
      if (!state.designModalOpen) return;
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeDesignModal();
    });

    function handleViewportChange() {
      const railSteps = getRailSteps();
      const firstStep = railSteps[0] || getVisibleSteps()[0] || null;
      if (!isMobileLayout() && !state.mobileOpenStepId) {
        state.mobileOpenStepId = state.activeStepId || (firstStep ? firstStep.id : "");
      }
      render();
    }

    if (typeof mobileMq.addEventListener === "function") {
      mobileMq.addEventListener("change", handleViewportChange);
    } else if (typeof mobileMq.addListener === "function") {
      mobileMq.addListener(handleViewportChange);
    }

    (async () => {
      logCustomerIdentity("boot-immediate");
      if (isApiOrderMode()) {
        await fetchCustomerContext();
        logCustomerIdentity("boot-after-customer-context");
      }
      await loadEntryProduct();
      await replaceTrackSystemOptionsFromCollection();
      await replaceWindowStepsFromCollection();
      applyEntryContextSelections();
      ensureInitialFocus();
      render();
      logCustomerIdentity("boot-after-render");

      root.setAttribute("data-gd-booted", "1");
      document.dispatchEvent(new CustomEvent("gd:booted"));
    })();
  } catch (error) {
    console.error("[gd-configurator] boot failure", error);
  }
})();
